if (!customElements.get('media-gallery')) {
  let productDesignStudioInstanceCounter = 0;
  const PRODUCT_DESIGN_MAX_FRAME_SIZE = 400;

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.selectedMediaIndex = 0;
        this.thumbHoverCleanup = null;
        this.thumbHoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.mediaPreloadHandle = null;
        this.markingEventsAbortController = null;
        this.designStudioState = null;
        this.designStudioOverlays = new WeakMap();
        this.designArtworkPlacements = new Map();
        this.designArtworkCopies = new Map();
        this.designActiveArtworkIds = new Map();
        this.designArtworkCopyCounter = 0;
        this.designArtworkClipCounter = 0;
        this.designViewSwitcher = null;
        this.designArtworkImageMetricKeys = new Map();
        this.designArtworkContentFrameBindings = new Map();
        this.designArtworkNativePlanes = new Map();
        this.designArtworkCoordinateModes = new Map();
        this.designArtworkRestoredPlanes = new Map();
        this.designArtworkImageUpdateModes = new Map();
        this.designZoneSourceKeys = new Map();
        this.designZoneOriginalIdentities = new Map();
        this.designArtworkHistory = new Map();
        this.designInteraction = null;
        this.designToolbarTimer = null;
        this.designControlsController = null;
        this.designControlsFrame = 0;
        this.designControlsTimers = new Map();
        this.designControlsPointer = null;
        this.designControlsTouchOverlay = null;
        this.designControlsPressedOverlay = null;
        this.designZoneIntroDone = false;
        this.designZoneIntroController = null;
        this.designZoneIntroObserver = null;
        this.designZoneIntroOverlay = null;
        this.designZoneIntroTargetController = null;
        this.designZoneIntroTimer = null;
        this.designImageZoom = 1;
        this.designImageZoomMin = 1;
        this.designImageZoomMax = 2;
        this.designImageZoomStep = 0.1;
        this.designImageZoomFrame = 0;
        this.designImagePanX = 0;
        this.designImagePanY = 0;
        this.designImagePanInteraction = null;
        this.designImageTrackpadZoomTimer = null;
        this.designImageTrackpadGesture = null;
        this.photoZoomEventsAbortController = null;
        this.photoZoomSlide = null;
        this.photoZoomVariantId = null;
        this.photoZoomImage = null;
        this.photoZoomOriginalSizes = null;
        this.designStudioResizeObserver = null;
        this.designStudioResizeFrame = 0;
        productDesignStudioInstanceCounter += 1;
        this.designStudioInstanceId = productDesignStudioInstanceCounter;
        this.pendingDesignViewKey = '';
        this.mediaViewMap = new Map();
        this.mediaViewSlideMap = new Map();
        this.activeVariantId = '';
        this.variantChangeUnsubscriber = null;
        this.completedVariantMediaNotification = null;
        this.strings = null;
        this.todayStudioConfig = undefined;
        this.todayVariantGallery = undefined;
        this.boundOnMarkingZoneChange = this.onMarkingZoneChange.bind(this);
        this.boundOnMarkingStateChange = this.onMarkingStateChange.bind(this);
        this.boundOnThemeVariantChange = this.onThemeVariantChange.bind(this);
      }

      readStrings() {
        const script = this.querySelector('[data-media-gallery-strings]');
        if (!script) return {};

        try {
          return JSON.parse(script.textContent || '{}');
        } catch (error) {
          console.warn('[Today Merch] Media gallery translations are invalid.', error);
          return {};
        }
      }

      string(key, replacements = {}) {
        if (!this.strings || !Object.keys(this.strings).length) this.strings = this.readStrings();
        const colorLabels = { fr: 'Couleurs', en: 'Colors', de: 'Farben', es: 'Colores', it: 'Colori', nl: 'Kleuren', pt: 'Cores' };
        const template = this.strings?.[key] || (key === 'colorsLabel' ? colorLabels[(document.documentElement.lang || 'fr').split('-')[0]] || colorLabels.en : '');
        return String(template).replace(/__([A-Z0-9_]+)__/g, (match, token) => (
          Object.prototype.hasOwnProperty.call(replacements, token) ? replacements[token] : match
        ));
      }

      supportsProductPhotoZoom() {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches ||
          (this.classList.contains('is-design-mode') && window.matchMedia('(min-width: 1100px)').matches);
      }

      getProductPhotoZoomContainer(event, allowArtwork = false) {
        const target = event.target;
        const container = target?.closest?.('.product__media-item-container');
        if (!container || !this.contains(container) || container.closest('[data-mobile-design-surface="true"]')) return null;
        if (target.closest('button, a, input, select, textarea, [contenteditable="true"], [data-role="product-design-image-zoom"], [data-role="product-design-toolbar"], [data-role="product-design-view-switcher"]')) return null;
        if (!allowArtwork && target.closest('[data-role="product-design-zone-shell"]')) return null;
        return this.canUseDesignImageTrackpadZoom(container) ? container : null;
      }

      bindProductPhotoZoomEvents() {
        if (this.photoZoomEventsAbortController) return;
        this.photoZoomEventsAbortController = new AbortController();
        const { signal } = this.photoZoomEventsAbortController;
        this.addEventListener('wheel', (event) => {
          const container = this.getProductPhotoZoomContainer(event, true);
          if (container) this.handleDesignImageTrackpadWheel(event, container);
        }, { passive: false, signal });
        this.addEventListener('gesturestart', (event) => {
          const container = this.getProductPhotoZoomContainer(event, true);
          if (container) this.startDesignImageTrackpadGesture(event, container);
        }, { passive: false, signal });
        this.addEventListener('gesturechange', (event) => this.moveDesignImageTrackpadGesture(event), { passive: false, signal });
        this.addEventListener('gestureend', (event) => this.endDesignImageTrackpadGesture(event), { passive: false, signal });
        this.addEventListener('pointerdown', (event) => {
          const container = this.getProductPhotoZoomContainer(event);
          if (container) this.startDesignImagePan(event, container);
        }, { signal });
        this.addEventListener('dragstart', (event) => {
          if (this.getProductPhotoZoomContainer(event)) event.preventDefault();
        }, { signal });
        this.addEventListener('dblclick', (event) => {
          const container = this.getProductPhotoZoomContainer(event);
          if (!container || event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          this.setDesignImageZoomAroundPoint(
            this.designImageZoom > this.designImageZoomMin ? this.designImageZoomMin : this.designImageZoomMax,
            event.clientX, event.clientY, container,
          );
        }, { signal });
        this.addEventListener('click', (event) => {
          const button = event.target.closest('.product__photo-image-zoom [data-design-image-zoom-action]');
          if (button) this.handleDesignImageZoom(event, button.dataset.designImageZoomAction);
        }, { signal });
        document.addEventListener('keydown', (event) => {
          if (event.metaKey || event.ctrlKey || event.altKey ||
            !['Escape', '+', '=', '-'].includes(event.key) || !this.supportsProductPhotoZoom() ||
            !this.classList.contains('has-design-image-zoom') ||
            (!this.contains(document.activeElement) && !this.matches(':hover')) ||
            event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
          if (event.key === 'Escape') {
            if (this.designImageZoom <= this.designImageZoomMin) return;
            event.preventDefault();
            this.setDesignImageZoom(this.designImageZoomMin);
          } else {
            this.handleDesignImageZoom(event, event.key === '-' ? 'decrease' : 'increase');
          }
        }, { signal });
        ['(hover: hover) and (pointer: fine)', '(min-width: 1100px)'].forEach((query) => {
          window.matchMedia(query).addEventListener('change', () => this.renderDesignStudio(), { signal });
        });
        if (this.initialized) this.renderDesignStudio();
      }

      syncProductPhotoZoom(slide) {
        if (slide !== this.photoZoomSlide || this.photoZoomVariantId !== this.activeVariantId) this.resetDesignImageZoom();
        this.photoZoomSlide = slide;
        this.photoZoomVariantId = this.activeVariantId;
        const container = slide?.querySelector('.product__media-item-container');
        const canZoom = this.supportsProductPhotoZoom() && slide?.dataset.mediaType === 'image' &&
          Boolean(container?.querySelector('.product__media > img'));
        this.querySelectorAll('.product__photo-image-zoom').forEach((control) => { control.hidden = true; });
        if (!canZoom) {
          this.resetDesignImageZoom();
          return;
        }
        slide.classList.add('is-design-image-zoom-target');
        this.classList.add('has-design-image-zoom');
        this.observeDesignStudioContainer(container);
        const overlay = this.designStudioOverlays.get(slide);
        if (!overlay || overlay.hidden) {
          let control = container.querySelector('.product__photo-image-zoom');
          if (!control) {
            container.insertAdjacentHTML('beforeend', this.getDesignImageZoomMarkup('product__photo-image-zoom'));
            control = container.querySelector('.product__photo-image-zoom');
          }
          control.hidden = false;
        }
        this.setDesignImageZoom(this.designImageZoom);
      }

      updateProductPhotoZoomSource() {
        const image = this.designImageZoom > this.designImageZoomMin
          ? this.getActiveMediaSlide()?.querySelector('.product__media > img[srcset]')
          : null;
        if (this.photoZoomImage && this.photoZoomImage !== image) {
          if (this.photoZoomOriginalSizes === null) this.photoZoomImage.removeAttribute('sizes');
          else this.photoZoomImage.setAttribute('sizes', this.photoZoomOriginalSizes);
          this.photoZoomImage = null;
        }
        if (!image) return;
        if (!this.photoZoomImage) {
          this.photoZoomImage = image;
          this.photoZoomOriginalSizes = image.getAttribute('sizes');
        }
        // Fetch a sharp source once for the maximum zoom, not on every pinch increment.
        const sizes = `${Math.ceil(image.offsetWidth * this.designImageZoomMax)}px`;
        if (image.getAttribute('sizes') !== sizes) image.setAttribute('sizes', sizes);
      }

      getDesignImageZoomMarkup(extraClass = '') {
        const label = (key) => this.escapeAttr(this.string(key));
        return `<div class="product__design-image-zoom ${extraClass}" data-role="product-design-image-zoom" role="group" aria-label="${label('imageZoomLabel')}">
            <button type="button" class="product__design-image-zoom-button" data-design-image-zoom-action="decrease" aria-label="${label('imageZoomOut')}" title="${label('imageZoomOut')}" disabled>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12h14"></path></svg>
            </button>
            <output class="product__design-image-zoom-value" data-role="product-design-image-zoom-value" aria-live="polite">100%</output>
            <button type="button" class="product__design-image-zoom-button" data-design-image-zoom-action="increase" aria-label="${label('imageZoomIn')}" title="${label('imageZoomIn')}">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14"></path></svg>
            </button>
          </div>`;
      }

      setDesignImageZoom(value) {
        const boundedValue = this.clampNumber(
          Number(value) || this.designImageZoomMin,
          this.designImageZoomMin,
          this.designImageZoomMax,
        );
        const normalizedValue = Math.round(boundedValue * 1_000) / 1_000;

        if (normalizedValue > this.designImageZoomMin && !this.designZoneIntroDone) this.stopDesignZoneIntro();
        this.designImageZoom = normalizedValue;
        this.updateProductPhotoZoomSource();
        this.style.setProperty('--product-design-image-scale', String(normalizedValue));
        this.dataset.designImageZoom = String(Math.round(normalizedValue * 100));
        if (normalizedValue <= this.designImageZoomMin) {
          this.setDesignImagePan(0, 0);
        } else {
          this.setDesignImagePan(this.designImagePanX, this.designImagePanY);
        }
        this.updateDesignImageZoomControls();
        this.updateDesignImagePanAvailability();
        this.syncDesignImageZoomOverlay();
      }

      getDesignImagePanBounds(slide = this.getActiveMediaSlide()) {
        const container = slide?.querySelector('.product__media-item-container');
        const image = container?.querySelector('.product__media > img');
        if (!container || !image) return { x: 0, y: 0 };

        const imageWidth = image.offsetWidth || 0;
        const imageHeight = image.offsetHeight || 0;
        const containerWidth = container.clientWidth || 0;
        const containerHeight = container.clientHeight || 0;
        return {
          x: Math.max(0, ((imageWidth * this.designImageZoom) - containerWidth) / 2),
          y: Math.max(0, ((imageHeight * this.designImageZoom) - containerHeight) / 2),
        };
      }

      setDesignImagePan(x = 0, y = 0) {
        const isDesktopZoom = this.supportsProductPhotoZoom();
        const bounds = isDesktopZoom && this.designImageZoom > this.designImageZoomMin
          ? this.getDesignImagePanBounds()
          : { x: Number.POSITIVE_INFINITY, y: Number.POSITIVE_INFINITY };
        const nextX = this.designImageZoom > this.designImageZoomMin
          ? this.clampNumber(Number(x) || 0, -bounds.x, bounds.x)
          : 0;
        const nextY = this.designImageZoom > this.designImageZoomMin
          ? this.clampNumber(Number(y) || 0, -bounds.y, bounds.y)
          : 0;

        if (nextX === this.designImagePanX && nextY === this.designImagePanY) return;
        this.designImagePanX = nextX;
        this.designImagePanY = nextY;
        this.style.setProperty('--product-design-image-pan-x', `${nextX}px`);
        this.style.setProperty('--product-design-image-pan-y', `${nextY}px`);
        this.dataset.designImagePanX = String(Math.round(nextX * 100) / 100);
        this.dataset.designImagePanY = String(Math.round(nextY * 100) / 100);
        this.syncDesignImageZoomOverlay();
      }

      updateDesignImagePanAvailability() {
        const activeSlide = this.getActiveMediaSlide();
        const canPan = Boolean(
          this.canUseDesignImageTrackpadZoom(activeSlide?.querySelector('.product__media-item-container')) &&
          this.designImageZoom > this.designImageZoomMin
        );

        this.classList.toggle('is-design-image-pannable', canPan);
        if (!canPan) this.cancelDesignImagePan();
      }

      startDesignImagePan(event, container) {
        if (
          !event.isPrimary || event.button !== 0 ||
          (event.pointerType === 'touch' && !this.classList.contains('is-design-mode')) ||
          this.designImageZoom <= this.designImageZoomMin ||
          !this.canUseDesignImageTrackpadZoom(container) ||
          !this.getProductPhotoZoomContainer(event)
        ) return;

        event.preventDefault();
        event.stopPropagation();
        this.cancelDesignImagePan();
        container.setPointerCapture?.(event.pointerId);
        this.classList.add('is-design-image-panning');

        const interaction = {
          pointerId: event.pointerId,
          container,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startPanX: this.designImagePanX,
          startPanY: this.designImagePanY,
        };
        const move = (moveEvent) => {
          if (moveEvent.pointerId !== interaction.pointerId) return;
          moveEvent.preventDefault();
          interaction.lastPoint = { x: moveEvent.clientX, y: moveEvent.clientY };
          if (!interaction.frame) interaction.frame = window.requestAnimationFrame(() => {
            interaction.frame = 0;
            if (this.designImagePanInteraction === interaction) this.flushDesignImagePan(interaction);
          });
        };
        const end = (endEvent) => {
          if (endEvent.pointerId !== interaction.pointerId) return;
          // Commit the last sampled point even when release occurs before the frame.
          this.cancelDesignImagePan({ flush: true });
        };

        interaction.move = move;
        interaction.end = end;
        interaction.blur = () => this.cancelDesignImagePan({ flush: true });
        this.designImagePanInteraction = interaction;
        window.addEventListener('pointermove', move, { passive: false });
        window.addEventListener('pointerup', end);
        window.addEventListener('blur', interaction.blur);
        window.addEventListener('pointercancel', end);
        container.addEventListener('lostpointercapture', end);
      }

      flushDesignImagePan(interaction) {
        const point = interaction?.lastPoint;
        if (!point) return;
        interaction.lastPoint = null;
        this.setDesignImagePan(
          interaction.startPanX + point.x - interaction.startClientX,
          interaction.startPanY + point.y - interaction.startClientY,
        );
      }

      cancelDesignImagePan({ flush = false } = {}) {
        const interaction = this.designImagePanInteraction;
        if (interaction) {
          window.cancelAnimationFrame(interaction.frame);
          if (flush) this.flushDesignImagePan(interaction);
          window.removeEventListener('pointermove', interaction.move);
          window.removeEventListener('pointerup', interaction.end);
          window.removeEventListener('blur', interaction.blur);
          window.removeEventListener('pointercancel', interaction.end);
          interaction.container?.removeEventListener('lostpointercapture', interaction.end);
          if (interaction.container?.hasPointerCapture?.(interaction.pointerId)) {
            interaction.container.releasePointerCapture(interaction.pointerId);
          }
        }
        this.designImagePanInteraction = null;
        this.classList.remove('is-design-image-panning');
      }

      canUseDesignImageTrackpadZoom(container) {
        const slide = container?.closest('.product__media-item');
        return Boolean(
          this.supportsProductPhotoZoom() &&
          slide?.dataset.mediaType === 'image' &&
          slide === this.getActiveMediaSlide() &&
          slide.classList.contains('is-design-image-zoom-target')
        );
      }

      beginDesignImageTrackpadZoom() {
        window.clearTimeout(this.designImageTrackpadZoomTimer);
        this.designImageTrackpadZoomTimer = null;
        this.classList.add('is-design-image-trackpad-zooming');
      }

      finishDesignImageTrackpadZoom(delay = 160) {
        window.clearTimeout(this.designImageTrackpadZoomTimer);
        this.designImageTrackpadZoomTimer = window.setTimeout(() => {
          this.designImageTrackpadZoomTimer = null;
          this.classList.remove('is-design-image-trackpad-zooming');
        }, delay);
      }

      cancelDesignImageTrackpadZoom() {
        window.clearTimeout(this.designImageTrackpadZoomTimer);
        this.designImageTrackpadZoomTimer = null;
        this.designImageTrackpadGesture = null;
        this.classList.remove('is-design-image-trackpad-zooming');
      }

      setDesignImageZoomAroundPoint(value, clientX, clientY, container) {
        const previousZoom = this.designImageZoom;
        const nextZoom = Math.round(this.clampNumber(
          Number(value) || previousZoom,
          this.designImageZoomMin,
          this.designImageZoomMax,
        ) * 1_000) / 1_000;
        if (!container || !previousZoom) {
          this.setDesignImageZoom(nextZoom);
          return;
        }

        const rect = container.getBoundingClientRect();
        const anchorX = (Number.isFinite(clientX) ? clientX : rect.left + (rect.width / 2)) - (rect.left + (rect.width / 2));
        const anchorY = (Number.isFinite(clientY) ? clientY : rect.top + (rect.height / 2)) - (rect.top + (rect.height / 2));
        const zoomRatio = nextZoom / previousZoom;
        const nextPanX = anchorX - ((anchorX - this.designImagePanX) * zoomRatio);
        const nextPanY = anchorY - ((anchorY - this.designImagePanY) * zoomRatio);

        this.setDesignImageZoom(nextZoom);
        this.setDesignImagePan(nextPanX, nextPanY);
      }

      handleDesignImageTrackpadWheel(event, container) {
        if (
          !event.cancelable ||
          (!event.ctrlKey && this.designImageZoom <= this.designImageZoomMin) ||
          !this.canUseDesignImageTrackpadZoom(container) ||
          !this.getProductPhotoZoomContainer(event, true)
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (this.designImageTrackpadGesture) return;
        const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? Math.max(container.clientHeight, 1)
            : 1;
        const normalizedDelta = this.clampNumber(event.deltaY * deltaMultiplier, -120, 120);
        const zoomFactor = Math.exp(-normalizedDelta * 0.006);

        this.beginDesignImageTrackpadZoom();
        this.setDesignImageZoomAroundPoint(
          this.designImageZoom * zoomFactor,
          event.clientX,
          event.clientY,
          container,
        );
        this.finishDesignImageTrackpadZoom();
      }

      startDesignImageTrackpadGesture(event, container) {
        if (!event.cancelable || !this.canUseDesignImageTrackpadZoom(container) ||
          !this.getProductPhotoZoomContainer(event, true)) return;

        event.preventDefault();
        event.stopPropagation();
        this.designImageTrackpadGesture = {
          container,
          startZoom: this.designImageZoom,
          clientX: event.clientX,
          clientY: event.clientY,
        };
        this.beginDesignImageTrackpadZoom();
      }

      moveDesignImageTrackpadGesture(event) {
        const gesture = this.designImageTrackpadGesture;
        if (!gesture || !event.cancelable) return;

        event.preventDefault();
        event.stopPropagation();
        this.beginDesignImageTrackpadZoom();
        this.setDesignImageZoomAroundPoint(
          gesture.startZoom * (Number(event.scale) || 1),
          Number.isFinite(event.clientX) ? event.clientX : gesture.clientX,
          Number.isFinite(event.clientY) ? event.clientY : gesture.clientY,
          gesture.container,
        );
      }

      endDesignImageTrackpadGesture(event) {
        if (!this.designImageTrackpadGesture) return;

        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        this.designImageTrackpadGesture = null;
        this.finishDesignImageTrackpadZoom();
      }

      syncDesignImageZoomOverlay() {
        if (this.designImageZoomFrame) window.cancelAnimationFrame(this.designImageZoomFrame);
        this.designImageZoomFrame = 0;

        const activeOverlay = this.designStudioOverlays.get(this.getActiveMediaSlide());
        if (!activeOverlay || activeOverlay.hidden) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const directManipulation = this.classList.contains('is-design-image-trackpad-zooming') ||
          this.classList.contains('is-design-image-panning');
        const duration = reducedMotion || directManipulation ? 0 : 360;
        const minimumFrames = directManipulation ? 1 : 3;
        const startedAt = window.performance.now();
        let layoutFrames = 0;
        const updateLayout = (timestamp) => {
          this.designImageZoomFrame = 0;
          if (!this.isConnected) return;

          const slide = this.getActiveMediaSlide();
          const overlay = this.designStudioOverlays.get(slide) || slide?.querySelector('[data-role="product-design-studio"]');
          if (overlay && !overlay.hidden) this.updateDesignArtworkToolbar(overlay);

          // Re-measure after style application and the reduced-motion transform have both settled.
          layoutFrames += 1;
          if (timestamp - startedAt < duration || layoutFrames < minimumFrames) {
            this.designImageZoomFrame = window.requestAnimationFrame(updateLayout);
          }
        };

        this.designImageZoomFrame = window.requestAnimationFrame(updateLayout);
      }

      resetDesignImageZoom() {
        if (this.designImageZoomFrame) window.cancelAnimationFrame(this.designImageZoomFrame);
        this.designImageZoomFrame = 0;
        this.designImageZoom = this.designImageZoomMin;
        this.updateProductPhotoZoomSource();
        this.cancelDesignImagePan();
        this.cancelDesignImageTrackpadZoom();
        this.designImagePanX = 0;
        this.designImagePanY = 0;
        this.style.removeProperty('--product-design-image-scale');
        this.style.removeProperty('--product-design-image-pan-x');
        this.style.removeProperty('--product-design-image-pan-y');
        delete this.dataset.designImageZoom;
        delete this.dataset.designImagePanX;
        delete this.dataset.designImagePanY;
        this.classList.remove('has-design-image-zoom', 'is-design-image-pannable');
        this.querySelectorAll('.is-design-image-zoom-target').forEach((slide) => {
          slide.classList.remove('is-design-image-zoom-target');
        });
        this.updateDesignImageZoomControls();
      }

      updateDesignImageZoomControls(root = this) {
        const percentage = Math.round(this.designImageZoom * 100);
        const atMinimum = this.designImageZoom <= this.designImageZoomMin;
        const atMaximum = this.designImageZoom >= this.designImageZoomMax;

        root.querySelectorAll('[data-role="product-design-image-zoom"]').forEach((control) => {
          control.dataset.zoomLevel = String(percentage);

          const output = control.querySelector('[data-role="product-design-image-zoom-value"]');
          if (output) {
            output.value = `${percentage}%`;
            output.textContent = `${percentage}%`;
          }

          const decreaseButton = control.querySelector('[data-design-image-zoom-action="decrease"]');
          const increaseButton = control.querySelector('[data-design-image-zoom-action="increase"]');
          if (decreaseButton) decreaseButton.disabled = atMinimum;
          if (increaseButton) increaseButton.disabled = atMaximum;
        });
      }

      handleDesignImageZoom(event, action) {
        event.preventDefault();
        event.stopPropagation();

        const direction = action === 'increase' ? 1 : -1;
        this.setDesignImageZoom(this.designImageZoom + (this.designImageZoomStep * direction));
      }

      connectedCallback() {
        this.productInfoOwner = this.closest('product-info');
        if (!this.zoneInteractions && window.TodayMerchProductZoneInteractions) {
          this.zoneInteractions = new window.TodayMerchProductZoneInteractions(this);
        }
        this.bindProductPhotoZoomEvents();
        this.bindDesignControlsVisibilityEvents();
        this.setupMobileDesignEditor();
        this.setupDesignZoneIntro();
        this.bindMarkingStudioEvents();
        this.bindVariantMediaEvents();
        this.querySelectorAll('[data-role="product-design-studio"]').forEach((overlay) => {
          this.observeDesignStudioContainer(overlay.parentElement);
        });
        if (this.designViewSwitcher?.isConnected) this.updateDesignViewGlider(this.designViewSwitcher);

        this.prepareVariantMediaReveal();
        if (this.closest('product-info[id^="MainProduct"]')) {
          this.init();
          return;
        }

        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      disconnectedCallback() {
        this.completedVariantMediaNotification = null;
        this.inactiveArtworkMetricLoads?.clear();
        this.zoneInteractions?.destroy();
        this.zoneInteractions = null;
        this.querySelectorAll('[data-role="product-design-artwork"], [data-role="product-design-artwork-copy"]')
          .forEach(artwork => window.TodayMerchProductPreviewEffects?.restore(artwork));
        this.mobileDesignEditor?.dialog?.querySelectorAll('[data-role="product-design-artwork"], [data-role="product-design-artwork-copy"]')
          .forEach(artwork => window.TodayMerchProductPreviewEffects?.restore(artwork));
        this.cancelDesignArtworkInteraction();
        this.designControlsController?.abort();
        this.designControlsController = null;
        window.cancelAnimationFrame(this.designControlsFrame);
        this.designControlsFrame = 0;
        this.designControlsTimers.forEach((timer) => window.clearTimeout(timer));
        this.designControlsTimers.clear();
        this.designControlsPointer = null;
        this.designControlsTouchOverlay = null;
        this.designControlsPressedOverlay = null;
        this.photoZoomEventsAbortController?.abort();
        this.photoZoomEventsAbortController = null;
        this.resetDesignImageZoom();
        this.photoZoomSlide = null;
        this.productInfoOwner?.dispatchEvent(new CustomEvent('todaymerch:mobile-design-editor-disconnected', { bubbles: true }));
        this.mobileDesignEditor?.destroy();
        this.mobileDesignEditor = null;
        this.mobileDesignEditorRequest = null;
        window.cancelAnimationFrame(this.mobileDesignEditorFrame);
        this.mobileDesignEditorFrame = 0;
        this.clearMobileDesignZoneCue();
        this.stopDesignZoneIntro();
        this.markingEventsAbortController?.abort();
        this.markingEventsAbortController = null;
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
        this.cancelMediaPreload();
        this.cancelDesignImagePan();
        this.cancelDesignImageTrackpadZoom();
        this.teardownDesignStudioResizeObserver();
        this.teardownDesignViewSwitcher();
      }

      setupMobileDesignEditor() {
        // A loaded script alone is insufficient: the lazy group's stylesheet must be ready too.
        if (window.TodayMerchProductModules?.has(this.closest('product-info'), 'editor') && !this.mobileDesignEditorAssetsReady) return;
        if (this.dataset.context === 'quickview' || this.mobileDesignEditor || !window.TodayMerchMobileDesignEditor) return;
        this.mobileDesignEditor = new window.TodayMerchMobileDesignEditor(this);
      }

      ensureMobileDesignEditor() {
        this.setupMobileDesignEditor();
        if (this.mobileDesignEditor) return Promise.resolve(this.mobileDesignEditor);
        const modules = window.TodayMerchProductModules;
        const root = this.closest('product-info');
        if (this.dataset.context === 'quickview' || !modules?.has(root, 'editor')) return Promise.resolve(null);
        if (!this.mobileDesignEditorLoad) {
          this.mobileDesignEditorLoad = modules.ensure(root, 'editor').then(() => {
            if (!this.isConnected) return null;
            this.mobileDesignEditorAssetsReady = true;
            this.setupMobileDesignEditor();
            return this.mobileDesignEditor;
          }).finally(() => { this.mobileDesignEditorLoad = null; });
        }
        return this.mobileDesignEditorLoad;
      }

      isDesignControlsTarget(overlay, target) {
        return Boolean(target?.closest?.('[data-role="product-design-zone-shell"], [data-role="product-design-toolbar"]')
          && overlay.contains(target));
      }

      bindDesignControlsVisibilityEvents() {
        if (this.designControlsController) return;
        this.designControlsController = new AbortController();
        const { signal } = this.designControlsController;
        const update = () => {
          window.cancelAnimationFrame(this.designControlsFrame);
          this.designControlsFrame = 0;
          this.syncDesignControlsVisibility();
        };
        const schedule = () => this.scheduleDesignControlsVisibility();
        const productRoot = this.closest('product-info') || this.getProductRoot();
        productRoot.addEventListener('todaymerch:artwork-colors-visibility', (event) => {
          const detail = event.detail || {};
          this.designColorsContext = detail.open ? { zoneKey: detail.zoneKey, artworkId: detail.artworkId } : null;
          const mobileButton = this.mobileDesignEditor?.dialog?.querySelector('[data-mobile-design-action="colors"]');
          mobileButton?.setAttribute('aria-expanded', String(Boolean(detail.open
            && this.mobileDesignEditor.context?.zone?.key === detail.zoneKey
            && this.mobileDesignEditor.context?.artworkId === detail.artworkId)));
          this.querySelectorAll('[data-design-tool="colors"]').forEach((button) => {
            const overlay = button.closest('[data-role="product-design-studio"]');
            button.setAttribute('aria-expanded', String(Boolean(detail.open && overlay?.dataset.zoneKey === detail.zoneKey
              && overlay?.dataset.artworkId === detail.artworkId)));
          });
          update();
        }, { signal });
        productRoot.addEventListener('todaymerch:artwork-colors-committed', (event) => {
          this.recordDesignArtworkColorHistory(event.detail || {});
        }, { signal });
        document.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'touch') return;
          this.designControlsPointer = { x: event.clientX, y: event.clientY };
          this.designControlsTouchOverlay = null;
          schedule();
        }, { passive: true, signal });
        document.addEventListener('pointerdown', (event) => {
          const overlay = event.target.closest?.('[data-role="product-design-studio"]');
          const selected = overlay && this.contains(overlay) && this.isDesignControlsTarget(overlay, event.target)
            ? overlay : null;
          this.designControlsPressedOverlay = selected;
          if (event.pointerType === 'touch') {
            this.designControlsPointer = null;
            this.designControlsTouchOverlay = selected;
          }
          update();
        }, { capture: true, passive: true, signal });
        const release = () => {
          this.designControlsPressedOverlay = null;
          update();
        };
        document.addEventListener('pointerup', release, { signal });
        document.addEventListener('pointercancel', release, { signal });
        document.addEventListener('pointerout', (event) => {
          if (event.relatedTarget) return;
          this.designControlsPointer = null;
          update();
        }, { signal });
        document.addEventListener('focusin', update, { signal });
        document.addEventListener('focusout', () => queueMicrotask(update), { signal });
        window.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
        window.addEventListener('blur', () => {
          this.designControlsPointer = null;
          this.designControlsPressedOverlay = null;
          update();
        }, { signal });
      }

      scheduleDesignControlsVisibility() {
        if (this.designControlsFrame || !this.isConnected) return;
        this.designControlsFrame = window.requestAnimationFrame(() => {
          this.designControlsFrame = 0;
          this.syncDesignControlsVisibility();
        });
      }

      isPointerWithinDesignControls(overlay) {
        const point = this.designControlsPointer;
        if (!point) return false;
        const shell = overlay.querySelector('[data-role="product-design-zone-shell"]');
        const toolbar = overlay.querySelector('[data-role="product-design-toolbar"]');
        const within = (rect) => rect && point.x >= rect.left && point.x <= rect.right
          && point.y >= rect.top && point.y <= rect.bottom;
        const zoneRect = shell?.getBoundingClientRect();
        if (within(zoneRect)) return true;
        const hit = document.elementFromPoint(point.x, point.y);
        if (this.isDesignControlsTarget(overlay, hit)) return true;
        if (!toolbar || toolbar.hidden || overlay.classList.contains('is-controls-idle')) return false;
        const toolbarRect = toolbar.getBoundingClientRect();
        // Keep the route to the floating tools open even when the pointer crosses slowly.
        return within({
          left: Math.min(zoneRect.left, toolbarRect.left),
          right: Math.max(zoneRect.right, toolbarRect.right),
          top: Math.min(zoneRect.top, toolbarRect.top),
          bottom: Math.max(zoneRect.bottom, toolbarRect.bottom),
        });
      }

      shouldShowDesignControls(overlay) {
        const focus = document.activeElement;
        const keyboardFocus = this.isDesignControlsTarget(overlay, focus) && focus.matches(':focus-visible');
        const colorsOpen = this.designColorsContext?.zoneKey === overlay.dataset.zoneKey
          && this.designColorsContext?.artworkId === overlay.dataset.artworkId;
        return colorsOpen || overlay.classList.contains('is-manipulating') || overlay.classList.contains('is-removing-background')
          || this.designControlsPressedOverlay === overlay || this.designControlsTouchOverlay === overlay
          || keyboardFocus || this.isPointerWithinDesignControls(overlay);
      }

      syncDesignControlsVisibility() {
        if (!this.isConnected) return;
        this.querySelectorAll('[data-role="product-design-studio"]').forEach((overlay) => {
          if (overlay.hidden || overlay.dataset.mobileDesignSurface === 'true') return;
          if (this.shouldShowDesignControls(overlay)) {
            window.clearTimeout(this.designControlsTimers.get(overlay));
            this.designControlsTimers.delete(overlay);
            overlay.classList.remove('is-controls-idle');
            if (overlay.classList.contains('is-previewing')) this.setDesignArtworkSelected(overlay, true);
          } else if (!overlay.classList.contains('is-controls-idle') && !this.designControlsTimers.has(overlay)) {
            this.designControlsTimers.set(overlay, window.setTimeout(() => {
              this.designControlsTimers.delete(overlay);
              if (!this.shouldShowDesignControls(overlay)) overlay.classList.add('is-controls-idle');
            }, 200));
          }
        });
      }

      scheduleMobileDesignEditorSync() {
        if (!this.isConnected || this.mobileDesignEditorFrame) return;
        this.mobileDesignEditorFrame = window.requestAnimationFrame(() => {
          this.mobileDesignEditorFrame = 0;
          this.setupMobileDesignEditor();
          if (this.mobileDesignEditor) { this.mobileDesignEditor.sync(); return; }
          const mobile = window.matchMedia('(max-width: 1099.98px) and (pointer: coarse)').matches;
          const needsEditor = (this.designStudioState?.zones || []).some(zone => zone.supportsLogo
            && (zone.previewUrl || zone.artworks?.some(artwork => artwork.previewUrl))
            && (mobile || this.getSupplierPrintPlane(zone) && !this.hasDesignPhotoSurface(zone)));
          if (needsEditor && !this.mobileDesignEditorSyncPending) {
            this.mobileDesignEditorSyncPending = true;
            this.ensureMobileDesignEditor().then(editor => editor?.sync()).catch(() => {}).finally(() => {
              this.mobileDesignEditorSyncPending = false;
            });
          }
        });
      }

      requestMobileDesignEditor(request = {}) {
        this.setupMobileDesignEditor();
        if (this.mobileDesignEditor) return this.mobileDesignEditor.requestOpen(request);
        const root = this.closest('product-info');
        const modules = window.TodayMerchProductModules;
        if (!modules?.has(root, 'editor')) return;
        const intent = {
          variantId: String(this.activeVariantId || this.designStudioState?.variantId || ''),
          zoneKey: this.designStudioState?.activeZoneKey,
          ...request,
        };
        this.mobileDesignEditorRequest = intent;
        modules.report?.(root, 'loading');
        return this.ensureMobileDesignEditor().then(editor => {
          if (!this.isConnected || this.mobileDesignEditorRequest !== intent) return;
          if (String(this.activeVariantId || this.designStudioState?.variantId || '') !== String(intent.variantId)) return;
          return editor?.requestOpen(intent);
        }).catch(() => {
          if (this.isConnected && this.mobileDesignEditorRequest === intent) modules.report?.(root, 'error');
        }).finally(() => {
          if (this.mobileDesignEditorRequest === intent && this.mobileDesignEditor) modules.clearReport?.(root);
        });
      }

      isQualifiedXDDesignZone(zone) {
        return zone?.supplierGeometry === true
          && this.closest('product-info')?.dataset.personalizationExperience === 'xd-v2';
      }

      getMobileDesignEditorContext(zoneKey = this.designStudioState?.activeZoneKey) {
        if (!this.isDesignStudioStateForActiveVariant()) return null;
        const zones = this.designStudioState?.zones || [];
        const zone = zones.find((item) => item.key === zoneKey && item.supportsLogo)
          || (!zoneKey ? zones.find((item) => item.supportsLogo) : null);
        if (!zone || !this.getConfiguredDesignZonePlacement(zone)) return null;
        const supplierPlaneEditor = Boolean(this.getSupplierPrintPlane(zone)) && !this.hasDesignPhotoSurface(zone);
        const slides = [...this.querySelectorAll('.product__media-item')];
        const activeSlide = this.getActiveMediaSlide();
        const slide = [activeSlide, ...slides].find((item) => item?.dataset.mediaType === 'image'
          && !item.hidden && (supplierPlaneEditor || this.isZoneVisibleInView(zone, item.dataset.markingView, item)));
        const container = slide?.querySelector('.product__media-item-container');
        const image = container?.querySelector('.product__media > img');
        if (!image) return null;
        this.syncDesignArtworkSources(zone);
        const artworkId = this.getActiveDesignArtworkId(zone.key);
        const styles = window.getComputedStyle(image);
        return {
          variantId: String(this.activeVariantId || this.designStudioState.variantId || ''),
          zone, slide, artworkId, supplierPlaneEditor,
          artworks: this.getDesignArtworkItems(zone),
          placement: this.getDesignArtworkPlacement(zone, artworkId),
          source: this.getDesignArtworkSource(zone, artworkId),
          productImage: supplierPlaneEditor ? { src: '', alt: '', aspectRatio: 1 } : {
            src: image.currentSrc || image.src || image.dataset.src || '',
            alt: image.alt || '',
            objectFit: styles.objectFit || 'contain',
            objectPosition: styles.objectPosition || '50% 50%',
            aspectRatio: this.isQualifiedXDDesignZone(zone)
              && container.dataset.xdV2QualifiedPhoto === 'true' && image.naturalWidth > 0 && image.naturalHeight > 0
              ? image.naturalWidth / image.naturalHeight
              : container.clientWidth && container.clientHeight
                ? container.clientWidth / container.clientHeight
                : Number.parseFloat(getComputedStyle(container).aspectRatio) || (image.naturalWidth / image.naturalHeight) || 1,
          },
        };
      }

      renderMobileDesignSurface(slide, zone) {
        if (!slide || !zone) return null;
        slide.dataset.mobileDesignSurface = 'true';
        return this.renderDesignStudioOverlay(slide, zone, { mobileSurface: true });
      }

      isMobileDesignArtworkReady(context) {
        const detached = this.mobileDesignEditor?.context?.zone.key === context.zone.key ? this.mobileDesignEditor?.overlay : null;
        const image = (context.supplierPlaneEditor ? detached : this.designStudioOverlays.get(context.slide))
          ?.querySelector('[data-role="product-design-artwork-image"]');
        if (!image?.complete || !image.naturalWidth || image.getAttribute('src') !== context.source.previewUrl) return false;
        const stateKey = this.getDesignArtworkStateKey(context.zone.key, context.artworkId);
        return this.designArtworkImageMetricKeys.get(stateKey) === this.getDesignArtworkImageMetricKey(context.zone, image, context.source);
      }

      clearMobileDesignZoneCue() {
        this.mobileDesignZoneCueController?.abort();
        this.mobileDesignZoneCueController = null;
        this.mobileDesignZoneCueObserver?.disconnect();
        this.mobileDesignZoneCueObserver = null;
        window.clearTimeout(this.mobileDesignZoneCueTimer);
        this.mobileDesignZoneCue?.remove();
        this.mobileDesignZoneCue = null;
      }

      showMobileDesignZoneCue(zoneKey) {
        this.clearMobileDesignZoneCue();
        if (!this.isConnected) return;
        if (!window.matchMedia('(max-width: 1099.98px) and (pointer: coarse)').matches) return;
        if (this.mobileDesignEditor?.dialog?.open) return;
        const zone = this.designStudioState?.zones?.find((item) => item.key === zoneKey && item.supportsLogo);
        const slide = this.getActiveMediaSlide();
        const placement = this.getConfiguredDesignZonePlacement(zone);
        if (!this.isDesignStudioStateForActiveVariant() || !zone || !placement || !slide ||
            !this.isZoneVisibleInView(zone, slide.dataset.markingView, slide)) return;
        const container = slide.querySelector('.product__media-item-container');
        const image = container.querySelector('.product__media > img');
        if (!image) return;
        const cue = document.createElement('div');
        cue.className = 'product__design-studio product__design-zone-cue';
        cue.setAttribute('aria-hidden', 'true');
        cue.innerHTML = '<div class="product__design-zone-shell" data-role="product-design-zone-shell"><div class="product__design-zone" data-role="product-design-zone"><span class="product__design-zone-parts" data-role="product-design-zone-parts" hidden></span></div></div>';
        for (const key of ['x', 'y', 'width', 'height']) cue.style.setProperty(`--product-design-zone-${key}`, `${placement[key]}%`);
        container.append(cue);
        this.mobileDesignZoneCue = cue;
        this.renderDesignZoneParts(cue, zone, placement);
        const controller = new AbortController();
        this.mobileDesignZoneCueController = controller;
        const { signal } = controller;
        let visible = false;
        let started = false;
        const update = () => {
          if (started || !visible || document.hidden || !image.complete || !image.naturalWidth) return;
          started = true;
          cue.classList.add(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'is-static' : 'is-pulsing');
          this.mobileDesignZoneCueTimer = window.setTimeout(() => this.clearMobileDesignZoneCue(), 2000);
        };
        if ('IntersectionObserver' in window) {
          this.mobileDesignZoneCueObserver = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting && entry.intersectionRatio >= 0.65;
            update();
          }, { threshold: [0, 0.65] });
          this.mobileDesignZoneCueObserver.observe(cue.querySelector('[data-role="product-design-zone-shell"]'));
        } else { visible = true; }
        image.addEventListener('load', update, { signal });
        document.addEventListener('visibilitychange', update, { signal });
        container.addEventListener('pointerdown', () => this.clearMobileDesignZoneCue(), { signal, once: true });
        update();
      }

      setupDesignZoneIntro() {
        if (this.designZoneIntroDone || this.designZoneIntroController) return;
        if (this.dataset.context === 'quickview' || !('IntersectionObserver' in window)) {
          this.designZoneIntroDone = true;
          return;
        }

        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (motionQuery.matches) {
          this.designZoneIntroDone = true;
          return;
        }

        this.designZoneIntroController = new AbortController();
        const { signal } = this.designZoneIntroController;
        const root = this.getProductRoot();
        const stop = (event) => {
          // A touch scroll may be bringing the product into view for the first time.
          if (event.type === 'pointerdown' && event.pointerType === 'touch' &&
              !this.designZoneIntroOverlay?.classList.contains('is-pulsing') &&
              !event.target.closest('button, a, input, select, textarea, [role="button"], [tabindex]')) return;
          this.stopDesignZoneIntro();
        };
        ['pointerdown', 'click', 'keydown', 'change'].forEach((type) => {
          root.addEventListener(type, stop, { capture: true, passive: true, signal });
        });
        if (typeof motionQuery.addEventListener === 'function') {
          motionQuery.addEventListener('change', stop, { signal });
        } else if (typeof motionQuery.addListener === 'function') {
          motionQuery.addListener(stop);
          signal.addEventListener('abort', () => motionQuery.removeListener(stop), { once: true });
        }
      }

      stopDesignZoneIntro() {
        this.designZoneIntroDone = true;
        this.designZoneIntroController?.abort();
        this.designZoneIntroController = null;
        this.clearDesignZoneIntro();
      }

      clearDesignZoneIntro() {
        window.clearTimeout(this.designZoneIntroTimer);
        this.designZoneIntroTimer = null;
        this.designZoneIntroObserver?.disconnect();
        this.designZoneIntroObserver = null;
        this.designZoneIntroTargetController?.abort();
        this.designZoneIntroTargetController = null;
        this.designZoneIntroOverlay?.remove();
        this.designZoneIntroOverlay = null;
      }

      syncDesignZoneIntro() {
        if (this.designZoneIntroDone || !this.designZoneIntroController) return;
        if (this.designStudioState?.hasLogo) {
          this.stopDesignZoneIntro();
          return;
        }

        const zone = this.getDesignZoneIntroTarget();
        const slide = this.getActiveMediaSlide();
        const placement = this.getConfiguredDesignZonePlacement(zone);
        const container = slide?.querySelector('.product__media-item-container');
        const image = container?.querySelector('.product__media > img');
        if (!this.isDesignStudioStateForActiveVariant() || !zone || !placement || !image ||
            slide.dataset.mediaType !== 'image' || !this.isZoneVisibleInView(zone, slide.dataset.markingView, slide)) {
          this.clearDesignZoneIntro();
          return;
        }
        if (this.designZoneIntroOverlay?.parentElement === container &&
            this.designZoneIntroOverlay.dataset.zoneKey === zone.key) return;
        this.clearDesignZoneIntro();

        // Show the saved printing geometry without entering the interactive logo editor.
        const overlay = document.createElement('div');
        overlay.className = 'product__design-studio product__design-zone-intro';
        overlay.dataset.zoneKey = zone.key;
        overlay.setAttribute('aria-hidden', 'true');
        overlay.innerHTML = `
          <div class="product__design-zone-shell" data-role="product-design-zone-shell">
            <div class="product__design-zone" data-role="product-design-zone">
              <span class="product__design-zone-parts" data-role="product-design-zone-parts" hidden></span>
            </div>
          </div>
        `;
        for (const key of ['x', 'y', 'width', 'height']) {
          overlay.style.setProperty(`--product-design-zone-${key}`, `${placement[key]}%`);
        }
        container.append(overlay);
        this.designZoneIntroOverlay = overlay;
        this.renderDesignZoneParts(overlay, zone, placement);

        this.designZoneIntroTargetController = new AbortController();
        const { signal } = this.designZoneIntroTargetController;
        let visible = false;
        let started = false;
        const update = () => {
          const ready = visible && !document.hidden && image.complete && image.naturalWidth > 0 &&
            this.getActiveMediaSlide() === slide && this.isConnected;
          if (!ready) {
            window.clearTimeout(this.designZoneIntroTimer);
            this.designZoneIntroTimer = null;
            if (started) this.stopDesignZoneIntro();
            return;
          }
          if (started || this.designZoneIntroTimer) return;
          this.designZoneIntroTimer = window.setTimeout(() => {
            this.designZoneIntroTimer = null;
            if (!visible || document.hidden || !image.complete || !image.naturalWidth) return;
            started = true;
            overlay.classList.add('is-pulsing');
            this.designZoneIntroTimer = window.setTimeout(() => this.stopDesignZoneIntro(), 2000);
          }, 500);
        };
        this.designZoneIntroObserver = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting && entry.intersectionRatio >= 0.65;
          update();
        }, { threshold: [0, 0.65] });
        this.designZoneIntroObserver.observe(overlay.querySelector('[data-role="product-design-zone-shell"]'));
        image.addEventListener('load', update, { signal });
        document.addEventListener('visibilitychange', update, { signal });
      }

      getDesignZoneIntroTarget() {
        const zones = Array.isArray(this.designStudioState?.zones) ? this.designStudioState.zones : [];
        const candidates = zones.filter((zone) => zone?.supportsLogo && !zone.hasLogo);
        if (!candidates.length) return null;

        const activeZone = candidates.find((zone) => zone.key === this.designStudioState?.activeZoneKey);
        if (activeZone) return this.getConfiguredDesignZonePlacement(activeZone) ? activeZone : null;

        const firstPlacement = candidates.find((zone) => {
          const key = this.normalizeStudioKey(zone?.key || '');
          const label = this.normalizeStudioKey(zone?.label || '');
          return key === '1' ||
            key === 'placement_1' ||
            key === 'emplacement_1' ||
            label === 'placement_1' ||
            label === 'emplacement_1';
        });
        if (firstPlacement) return this.getConfiguredDesignZonePlacement(firstPlacement) ? firstPlacement : null;

        return candidates.find((zone) => this.getConfiguredDesignZonePlacement(zone)) || null;
      }

      setupDesignStudioResizeObserver() {
        if (this.designStudioResizeObserver || !('ResizeObserver' in window)) return;

        this.designStudioResizeObserver = new ResizeObserver(() => this.scheduleDesignStudioLayoutUpdate());
      }

      observeDesignStudioContainer(container) {
        if (!container) return;

        this.setupDesignStudioResizeObserver();
        this.designStudioResizeObserver?.observe(container);
      }

      scheduleDesignStudioLayoutUpdate() {
        if (!this.isConnected || this.designStudioResizeFrame) return;

        this.designStudioResizeFrame = window.requestAnimationFrame(() => {
          this.designStudioResizeFrame = 0;
          if (!this.isConnected) return;

          const slide = this.getActiveMediaSlide();
          const overlay = this.designStudioOverlays.get(slide) || slide?.querySelector('[data-role="product-design-studio"]');
          this.setDesignImagePan(this.designImagePanX, this.designImagePanY);
          this.updateProductPhotoZoomSource();
          if (!overlay || overlay.hidden) return;

          this.refreshDesignStudioOverlayLayout(overlay);
        });
      }

      refreshDesignStudioOverlayLayout(overlay) {
        const interaction = this.designInteraction;
        if (interaction?.overlay === overlay && this.isDesignArtworkInteractionCurrent(interaction)) {
          interaction.layoutRefreshPending = true;
          return;
        }
        const zone = this.getActiveOverlayZone(overlay);
        if (!zone?.key) return;

        const activeArtwork = overlay.querySelector('[data-role="product-design-artwork"]');
        const activeArtworkId = activeArtwork?.dataset.artworkId || this.getActiveDesignArtworkId(zone.key);
        const activePlacement = this.getDesignArtworkPlacement(zone, activeArtworkId);
        this.updateDesignArtworkElement(activeArtwork, activePlacement, zone, overlay);

        overlay.querySelectorAll('[data-role="product-design-artwork-copy"]').forEach((copy) => {
          const artworkId = copy.dataset.artworkId || '';
          this.updateDesignArtworkElement(copy, this.getDesignArtworkPlacement(zone, artworkId), zone, overlay);
        });

        this.renderDesignZoneParts(overlay, zone, this.getDesignZonePlacement(zone), activePlacement);
        this.updateDesignArtworkToolbar(overlay);
        this.setDesignImagePan(this.designImagePanX, this.designImagePanY);
        this.updateDesignImagePanAvailability(overlay);
      }

      teardownDesignStudioResizeObserver() {
        this.designStudioResizeObserver?.disconnect();
        this.designStudioResizeObserver = null;

        if (this.designStudioResizeFrame) window.cancelAnimationFrame(this.designStudioResizeFrame);
        this.designStudioResizeFrame = 0;
        if (this.designImageZoomFrame) window.cancelAnimationFrame(this.designImageZoomFrame);
        this.designImageZoomFrame = 0;
        window.clearTimeout(this.designToolbarTimer);
        this.designToolbarTimer = null;
      }

      bindMarkingStudioEvents() {
        if (this.markingEventsAbortController) return;

        this.markingEventsAbortController = new AbortController();
        const root = this.closest('.section-main-product') || this.closest('product-info') || document;
        const { signal } = this.markingEventsAbortController;

        root.addEventListener('todaymerch:marking-zone-change', this.boundOnMarkingZoneChange, { signal });
        root.addEventListener('todaymerch:marking-state-change', this.boundOnMarkingStateChange, { signal });
      }

      prepareVariantMediaReveal() {
        if (this.dataset.awaitVariantMedia !== 'true' || this.classList.contains('is-variant-media-ready')) return;
        this.revealVariantMedia();
      }

      revealVariantMedia() {
        if (this.classList.contains('is-variant-media-ready')) return;

        this.classList.remove('is-awaiting-variant-media');
        this.classList.add('is-variant-media-ready');
        this.sliderInstance?.slider?.update?.();
        this.thumbsInstance?.slider?.update?.();
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.activeVariantId = this.getCurrentVariantId();
        this.initializingMedia = true;
        try {
          this.applyActiveVariantMedia({ render: false, announce: false });
          this.setSliderOptions();

          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.onchange = this.updateMediaLayout.bind(this);
          this.updateMediaLayout();

          if (this.enableImageZoom) {
            this.initImageZoom();
          }
        } finally { this.initializingMedia = false; }

        this.initialized = true;
        this.applyPendingDesignView();
        this.renderDesignStudio();
        const prepared = this.preparedVariantMedia;
        this.preparedVariantMedia = null;
        if (prepared) this.dispatchVariantMediaUpdated(prepared.variantId, prepared.filtered, prepared.slides);
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          // Swiper's image copies bypass native lazy loading and fetch hidden colours.
          preloadImages: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: false,
          allowTouchMove: false,
          simulateTouch: false,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          preloadImages: false,
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 0,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.initThumbHoverSelection();
        }
      }

      destroySlider() {
        this.destroyThumbHoverSelection();

        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      buildFullscreenMediaRecords() {
        return [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')].map((media) => {
          const { mediaType, mediaIndex, mediaId, src, pswpWidth, pswpHeight } = media.dataset;
          const style = window.getComputedStyle(media);
          let html = '';

          if (['model', 'video', 'external_video'].includes(mediaType)) {
            const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';
            const mediaElement = media.querySelector(htmlTag);
            if (mediaElement) html = `<div class="pswp__item--${mediaType}">${mediaElement.outerHTML}</div>`;
          }

          return {
            mediaType,
            mediaIndex,
            mediaId,
            src,
            width: pswpWidth,
            height: pswpHeight,
            html,
            sourceElement: media,
            hidden: media.hidden || media.classList.contains('is-today-variant-media-hidden'),
            ariaHidden: media.getAttribute('aria-hidden'),
            display: style.display,
          };
        });
      }

      buildFullscreenDataSource() {
        const helper = window.TodayMerchGalleryFullscreen;
        if (!helper?.buildVisiblePhotoSwipeDataSource) return [];
        return helper.buildVisiblePhotoSwipeDataSource(this.buildFullscreenMediaRecords());
      }

      removeFullscreenDesignOverlay(slide) {
        slide?.todayMerchDesignOverlay?.remove();
        if (slide) slide.todayMerchDesignOverlay = null;
      }

      sanitizeFullscreenDesignLayer(layer) {
        layer.querySelectorAll([
          '[data-role="product-design-zone-grid"]',
          '[data-role="product-design-zone-parts"]',
          '[data-role="product-design-alignment-guides"]',
          '[data-role="product-design-toolbar"]',
          '[data-role="product-design-handle"]',
          '[data-role="product-design-rotate"]',
          '[data-role="product-design-zone-label"]',
        ].join(',')).forEach((element) => element.remove());

        layer.querySelectorAll('*').forEach((element) => {
          element.removeAttribute('tabindex');
          element.removeAttribute('role');
          element.removeAttribute('aria-label');
          element.removeAttribute('title');
          if (element.matches('button, input, select, textarea')) element.disabled = true;
        });
        layer.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
        layer.setAttribute('aria-hidden', 'true');
        layer.setAttribute('inert', '');
        layer.style.transform = 'none';
        return layer;
      }

      resizeFullscreenDesignOverlay(slide, width, height) {
        const wrapper = slide?.todayMerchDesignOverlay;
        if (!wrapper) return;
        const content = slide.content;
        const resolvedWidth = Math.max(1, Number(width || content?.displayedImageWidth || slide.width) || 1);
        const resolvedHeight = Math.max(1, Number(height || content?.displayedImageHeight || slide.height) || 1);
        wrapper.style.width = `${resolvedWidth}px`;
        wrapper.style.height = `${resolvedHeight}px`;
      }

      syncFullscreenDesignOverlay(slide) {
        this.removeFullscreenDesignOverlay(slide);
        const sourceSlide = slide?.data?.sourceElement;
        if (!sourceSlide || slide.data?.mediaType !== 'image') return;

        this.renderDesignStudio({ slide: sourceSlide });
        const overlay = this.designStudioOverlays.get(sourceSlide)
          || sourceSlide.querySelector('[data-role="product-design-studio"]');
        const visualLayer = overlay?.querySelector('[data-role="product-design-visual-layer"]');
        const helper = window.TodayMerchGalleryFullscreen;
        const hasLogo = this.isDesignStudioStateForActiveVariant()
          && Boolean(this.designStudioState?.hasLogo)
          && Boolean(overlay?.classList.contains('has-artwork-file'));
        const eligible = helper?.isPresentationOverlayEligible?.({
          hasLogo,
          stateVariantId: this.designStudioState?.variantId,
          activeVariantId: this.activeVariantId || this.getCurrentVariantId(),
          sourceMediaId: slide.data.mediaId,
          activeMediaId: sourceSlide.dataset.mediaId,
          overlayHidden: Boolean(overlay?.hidden),
          hasVisualLayer: Boolean(visualLayer),
        });
        if (!eligible) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'product__design-fullscreen-overlay';
        wrapper.dataset.mediaId = String(slide.data.mediaId || '');
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.append(this.sanitizeFullscreenDesignLayer(visualLayer.cloneNode(true)));
        slide.container.append(wrapper);
        slide.todayMerchDesignOverlay = wrapper;
        this.resizeFullscreenDesignOverlay(slide);
      }

      syncGalleryToFullscreenMedia(mediaId) {
        const targetSlide = [...this.querySelectorAll('.product__media-item')]
          .find((slide) => String(slide.dataset.mediaId || '') === String(mediaId || ''));
        if (!targetSlide) return;

        const slider = this.sliderInstance?.slider;
        if (slider && !slider.destroyed) {
          const index = [...slider.slides].indexOf(targetSlide);
          if (index >= 0) {
            this.selectedMediaIndex = index;
            slider.slideTo(index, 0, false);
            this.syncThumbActiveState(index, targetSlide);
          }
        }
        this.renderDesignStudio({ slide: targetSlide });
      }

      lockFullscreenPageScroll() {
        if (this.fullscreenScrollLock) return;

        const root = document.documentElement;
        const body = document.body;
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        this.fullscreenScrollLock = {
          scrollX,
          scrollY,
          rootHadClass: root.classList.contains('is-product-media-fullscreen-open'),
          bodyHadClass: body.classList.contains('is-product-media-fullscreen-open'),
          rootOverflow: root.style.overflow,
          bodyPosition: body.style.position,
          bodyTop: body.style.top,
          bodyLeft: body.style.left,
          bodyRight: body.style.right,
          bodyWidth: body.style.width,
          bodyOverflow: body.style.overflow,
        };

        root.classList.add('is-product-media-fullscreen-open');
        body.classList.add('is-product-media-fullscreen-open');
        root.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';
      }

      unlockFullscreenPageScroll() {
        const scrollLock = this.fullscreenScrollLock;
        if (!scrollLock) return;

        const root = document.documentElement;
        const body = document.body;
        root.style.overflow = scrollLock.rootOverflow;
        body.style.position = scrollLock.bodyPosition;
        body.style.top = scrollLock.bodyTop;
        body.style.left = scrollLock.bodyLeft;
        body.style.right = scrollLock.bodyRight;
        body.style.width = scrollLock.bodyWidth;
        body.style.overflow = scrollLock.bodyOverflow;
        if (!scrollLock.rootHadClass) root.classList.remove('is-product-media-fullscreen-open');
        if (!scrollLock.bodyHadClass) body.classList.remove('is-product-media-fullscreen-open');
        this.fullscreenScrollLock = null;
        window.scrollTo(scrollLock.scrollX, scrollLock.scrollY);
      }

      initImageZoom() {
        const dataSource = [];

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: true,
          close: true,
          counter: true,
          preloader: true,
          initialZoomLevel: 'fit',
          maxZoomLevel: 4,
          wheelToZoom: true,
          returnFocus: true,
          trapFocus: true,
          escKey: true,
          arrowKeys: true,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, itemData) => {
          return itemData?.sourceElement?.querySelector('img') || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, content) => {
          const image = content?.data?.sourceElement?.querySelector('img');
          return image?.currentSrc || image?.src || placeholderSrc;
        });

        let lastLightboxMediaId = '';
        this.lightbox.on('beforeOpen', () => {
          this.lockFullscreenPageScroll();
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const slide = this.lightbox?.pswp?.currSlide;
          lastLightboxMediaId = String(slide?.data?.mediaId || '');
          this.syncFullscreenDesignOverlay(slide);
          requestAnimationFrame(() => this.syncFullscreenDesignOverlay(this.lightbox?.pswp?.currSlide));
        });

        this.lightbox.on('afterSetContent', ({ slide }) => {
          if (slide?.isActive) this.syncFullscreenDesignOverlay(slide);
        });

        this.lightbox.on('contentResize', ({ content, width, height }) => {
          this.resizeFullscreenDesignOverlay(content?.slide, width, height);
        });

        this.lightbox.on('slideDestroy', ({ slide }) => {
          this.removeFullscreenDesignOverlay(slide);
        });

        this.lightbox.on('close', () => {
          lastLightboxMediaId = String(this.lightbox?.pswp?.currSlide?.data?.mediaId || lastLightboxMediaId);
        });

        this.lightbox.on('destroy', () => {
          if (lastLightboxMediaId) this.syncGalleryToFullscreenMedia(lastLightboxMediaId);
          lastLightboxMediaId = '';
          this.unlockFullscreenPageScroll();
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: this.string('nextSlide'),
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: this.string('previousSlide'),
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const sourceSlide = media.closest('.product__media-item');
              const dataSource = this.buildFullscreenDataSource();
              const index = dataSource.findIndex((item) => item.sourceElement === sourceSlide);
              if (index < 0) return;
              this.lightbox.options.dataSource = dataSource;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
          this.selectedMediaIndex = activeIndex || 0;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          this.primeMediaAroundIndex(activeIndex || 0);
          // The initial DOM is already filtered and ordered before Swiper measures it.
          if (this.initializingMedia) {
            this.syncThumbActiveState(activeIndex || 0, slides[activeIndex || 0]);
          } else this.applyActiveVariantMedia({ forceSlide: true });
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex } = swiper;

          this.primeMediaAroundIndex(activeIndex || 0);

          // The Thumbs module reveals offscreen selections without moving a
          // visible thumbnail away from the pointer during hover selection.

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          this.selectedMediaIndex = activeIndex;
          if (this.designViewSwitchInProgress || this.initializingMedia) return;
          this.emitMediaViewSelection(activeIndex);

          this.renderDesignStudio();
        });
      }

      primeMediaAroundIndex(activeIndex = 0) {
        this.cancelMediaPreload();
        this.updateMediaLoadingState(activeIndex, false);

        const schedule = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 120));
        this.mediaPreloadHandle = schedule(
          () => {
            this.mediaPreloadHandle = null;
            this.updateMediaLoadingState(activeIndex, true);
          },
          { timeout: 700 },
        );
      }

      updateMediaLoadingState(activeIndex, includeAdjacent) {
        const slider = this.sliderInstance?.slider;
        if (!slider || slider.destroyed) return;

        [...slider.slides].forEach((slide, index) => {
          const image = slide.querySelector('img');
          if (!image) return;

          const distance = Math.abs(index - activeIndex);
          const isActive = distance === 0;
          const shouldPreload = isActive || (includeAdjacent && distance === 1);

          image.loading = shouldPreload ? 'eager' : 'lazy';
          image.setAttribute('fetchpriority', isActive ? 'high' : 'low');

          if (shouldPreload && image.complete && typeof image.decode === 'function') {
            image.decode().catch(() => {});
          }
        });
      }

      cancelMediaPreload() {
        if (!this.mediaPreloadHandle) return;

        if (window.cancelIdleCallback) {
          window.cancelIdleCallback(this.mediaPreloadHandle);
        } else {
          window.clearTimeout(this.mediaPreloadHandle);
        }

        this.mediaPreloadHandle = null;
      }

      initThumbHoverSelection() {
        if (this.thumbHoverCleanup || !this.closest('.section-main-product') || !this.elements.thumbnails) return;

        const thumbnailItems = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
        if (!thumbnailItems.length) return;

        const handleThumbMouseEnter = (event) => {
          if (!this.canUseThumbHoverSelection()) return;

          const previewIndex = this.getThumbSlideIndex(event.currentTarget);
          if (previewIndex < 0) return;

          this.commitThumbSelection(previewIndex);
        };

        const handleThumbClick = (event) => {
          const selectedIndex = this.getThumbSlideIndex(event.currentTarget);
          if (selectedIndex < 0) return;

          this.commitThumbSelection(selectedIndex);
        };

        thumbnailItems.forEach((thumbnail) => {
          thumbnail.addEventListener('mouseenter', handleThumbMouseEnter);
          thumbnail.addEventListener('click', handleThumbClick);
        });

        this.thumbHoverCleanup = () => {
          thumbnailItems.forEach((thumbnail) => {
            thumbnail.removeEventListener('mouseenter', handleThumbMouseEnter);
            thumbnail.removeEventListener('click', handleThumbClick);
          });
          this.thumbHoverCleanup = null;
        };
      }

      destroyThumbHoverSelection() {
        if (this.thumbHoverCleanup) {
          this.thumbHoverCleanup();
        }
      }

      canUseThumbHoverSelection() {
        return Boolean(
          this.thumbHoverMediaQuery.matches &&
            this.sliderInstance?.slider &&
            !this.sliderInstance.slider.destroyed &&
            !this.lightbox?.pswp
        );
      }

      getThumbSlideIndex(thumbnail) {
        const slider = this.sliderInstance?.slider;
        if (!slider || !thumbnail) return -1;

        const targetMediaId = thumbnail.dataset.target;
        const slides = [...slider.slides];

        if (targetMediaId) {
          const mediaIndex = slides.findIndex((slide) => slide.dataset.mediaId === targetMediaId);
          if (mediaIndex >= 0) return mediaIndex;
        }

        const thumbnailItems = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
        return thumbnailItems.indexOf(thumbnail);
      }

      commitThumbSelection(selectedIndex) {
        if (this.zoneInteractions?.locked) return;
        const slider = this.sliderInstance?.slider;
        if (!slider) return;

        this.selectedMediaIndex = selectedIndex;

        requestAnimationFrame(() => {
          if (!this.sliderInstance?.slider || this.sliderInstance.slider.destroyed) return;

          if (this.sliderInstance.slider.activeIndex !== selectedIndex) {
            this.sliderInstance.slider.slideTo(selectedIndex, 0);
          }

          this.syncThumbActiveState(selectedIndex);
          this.emitMediaViewSelection(selectedIndex);
          this.renderDesignStudio();
        });
      }

      syncThumbActiveState(activeIndex, activeSlide = null) {
        if (!this.elements.thumbnails) return;

        const activeMediaId = String(activeSlide?.dataset?.mediaId || '').trim();
        const thumbnailItems = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item')];
        thumbnailItems.forEach((thumbnail, index) => {
          const matchesMedia = activeMediaId && String(thumbnail.dataset.target || '') === activeMediaId;
          thumbnail.classList.toggle('swiper-slide-thumb-active', matchesMedia || (!activeMediaId && index === activeIndex));
        });
      }

      onMarkingZoneChange(event) {
        if (event.detail?.source !== 'marking-selector' || this.designZoneSelectionInProgress) return;

        const selectedZone = event.detail?.zone;
        if (this.getSupplierPrintPlane(selectedZone) && !this.hasDesignPhotoSurface(selectedZone)) {
          window.requestAnimationFrame(() => this.requestMobileDesignEditor({ zoneKey: selectedZone.key, allowEmpty: true }));
          return;
        }
        const viewKey = event.detail?.zone?.viewKey;
        if (!viewKey) return;

        this.slideToDesignView(viewKey);
        const zoneKey = event.detail?.zone?.key;
        if (zoneKey) window.requestAnimationFrame(() => this.showMobileDesignZoneCue(zoneKey));
      }

      onMarkingStateChange(event) {
        if (event.detail?.source !== 'marking-selector') return;

        this.designStudioState = event.detail;
        if (this.designZoneSelectionInProgress) return;

        const activeViewKey =
          this.getActiveDesignZone()?.viewKey ||
          this.designStudioState?.activeViewKey ||
          this.designStudioState?.logoZones?.[0]?.viewKey ||
          '';

        const initialView = this.designViewStateVariantId !== this.designStudioState.variantId;
        if (this.designStudioState.zones?.length) this.designViewStateVariantId = this.designStudioState.variantId;
        if (!this.designZoneSelectionInProgress && (this.designStudioState?.hasLogo || initialView) && activeViewKey
          && this.hasDesignPhotoSurface(this.getActiveDesignZone())) {
          this.slideToDesignView(activeViewKey, { preserveViewport: true, render: false });
        }

        this.renderDesignStudio();
        event.todayMerchStudioRenderHandled = this;
      }

      bindVariantMediaEvents() {
        if (!this.markingEventsAbortController) {
          this.markingEventsAbortController = new AbortController();
        }

        const { signal } = this.markingEventsAbortController;
        document.addEventListener('variant:changed', this.boundOnThemeVariantChange, { signal });
        this.variantChangeUnsubscriber = window.FoxTheme?.pubsub?.subscribe?.(
          window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange,
          this.boundOnThemeVariantChange,
        );
      }

      onThemeVariantChange(event = {}) {
        const variant = event.detail?.variant || event.data?.variant;
        const variantId = variant?.id || event.detail?.variantId || '';
        const nextVariantId = String(variantId || this.getCurrentVariantId() || '').trim();
        if (!nextVariantId) return;
        const completed = this.completedVariantMediaNotification;
        if (completed && completed.variant === variant && completed.variantId === nextVariantId
          && this.activeVariantId === nextVariantId && completed.studioConfig === this.todayStudioConfig
          && completed.variantGallery === this.todayVariantGallery) return;
        this.completedVariantMediaNotification = null;
        if (nextVariantId !== this.activeVariantId) {
          this.mobileDesignEditor?.close();
          this.clearMobileDesignZoneCue();
        }
        this.activeVariantId = nextVariantId;
        this.renderDesignStudio();
        this.applyVariantMediaForVariant(nextVariantId, variant, { forceSlide: true });
      }

      getProductRoot() {
        return this.closest('.section-main-product') || this.closest('product-info') || document;
      }

      getCurrentVariantId() {
        const root = this.getProductRoot();
        const variantInput = root?.querySelector?.('input[name="id"]') || document.querySelector('form[action*="/cart/add"] input[name="id"]');
        const selectedVariant = this.getSelectedVariantFromDocument();
        return String(variantInput?.value || this.activeVariantId || this.getUrlVariantId() || selectedVariant?.id || '').trim();
      }

      getUrlVariantId() {
        try {
          return String(new URLSearchParams(window.location.search).get('variant') || '').trim();
        } catch (error) {
          return '';
        }
      }

      getSelectedVariantFromDocument() {
        const root = this.getProductRoot();
        const script = root?.querySelector?.('[data-selected-variant]') || document.querySelector('[data-selected-variant]');
        if (!script) return null;

        try {
          return JSON.parse(script.textContent || 'null');
        } catch (error) {
          return null;
        }
      }

      getVariantLookupKeys(value = this.activeVariantId) {
        const variant = value && typeof value === 'object' ? value : { id: value };
        const keys = new Set();
        [
          variant?.id,
          variant?.gid,
          variant?.adminGraphqlApiId,
          variant?.admin_graphql_api_id,
          variant?.numericId,
        ].filter(Boolean).forEach((item) => {
          const stringValue = String(item);
          keys.add(stringValue);
          const numeric = this.getResourceNumericId(stringValue);
          if (numeric) keys.add(numeric);
        });
        return Array.from(keys);
      }

      getResourceNumericId(value) {
        return String(value || '').match(/(\d+)(?:\D*)$/)?.[1] || '';
      }

      getVariantMediaConfig() {
        const galleryConfig = this.getTodayVariantGallery();
        if (galleryConfig?.variants && typeof galleryConfig.variants === 'object') {
          return galleryConfig;
        }
        const config = this.getTodayStudioConfig();
        const variantMedia = config?.variantMedia || config?.variant_media || {};
        return variantMedia && typeof variantMedia === 'object' ? variantMedia : {};
      }

      getTodayVariantGallery() {
        if (this.todayVariantGallery !== undefined) return this.todayVariantGallery;
        const root = this.getProductRoot();
        const script = root?.querySelector?.('[data-today-variant-gallery]')
          || document.querySelector('[data-today-variant-gallery]');
        if (!script) {
          this.todayVariantGallery = null;
          return this.todayVariantGallery;
        }

        try {
          const parsed = JSON.parse(script.textContent || '{}');
          this.todayVariantGallery = parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
          console.warn('[Today Merch] Variant gallery configuration is invalid.', error);
          this.todayVariantGallery = null;
        }
        return this.todayVariantGallery;
      }

      getVariantMediaEntry(variantId = this.activeVariantId, variant = null) {
        const variantMedia = this.getVariantMediaConfig();
        const entries = variantMedia.variants && typeof variantMedia.variants === 'object'
          ? variantMedia.variants
          : {};
        const keys = this.getVariantLookupKeys(variant || variantId);
        const keySet = new Set(keys);
        const exactMatchKey = keys.find((key) => entries[key]);
        if (exactMatchKey) return entries[exactMatchKey];

        const matchKey = Object.entries(entries).find(([entryKey, entry]) => {
          const entryKeys = this.normalizeIdList([
            entryKey,
            entry?.variantId,
            entry?.variant_id,
            entry?.id,
            entry?.gid,
            entry?.adminGraphqlApiId,
            entry?.admin_graphql_api_id,
            entry?.numericId,
            entry?.numeric_id,
          ]);
          return entryKeys.some((entryLookupKey) => keySet.has(entryLookupKey));
        })?.[0];
        return matchKey ? entries[matchKey] : null;
      }

      getVariantMediaAssignments(variantId = this.activeVariantId, variant = null) {
        const entry = this.getVariantMediaEntry(variantId, variant);
        const gallery = Array.isArray(entry?.gallery) ? entry.gallery : [];
        const galleryAssignments = gallery
          .filter((assignment) => assignment && (assignment.image || assignment.mediaId || assignment.media_id));
        const media = entry?.media && typeof entry.media === 'object' ? entry.media : {};
        const mediaAssignments = Object.values(media)
          .filter((assignment) => assignment && (assignment.image || assignment.mediaId || assignment.media_id));
        const primaryAssignments = galleryAssignments.length ? galleryAssignments : mediaAssignments;
        const studioAssignments = this.getStudioVariantMediaAssignments(variantId, variant);
        const slides = [...(this.querySelectorAll?.('.product__media-item') || [])];
        // Reuse identities only for this synchronous resolution. A subsequent
        // read still sees changed variants, media nodes, URLs and Studio data.
        const identities = { slides: new Map(), assignments: new Map() };
        const currentStudioAssignments = studioAssignments.flatMap((assignment) => {
          if (slides.some((slide) => this.getVariantMediaMatchRank(slide, assignment, identities) >= 2)) return [assignment];

          const lineageMatches = slides.filter((slide) => this.getVariantMediaMatchRank(slide, assignment, identities) === 1);
          const assignmentOrientation = this.getStudioOrientation(
            `${assignment?.placementKey || ''} ${assignment?.label || ''} ${assignment?.alt || ''}`,
          );
          const orientedMatches = assignmentOrientation
            ? lineageMatches.filter((slide) => (
                this.getStudioOrientation(identities.slides.get(slide).text) === assignmentOrientation
              ))
            : lineageMatches;
          if (orientedMatches.length !== 1) return [];

          const matchedSlide = orientedMatches[0];
          const image = matchedSlide.querySelector('img');
          const currentMediaId = String(matchedSlide.dataset.mediaId || matchedSlide.dataset.media_id || '').trim();
          const currentImage = String(
            image?.currentSrc || image?.src || image?.getAttribute('src') || matchedSlide.dataset.src || '',
          ).trim();
          if (!currentMediaId && !currentImage) return [];
          return [{
            ...assignment,
            mediaId: currentMediaId || assignment.mediaId,
            fileId: currentMediaId || assignment.fileId,
            image: currentImage || assignment.image,
            thumbnail: currentImage || assignment.thumbnail,
            method: 'studio-photoroom-lineage',
          }];
        });
        const orderedAssignments = this.getPlacementPrioritizedMediaAssignments(
          primaryAssignments,
          currentStudioAssignments,
          identities,
        );
        return orderedAssignments.length ? orderedAssignments : this.getVariantViewAssignments(variantId, variant);
      }

      getPlacementPrioritizedMediaAssignments(primaryAssignments = [], studioAssignments = [], identities = null) {
        if (!studioAssignments.length) return [...primaryAssignments];

        const orderedAssignments = [];
        const mediaIds = new Set();
        const imageKeys = new Set();
        const appendUniqueAssignment = (assignment) => {
          if (!assignment) return;
          const identity = identities?.assignments.get(assignment) || this.getVariantMediaIdentity(assignment);
          if (Array.from(identity.mediaIds).some((id) => mediaIds.has(id)) ||
            Array.from(identity.imageKeys).some((key) => imageKeys.has(key))) return;
          orderedAssignments.push(assignment);
          for (const id of identity.mediaIds) mediaIds.add(id);
          for (const key of identity.imageKeys) imageKeys.add(key);
        };

        appendUniqueAssignment(primaryAssignments[0]);
        for (const assignment of studioAssignments) appendUniqueAssignment(assignment);
        for (const assignment of primaryAssignments.slice(1)) appendUniqueAssignment(assignment);

        return orderedAssignments;
      }

      getStudioVariantMediaAssignments(variantId = this.activeVariantId, variant = null) {
        const config = this.getTodayStudioConfig();
        const variantMedia = config?.variantMedia || config?.variant_media || {};
        const entries = variantMedia?.variants && typeof variantMedia.variants === 'object'
          ? variantMedia.variants
          : variantMedia;
        if (!entries || typeof entries !== 'object') return [];

        const keys = new Set(this.getVariantLookupKeys(variant || variantId));
        const entry = Object.entries(entries).find(([entryKey, value]) => {
          const entryKeys = this.normalizeIdList([
            entryKey,
            value?.variantId,
            value?.variant_id,
            value?.id,
            value?.gid,
          ]);
          return entryKeys.some((key) => keys.has(key));
        })?.[1];
        if (!entry || typeof entry !== 'object') return [];

        const views = Array.isArray(config?.views) ? config.views : [];
        const viewByKey = new Map(views.map((view) => [String(view?.key || view?.view || ''), view]));
        const assignments = entry.media && typeof entry.media === 'object' ? entry.media : entry;
        const studioAssignments = Object.entries(assignments).flatMap(([placementKey, assignment]) => {
          if (['variantId', 'variant_id', 'label', 'color', 'optionKey', 'gallery'].includes(placementKey)) return [];
          if (assignment && typeof assignment === 'object' && (
            assignment.image || assignment.mediaId || assignment.media_id || assignment.fileId || assignment.file_id
          )) {
            const assignmentViewKey = String(
              assignment.viewKey || assignment.view_key || assignment.viewId || assignment.view_id || '',
            ).trim();
            const assignmentView = viewByKey.get(assignmentViewKey);
            return [{
              ...assignment,
              placementKey,
              label: assignment.label || assignmentView?.label || '',
              alt: assignment.alt || assignmentView?.alt || '',
            }];
          }
          const viewKey = String(assignment?.viewKey || assignment?.view_key || assignment || '').trim();
          const view = viewByKey.get(viewKey);
          if (!view) return [];
          return [{
            placementKey,
            viewKey,
            mediaId: view.fileId || view.file_id || view.mediaId || view.media_id || '',
            fileId: view.fileId || view.file_id || view.mediaId || view.media_id || '',
            image: view.image || view.previewImage || view.imageUrl || '',
            thumbnail: view.thumbnail || view.image || view.previewImage || view.imageUrl || '',
            label: view.label || '',
            alt: view.alt || '',
            method: 'studio-variant-placement',
            confidence: 1,
          }];
        });
        return this.sortStudioMediaAssignmentsByPlacementOrder(studioAssignments, config);
      }

      sortStudioMediaAssignmentsByPlacementOrder(assignments, config = this.getTodayStudioConfig()) {
        if (assignments.length < 2) return assignments;

        const configuredPlacements = Array.isArray(config?.zones) && config.zones.length
          ? config.zones
          : Array.isArray(config?.placements) ? config.placements : [];
        if (!configuredPlacements.length) return assignments;

        const placementOrder = new Map();
        for (const [index, placement] of configuredPlacements.entries()) {
          if (!placement || placement.active === false || placement.active === 'false') continue;
          const aliases = [
            placement.groupId,
            placement.group_id,
            placement.placementId,
            placement.placement_id,
            placement.placementKey,
            placement.placement_key,
            placement.id,
            placement.key,
          ];
          for (const alias of aliases) {
            const normalizedAlias = this.normalizeStudioKey(alias);
            if (normalizedAlias && !placementOrder.has(normalizedAlias)) placementOrder.set(normalizedAlias, index);
          }
        }
        if (!placementOrder.size) return assignments;

        return assignments
          .map((assignment, index) => ({
            assignment,
            index,
            placementIndex: placementOrder.get(this.normalizeStudioKey(
              assignment?.placementKey || assignment?.placement_key || '',
            )),
          }))
          .sort((left, right) => {
            const leftIndex = left.placementIndex ?? Number.MAX_SAFE_INTEGER;
            const rightIndex = right.placementIndex ?? Number.MAX_SAFE_INTEGER;
            return leftIndex - rightIndex || left.index - right.index;
          })
          .map(({ assignment }) => assignment);
      }

      getExplicitVariantMediaAssignments() {
        const variantMedia = this.getVariantMediaConfig();
        const entries = variantMedia.variants && typeof variantMedia.variants === 'object'
          ? Object.values(variantMedia.variants)
          : [];
        const assignments = [];

        entries.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          const gallery = Array.isArray(entry.gallery) ? entry.gallery : [];
          const media = entry.media && typeof entry.media === 'object' ? Object.values(entry.media) : [];
          [...gallery, ...media].forEach((assignment) => {
            if (assignment && (assignment.image || assignment.mediaId || assignment.media_id)) assignments.push(assignment);
          });
        });

        return assignments;
      }

      getExplicitCommonMediaAssignments() {
        const variantMedia = this.getVariantMediaConfig();
        const commonMedia = Array.isArray(variantMedia.commonMedia) ? variantMedia.commonMedia : null;
        if (!commonMedia) return null;
        return commonMedia
          .filter((assignment) => assignment && (assignment.image || assignment.mediaId || assignment.media_id));
      }

      normalizeIdList(value) {
        const entries = Array.isArray(value) ? value : value ? [value] : [];
        const ids = entries
          .map((entry) => String(entry?.id || entry || '').trim())
          .filter(Boolean);
        return Array.from(new Set(ids.flatMap((id) => {
          const numeric = this.getResourceNumericId(id);
          return numeric && numeric !== id ? [id, numeric] : [id];
        })));
      }

      getVariantViewAssignments(variantId = this.activeVariantId, variant = null) {
        const config = this.getTodayStudioConfig();
        const views = Array.isArray(config?.views) ? config.views : [];
        if (!views.length) return [];

        const variantKeys = new Set(this.getVariantLookupKeys(variant || variantId));
        if (!variantKeys.size) return [];

        return views
          .filter((view) => {
            if (!view || view.designEnabled === false || view.designEnabled === 'false') return false;
            const ids = this.normalizeIdList(view.variantIds || view.variant_ids || []);
            return ids.some((id) => variantKeys.has(id));
          })
          .map((view) => ({
            placementKey: view.placementKey || view.placement_key || '',
            label: view.label || '',
            viewKey: view.key || view.view || '',
            mediaId: view.fileId || view.file_id || '',
            fileId: view.fileId || view.file_id || '',
            image: view.image || view.previewImage || view.imageUrl || '',
            thumbnail: view.thumbnail || view.image || view.previewImage || view.imageUrl || '',
            alt: view.alt || '',
            method: 'studio-view-variant',
            confidence: 1,
          }))
          .filter((assignment) => assignment.image || assignment.mediaId);
      }

      getVariantMediaIdentity(assignment) {
        const imageUrls = [
          assignment?.image,
          assignment?.thumbnail,
          assignment?.previewUrl,
          assignment?.preview_url,
        ];
        return {
          mediaIds: new Set([
            assignment?.mediaId,
            assignment?.media_id,
            assignment?.fileId,
            assignment?.file_id,
          ].filter(Boolean).flatMap((value) => {
            const stringValue = String(value);
            const numeric = this.getResourceNumericId(stringValue);
            return numeric && numeric !== stringValue ? [stringValue, numeric] : [stringValue];
          })),
          imageKeys: new Set(imageUrls.map((url) => this.getImageFileKey(url)).filter(Boolean)),
          imageLineages: new Set(imageUrls.map((url) => this.getStableImageLineage(url)).filter(Boolean)),
        };
      }

      getSlideIdentity(slide) {
        const image = slide?.querySelector('img');
        const imageUrls = [
          image?.currentSrc,
          image?.src,
          image?.getAttribute('src'),
          image?.getAttribute('data-src'),
          slide?.dataset?.src,
        ];
        return {
          mediaIds: new Set([
            slide?.dataset?.mediaId,
            slide?.dataset?.media_id,
          ].filter(Boolean).flatMap((value) => {
            const stringValue = String(value);
            const numeric = this.getResourceNumericId(stringValue);
            return numeric && numeric !== stringValue ? [stringValue, numeric] : [stringValue];
          })),
          imageKeys: this.getSlideImageKeys(slide),
          imageLineages: new Set(imageUrls.map((url) => this.getStableImageLineage(url)).filter(Boolean)),
          text: this.normalizeStudioKey([
            image?.getAttribute('alt'),
            image?.currentSrc || image?.src,
            slide?.dataset?.src,
          ].filter(Boolean).join(' ')),
        };
      }

      slideMatchesVariantMedia(slide, assignments) {
        if (!slide || !assignments.length) return false;
        return assignments.some((assignment) => this.getVariantMediaMatchRank(slide, assignment) > 0);
      }

      getVariantMediaMatchRank(slide, assignment, identities = null) {
        if (!slide || !assignment) return 0;
        if (identities && !identities.slides.has(slide)) identities.slides.set(slide, this.getSlideIdentity(slide));
        if (identities && !identities.assignments.has(assignment)) identities.assignments.set(assignment, this.getVariantMediaIdentity(assignment));
        const slideIdentity = identities ? identities.slides.get(slide) : this.getSlideIdentity(slide);
        const identity = identities ? identities.assignments.get(assignment) : this.getVariantMediaIdentity(assignment);
        const mediaMatch = Array.from(identity.mediaIds).some((id) => slideIdentity.mediaIds.has(id));
        if (mediaMatch) return 3;
        const imageMatch = Array.from(identity.imageKeys).some((key) => slideIdentity.imageKeys.has(key));
        if (imageMatch) return 2;
        const lineageMatch = Array.from(identity.imageLineages).some((key) => slideIdentity.imageLineages.has(key));
        return lineageMatch ? 1 : 0;
      }

      getOrderedVariantMediaSlides(slides, assignments, identities = { slides: new Map(), assignments: new Map() }) {
        const used = new Set();
        const ordered = [];
        assignments.forEach((assignment) => {
          let match = null;
          let bestRank = 0;
          for (const slide of slides) {
            if (used.has(slide)) continue;
            const rank = this.getVariantMediaMatchRank(slide, assignment, identities);
            // Strict comparison retains the original order for equal ranks.
            if (rank > bestRank) { match = slide; bestRank = rank; }
            if (rank === 3) break;
          }
          if (!match) return;
          used.add(match);
          ordered.push(match);
        });
        return ordered;
      }

      getGlobalVariantMediaSlides(slides, identities) {
        const commonAssignments = this.getExplicitCommonMediaAssignments();
        if (commonAssignments) {
          return this.getOrderedVariantMediaSlides(slides, commonAssignments, identities);
        }

        const assignedToVariants = this.getExplicitVariantMediaAssignments();
        if (!assignedToVariants.length) return [];
        const assignedSlides = new Set(this.getOrderedVariantMediaSlides(slides, assignedToVariants, identities));
        return this.sortByOriginalMediaOrder(
          slides.filter((slide) => !assignedSlides.has(slide)),
        );
      }

      mergeMediaSlides(...groups) {
        const used = new Set();
        return groups.flat().filter((slide) => {
          if (!slide || used.has(slide)) return false;
          used.add(slide);
          return true;
        });
      }

      ensureOriginalMediaOrder(elements) {
        elements.forEach((element, index) => {
          if (!element.dataset.todayOriginalOrder) {
            element.dataset.todayOriginalOrder = String(index);
          }
        });
      }

      sortByOriginalMediaOrder(elements) {
        return [...elements].sort((left, right) => {
          const leftIndex = Number(left.dataset.todayOriginalOrder || 0);
          const rightIndex = Number(right.dataset.todayOriginalOrder || 0);
          return leftIndex - rightIndex;
        });
      }

      applyVariantMediaGalleryOrder(visibleSlides) {
        const slides = [...this.querySelectorAll('.product__media-item')];
        this.ensureOriginalMediaOrder(slides);
        const orderedSlides = this.mergeMediaSlides(
          visibleSlides,
          this.sortByOriginalMediaOrder(slides.filter((slide) => !visibleSlides.includes(slide))),
        );

        this.applyMediaElementOrder(orderedSlides);

        const thumbnails = [...(this.elements?.thumbnails?.querySelectorAll('.product__thumbs-item') || this.querySelectorAll('.product__thumbs-item'))];
        this.ensureOriginalMediaOrder(thumbnails);
        if (!thumbnails.length) return;

        const thumbnailByTarget = new Map(thumbnails.map((thumbnail) => [String(thumbnail.dataset.target || ''), thumbnail]));
        const orderedTargets = visibleSlides.map((slide) => String(slide.dataset.mediaId || '')).filter(Boolean);
        const orderedThumbnailSet = new Set();
        const orderedThumbnails = [
          ...orderedTargets.map((target) => thumbnailByTarget.get(target)).filter((thumbnail) => {
            if (!thumbnail || orderedThumbnailSet.has(thumbnail)) return false;
            orderedThumbnailSet.add(thumbnail);
            return true;
          }),
          ...this.sortByOriginalMediaOrder(thumbnails.filter((thumbnail) => !orderedThumbnailSet.has(thumbnail))),
        ];
        this.applyMediaElementOrder(orderedThumbnails);
      }

      applyMediaElementOrder(elements) {
        const parent = elements[0]?.parentElement;
        if (!parent) return;
        const members = new Set(elements);
        const current = [...parent.children].filter(element => members.has(element));
        if (current.length === elements.length && current.every((element, index) => element === elements[index])) return;
        elements.forEach(element => parent.appendChild(element));
      }

      setVariantMediaElementVisibility(element, visible) {
        if (!element) return;
        if (element.hidden === visible) element.hidden = !visible;
        const display = visible ? '' : 'none';
        if (element.style.display !== display) element.style.display = display;
        const ariaHidden = visible ? 'false' : 'true';
        if (element.getAttribute('aria-hidden') !== ariaHidden) element.setAttribute('aria-hidden', ariaHidden);
        if (element.classList.contains('is-today-variant-media-hidden') === visible) element.classList.toggle('is-today-variant-media-hidden', !visible);
      }

      applyActiveVariantMedia(options = {}) {
        return this.applyVariantMediaForVariant(this.activeVariantId || this.getCurrentVariantId(), null, options);
      }

      applyVariantMediaForVariant(variantId, variant = null, options = {}) {
        const assignments = this.getVariantMediaAssignments(variantId, variant);
        const slides = [...this.querySelectorAll('.product__media-item')];
        if (!slides.length) return false;

        this.ensureOriginalMediaOrder(slides);
        // Identities stay fresh across operations, including responsive source changes.
        const identities = { slides: new Map(), assignments: new Map() };
        const matchingSlides = assignments.length
          ? this.getOrderedVariantMediaSlides(slides, assignments, identities)
          : [];
        const globalSlides = this.getGlobalVariantMediaSlides(slides, identities);
        const hasGlobalVariantMedia = globalSlides.length > 0;
        const hasExplicitVariantMedia = assignments.some((assignment) => assignment.method !== 'studio-view-variant');
        const minimumMatches = hasExplicitVariantMedia ? 1 : 2;
        const shouldFilter = hasGlobalVariantMedia || (assignments.length > 0 && matchingSlides.length >= minimumMatches);
        const originalSlides = this.sortByOriginalMediaOrder(slides);
        const visibleSlides = shouldFilter
          ? this.mergeMediaSlides(matchingSlides, globalSlides)
          : this.mergeMediaSlides(originalSlides.slice(0, 1), matchingSlides, originalSlides);
        const visibleSlideSet = new Set(visibleSlides);
        const visibleMediaIds = new Set(visibleSlides.map((slide) => String(slide.dataset.mediaId || '')).filter(Boolean));

        slides.forEach((slide) => this.setVariantMediaElementVisibility(slide, !shouldFilter || visibleSlideSet.has(slide)));

        const thumbnails = [...(this.elements?.thumbnails?.querySelectorAll('.product__thumbs-item') || this.querySelectorAll('.product__thumbs-item'))];
        thumbnails.forEach((thumbnail) => {
          const target = String(thumbnail.dataset.target || '').trim();
          this.setVariantMediaElementVisibility(thumbnail, !shouldFilter || (target && visibleMediaIds.has(target)));
        });

        this.classList.toggle('is-today-variant-media-filtered', shouldFilter);
        this.applyVariantMediaGalleryOrder(visibleSlides);
        this.refreshDesignStudioMediaMap();
        this.updateVariantMediaSlider(visibleSlides, { shouldFilter, ...options });
        if (options.announce !== false) this.dispatchVariantMediaUpdated(variantId, shouldFilter, visibleSlides);
        else if (this.initializingMedia) this.preparedVariantMedia = { variantId, filtered: shouldFilter, slides: visibleSlides };
        return shouldFilter;
      }

      updateVariantMediaSlider(visibleSlides, options = {}) {
        const slider = this.sliderInstance?.slider;
        const firstVisibleSlide = visibleSlides.find((slide) => !slide.hidden);
        const targetIndex = firstVisibleSlide ? [...this.querySelectorAll('.product__media-item')].indexOf(firstVisibleSlide) : -1;

        this.sliderInstance?.slider?.update?.();
        this.thumbsInstance?.slider?.update?.();

        if (!slider || slider.destroyed || targetIndex < 0) return;
        if (options.render === false) return;

        const activeSlide = slider.slides?.[slider.activeIndex];
        const activeStillVisible = activeSlide && !activeSlide.hidden;
        if (!activeStillVisible || options.forceSlide) {
          slider.slideTo(targetIndex, 0, false);
          this.selectedMediaIndex = targetIndex;
          this.syncThumbActiveState(targetIndex, firstVisibleSlide);
          this.primeMediaAroundIndex(targetIndex);
        } else {
          this.syncThumbActiveState(slider.activeIndex, activeSlide);
        }

        this.renderDesignStudio();
      }

      dispatchVariantMediaUpdated(variantId, filtered, visibleSlides) {
        this.dispatchEvent(
          new CustomEvent('todaymerch:variant-media-updated', {
            bubbles: true,
            detail: {
              source: 'media-gallery',
              variantId: String(variantId || ''),
              filtered,
              mediaIds: visibleSlides.map((slide) => String(slide.dataset.mediaId || '')).filter(Boolean),
            },
          }),
        );
      }

      refreshDesignStudioMediaMap() {
        this.mediaViewMap = new Map();
        this.mediaViewSlideMap = new Map();
        const slides = [...this.querySelectorAll('.product__media-item')];

        slides.forEach((slide, index) => {
          const configuredView = this.getConfiguredStudioViewForSlide(slide, index);
          const viewKey = configuredView?.key || this.inferMediaViewKey(slide, index);
          if (slide.dataset.markingView !== viewKey) slide.dataset.markingView = viewKey;
        });

        const navigationSlides = this.getDesignNavigationSlides(slides);
        for (const [navigationIndex, slide] of navigationSlides.entries()) {
          const slideIndex = slides.indexOf(slide);
          if (slideIndex < 0) continue;

          const viewKey = slide.dataset.markingView || this.inferMediaViewKey(slide, slideIndex);
          const photoViewKey = this.getPhotoViewKey(slide);
          this.setMediaViewMapEntry(viewKey, slideIndex);
          this.setMediaViewMapEntry(photoViewKey, slideIndex);
          this.setMediaViewSlideMapEntry(viewKey, slide);
          this.setMediaViewSlideMapEntry(photoViewKey, slide);
          if (navigationIndex === 0) {
            this.setMediaViewMapEntry('front', slideIndex);
            this.setMediaViewSlideMapEntry('front', slide);
          }
        }
      }

      getDesignNavigationSlides(slides = [...this.querySelectorAll('.product__media-item')]) {
        const styles = new Map();
        const visibleSlides = slides.filter((slide) => this.isDesignNavigationSlideVisible(slide, styles));
        return visibleSlides.length ? visibleSlides : slides;
      }

      isDesignNavigationSlideVisible(slide, styles = new Map()) {
        if (!slide || slide.hidden || slide.getAttribute('aria-hidden') === 'true') return false;
        if (!slide.isConnected) return false;

        const readStyle = (element) => {
          if (!styles.has(element)) styles.set(element, window.getComputedStyle(element));
          return styles.get(element);
        };
        const style = readStyle(slide);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        let parent = slide.parentElement;
        while (parent && parent !== this) {
          const parentStyle = readStyle(parent);
          if (parent.hidden || parentStyle.display === 'none' || parentStyle.visibility === 'hidden') return false;
          parent = parent.parentElement;
        }

        return true;
      }

      inferMediaViewKey(slide, index = 0) {
        const image = slide?.querySelector('img');
        const source = this.normalizeStudioKey(
          [
            slide?.dataset.markingView,
            image?.getAttribute('alt'),
            image?.currentSrc || image?.src,
            slide?.dataset.src,
          ].filter(Boolean).join(' '),
        );

        const orientation = this.getStudioOrientation(source);
        if (['left_sleeve', 'right_sleeve', 'back', 'sleeve', 'front'].includes(orientation)) return orientation;

        return index === 0 ? 'front' : this.getPhotoViewKey(slide) || `photo-${index + 1}`;
      }

      getStudioOrientation(value) {
        const normalized = this.normalizeStudioKey(value);
        if (/(^|_)(dos|back|rear|arriere|verso)($|_)/.test(normalized)) return 'back';
        if (/(^|_)(face|front|devant)($|_)/.test(normalized)) return 'front';
        if (/(^|_)(manche_gauche|left_sleeve|sleeve_left)($|_)/.test(normalized)) return 'left_sleeve';
        if (/(^|_)(manche_droite|right_sleeve|sleeve_right)($|_)/.test(normalized)) return 'right_sleeve';
        if (/(^|_)(manche|sleeve)($|_)/.test(normalized)) return 'sleeve';
        if (/(^|_)(cote|side|lateral)($|_)/.test(normalized)) return 'side';
        if (/(^|_)(360|wrap|tour_complet|marquage_360)($|_)/.test(normalized)) return 'wrap';
        return '';
      }

      setMediaViewMapEntry(viewKey, index) {
        if (!viewKey) return;
        const keys = [String(viewKey), this.normalizeStudioKey(viewKey)].filter(Boolean);
        keys.forEach((key) => {
          if (!this.mediaViewMap.has(key)) this.mediaViewMap.set(key, index);
        });
      }

      setMediaViewSlideMapEntry(viewKey, slide) {
        if (!viewKey || !slide) return;
        const keys = [String(viewKey), this.normalizeStudioKey(viewKey)].filter(Boolean);
        keys.forEach((key) => {
          if (!this.mediaViewSlideMap.has(key)) this.mediaViewSlideMap.set(key, slide);
        });
      }

      getPhotoViewKey(slide) {
        const mediaId = String(slide?.dataset?.mediaId || '').trim();
        return mediaId ? `photo-${mediaId}` : '';
      }

      getTodayStudioConfig() {
        if (this.todayStudioConfig !== undefined) return this.todayStudioConfig;
        const root = this.getProductRoot();
        const script = root?.querySelector?.('[data-todays-studio-config]') || document.querySelector('[data-todays-studio-config]');
        if (!script) {
          this.todayStudioConfig = null;
          return this.todayStudioConfig;
        }

        try {
          const parsedConfig = JSON.parse(script.textContent || '{}');
          const localizedConfig = window.TodayMerchStudioI18n?.localizeConfigNode?.(script, parsedConfig) || parsedConfig;
          this.todayStudioConfig = window.TodayMerchStudioConfigCompat?.adapt?.(localizedConfig) || localizedConfig;
        } catch (error) {
          this.todayStudioConfig = null;
        }
        return this.todayStudioConfig;
      }

      getTodayStudioViews() {
        const config = this.getTodayStudioConfig();
        const views = Array.isArray(config?.views) ? config.views : [];
        const zones = Array.isArray(config?.zones) ? config.zones : [];
        const zoneViewKeys = new Set(
          zones
            .filter((zone) => zone && zone.active !== false && zone.active !== 'false')
            .map((zone) => String(zone.view || '').trim())
            .filter(Boolean),
        );

        if (!zoneViewKeys.size) return views;
        return views.filter((view) => zoneViewKeys.has(String(view?.key || view?.view || '').trim()));
      }

      getConfiguredStudioViewForSlide(slide, index = 0) {
        const views = this.getTodayStudioViews();
        if (!views.length) return null;

        const slideMediaId = this.getResourceNumericId(slide?.dataset?.mediaId || '');
        if (slideMediaId) {
          const mediaIdMatch = views.find((view) => (
            this.getResourceNumericId(view?.mediaId || view?.media_id || view?.fileId || view?.file_id || '') === slideMediaId
          ));
          if (mediaIdMatch) return mediaIdMatch;
        }

        const photoViewKey = this.getPhotoViewKey(slide);
        const normalizedPhotoViewKey = this.normalizeStudioKey(photoViewKey);
        if (normalizedPhotoViewKey) {
          const mediaIdMatch = views.find((view) => this.normalizeStudioKey(view?.key || view?.view || '') === normalizedPhotoViewKey);
          if (mediaIdMatch) return mediaIdMatch;
        }

        const mappedVariantView = this.getMappedStudioViewForSlide(slide, views, index);
        if (mappedVariantView) return mappedVariantView;

        const galleryMediaIds = new Set();
        for (const gallerySlide of this.querySelectorAll?.('.product__media-item') || []) {
          const galleryMediaId = this.getResourceNumericId(gallerySlide?.dataset?.mediaId || '');
          if (galleryMediaId) galleryMediaIds.add(galleryMediaId);
        }

        const slideImageKeys = this.getSlideImageKeys(slide);
        const imageMatch = views.find((view) => {
          const viewMediaId = this.getResourceNumericId(
            view?.mediaId || view?.media_id || view?.fileId || view?.file_id || '',
          );
          if (viewMediaId && galleryMediaIds.has(viewMediaId)) return false;

          const viewImageKey = this.getImageFileKey(view?.image || view?.previewImage || view?.imageUrl || '');
          if (viewImageKey && slideImageKeys.has(viewImageKey)) return true;
          const viewLineage = this.getStableImageLineage(view?.image || view?.previewImage || view?.imageUrl || '');
          if (!viewLineage) return false;
          return Array.from(slideImageKeys).some((key) => this.getStableImageLineage(key) === viewLineage);
        });
        if (imageMatch) return imageMatch;

        return index === 0 ? views.find((view) => this.normalizeStudioKey(view?.key || view?.view || '') === 'front') || null : null;
      }

      getMappedStudioViewForSlide(slide, views = this.getTodayStudioViews(), index = 0) {
        const config = this.getTodayStudioConfig();
        const variantMedia = config?.variantMedia || config?.variant_media || {};
        const entries = variantMedia?.variants && typeof variantMedia.variants === 'object'
          ? variantMedia.variants
          : variantMedia;
        if (!entries || typeof entries !== 'object') return null;

        const variantKeys = new Set(this.getVariantLookupKeys(this.activeVariantId || this.getCurrentVariantId()));
        const entry = Object.entries(entries).find(([entryKey, value]) => {
          const entryKeys = this.normalizeIdList([
            entryKey,
            value?.variantId,
            value?.variant_id,
            value?.id,
            value?.gid,
          ]);
          return entryKeys.some((key) => variantKeys.has(key));
        })?.[1];
        if (!entry || typeof entry !== 'object') return null;

        const media = entry.media && typeof entry.media === 'object' ? entry.media : entry;
        const viewByKey = new Map(views.map((view) => [String(view?.key || view?.view || ''), view]));
        const slideMediaId = this.getResourceNumericId(slide?.dataset?.mediaId || '');
        const galleryMediaIds = new Set(Array.from(this.querySelectorAll?.('.product__media-item') || [])
          .map((gallerySlide) => this.getResourceNumericId(gallerySlide?.dataset?.mediaId || ''))
          .filter(Boolean));
        const candidates = Object.entries(media).flatMap(([placementKey, assignment]) => {
          if (['variantId', 'variant_id', 'label', 'color', 'optionKey', 'gallery'].includes(placementKey)) return [];
          const viewKey = String(
            assignment?.viewKey
            || assignment?.view_key
            || assignment?.viewId
            || assignment?.view_id
            || assignment
            || '',
          ).trim();
          const view = viewByKey.get(viewKey);
          const viewMediaId = this.getResourceNumericId(
            view?.mediaId || view?.media_id || view?.fileId || view?.file_id || '',
          );
          // A mapped photo already in the gallery must not claim another slide.
          // Keep the variant fallback for missing or replaced legacy media.
          if (viewMediaId && galleryMediaIds.has(viewMediaId) && viewMediaId !== slideMediaId) return [];
          return view ? [{ placementKey, view }] : [];
        });
        if (!candidates.length) return null;

        const inferred = this.normalizeStudioKey(this.inferMediaViewKey(slide, index));
        const slideOrientation = this.getStudioOrientation(inferred);
        if (slideOrientation) {
          const oriented = candidates.filter(({ placementKey, view }) => (
            this.getStudioOrientation(`${placementKey} ${view?.label || ''} ${view?.alt || ''}`) === slideOrientation
          ));
          if (oriented.length === 1) return oriented[0].view;
        }
        return candidates.length === 1 ? candidates[0].view : null;
      }

      getSlideImageKeys(slide) {
        const image = slide?.querySelector('img');
        return [
          slide?.dataset?.src,
          image?.currentSrc,
          image?.src,
          image?.getAttribute('src'),
          image?.getAttribute('data-src'),
        ].reduce((keys, url) => {
          const key = this.getImageFileKey(url);
          if (key) keys.add(key);
          return keys;
        }, new Set());
      }

      getImageFileKey(url) {
        if (!url) return '';
        const source = String(url);
        // Only memoize pure string normalization, never photo/variant readiness.
        // Large data URLs must not occupy the bounded product-media cache.
        const cacheable = source.length <= 2048;
        const cache = this.imageFileKeyCache ||= new Map();
        const cacheKey = `${window.location.origin}|${source}`;
        if (cacheable && cache.has(cacheKey)) return cache.get(cacheKey);
        let result;
        try {
          const parsed = new URL(source, window.location.origin);
          result = decodeURIComponent(parsed.pathname.split('/').pop() || '').toLowerCase();
        } catch (error) {
          result = source.split('?')[0].split('/').pop().toLowerCase();
        }
        if (cacheable) {
          if (cache.size >= 512) cache.delete(cache.keys().next().value);
          cache.set(cacheKey, result);
        }
        return result;
      }

      getStableImageLineage(url) {
        let stem = this.getImageFileKey(url).replace(/\.[a-z0-9]+$/i, '');
        let previous = '';
        while (stem && stem !== previous) {
          previous = stem;
          stem = stem.replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
        }
        return stem;
      }

      escapeHtml(value) {
        return String(value || '').replace(/[&<>"']/g, (char) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[char]));
      }

      escapeAttr(value) {
        return this.escapeHtml(value);
      }

      normalizeStudioKey(value) {
        const source = String(value || '');
        const cacheable = source.length <= 512;
        const cache = this.studioKeyCache ||= new Map();
        if (cacheable && cache.has(source)) return cache.get(source);
        const result = source
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/&/g, 'and')
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .replace(/_+/g, '_');
        if (cacheable) {
          if (cache.size >= 512) cache.delete(cache.keys().next().value);
          cache.set(source, result);
        }
        return result;
      }

      slideToDesignView(viewKey, { preserveViewport = false, render = true } = {}) {
        this.refreshDesignStudioMediaMap();

        const targetIndex = this.getMediaIndexForView(viewKey);
        if (targetIndex < 0) return;
        const targetSlide = this.getMediaSlideForView(viewKey);

        const slider = this.sliderInstance?.slider;

        if (!this.initialized && (!slider || slider.destroyed)) {
          this.pendingDesignViewKey = viewKey;
          return;
        }

        if (slider && !slider.destroyed) {
          this.selectedMediaIndex = targetIndex;

          if (slider.activeIndex !== targetIndex) {
            this.designViewSwitchInProgress = true;
            try { slider.slideTo(targetIndex, 0, false); }
            finally { this.designViewSwitchInProgress = false; }
          }

          this.syncThumbActiveState(targetIndex, targetSlide);
          this.primeMediaAroundIndex(targetIndex);
          if (render) this.renderDesignStudio();
          return;
        }

        if (targetSlide && !FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const isXdV2 = this.closest('product-info')?.dataset.personalizationExperience === 'xd-v2';
          // Only the synchronous technique change may preserve the document viewport.
          const techniqueViewKey = this.xdTechniqueChangeViewKey;
          const preserveTechniqueViewport = isXdV2 && techniqueViewKey
            && this.normalizeStudioKey(techniqueViewKey) === this.normalizeStudioKey(viewKey);
          if (!preserveViewport && !preserveTechniqueViewport) {
            targetSlide.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }

        if (render) this.renderDesignStudio();
      }

      applyPendingDesignView() {
        if (!this.pendingDesignViewKey) return;

        const pendingViewKey = this.pendingDesignViewKey;
        this.pendingDesignViewKey = '';
        this.slideToDesignView(pendingViewKey);
      }

      getMediaIndexForView(viewKey) {
        if (!viewKey) return -1;
        if (!this.mediaViewMap || !this.mediaViewMap.size) {
          this.refreshDesignStudioMediaMap();
        }

        const normalizedViewKey = this.normalizeStudioKey(viewKey);
        const fallbackKeys = {
          heart: ['front'],
          chest: ['front'],
          custom: ['front'],
          right_sleeve: ['right_sleeve', 'sleeve'],
          left_sleeve: ['left_sleeve', 'sleeve'],
          sleeve: ['sleeve', 'right_sleeve', 'left_sleeve'],
          back: ['back'],
          front: ['front'],
        };
        const candidates = fallbackKeys[normalizedViewKey] || [normalizedViewKey];

        for (const candidate of candidates) {
          if (this.mediaViewMap.has(candidate)) return this.mediaViewMap.get(candidate);
        }

        return -1;
      }

      getMediaSlideForView(viewKey) {
        if (!viewKey) return null;
        if (!this.mediaViewSlideMap || !this.mediaViewSlideMap.size) {
          this.refreshDesignStudioMediaMap();
        }

        const normalizedViewKey = this.normalizeStudioKey(viewKey);
        const fallbackKeys = {
          heart: ['front'],
          chest: ['front'],
          custom: ['front'],
          right_sleeve: ['right_sleeve', 'sleeve'],
          left_sleeve: ['left_sleeve', 'sleeve'],
          sleeve: ['sleeve', 'right_sleeve', 'left_sleeve'],
          back: ['back'],
          front: ['front'],
        };
        const candidates = fallbackKeys[normalizedViewKey] || [normalizedViewKey];

        for (const candidate of candidates) {
          if (this.mediaViewSlideMap.has(candidate)) return this.mediaViewSlideMap.get(candidate);
        }

        return null;
      }

      getActiveMediaSlide() {
        const slider = this.sliderInstance?.slider;
        if (slider && !slider.destroyed) {
          return slider.slides?.[slider.activeIndex] || null;
        }

        return this.querySelector('.product__media-item');
      }

      getActiveMediaIndex() {
        const slider = this.sliderInstance?.slider;
        if (slider && !slider.destroyed) {
          return slider.activeIndex || 0;
        }

        return 0;
      }

      emitMediaViewSelection(index = this.getActiveMediaIndex()) {
        const slide = this.querySelectorAll('.product__media-item')[index];
        if (!slide) return;

        const viewKey = slide.dataset.markingView || this.inferMediaViewKey(slide, index);
        if (!viewKey) return;

        this.dispatchEvent(
          new CustomEvent('todaymerch:product-media-view-selected', {
            bubbles: true,
            detail: {
              source: 'media-gallery',
              viewKey,
              mediaId: slide.dataset.mediaId || '',
              mediaIndex: index,
            },
          }),
        );
      }

      renderDesignStudio(options = {}) {
        this.scheduleMobileDesignEditorSync();
        this.zoneInteractions?.sync();
        this.syncDesignZoneIntro();
        const hasLogo = this.isDesignStudioStateForActiveVariant() && Boolean(this.designStudioState?.hasLogo);
        this.classList.toggle('is-design-mode', hasLogo);

        const slides = [...this.querySelectorAll('.product__media-item')];
        const activeSlide = options.slide || this.getActiveMediaSlide();
        const activeZone = options.zone || this.getRenderableDesignZone(activeSlide);
        if (hasLogo) this.syncInactiveDesignArtworkImageMetrics(activeZone);

        slides.forEach((slide) => {
          const overlay = this.designStudioOverlays.get(slide);
          if (overlay && (!hasLogo || slide !== activeSlide || overlay.dataset.zoneKey !== activeZone?.key)) {
            overlay.querySelectorAll('[data-role="product-design-artwork"], [data-role="product-design-artwork-copy"]')
              .forEach(artwork => window.TodayMerchProductPreviewEffects?.restore(artwork));
          }
          if (overlay) overlay.hidden = true;
          slide.classList.remove('is-design-image-zoom-target');
        });

        this.syncProductPhotoZoom(activeSlide);

        if (!hasLogo) {
          if (this.isDesignStudioStateForActiveVariant()) {
            (this.designStudioState?.zones || []).forEach((zone) => this.syncDesignArtworkSources(zone));
          }
          this.updateDesignViewSwitcher(null, activeZone, activeSlide);
          this.zoneInteractions?.sync();
          return;
        }

        if (!activeSlide || activeSlide.dataset.mediaType !== 'image' || !activeZone) {
          this.hideDesignViewSwitcher();
          return;
        }

        if (!this.hasDesignPhotoSurface(activeZone)) {
          this.updateDesignViewSwitcher(null, activeZone, activeSlide);
          return;
        }

        this.renderDesignStudioOverlay(activeSlide, activeZone);
        this.syncProductPhotoZoom(activeSlide);
        this.zoneInteractions?.sync();
      }

      renderDesignStudioOverlay(activeSlide, activeZone, { mobileSurface = false } = {}) {
        const overlay = this.ensureDesignStudioOverlay(activeSlide);
        if (!mobileSurface && (!this.hasDesignPhotoSurface(activeZone)
          || activeZone?.photoMediaId && !this.isZoneVisibleInView(activeZone, activeSlide?.dataset.markingView, activeSlide))) {
          overlay.hidden = true;
          return overlay;
        }
        if (mobileSurface) overlay.dataset.mobileDesignSurface = 'true';
        const zoneControl = overlay.querySelector('[data-role="product-design-zone"]');
        const artwork = overlay.querySelector('[data-role="product-design-artwork"]');
        const artworkImage = overlay.querySelector('[data-role="product-design-artwork-image"]');
        const artworkName = overlay.querySelector('[data-role="product-design-artwork-name"]');
        const zoneLabel = overlay.querySelector('[data-role="product-design-zone-label"]');
        const clearButton = overlay.querySelector('[data-role="product-design-clear"]');
        const toolbar = overlay.querySelector('[data-role="product-design-toolbar"]');
        const downloadButton = overlay.querySelector('[data-role="product-design-download"]');
        const shareButton = overlay.querySelector('[data-role="product-design-share"]');
        const colorsButton = overlay.querySelector('[data-design-tool="colors"]');
        const removeBackgroundButton = overlay.querySelector('[data-design-tool="remove-background"]');
        const duplicateButton = overlay.querySelector('[data-design-tool="duplicate"]');
        const placement = this.getSupplierPrintPlanePlacement(activeSlide, activeZone, { allowUnprojected: mobileSurface }) || this.getDesignZonePlacement(activeZone);
        if (!mobileSurface) this.syncDesignArtworkSources(activeZone);
        const activeArtworkId = this.getActiveDesignArtworkId(activeZone.key);
        const activeArtworkSource = this.getDesignArtworkSource(activeZone, activeArtworkId);
        const hasDownloadableArtwork = this.getDesignArtworkItems(activeZone).some((item) => item.previewUrl);

        overlay.hidden = false;
        overlay.dataset.viewKey = activeZone.viewKey || '';
        overlay.dataset.previewMode = this.isUnfoldedDesignPrintPlane(activeZone) ? 'unfolded_360'
          : this.usesNativeDesignPrintPlane(activeZone) ? 'supplier_plane' : activeZone.photoPreviewMode || '';
        overlay.dataset.zoneKey = activeZone.key || '';
        overlay.dataset.artworkId = activeArtworkId;
        const supplierPlane = this.getSupplierPrintPlane(activeZone);
        // Millimetre editing coordinates are not a photo projection. Only the
        // separate editor may show a blank plane; the product keeps its photo.
        overlay.classList.toggle('has-supplier-print-plane', Boolean(supplierPlane) && !this.hasDesignPhotoSurface(activeZone));
        if (supplierPlane) {
          overlay.dataset.supplierPrintPlane = 'true';
          overlay.dataset.supplierPrintPlaneId = supplierPlane.planeId || supplierPlane.id || supplierPlane.editingPlaneId || '';
          overlay.style.setProperty('--product-design-supplier-plane-aspect', String(supplierPlane.aspectRatio || 1));
        } else {
          delete overlay.dataset.supplierPrintPlane; delete overlay.dataset.supplierPrintPlaneId;
          overlay.style.removeProperty('--product-design-supplier-plane-aspect');
        }
        overlay.style.setProperty('--product-design-zone-x', `${placement.x}%`);
        overlay.style.setProperty('--product-design-zone-y', `${placement.y}%`);
        overlay.style.setProperty('--product-design-zone-width', `${placement.width}%`);
        overlay.style.setProperty('--product-design-zone-height', `${placement.height}%`);
        const shell = overlay.querySelector('[data-role="product-design-zone-shell"]');
        const photoRotation = Number(placement.rotation || 0);
        if (shell) {
          if (photoRotation) {
            shell.dataset.photoRotation = String(photoRotation);
            shell.style.transform = `rotate(${photoRotation}deg)`;
          } else {
            delete shell.dataset.photoRotation;
            shell.style.removeProperty('transform');
          }
        }

        this.renderDesignZoneParts(
          overlay,
          activeZone,
          placement,
          this.getDesignArtworkPlacement(activeZone, activeArtworkId),
        );

        if (zoneControl) {
          zoneControl.dataset.zoneKey = activeZone.key || '';
          zoneControl.setAttribute('aria-label', activeZone.label || this.string('zoneFallback'));
        }

        if (zoneLabel) zoneLabel.textContent = this.usesNativeDesignPrintPlane(activeZone)
          ? `${activeZone.label || 'Zone de marquage'} · aperçu indicatif` : activeZone.label || '';
        if (clearButton) clearButton.dataset.zoneKey = activeZone.key || '';
        if (toolbar) toolbar.dataset.zoneKey = activeZone.key || '';
        if (downloadButton) downloadButton.dataset.zoneKey = activeZone.key || '';

        overlay.classList.toggle('has-artwork-preview', Boolean(activeArtworkSource.previewUrl));
        overlay.classList.toggle('has-artwork-file', Boolean(activeArtworkSource.fileName));
        overlay.classList.toggle('has-vector-artwork', activeArtworkSource.fileCategory === 'vector');
        overlay.classList.toggle('has-raster-artwork', activeArtworkSource.fileCategory === 'raster');
        overlay.classList.toggle('has-document-artwork', activeArtworkSource.fileCategory === 'document');

        if (artworkImage) {
          artworkImage.onload = null;
          artworkImage.onerror = null;
          // The file name belongs in the upload card, never in the product mock-up.
          artworkImage.alt = '';
          artworkImage.hidden = !activeArtworkSource.previewUrl;
          if (activeArtworkSource.previewUrl) {
            const renderedVariantScope = this.getDesignVariantScopeKey();
            const currentSource = () => {
              if (this.getDesignVariantScopeKey() !== renderedVariantScope
                || artworkImage.getAttribute('src') !== activeArtworkSource.previewUrl) return null;
              const currentZone = this.designStudioState?.zones?.find((zone) => zone.key === activeZone.key);
              return currentZone && this.getDesignArtworkSource(currentZone, activeArtworkId).previewUrl === activeArtworkSource.previewUrl
                ? currentZone : null;
            };
            artworkImage.onload = () => {
              const currentZone = currentSource();
              if (!currentZone || !artworkImage.complete || !artworkImage.naturalWidth) return;
              this.renderDesignArtworkPreviewStatus(artwork, artworkName, activeArtworkSource, artworkImage);
              if (!mobileSurface || !this.hasDesignPhotoSurface(currentZone)) {
                this.syncDesignArtworkImageMetrics(currentZone, artworkImage, activeArtworkId, activeArtworkSource);
                this.updateDesignArtwork(overlay, this.getDesignArtworkPlacement(currentZone, activeArtworkId));
              }
              // A new preview can retain the same original file key and size.
              // Re-enable mobile controls when it loads even if metrics match.
              this.scheduleMobileDesignEditorSync();
            };
            artworkImage.onerror = () => {
              if (currentSource()) this.renderDesignArtworkPreviewStatus(artwork, artworkName, activeArtworkSource, artworkImage, true);
            };

            if (artworkImage.getAttribute('src') !== activeArtworkSource.previewUrl) {
              artworkImage.src = activeArtworkSource.previewUrl;
            } else if ((!mobileSurface || !this.hasDesignPhotoSurface(activeZone)) && artworkImage.complete) {
              this.syncDesignArtworkImageMetrics(activeZone, artworkImage, activeArtworkId, activeArtworkSource);
            }
          } else {
            artworkImage.removeAttribute('src');
          }
        }
        this.renderDesignArtworkPreviewStatus(artwork, artworkName, activeArtworkSource, artworkImage);

        if (artwork) {
          artwork.hidden = !activeArtworkSource.fileName;
          artwork.dataset.zoneKey = activeZone.key || '';
          artwork.dataset.artworkId = activeArtworkId;
          artwork.classList.toggle('is-selected', Boolean(activeArtworkSource.fileName) && !overlay.classList.contains('is-previewing'));
        }

        this.renderDesignArtworkCopies(overlay, activeZone);

        if (clearButton) {
          clearButton.hidden = !activeArtworkSource.fileName;
        }

        if (colorsButton) {
          colorsButton.hidden = !activeArtworkSource.fileName;
          colorsButton.disabled = !activeArtworkSource.fileName;
        }
        if (removeBackgroundButton) {
          removeBackgroundButton.hidden = !activeArtworkSource.previewUrl;
          removeBackgroundButton.disabled = !activeArtworkSource.previewUrl;
        }

        if (duplicateButton) {
          duplicateButton.hidden = !activeArtworkSource.previewUrl;
          duplicateButton.disabled = !activeArtworkSource.previewUrl;
        }

        if (downloadButton) {
          downloadButton.hidden = !hasDownloadableArtwork;
          downloadButton.disabled = !hasDownloadableArtwork;
        }
        if (shareButton) {
          shareButton.hidden = !hasDownloadableArtwork;
          shareButton.disabled = !hasDownloadableArtwork;
        }

        this.updateDesignArtwork(overlay, this.getDesignArtworkPlacement(activeZone, activeArtworkId));
        if (!mobileSurface) {
          this.updateDesignImageZoomControls(overlay);
          this.emitDesignArtworkTransform(activeZone);
          this.updateDesignViewSwitcher(overlay, activeZone, activeSlide);
          this.updateDesignImagePanAvailability(overlay);
          this.syncDesignControlsVisibility();
        }
        return overlay;
      }

      getBaseDesignArtworkId() {
        return 'base';
      }

      isDesignStudioStateForActiveVariant(state = this.designStudioState) {
        if (!state) return false;

        const activeVariantId = String(this.activeVariantId || this.getCurrentVariantId() || '').trim();
        const stateVariantId = String(state.variantId || '').trim();
        const hasDefaultState = !stateVariantId || stateVariantId === 'default';
        const hasDefaultActiveVariant = !activeVariantId || activeVariantId === 'default';

        if (hasDefaultState || hasDefaultActiveVariant) return hasDefaultState && hasDefaultActiveVariant;
        if (stateVariantId === activeVariantId) return true;

        const activeKeys = new Set(this.getVariantLookupKeys(activeVariantId));
        return this.getVariantLookupKeys(stateVariantId).some((key) => activeKeys.has(key));
      }

      getDesignVariantScopeKey() {
        const variantId = String(this.designStudioState?.variantId || '').trim();
        return variantId ? `variant-${variantId}` : 'variant-default';
      }

      getScopedDesignZoneKey(zoneKey) {
        return `${this.getDesignVariantScopeKey()}::${zoneKey || 'default'}`;
      }

      preparePersonalizationResumePlacement({ variantId, zoneKey, transform, partId, image }) {
        const keys = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop'];
        if (!zoneKey || String(this.designStudioState?.variantId) !== String(variantId)
          || this.designInteraction || this.mobileDesignEditor?.gesture || this.mobileDesignEditor?.control
          || !image?.complete || !image.naturalWidth || !image.naturalHeight || !transform
          || !['x', 'y', 'width', 'height', 'rotation'].every(key => Number.isFinite(transform[key]))
          || transform.width <= 0 || transform.height <= 0
          || (this.designArtworkCopies.get(this.getScopedDesignZoneKey(zoneKey)) || []).length) throw new Error('resume_geometry_unavailable');
        const placement = this.getPlacementPreservingArtworkImageMetrics({ ...transform,
          partId: partId || '', artworkRatio: transform.width / transform.height }, image);
        if (keys.some(key => transform[key] !== undefined && placement[key] !== transform[key])) throw new Error('resume_geometry_changed');
        const names = ['designArtworkPlacements', 'designArtworkCopies', 'designActiveArtworkIds', 'designArtworkImageMetricKeys',
          'designArtworkContentFrameBindings', 'designArtworkNativePlanes', 'designArtworkCoordinateModes', 'designArtworkRestoredPlanes',
          'designArtworkImageUpdateModes', 'designZoneSourceKeys', 'designZoneOriginalIdentities', 'designArtworkHistory'];
        const before = Object.fromEntries(names.map(name => [name, this[name]]));
        const previousState = this.designStudioState;
        let committed = false;
        return Object.freeze({
          placement: Object.freeze({ ...placement }),
          commit: ({ state, zone }) => {
            if (committed || String(state?.variantId) !== String(variantId) || zone?.key !== zoneKey
              || this.designInteraction || this.mobileDesignEditor?.gesture || this.mobileDesignEditor?.control) return false;
            committed = true;
            for (const name of names) this[name] = new Map(this[name]);
            this.designStudioState = state;
            this.restoreDesignArtworkCoordinateMode(zoneKey, transform);
            const scoped = this.getScopedDesignZoneKey(zoneKey), artwork = this.getDesignArtworkStateKey(zoneKey, 'base');
            this.designArtworkPlacements.set(scoped, { ...placement });
            this.designArtworkContentFrameBindings.delete(artwork);
            const nativePlane = this.usesNativeDesignPrintPlane(zone) && this.getSupplierPrintPlane(zone);
            if (nativePlane) this.designArtworkNativePlanes.set(scoped, this.getDesignCoordinatePlane(zone) || { widthMm: nativePlane.widthMm, heightMm: nativePlane.heightMm });
            else this.designArtworkNativePlanes.delete(scoped);
            this.designArtworkCopies.delete(scoped); this.designActiveArtworkIds.set(scoped, 'base');
            this.designArtworkHistory.set(artwork, { past: [], future: [] });
            this.designZoneSourceKeys.set(scoped, `${zone.fileKey || zone.previewUrl || ''}|${zone.fileName || ''}|${zone.fileCategory || ''}|${zone.productionFileKey || ''}`);
            this.designZoneOriginalIdentities.set(scoped, zone.originalIdentity || '');
            this.designArtworkImageMetricKeys.set(artwork, this.getDesignArtworkImageMetricKey(zone, image));
            this.designArtworkImageUpdateModes.set(artwork, 'preserve-production-placement');
            return true;
          },
          rollback: () => { if (!committed) return; for (const name of names) this[name] = before[name]; this.designStudioState = previousState; committed = false; },
        });
      }

      getDesignArtworkStateKey(zoneKey, artworkId = this.getActiveDesignArtworkId(zoneKey)) {
        return `${this.getScopedDesignZoneKey(zoneKey)}::${artworkId || this.getBaseDesignArtworkId()}`;
      }

      createDesignArtworkCopyId() {
        this.designArtworkCopyCounter += 1;
        return `copy-${Date.now().toString(36)}-${this.designArtworkCopyCounter}`;
      }

      getActiveDesignArtworkId(zoneKey) {
        if (!zoneKey) return this.getBaseDesignArtworkId();

        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);
        const activeArtworkId = this.designActiveArtworkIds.get(scopedZoneKey) || this.getBaseDesignArtworkId();
        if (activeArtworkId === this.getBaseDesignArtworkId() || this.getDesignArtworkCopy(zoneKey, activeArtworkId)) {
          return activeArtworkId;
        }

        this.designActiveArtworkIds.set(scopedZoneKey, this.getBaseDesignArtworkId());
        return this.getBaseDesignArtworkId();
      }

      setActiveDesignArtworkId(zoneKey, artworkId) {
        if (!zoneKey) return;
        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);
        const nextArtworkId = artworkId && (artworkId === this.getBaseDesignArtworkId() || this.getDesignArtworkCopy(zoneKey, artworkId))
          ? artworkId
          : this.getBaseDesignArtworkId();
        this.designActiveArtworkIds.set(scopedZoneKey, nextArtworkId);
      }

      getDesignArtworkProductionContext(zone, artworkId, source = this.getStoredDesignArtworkSource(zone, artworkId)) {
        const selector = this.getProductRoot()?.querySelector?.('product-marking-selector');
        const wrap = selector?.getZoneWrap?.(zone?.key);
        const files = wrap && selector.getLogoFiles?.(wrap) || [];
        if (!selector?.getArtworkProductionSource || !files.length) return null;
        const matches = files.filter((file) => {
          if (selector.getLogoFileKey(file) !== source.fileKey) return false;
          const record = selector.getArtworkProductionSource(file, zone.key, { artworkId });
          return !source.originalIdentity || record.originalIdentity === source.originalIdentity;
        });
        if (matches.length !== 1) return null;
        const original = matches[0];
        return { selector, original, record: selector.getArtworkProductionSource(original, zone.key, { artworkId }) };
      }

      getDesignArtworkSource(zone, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        const source = this.getStoredDesignArtworkSource(zone, artworkId);
        const context = this.getDesignArtworkProductionContext(zone, artworkId, source);
        if (!context?.record) return source;
        const { selector, original, record } = context;
        const previewUrl = selector.getStudioPreviewUrl?.(original, zone.key, source.fileKey, { artworkId });
        const preview = selector.getStudioPreviewRecord?.(original, zone.key, source.fileKey, { artworkId });
        return { ...source, originalIdentity: record.originalIdentity || source.originalIdentity,
          productionFileKey: record.productionFileKey || source.productionFileKey,
          previewUrl: previewUrl === undefined ? source.previewUrl : previewUrl,
          previewStatus: preview?.status || source.previewStatus,
          previewLabel: preview?.label || source.previewLabel };
      }

      async openDesignArtworkColors(zone, artworkId, anchor) {
        if (!zone?.key || !anchor) return;
        const productRoot = this.closest('product-info') || this.getProductRoot();
        const intent = { anchor };
        const initialSource = this.getStoredDesignArtworkSource(zone, artworkId);
        const variantId = String(this.activeVariantId || this.designStudioState?.variantId || '');
        this.designColorsOpenIntent = intent;
        window.TodayMerchProductModules?.clearReport?.(productRoot);
        anchor.setAttribute('aria-busy', 'true');
        try {
          const modules = window.TodayMerchProductModules;
          const localColors = modules?.has(productRoot, 'artworkColors') && !modules.has(productRoot, 'preparation')
            && !['xd-v2', 'gobelet-v1'].includes(productRoot.dataset.personalizationExperience)
            && !['xd', 'midocean', 'pfconcept'].includes(productRoot.dataset.supplierPricing)
            && !productRoot.querySelector('product-marking-selector')?.supplierV2;
          if (localColors) await modules?.ensure(productRoot, 'artworkColors');
          else if (modules?.has(productRoot, 'preparation')) await modules.ensure(productRoot, 'preparation');
          const latestZone = this.designStudioState?.zones?.find((item) => item.key === zone.key);
          const latestSource = latestZone && this.getStoredDesignArtworkSource(latestZone, artworkId);
          if (this.designColorsOpenIntent !== intent || !this.isConnected || !anchor.isConnected
            || latestSource?.fileKey !== initialSource.fileKey || latestSource?.originalIdentity !== initialSource.originalIdentity
            || variantId !== String(this.activeVariantId || this.designStudioState?.variantId || '')
            || this.getActiveDesignArtworkId(zone.key) !== artworkId
            || this.getActiveDesignZone()?.key !== zone.key) return;
          if (localColors) {
            if (!window.TodayMerchArtworkLocalColors?.open) throw new Error('Artwork color editor unavailable');
            if (!await window.TodayMerchArtworkLocalColors.open({ gallery: this, zone: latestZone, artworkId, anchor })) {
              throw new Error('Artwork source unavailable');
            }
            return;
          }
          window.TodayMerchPersonalizationOrchestrator?.get?.(productRoot);
          window.TodayMerchPersonalizationOrchestrator?.attach?.(productRoot);
          productRoot.dispatchEvent(new CustomEvent('todaymerch:artwork-colors-open', {
            bubbles: true, detail: { artworkId, zoneKey: zone.key, anchor },
          }));
        } catch (error) {
          if (this.designColorsOpenIntent !== intent || !anchor.isConnected) return;
          window.TodayMerchProductModules?.report?.(productRoot, 'error');
          productRoot.dispatchEvent(new CustomEvent('todaymerch:artwork-colors-error', {
            bubbles: true, detail: { zoneKey: zone.key, artworkId, reason: 'module-unavailable' },
          }));
        } finally {
          if (this.designColorsOpenIntent === intent || this.designColorsOpenIntent?.anchor !== anchor) anchor.removeAttribute('aria-busy');
        }
      }

      getStoredDesignArtworkSource(zone, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        const copy = artworkId && artworkId !== this.getBaseDesignArtworkId() ? this.getDesignArtworkCopy(zone?.key, artworkId) : null;
        if (copy) {
          return {
            previewUrl: copy.sourceUploadId ? copy.previewUrl || '' : copy.previewUrl || zone?.previewUrl || '',
            previewStatus: copy.previewStatus || '',
            previewLabel: copy.previewLabel || '',
            fileKey: copy.fileKey || zone?.fileKey || '',
            originalIdentity: copy.originalIdentity || '',
            productionFileKey: copy.productionFileKey || '',
            fileName: copy.fileName || zone?.fileName || '',
            fileCategory: copy.fileCategory || zone?.fileCategory || '',
            previewObjectUrl: copy.previewObjectUrl || '',
          };
        }

        return {
          previewUrl: zone?.previewUrl || '',
          previewStatus: zone?.previewStatus || '',
          previewLabel: zone?.previewLabel || '',
          fileKey: zone?.fileKey || '',
          originalIdentity: zone?.originalIdentity || '',
          productionFileKey: zone?.productionFileKey || '',
          fileName: zone?.fileName || '',
          fileCategory: zone?.fileCategory || '',
          previewObjectUrl: '',
        };
      }

      syncBaseDesignArtworkSource(zone) {
        const zoneKey = zone?.key || '';
        if (!zoneKey) return;
        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);

        const nextSourceKey = `${zone.fileKey || zone.previewUrl || ''}|${zone.fileName || ''}|${zone.fileCategory || ''}|${zone.productionFileKey || ''}`;
        const previousSourceKey = this.designZoneSourceKeys.get(scopedZoneKey);
        const hasSource = Boolean(nextSourceKey.replace(/\|/g, ''));

        if (!hasSource) {
          this.clearDesignArtworkForZone(zoneKey);
          this.designZoneSourceKeys.set(scopedZoneKey, nextSourceKey);
          return;
        }

        if (previousSourceKey === nextSourceKey) return;

        const baseStateKey = this.getDesignArtworkStateKey(zoneKey, this.getBaseDesignArtworkId());
        const sameOriginal = zone.originalIdentity && this.designZoneOriginalIdentities.get(scopedZoneKey) === zone.originalIdentity;
        if (sameOriginal) {
          // Adapting a palette changes preview pixel dimensions (SVG previews
          // are upscaled); it must not reapply the initial placement suggestion.
          this.designArtworkImageUpdateModes.set(baseStateKey, 'preserve-production-placement');
        } else if (this.designArtworkImageUpdateModes.get(baseStateKey) === 'preserve-production-placement') {
          this.designArtworkImageUpdateModes.delete(baseStateKey);
        }
        const preserveBase = ['preserve-placement', 'preserve-production-placement'].includes(this.designArtworkImageUpdateModes.get(baseStateKey));
        const isInitialSource = previousSourceKey === undefined;
        const previousOriginal = this.designZoneOriginalIdentities.get(scopedZoneKey);
        if (!isInitialSource && previousOriginal && zone.originalIdentity && previousOriginal !== zone.originalIdentity) {
          this.designArtworkCoordinateModes.delete(scopedZoneKey); this.designArtworkNativePlanes.delete(scopedZoneKey); this.designArtworkRestoredPlanes.delete(scopedZoneKey);
        }

        if (!isInitialSource && !preserveBase) {
          this.clearDesignArtworkCopies(zoneKey);
          this.setActiveDesignArtworkId(zoneKey, this.getBaseDesignArtworkId());
          this.resetDesignArtworkHistory(zoneKey, this.getBaseDesignArtworkId());
          this.designArtworkImageMetricKeys.delete(baseStateKey);
        }

        this.designZoneSourceKeys.set(scopedZoneKey, nextSourceKey);
        this.designZoneOriginalIdentities.set(scopedZoneKey, zone.originalIdentity || '');
      }

      syncDesignArtworkSources(zone) {
        if (!zone?.key) return;

        this.syncBaseDesignArtworkSource(zone);
        this.syncAdditionalDesignArtworkSources(zone);
      }

      getAdditionalDesignArtworkSources(zone) {
        const artworks = Array.isArray(zone?.artworks) ? zone.artworks : [];
        return artworks
          .slice(1)
          .map((artwork, index) => {
            const uploadId = artwork.id || `uploaded-${index + 1}-${artwork.fileKey || artwork.fileName || ''}`;
            return {
              sourceUploadId: uploadId,
              id: uploadId,
              previewUrl: artwork.previewUrl || '',
              previewStatus: artwork.previewStatus || '',
              previewLabel: artwork.previewLabel || '',
              fileKey: artwork.fileKey || '',
              originalIdentity: artwork.originalIdentity || '',
              productionFileKey: artwork.productionFileKey || '',
              fileName: artwork.fileName || '',
              fileCategory: artwork.fileCategory || '',
            };
          })
          .filter((artwork) => artwork.fileName || artwork.previewUrl);
      }

      hasAdditionalDesignArtworkSources(zone) {
        return this.getAdditionalDesignArtworkSources(zone).length > 0;
      }

      getAdditionalDesignArtworkPlacement(zone, index = 0) {
        const basePlacement = this.getDefaultDesignArtworkPlacement(zone);
        if (basePlacement.partId) return this.constrainDesignArtwork(basePlacement);
        const offset = Math.min(18, 7 * (index + 1));

        return this.constrainDesignArtwork({
          ...basePlacement,
          x: this.clampNumber((basePlacement.x ?? 50) + offset, 12, 88),
          y: this.clampNumber((basePlacement.y ?? 50) + offset, 12, 88),
        });
      }

      syncAdditionalDesignArtworkSources(zone) {
        const zoneKey = zone?.key || '';
        if (!zoneKey) return;

        const additionalSources = this.getAdditionalDesignArtworkSources(zone);
        const sourceIds = new Set(additionalSources.map((source) => source.sourceUploadId));
        const copies = this.getDesignArtworkCopies(zoneKey).filter((copy) => {
          return !copy.sourceUploadId || sourceIds.has(copy.sourceUploadId);
        });

        additionalSources.forEach((source, index) => {
          const existingCopy = copies.find((copy) => copy.sourceUploadId === source.sourceUploadId || copy.id === source.id);

          if (existingCopy) {
            if (source.originalIdentity && existingCopy.originalIdentity === source.originalIdentity && existingCopy.productionFileKey !== source.productionFileKey) {
              this.designArtworkImageUpdateModes.set(this.getDesignArtworkStateKey(zoneKey, existingCopy.id), 'preserve-production-placement');
            } else if (source.originalIdentity !== existingCopy.originalIdentity && this.designArtworkImageUpdateModes.get(this.getDesignArtworkStateKey(zoneKey, existingCopy.id)) === 'preserve-production-placement') {
              this.designArtworkImageUpdateModes.delete(this.getDesignArtworkStateKey(zoneKey, existingCopy.id));
            }
            existingCopy.sourceUploadId = source.sourceUploadId;
            existingCopy.previewUrl = source.previewUrl;
            existingCopy.previewStatus = source.previewStatus;
            existingCopy.previewLabel = source.previewLabel;
            existingCopy.fileKey = source.fileKey;
            existingCopy.originalIdentity = source.originalIdentity;
            existingCopy.productionFileKey = source.productionFileKey;
            existingCopy.fileName = source.fileName;
            existingCopy.fileCategory = source.fileCategory;
            existingCopy.previewObjectUrl = '';
            existingCopy.placement = this.constrainDesignArtwork(existingCopy.placement || this.getAdditionalDesignArtworkPlacement(zone, index));
            return;
          }

          copies.push({
            ...source,
            previewObjectUrl: '',
            placement: this.getAdditionalDesignArtworkPlacement(zone, index),
          });
        });

        this.designArtworkCopies.set(this.getScopedDesignZoneKey(zoneKey), copies);
      }

      clearDesignArtworkForZone(zoneKey) {
        if (!zoneKey) return;

        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);
        const baseStateKey = this.getDesignArtworkStateKey(zoneKey, this.getBaseDesignArtworkId());
        this.clearDesignArtworkCopies(zoneKey);
        this.setActiveDesignArtworkId(zoneKey, this.getBaseDesignArtworkId());
        this.designArtworkPlacements.delete(scopedZoneKey);
        this.designArtworkImageMetricKeys.delete(baseStateKey);
        this.designArtworkContentFrameBindings.delete(baseStateKey);
        this.designArtworkCoordinateModes.delete(scopedZoneKey);
        this.designArtworkNativePlanes.delete(scopedZoneKey);
        this.designArtworkRestoredPlanes.delete(scopedZoneKey);
        this.designArtworkImageUpdateModes.delete(baseStateKey);
        this.designZoneSourceKeys.delete(scopedZoneKey);
        this.designZoneOriginalIdentities.delete(scopedZoneKey);
        this.resetDesignArtworkHistory(zoneKey, this.getBaseDesignArtworkId());
      }

      syncDesignArtworkNativePlane(zone) {
        const currentZone = this.designStudioState?.zones?.find(item => item.key === zone?.key) || zone;
        if (!this.usesNativeDesignPrintPlane(currentZone)) return;
        const plane = this.getSupplierPrintPlane(currentZone), scoped = this.getScopedDesignZoneKey(currentZone.key);
        const previous = this.designArtworkNativePlanes.get(scoped);
        if (!previous || previous.widthMm === plane.widthMm && previous.heightMm === plane.heightMm) {
          this.designArtworkNativePlanes.set(scoped, this.getDesignCoordinatePlane(currentZone) || { widthMm: plane.widthMm, heightMm: plane.heightMm });
          return;
        }
        // Keep physical size when another technique changes the unfolded editing plane.
        // The same conversion applies to copies and undo snapshots; source/media pixels stay intact.
        const rescale = placement => {
          if (!placement) return placement;
          const width = placement.width * previous.widthMm / plane.widthMm;
          const height = placement.height * previous.heightMm / plane.heightMm;
          return { ...placement, x: placement.x * previous.widthMm / plane.widthMm,
            y: placement.y * previous.heightMm / plane.heightMm, width, height, artworkRatio: width / height };
        };
        const base = this.designArtworkPlacements.get(scoped);
        const copies = this.designArtworkCopies.get(scoped) || [];
        const snapshots = [base, ...copies.map(copy => copy.placement)];
        for (const id of [this.getBaseDesignArtworkId(), ...copies.map(copy => copy.id)]) {
          const history = this.designArtworkHistory.get(this.getDesignArtworkStateKey(currentZone.key, id));
          if (history) snapshots.push(...history.past, ...history.future);
        }
        if (snapshots.filter(Boolean).some(snapshot => {
          const next = rescale(snapshot);
          return ![next.x, next.y, next.width, next.height].every(Number.isFinite)
            || Math.abs(next.x) > 400 || Math.abs(next.y) > 400 || next.width > 400 || next.height > 400;
        })) {
          // The serializer cannot express this conversion. Keep the old frame and all snapshots intact.
          this.designArtworkCoordinateModes.set(scoped, 'native_restore_pending');
          this.designArtworkRestoredPlanes.set(scoped, previous);
          return;
        }
        this.designArtworkNativePlanes.set(scoped, this.getDesignCoordinatePlane(currentZone) || { widthMm: plane.widthMm, heightMm: plane.heightMm });
        if (base) this.designArtworkPlacements.set(scoped, rescale(base));
        for (const copy of copies) copy.placement = rescale(copy.placement);
        for (const id of [this.getBaseDesignArtworkId(), ...copies.map(copy => copy.id)]) {
          const history = this.designArtworkHistory.get(this.getDesignArtworkStateKey(currentZone.key, id));
          if (history) { history.past = history.past.map(rescale); history.future = history.future.map(rescale); }
        }
      }

      getDesignArtworkPlacement(zone, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        this.syncDesignArtworkNativePlane(zone);
        const zoneKey = zone?.key || 'default';
        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);
        const copy = artworkId && artworkId !== this.getBaseDesignArtworkId() ? this.getDesignArtworkCopy(zoneKey, artworkId) : null;
        if (copy) {
          copy.placement = this.constrainDesignArtworkToZone(
            copy.placement || this.getDefaultDesignArtworkPlacement(zone),
            zone,
            { preferLargestWithoutPartId: true },
          );
          return copy.placement;
        }

        if (!this.designArtworkPlacements.has(scopedZoneKey)) {
          this.designArtworkPlacements.set(scopedZoneKey, this.getDefaultDesignArtworkPlacement(zone));
        }

        const placement = this.constrainDesignArtworkToZone(
          this.designArtworkPlacements.get(scopedZoneKey),
          zone,
          { preferLargestWithoutPartId: true },
        );
        this.designArtworkPlacements.set(scopedZoneKey, placement);
        return placement;
      }

      getDefaultDesignArtworkPlacement(zone) {
        const parts = this.getDesignZoneParts(zone);
        if (parts.length > 1 || (parts.length === 1 && (zone?.supplierGeometry || parts[0].rotation))) {
          const part = parts[0];
          const inset = part.shape === 'circle' ? 0.7 : 0.8;
          return {
            x: part.left + part.width / 2,
            y: part.top + part.height / 2,
            width: Math.min(52, part.width * inset),
            height: Math.min(30, part.height * inset),
            partId: part.id,
            rotation: part.rotation || 0,
            cropTop: 0, cropRight: 0, cropBottom: 0, cropLeft: 0,
          };
        }

        const viewKey = this.normalizeStudioKey(zone?.viewKey || 'front');
        const labelKey = this.normalizeStudioKey(zone?.label || '');
        const key = this.normalizeStudioKey(zone?.key || '');

        if (viewKey.includes('sleeve')) {
          return { x: 50, y: 50, width: 68, height: 34, rotation: 0, cropTop: 0, cropRight: 0, cropBottom: 0, cropLeft: 0 };
        }

        if (labelKey.includes('coeur') || key === '2' || key === '3') {
          return { x: 50, y: 50, width: 74, height: 42, rotation: 0, cropTop: 0, cropRight: 0, cropBottom: 0, cropLeft: 0 };
        }

        return { x: 50, y: 50, width: 52, height: 30, rotation: 0, cropTop: 0, cropRight: 0, cropBottom: 0, cropLeft: 0 };
      }

      getDesignArtworkImageMetricKey(zone, image, source = this.getDesignArtworkSource(zone)) {
        if (!source?.previewUrl || !image?.naturalWidth || !image?.naturalHeight) return '';
        return `${source.productionFileKey || source.fileKey || source.previewUrl}|${image.naturalWidth}x${image.naturalHeight}`;
      }

      syncInactiveDesignArtworkImageMetrics(activeZone) {
        // Quote readiness covers every decorated side. A restored colour must
        // not wait for the visitor to open each side to apply decoded metrics.
        const variantScope = this.getDesignVariantScopeKey();
        const requests = this.inactiveArtworkMetricLoads ||= new Map();
        for (const key of requests.keys()) if (!key.startsWith(`${variantScope}::`)) requests.delete(key);
        let renderedImages;
        const signature = source => JSON.stringify([source.previewUrl, source.productionFileKey, source.fileKey, source.originalIdentity]);
        for (const zone of this.designStudioState?.zones || []) {
          if (!zone?.hasLogo || zone.key === activeZone?.key) continue;
          this.syncDesignArtworkSources(zone);
          const source = this.getDesignArtworkSource(zone, 'base');
          if (!source.previewUrl || source.previewStatus !== 'ready') continue;
          const stateKey = this.getDesignArtworkStateKey(zone.key, 'base');
          const metric = this.designArtworkImageMetricKeys.get(stateKey);
          const sourceKey = source.productionFileKey || source.fileKey || source.previewUrl;
          if (typeof metric === 'string' && metric.slice(0, metric.lastIndexOf('|')) === sourceKey
            && this.designArtworkPlacements.get(this.getScopedDesignZoneKey(zone.key))?.imageNaturalRatio > 0) continue;
          const sourceSignature = signature(source);
          const current = () => {
            if (!this.isConnected || !this.isDesignStudioStateForActiveVariant() || this.getDesignVariantScopeKey() !== variantScope) return null;
            const latest = this.designStudioState?.zones?.find(item => item.key === zone.key && item.hasLogo);
            if (!latest || latest.key === this.designStudioState.activeZoneKey || this.designInteraction?.zoneKey === latest.key) return null;
            const latestSource = this.getDesignArtworkSource(latest, 'base');
            return latestSource.previewStatus === 'ready' && signature(latestSource) === sourceSignature ? { zone: latest, source: latestSource } : null;
          };
          const commit = image => {
            const latest = current();
            if (!latest || !image.complete || !image.naturalWidth || !image.naturalHeight || image.getAttribute('src') !== source.previewUrl) return;
            this.syncDesignArtworkImageMetrics(latest.zone, image, 'base', latest.source);
          };
          renderedImages ||= [...this.querySelectorAll('[data-role="product-design-artwork-image"]')];
          const image = renderedImages.find(item => item.getAttribute('src') === source.previewUrl && item.complete && item.naturalWidth > 0 && item.naturalHeight > 0);
          if (image) { commit(image); continue; }
          if (requests.get(stateKey)?.signature === sourceSignature) continue;
          const request = { signature: sourceSignature };
          requests.set(stateKey, request);
          const decoded = new Image();
          const loaded = typeof decoded.decode !== 'function' ? new Promise((resolve, reject) => {
            decoded.onload = resolve; decoded.onerror = reject;
          }) : null;
          decoded.src = source.previewUrl;
          Promise.resolve(loaded || decoded.decode()).then(() => {
            if (requests.get(stateKey) !== request) return;
            commit(decoded);
            requests.delete(stateKey);
          }).catch(() => {
            // Leave a failed source unqualified, without retrying on every
            // render. A different preview or variant creates a fresh request.
          });
        }
      }

      syncDesignArtworkImageMetrics(zone, image, artworkId = this.getActiveDesignArtworkId(zone?.key), source = this.getDesignArtworkSource(zone, artworkId)) {
        const zoneKey = zone?.key || '';
        const imageSource = image?.currentSrc || image?.src || '';
        let previewSource = source?.previewUrl || '';
        try { previewSource = new URL(previewSource, document.baseURI).href; } catch { return false; }
        if (!imageSource || imageSource !== previewSource || source?.previewStatus && source.previewStatus !== 'ready') return false;
        if (image?.dataset) image.dataset.designContentFrameKind = source?.fileCategory || '';
        const stateKey = this.getDesignArtworkStateKey(zoneKey, artworkId);
        const metricKey = this.getDesignArtworkImageMetricKey(zone, image, source);
        if (!zoneKey || !metricKey) return false;
        const previousMetricKey = this.designArtworkImageMetricKeys.get(stateKey);
        const frameBinding = this.designArtworkContentFrameBindings.get(stateKey);
        if (previousMetricKey === metricKey && frameBinding?.metricKey === metricKey && frameBinding.previewUrl === source.previewUrl
          && frameBinding.frameRevision === this.getDesignArtworkPlacement(zone, artworkId)?.contentFrame?.frameRevision) return false;

        const basePlacement = this.getDesignArtworkPlacement(zone, artworkId);
        const updateMode = this.designArtworkImageUpdateModes.get(stateKey);
        const sameDecodedOriginal = source?.originalIdentity && frameBinding?.originalIdentity === source.originalIdentity
          && frameBinding.frameRevision === basePlacement.contentFrame?.frameRevision;
        const preservePlacement = updateMode === 'preserve-placement' || updateMode === 'preserve-production-placement' && sameDecodedOriginal
          || previousMetricKey === metricKey && Boolean(basePlacement.contentFrame);
        let adjustedPlacement =
          preservePlacement
            ? this.getPlacementPreservingArtworkImageMetrics(basePlacement, image)
            : zone?.suggestedGeometry
              ? this.getSuggestedDesignArtworkPlacement(zone, basePlacement, image)
              : this.getImageAwareDesignArtworkPlacement(basePlacement, image, zone);
        const initialBaseArtwork = !preservePlacement && artworkId === this.getBaseDesignArtworkId();
        const artworkHistory = this.designArtworkHistory.get(stateKey);
        const hasArtworkHistory = Boolean(artworkHistory?.past?.length || artworkHistory?.future?.length);
        const canApplyInitialBaseArtworkIntent = initialBaseArtwork && !hasArtworkHistory;
        if (canApplyInitialBaseArtworkIntent && adjustedPlacement.contentFrame?.visibleBounds && source?.originalIdentity && !this.personalizationResumeSilenced && !this.designInteraction) {
          adjustedPlacement = this.getProductRoot?.()?.querySelector?.('product-marking-selector')?.supplierV2
            ?.fitInitialArtworkPlacement?.(zone, adjustedPlacement, this) || adjustedPlacement;
        }

        this.designArtworkImageMetricKeys.set(stateKey, metricKey);
        this.designArtworkContentFrameBindings.set(stateKey, { metricKey, previewUrl: source.previewUrl, originalIdentity: source.originalIdentity || '',
          frameRevision: adjustedPlacement.contentFrame?.frameRevision || '' });
        this.updateStoredDesignArtwork(zoneKey, adjustedPlacement, artworkId);
        if (canApplyInitialBaseArtworkIntent) {
          this.resetDesignArtworkHistory(zoneKey, artworkId);
          if (!this.hasAdditionalDesignArtworkSources(zone)) {
            this.clearDesignArtworkCopies(zoneKey);
          }
        }
        this.designArtworkImageUpdateModes.delete(stateKey);
        return true;
      }

      getSuggestedDesignArtworkPlacement(zone, basePlacement, image) {
        const placement = this.getImageAwareDesignArtworkPlacement(basePlacement, image, zone);
        const geometry = zone?.suggestedGeometry;
        const fitArtwork = window.TodayMerchBrandPlacement?.fitArtwork;
        if (!geometry || !fitArtwork || !image?.naturalWidth || !image?.naturalHeight) return placement;

        const overlay = image.closest?.('[data-role="product-design-studio"]');
        const shell = overlay?.querySelector('[data-role="product-design-zone-shell"]');
        const rect = this.getDesignZoneShellRect(shell);
        const bounds = this.getDesignZonePlacement(zone);
        if (!rect?.width || !rect.height || !(bounds.width > 0) || !(bounds.height > 0)) return placement;

        // Use the same authored surface and pixel proportions as website-logo imports.
        // Keep the upload's transparent-canvas treatment when calculating its visible size.
        const fit = fitArtwork({
          geometry,
          imageWidth: rect.width * 100 / bounds.width,
          imageHeight: rect.height * 100 / bounds.height,
          logoWidth: image.naturalWidth * 100 / (placement.mediaWidth || 100),
          logoHeight: image.naturalHeight * 100 / (placement.mediaHeight || 100),
        });
        if (!fit) return placement;

        const width = fit.width / bounds.width * 100;
        const height = fit.height / bounds.height * 100;
        return {
          ...placement,
          x: (fit.x + fit.width / 2 - bounds.x) / bounds.width * 100,
          y: (fit.y + fit.height / 2 - bounds.y) / bounds.height * 100,
          width,
          height,
          minimumFrameSize: 0.01,
          rotation: fit.rotation || 0,
          partId: fit.partId || '',
          artworkRatio: width / height,
        };
      }

      getPlacementPreservingArtworkImageMetrics(basePlacement, image) {
        if (!image?.naturalWidth || !image?.naturalHeight) return basePlacement;

        const transparentCrop = this.getTransparentImageCrop(image);
        const imageNaturalRatio = image.naturalWidth / image.naturalHeight;

        return this.normalizeDesignArtworkCrop({
          ...basePlacement,
          ...(transparentCrop.contentFrame ? { contentFrame: transparentCrop.contentFrame } : {}),
          mediaWidth: basePlacement.mediaWidth ?? 100,
          mediaHeight: basePlacement.mediaHeight ?? 100,
          mediaLeft: basePlacement.mediaLeft ?? 0,
          mediaTop: basePlacement.mediaTop ?? 0,
          artworkRatio: basePlacement.width > 0 && basePlacement.height > 0
            ? basePlacement.width / basePlacement.height : basePlacement.artworkRatio || imageNaturalRatio,
          imageNaturalRatio,
          imageCropTop: transparentCrop.cropTop || 0,
          imageCropRight: transparentCrop.cropRight || 0,
          imageCropBottom: transparentCrop.cropBottom || 0,
          imageCropLeft: transparentCrop.cropLeft || 0,
        });
      }

      getImageAwareDesignArtworkPlacement(basePlacement, image, zone = null) {
        if (!image?.naturalWidth || !image?.naturalHeight) return basePlacement;

        const transparentCrop = this.getTransparentImageCrop(image);
        const contentFrame = transparentCrop.contentFrame;
        if (!contentFrame?.visibleBounds) return this.getPlacementPreservingArtworkImageMetrics(basePlacement, image);
        const preserveCanvas = false;
        const visibleCropTop = transparentCrop.cropTop || 0;
        const visibleCropRight = transparentCrop.cropRight || 0;
        const visibleCropBottom = transparentCrop.cropBottom || 0;
        const visibleCropLeft = transparentCrop.cropLeft || 0;
        const cropTop = preserveCanvas ? 0 : visibleCropTop;
        const cropRight = preserveCanvas ? 0 : visibleCropRight;
        const cropBottom = preserveCanvas ? 0 : visibleCropBottom;
        const cropLeft = preserveCanvas ? 0 : visibleCropLeft;
        const visibleWidthRatio = 1 - cropLeft - cropRight;
        const visibleHeightRatio = 1 - cropTop - cropBottom;
        const imageNaturalRatio = image.naturalWidth / image.naturalHeight;
        const zoneAspect = this.getDesignArtworkZoneAspect(zone, image);
        const imageRatio = (image.naturalWidth * visibleWidthRatio) / (image.naturalHeight * visibleHeightRatio) / zoneAspect;
        const baseRatio = basePlacement.width / Math.max(basePlacement.height, 1);
        let width = basePlacement.width;
        let height = basePlacement.height;

        if (imageRatio > baseRatio) {
          height = width / imageRatio;
        } else {
          width = height * imageRatio;
        }

        return this.normalizeDesignArtworkCrop({
          ...basePlacement,
          width,
          height,
          contentFrame,
          cropTop: 0,
          cropRight: 0,
          cropBottom: 0,
          cropLeft: 0,
          mediaWidth: 100 / visibleWidthRatio,
          mediaHeight: 100 / visibleHeightRatio,
          mediaLeft: -(cropLeft / visibleWidthRatio) * 100,
          mediaTop: -(cropTop / visibleHeightRatio) * 100,
          artworkRatio: imageRatio,
          imageNaturalRatio,
          imageCropTop: visibleCropTop,
          imageCropRight: visibleCropRight,
          imageCropBottom: visibleCropBottom,
          imageCropLeft: visibleCropLeft,
        });
      }

      shouldPreserveTransparentArtworkCanvas() {
        // Transparent margin size and asymmetry are not production requirements.
        return false;
      }

      getTransparentImageCrop(image) {
        const api = window.TodayMerchStudioDesignRendering?.contentFrames;
        if (!api) return {};
        const contentFrame = api.fromImage(image, { kind: image?.dataset?.designContentFrameKind || undefined });
        return { ...(api.alphaCrop(contentFrame) || {}), contentFrame };
      }

      getDesignArtworkZoneAspect(zone, image = null) {
        const interaction = this.designInteraction;
        if (interaction?.zoneKey === zone?.key && interaction.renderMetrics?.zoneAspect > 0
          && this.isDesignArtworkInteractionCurrent(interaction)) return interaction.renderMetrics.zoneAspect;
        const retained = this.getRetainedDesignPrintPlane(zone);
        if (retained) return retained.aspectRatio;
        const nativePlane = this.getSupplierPrintPlane?.(zone);
        if (nativePlane && this.usesNativeDesignPrintPlane(zone)) return nativePlane.aspectRatio;
        const ownOverlay = image?.closest?.('[data-role="product-design-studio"]');
        const overlay = ownOverlay && (!zone?.key || ownOverlay.dataset.zoneKey === zone.key) ? ownOverlay
          : [...this.querySelectorAll('[data-role="product-design-studio"]')].find(node => !node.hidden && node.dataset.zoneKey === zone?.key);
        const shell = overlay?.querySelector('[data-role="product-design-zone-shell"]');
        // offset dimensions are unrotated; a bounding rectangle is not.
        const shellStyle = shell && getComputedStyle(shell);
        const shellWidth = Number.parseFloat(shellStyle?.width), shellHeight = Number.parseFloat(shellStyle?.height);
        if (shellWidth > 0 && shellHeight > 0) return shellWidth / shellHeight;
        const plane = this.getSupplierPrintPlane?.(zone);
        if (plane && !this.hasDesignPhotoSurface?.(zone)) return plane.widthMm / plane.heightMm;
        const bounds = zone && this.getDesignZonePlacement(zone);
        const slide = this.getActiveMediaSlide?.();
        const container = slide?.querySelector?.('.product__media-item-container') || slide;
        const containerAspect = container?.clientWidth > 0 && container?.clientHeight > 0 ? container.clientWidth / container.clientHeight : 1;
        return bounds?.width > 0 && bounds?.height > 0 ? bounds.width / bounds.height * containerAspect : 1;
      }

      getDesignArtworkContentFrame(zone, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        const api = window.TodayMerchStudioDesignRendering?.contentFrames;
        const frame = api?.normalize(this.getDesignArtworkPlacement(zone, artworkId)?.contentFrame);
        if (!frame) return null;
        const source = this.getDesignArtworkSource(zone, artworkId), stateKey = this.getDesignArtworkStateKey(zone?.key, artworkId);
        const metricKey = this.designArtworkImageMetricKeys.get(stateKey), bound = this.designArtworkContentFrameBindings.get(stateKey);
        const sourceKey = source?.productionFileKey || source?.fileKey || source?.previewUrl;
        if ((!source?.previewStatus || source.previewStatus === 'ready') && source?.previewUrl && sourceKey && bound && bound.metricKey === metricKey
          && bound.previewUrl === source.previewUrl && bound.frameRevision === frame.frameRevision
          && typeof metricKey === 'string' && metricKey.slice(0, metricKey.lastIndexOf('|')) === sourceKey) return frame;
        // Keep the historical placement, but never reuse its bounds as evidence for a replacement.
        return { ...frame, effectiveSha256: null, visibleBounds: null, cropMatrix: null, status: 'unavailable',
          frameRevision: `${frame.frameRevision.slice(0, 180)}:source-pending`, limitations: ['current_source_not_decoded'] };
      }

      getDesignArtworkContentFrameEvidence(zone, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        const source = this.getDesignArtworkSource(zone, artworkId);
        return { contentFrame: this.getDesignArtworkContentFrame(zone, artworkId), sourceSrc: source?.previewUrl || '',
          productionFileKey: source?.productionFileKey || '', fileCategory: source?.fileCategory || '', sourceBindingVerified: false };
      }

      resizeVisibleArtwork(placement, factor, { zoneAspect = 1 } = {}) {
        if (!(factor > 0) || !Number.isFinite(factor)) return null;
        const before = this.getDesignArtworkVisibleGeometry(placement, { zoneAspect });
        const next = { ...placement, width: placement.width * factor, height: placement.height * factor };
        const after = this.getDesignArtworkVisibleGeometry(next, { zoneAspect });
        return { ...next, x: next.x + before.centerX - after.centerX, y: next.y + before.centerY - after.centerY };
      }

      resizeDesignArtworkVisible(zoneKey, artworkId, factor, options = {}) {
        const zone = this.designStudioState?.zones?.find(item => item.key === zoneKey);
        const api = window.TodayMerchStudioDesignRendering?.contentFrames;
        if (!zone || !(factor > 0) || !Number.isFinite(factor) || this.designInteraction) return false;
        const current = this.getDesignArtworkPlacement(zone, artworkId);
        const contentFrame = this.getDesignArtworkContentFrame(zone, artworkId);
        if (!contentFrame?.visibleBounds || !options.expectedPlacement) return false;
        const expected = options.expectedPlacement;
        if (Object.keys(expected).some(key => typeof expected[key] === 'number' && (!Number.isFinite(current[key]) || Math.abs(current[key] - expected[key]) > 1e-7))
          || expected.contentFrame && expected.contentFrame.frameRevision !== contentFrame.frameRevision) return false;
        const root = this.getProductRoot?.() || this.closest('product-info');
        const controller = window.TodayMerchProductDesignMeasurement?.attach(root);
        const dimensions = placement => {
          const measured = controller?.measureZone(zone, placement, this.activeVariantId || this.designStudioState?.variantId, artworkId);
          if (measured?.available) return { widthMm: measured.preciseWidthMm || measured.widthMm, heightMm: measured.preciseHeightMm || measured.heightMm,
            placement: measured.canonicalPlacementMm };
          const fallback = window.TodayMerchXDProductV2?.get(root)?.readPersonalizationResumeGeometry(zone.key, placement);
          return fallback?.dimensions ? { ...fallback.dimensions, placement: fallback.placement } : null;
        };
        const sameDimensions = (actual, target) => actual && target && ['widthMm', 'heightMm'].every(key =>
          Number.isFinite(actual[key]) && actual[key] > 0 && Number.isFinite(target[key]) && target[key] > 0
          && Math.abs(actual[key] - target[key]) <= Math.max(0.01, target[key] * 0.0001));
        const sameTargetPlacement = actual => !options.targetPlacement || actual?.placement && ['xMm', 'yMm', 'rotationDeg'].every(key => {
          const value = key === 'xMm' ? actual.placement.preciseXMm ?? actual.placement.xMm
            : key === 'yMm' ? actual.placement.preciseYMm ?? actual.placement.yMm : actual.placement.rotationDeg;
          return Number.isFinite(value) && Number.isFinite(options.targetPlacement[key]) && Math.abs(value - options.targetPlacement[key]) <= .02;
        });
        if (!sameDimensions(dimensions(current), options.expectedDimensionsMm)) return false;
        const zoneAspect = this.getDesignArtworkZoneAspect(zone);
        let next = this.resizeVisibleArtwork(current, factor, { zoneAspect });
        if (!next) return false;
        if (options.translateVisibleCenter === true) {
          const center = value => value?.placement && [value.placement.preciseXMm ?? value.placement.xMm, value.placement.preciseYMm ?? value.placement.yMm];
          const origin = center(dimensions(next)), horizontal = center(dimensions({ ...next, x: next.x + 1 })), vertical = center(dimensions({ ...next, y: next.y + 1 }));
          const target = options.targetPlacement;
          if (!origin || !horizontal || !vertical || !target || ![...origin, ...horizontal, ...vertical, target.xMm, target.yMm].every(Number.isFinite)) return false;
          const a = horizontal[0] - origin[0], b = horizontal[1] - origin[1], c = vertical[0] - origin[0], d = vertical[1] - origin[1];
          const determinant = a * d - b * c;
          if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-10) return false;
          const dx = target.xMm - origin[0], dy = target.yMm - origin[1];
          next = { ...next, x: next.x + (d * dx - c * dy) / determinant, y: next.y + (a * dy - b * dx) / determinant };
        }
        const constrained = this.constrainDesignArtworkToZone(next, zone);
        // A proposed correction is exact: a supplier clamp must not silently apply another correction.
        if (['x','y','width','height','rotation'].some(key => Math.abs(constrained[key] - next[key]) > 1e-7)
          || !sameDimensions(dimensions(constrained), options.targetDimensionsMm) || !sameTargetPlacement(dimensions(constrained))) return false;
        const history = this.getDesignArtworkHistory(zoneKey, artworkId);
        const historyBefore = { past: [...history.past], future: [...history.future], colorSource: history.colorSource };
        this.recordDesignArtworkHistory(zoneKey, current, artworkId);
        this.updateStoredDesignArtwork(zoneKey, constrained, artworkId);
        const applied = this.getDesignArtworkPlacement(zone, artworkId);
        if (['x','y','width','height','rotation'].some(key => Math.abs(applied[key] - constrained[key]) > 1e-7)
          || !sameDimensions(dimensions(applied), options.targetDimensionsMm) || !sameTargetPlacement(dimensions(applied))) {
          this.updateStoredDesignArtwork(zoneKey, current, artworkId);
          Object.assign(history, historyBefore);
          this.renderDesignStudio();
          return false;
        }
        this.renderDesignStudio();
        return true;
      }

      repositionDesignArtworkVisible(zoneKey, artworkId, options = {}) {
        return this.resizeDesignArtworkVisible(zoneKey, artworkId, 1, { ...options, translateVisibleCenter: true });
      }

      normalizeDesignArtworkCrop(placement) {
        const normalized = { ...placement };
        const horizontalCrop = (normalized.cropLeft || 0) + (normalized.cropRight || 0);
        const verticalCrop = (normalized.cropTop || 0) + (normalized.cropBottom || 0);
        const maxTotalCrop = 0.88;

        if (horizontalCrop > maxTotalCrop) {
          const scale = maxTotalCrop / horizontalCrop;
          normalized.cropLeft *= scale;
          normalized.cropRight *= scale;
        }

        if (verticalCrop > maxTotalCrop) {
          const scale = maxTotalCrop / verticalCrop;
          normalized.cropTop *= scale;
          normalized.cropBottom *= scale;
        }

        return normalized;
      }

      updateStoredDesignArtwork(
        zoneKey,
        placement,
        artworkId = this.getActiveDesignArtworkId(zoneKey),
        options = {},
      ) {
        if (!zoneKey) return;
        // The desktop surface owns the active gesture. Its mobile mirror can
        // reconcile once on release instead of rebuilding on every pointer frame.
        if (!this.designInteraction || !this.isDesignArtworkInteractionCurrent(this.designInteraction)) this.scheduleMobileDesignEditorSync();
        const zone = (this.designStudioState?.zones || []).find((item) => item?.key === zoneKey) || null;
        const constrained = this.constrainDesignArtworkToZone(placement, zone, options);
        const copy = artworkId && artworkId !== this.getBaseDesignArtworkId() ? this.getDesignArtworkCopy(zoneKey, artworkId) : null;
        if (copy) {
          copy.placement = constrained;
          this.designArtworkCopies.set(this.getScopedDesignZoneKey(zoneKey), this.getDesignArtworkCopies(zoneKey));
          this.emitDesignArtworkTransform(zone);
          return;
        }

        this.designArtworkPlacements.set(this.getScopedDesignZoneKey(zoneKey), constrained);
        this.emitDesignArtworkTransform(zone);
      }

      normalizeDesignArtworkCopy(copy) {
        if (!copy) return null;
        const hasPlacement = typeof copy.placement === 'object' && copy.placement;
        const placement = hasPlacement ? copy.placement : copy;

        return {
          id: copy.id || this.createDesignArtworkCopyId(),
          sourceUploadId: copy.sourceUploadId || '',
          previewUrl: copy.previewUrl || '',
          previewStatus: copy.previewStatus || '',
          previewLabel: copy.previewLabel || '',
          fileKey: copy.fileKey || '',
          originalIdentity: copy.originalIdentity || '',
          productionFileKey: copy.productionFileKey || '',
          fileName: copy.fileName || '',
          fileCategory: copy.fileCategory || '',
          previewObjectUrl: copy.previewObjectUrl || '',
          placement: this.constrainDesignArtwork(placement),
        };
      }

      getDesignArtworkCopies(zoneKey) {
        if (!zoneKey) return [];
        const scopedZoneKey = this.getScopedDesignZoneKey(zoneKey);
        const copies = this.designArtworkCopies.get(scopedZoneKey) || [];
        const normalizedCopies = copies.map((copy) => this.normalizeDesignArtworkCopy(copy)).filter(Boolean);
        if (
          normalizedCopies.length !== copies.length ||
          normalizedCopies.some((copy, index) => copy !== copies[index])
        ) {
          this.designArtworkCopies.set(scopedZoneKey, normalizedCopies);
        }

        return normalizedCopies;
      }

      getDesignArtworkCopy(zoneKey, artworkId) {
        if (!zoneKey || !artworkId || artworkId === this.getBaseDesignArtworkId()) return null;
        return this.getDesignArtworkCopies(zoneKey).find((copy) => copy.id === artworkId) || null;
      }

      clearDesignArtworkCopies(zoneKey) {
        if (!zoneKey) return;
        this.designArtworkCopies.delete(this.getScopedDesignZoneKey(zoneKey));
        this.setActiveDesignArtworkId(zoneKey, this.getBaseDesignArtworkId());
      }

      addDesignArtworkCopy(zoneKey, placement, source = {}) {
        if (!zoneKey || !placement) return;

        const copies = this.getDesignArtworkCopies(zoneKey).slice(-7);
        const copy = {
          id: this.createDesignArtworkCopyId(),
          previewUrl: source.previewUrl || '',
          previewStatus: source.previewStatus || '',
          previewLabel: source.previewLabel || '',
          fileKey: source.fileKey || '',
          originalIdentity: source.originalIdentity || '',
          productionFileKey: source.productionFileKey || '',
          fileName: source.fileName || '',
          fileCategory: source.fileCategory || '',
          previewObjectUrl: source.previewObjectUrl || '',
          placement: this.constrainDesignArtwork(placement),
        };
        copies.push(copy);
        this.designArtworkCopies.set(this.getScopedDesignZoneKey(zoneKey), copies);
        return copy;
      }

      updateDesignArtworkCopy(zoneKey, artworkId, updates = {}) {
        const copies = this.getDesignArtworkCopies(zoneKey);
        const copy = copies.find((item) => item.id === artworkId);
        if (!copy) return null;

        Object.assign(copy, updates);
        if (updates.placement) {
          const zone = (this.designStudioState?.zones || []).find((item) => item?.key === zoneKey) || null;
          copy.placement = this.constrainDesignArtworkToZone(updates.placement, zone);
        }
        this.designArtworkCopies.set(this.getScopedDesignZoneKey(zoneKey), copies);
        return copy;
      }

      removeDesignArtworkCopy(zoneKey, artworkId) {
        const copies = this.getDesignArtworkCopies(zoneKey);
        const copy = copies.find((item) => item.id === artworkId);
        if (!copy) return false;

        this.designArtworkCopies.set(
          this.getScopedDesignZoneKey(zoneKey),
          copies.filter((item) => item.id !== artworkId),
        );
        this.resetDesignArtworkHistory(zoneKey, artworkId);
        return true;
      }

      getDesignArtworkItems(zone) {
        const zoneKey = zone?.key || '';
        const baseSource = this.getDesignArtworkSource(zone, this.getBaseDesignArtworkId());
        const basePlacement = this.getDesignArtworkPlacement(zone, this.getBaseDesignArtworkId());
        const items = [];

        if (baseSource.fileName || baseSource.previewUrl) {
          items.push({
            id: this.getBaseDesignArtworkId(),
            ...baseSource,
            placement: basePlacement,
          });
        }

        this.getDesignArtworkCopies(zoneKey).forEach((copy) => {
          const source = this.getDesignArtworkSource(zone, copy.id);
          if (!source.fileName && !source.previewUrl) return;
          items.push({
            id: copy.id,
            ...source,
            placement: this.constrainDesignArtworkToZone(copy.placement, zone),
          });
        });

        return items;
      }

      getDesignArtworkTransform(zone) {
        if (!zone?.key) return null;

        // A simple rectangle skips the compound-part renderer but still has a physical identity.
        const singlePartId = Array.isArray(zone.parts) && zone.parts.length === 1
          ? String(zone.parts[0]?.id || '')
          : '';
        const artworks = this.getDesignArtworkItems(zone).map((item) => {
          const placement = this.constrainDesignArtworkToZone(item.placement, zone);
          const partId = singlePartId || String(placement.partId || '');
          return {
            id: item.id,
            fileName: item.fileName || '',
            ...(this.usesNativeDesignPrintPlane(zone) || this.getRetainedDesignPrintPlane(zone) ? {
              previewMode: this.isUnfoldedDesignPrintPlane(zone) ? 'unfolded_360' : 'supplier_plane', placementReviewRequired: true,
              coordinatePlane: this.getRetainedDesignPrintPlane(zone)
                ? window.TodayMerchStudioDesignRendering.coordinatePlanes.normalize(this.designArtworkRestoredPlanes?.get(this.getScopedDesignZoneKey(zone.key)))
                : this.getDesignCoordinatePlane(zone) } : {}),
            partId,
            partIds: partId ? [partId] : [],
            x: placement.x,
            y: placement.y,
            width: placement.width,
            height: placement.height,
            rotation: placement.rotation || 0,
            ...(placement.contentFrame ? { contentFrame: placement.contentFrame } : {}),
            imageNaturalRatio: placement.imageNaturalRatio || 0,
            ...Object.fromEntries(['cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop']
              .filter(key => Number.isFinite(placement[key])).map(key => [key, placement[key]])),
          };
        });
        const usedPartIds = [...new Set(
          artworks.flatMap((artwork) => artwork.partIds).filter(Boolean),
        )];

        return {
          schemaVersion: 2,
          variantId: String(this.activeVariantId || this.designStudioState?.variantId || ''),
          view: String(zone.viewKey || ''),
          zone: String(zone.key || ''),
          artworks,
          usedPartIds,
          physicalLocationCount: usedPartIds.length,
          additionalLocationQuoteRequired: usedPartIds.length > 1,
        };
      }

      emitDesignArtworkTransform(zone) {
        if (this.personalizationResumeSilenced) return;
        const interaction = this.designInteraction;
        if (interaction && zone?.key && interaction.zoneKey === zone.key && this.isDesignArtworkInteractionCurrent(interaction)) {
          // Invalidate dependent quotes/proofs on the first actual move. The
          // saved placement continues tracking every frame; release publishes
          // its final value after clearing the interaction.
          if (interaction.transformNotified) return;
          interaction.transformNotified = true;
        }
        const transform = this.getDesignArtworkTransform(zone);
        if (!transform || typeof this.dispatchEvent !== 'function' || typeof CustomEvent === 'undefined') return;

        this.dispatchEvent(
          new CustomEvent('todaymerch:product-design-transform-change', {
            bubbles: true,
            detail: transform,
          }),
        );
      }

      renderDesignArtworkPreviewStatus(artwork, label, source, image, failed = false) {
        if (!artwork || !label) return;
        const hasFile = Boolean(source.fileName || source.previewUrl);
        const ready = Boolean(source.previewUrl && image?.complete && image.naturalWidth > 0 && !failed);
        const unavailable = failed || ['error', 'unsupported', 'unavailable'].includes(source.previewStatus)
          || Boolean(source.previewUrl && image?.complete && !image.naturalWidth && image.getAttribute('src') === source.previewUrl);
        const state = !hasFile ? 'empty' : ready ? 'ready' : unavailable ? 'unavailable' : 'loading';
        if (artwork.dataset.previewState !== state) artwork.dataset.previewState = state;
        const busy = String(state === 'loading'), hidden = state === 'empty' || state === 'ready';
        if (artwork.getAttribute('aria-busy') !== busy) artwork.setAttribute('aria-busy', busy);
        if (label.hidden !== hidden) label.hidden = hidden;
        const mask = label.closest('.product__design-artwork-mask');
        if (mask && mask.getAttribute('aria-hidden') !== String(hidden)) mask.setAttribute('aria-hidden', String(hidden));
        if (image && image.hidden !== !ready) image.hidden = !ready;
        const message = state === 'unavailable'
          ? this.string('previewUnavailable') || source.previewLabel || ''
          : this.string('previewPreparing') || source.previewLabel || '';
        // A neutral spinner occupies the design frame while the decoded image
        // is unavailable. Its accessible status never masquerades as artwork.
        const text = state === 'unavailable' ? message : '';
        if (label.textContent !== text) label.textContent = text;
        if (label.getAttribute('role') !== 'status') label.setAttribute('role', 'status');
        if (label.getAttribute('aria-label') !== message) label.setAttribute('aria-label', message);
      }

      renderDesignArtworkCopies(overlay, zone) {
        const container = overlay?.querySelector('[data-role="product-design-artwork-copies"]');
        if (!container || !zone?.key) return;

        const activeArtworkId = this.getActiveDesignArtworkId(zone.key);
        const scope = this.getScopedDesignZoneKey(zone.key);
        const items = this.getDesignArtworkItems(zone).filter((item) => item.id !== activeArtworkId);
        const ids = new Set(items.map((item) => item.id));
        const copies = new Map();
        container.querySelectorAll('[data-role="product-design-artwork-copy"]').forEach((copy) => {
          if (copy.designArtworkCopyView?.scope === scope && ids.has(copy.dataset.artworkId)) {
            copies.set(copy.dataset.artworkId, copy);
          } else {
            window.TodayMerchProductPreviewEffects?.restore(copy);
            this.removeDesignArtworkZoneClip(overlay, copy);
            copy.remove();
          }
        });

        // Keep decoded images, clip definitions and keyboard focus across full
        // renders. Source/status and geometry still refresh from current data.
        let nextCopy = container.firstElementChild;
        items.forEach((item) => {
          let copy = copies.get(item.id);
          if (!copy) {
            copy = document.createElement('div');
            copy.className = 'product__design-artwork product__design-artwork--copy';
            copy.dataset.role = 'product-design-artwork-copy';
            copy.dataset.zoneKey = zone.key;
            copy.dataset.artworkId = item.id;
            copy.tabIndex = 0;
            const mask = document.createElement('span');
            mask.className = 'product__design-artwork-mask';
            mask.setAttribute('aria-hidden', 'true');
            const name = document.createElement('span');
            name.dataset.role = 'product-design-artwork-name';
            mask.append(name);
            copy.append(mask);
            copy.designArtworkCopyView = { scope, name, image: null, source: item };
            const select = (event) => this.selectDesignArtworkItem(event, overlay, copy.dataset.zoneKey, copy.dataset.artworkId);
            copy.addEventListener('pointerdown', select);
            copy.addEventListener('click', select);
            copy.addEventListener('keydown', (event) => {
              if (event.key === 'Enter' || event.key === ' ') select(event);
            });
          }
          const view = copy.designArtworkCopyView;
          view.source = item;
          const ariaLabel = this.string('selectArtwork', { NAME: item.fileName || this.string('logoFallback') });
          if (copy.getAttribute('aria-label') !== ariaLabel) copy.setAttribute('aria-label', ariaLabel);
          if (item.previewUrl && !view.image) {
            const image = document.createElement('img');
            image.alt = '';
            image.width = 320;
            image.height = 220;
            const update = (failed = false) => {
              if (copy.isConnected && view.image === image && this.getScopedDesignZoneKey(copy.dataset.zoneKey) === view.scope
                && image.getAttribute('src') === view.source.previewUrl && image.complete) {
                this.renderDesignArtworkPreviewStatus(copy, view.name, view.source, image, failed && !image.naturalWidth);
              }
            };
            image.onload = () => update();
            image.onerror = () => update(true);
            view.image = image;
            view.name.before(image);
          }
          if (!item.previewUrl && view.image) {
            view.image.onload = view.image.onerror = null;
            view.image.remove();
            view.image = null;
          } else if (view.image && view.image.getAttribute('src') !== item.previewUrl) {
            view.image.src = item.previewUrl;
          }
          this.renderDesignArtworkPreviewStatus(copy, view.name, item, view.image);
          if (copy !== nextCopy) container.insertBefore(copy, nextCopy);
          nextCopy = copy.nextElementSibling;
          this.updateDesignArtworkElement(copy, item.placement, zone, overlay);
        });
      }

      getDesignArtworkHistory(zoneKey, artworkId = this.getActiveDesignArtworkId(zoneKey)) {
        if (!zoneKey) return null;
        const historyKey = this.getDesignArtworkStateKey(zoneKey, artworkId);
        if (!this.designArtworkHistory.has(historyKey)) {
          this.designArtworkHistory.set(historyKey, {
            past: [],
            future: [],
          });
        }

        return this.designArtworkHistory.get(historyKey);
      }

      resetDesignArtworkHistory(zoneKey, artworkId = this.getActiveDesignArtworkId(zoneKey)) {
        if (!zoneKey) return;
        this.designArtworkHistory.delete(this.getDesignArtworkStateKey(zoneKey, artworkId));
      }

      cloneDesignArtworkPlacement(placement) {
        return placement ? { ...placement, ...(placement.contentFrame ? { contentFrame: window.TodayMerchStudioDesignRendering?.contentFrames.normalize(placement.contentFrame) } : {}) } : null;
      }

      serializeDesignArtworkPlacement(placement) {
        if (!placement) return '';
        const numeric = [
          'x',
          'y',
          'width',
          'height',
          'minimumFrameSize',
          'rotation',
          'cropTop',
          'cropRight',
          'cropBottom',
          'cropLeft',
          'mediaWidth',
          'mediaHeight',
          'mediaLeft',
          'mediaTop',
          'artworkRatio',
          'imageNaturalRatio',
          'imageCropTop',
          'imageCropRight',
          'imageCropBottom',
          'imageCropLeft',
        ]
          .map((key) => `${key}:${Number(placement[key] || 0).toFixed(4)}`)
          .join('|');
        return `${numeric}|partId:${String(placement.partId || '')}|contentFrame:${placement.contentFrame?.frameRevision || ''}`;
      }

      areDesignArtworkHistorySnapshotsEqual(left, right) {
        if (this.serializeDesignArtworkPlacement(left) !== this.serializeDesignArtworkPlacement(right)) return false;
        const a = left?.colorSource, b = right?.colorSource;
        return a?.originalFile === b?.originalFile && a?.record?.file === b?.record?.file && a?.record?.kind === b?.record?.kind;
      }

      recordDesignArtworkColorHistory(detail) {
        const { zoneKey, artworkId, originalFile, before, after } = detail;
        if (!zoneKey || !artworkId || !originalFile || !after ||
          String(detail.variantId || '') !== String(this.activeVariantId || this.designStudioState?.variantId || '')) return;
        const zone = this.designStudioState?.zones?.find((item) => item.key === zoneKey);
        if (!zone || artworkId !== this.getBaseDesignArtworkId() && !this.getDesignArtworkCopy(zoneKey, artworkId)) return;
        const history = this.getDesignArtworkHistory(zoneKey, artworkId);
        const beforeSource = { originalFile, record: before || { original: originalFile, file: originalFile, kind: 'original', revision: 0 } };
        // Placements made before the first color edit still belong to the original source.
        for (const snapshot of [...history.past, ...history.future]) snapshot.colorSource ||= beforeSource;
        history.colorSource = beforeSource;
        this.recordDesignArtworkHistory(zoneKey, this.getDesignArtworkPlacement(zone, artworkId), artworkId, { colorSource: beforeSource });
        history.colorSource = { originalFile, record: after };
        this.designArtworkImageUpdateModes.set(this.getDesignArtworkStateKey(zoneKey, artworkId), 'preserve-production-placement');
        this.renderDesignStudio();
      }

      recordDesignArtworkHistory(zoneKey, placement, artworkId = this.getActiveDesignArtworkId(zoneKey), options = {}) {
        const history = this.getDesignArtworkHistory(zoneKey, artworkId);
        const snapshot = this.cloneDesignArtworkPlacement(this.constrainDesignArtwork(placement));
        if (!history || !snapshot) return;
        if (history.colorSource) {
          const selector = this.getProductRoot()?.querySelector?.('product-marking-selector');
          const actualRecord = selector?.getArtworkProductionSource?.(history.colorSource.originalFile, zoneKey, { artworkId });
          snapshot.colorSource = options.colorSource || { ...history.colorSource, record: actualRecord || history.colorSource.record };
          history.colorSource = snapshot.colorSource;
        }
        const lastSnapshot = history.past[history.past.length - 1];
        if (!this.areDesignArtworkHistorySnapshotsEqual(lastSnapshot, snapshot)) {
          history.past.push(snapshot);
          if (history.past.length > 36) history.past.shift();
        }
        history.future = [];
      }

      restoreDesignArtworkHistory(zoneKey, direction, artworkId = this.getActiveDesignArtworkId(zoneKey)) {
        const history = this.getDesignArtworkHistory(zoneKey, artworkId);
        const currentPlacement = this.cloneDesignArtworkPlacement(this.getDesignArtworkPlacement({ key: zoneKey }, artworkId));
        if (!history || !currentPlacement) return null;
        const selector = history.colorSource ? this.getProductRoot()?.querySelector?.('product-marking-selector') : null;
        if (history.colorSource) {
          const actualRecord = selector?.getArtworkProductionSource?.(history.colorSource.originalFile, zoneKey, { artworkId });
          currentPlacement.colorSource = { ...history.colorSource, record: actualRecord || history.colorSource.record };
        }
        const sourceStack = direction === 'undo' ? history.past : history.future;
        if (direction === 'undo') {
          while (sourceStack.length && this.areDesignArtworkHistorySnapshotsEqual(sourceStack[sourceStack.length - 1], currentPlacement)) sourceStack.pop();
        }
        const snapshot = sourceStack[sourceStack.length - 1];
        if (!snapshot) return null;
        const colorSource = snapshot.colorSource;
        if (colorSource && (colorSource.record.file !== currentPlacement.colorSource?.record?.file
          || colorSource.record.kind !== currentPlacement.colorSource?.record?.kind)) {
          if (!selector?.restoreArtworkProductionSource) return null;
          // The selector restores exact bytes, creates a new revision and rechecks the current technique.
          this.designArtworkImageUpdateModes.set(this.getDesignArtworkStateKey(zoneKey, artworkId), 'preserve-production-placement');
          const restored = selector.restoreArtworkProductionSource(colorSource.originalFile, colorSource.record, { zoneKey, artworkId });
          if (!restored) return null;
        }
        sourceStack.pop();
        (direction === 'undo' ? history.future : history.past).push(currentPlacement);
        history.colorSource = colorSource;
        const { colorSource: ignoredSource, ...placement } = snapshot;
        this.updateStoredDesignArtwork(zoneKey, placement, artworkId);
        return placement;
      }

      updateDesignHistoryButtons(overlay) {
        const zoneKey = overlay?.dataset.zoneKey || '';
        const artworkId = overlay?.dataset.artworkId || this.getActiveDesignArtworkId(zoneKey);
        const history = this.getDesignArtworkHistory(zoneKey, artworkId);
        const undoButton = overlay?.querySelector('[data-design-tool="undo"]');
        const redoButton = overlay?.querySelector('[data-design-tool="redo"]');

        if (undoButton) undoButton.disabled = !history?.past?.length;
        if (redoButton) redoButton.disabled = !history?.future?.length;
      }

      ensureDesignArtworkZoneClipLayer(artwork) {
        const mask = artwork?.querySelector('.product__design-artwork-mask');
        if (!mask) return null;

        let layer = mask.querySelector('[data-role="product-design-artwork-zone-clip"]');
        if (layer) return layer;

        layer = document.createElement('span');
        layer.className = 'product__design-artwork-zone-clip';
        layer.dataset.role = 'product-design-artwork-zone-clip';
        [...mask.childNodes].forEach((node) => layer.append(node));
        mask.append(layer);
        return layer;
      }

      ensureDesignArtworkClipDefinitions(overlay) {
        if (!overlay) return null;

        let svg = overlay.querySelector('[data-role="product-design-clip-definitions"]');
        if (svg) return svg.querySelector('defs');

        const namespace = 'http://www.w3.org/2000/svg';
        svg = document.createElementNS(namespace, 'svg');
        svg.classList.add('product__design-clip-definitions');
        svg.dataset.role = 'product-design-clip-definitions';
        svg.setAttribute('width', '0');
        svg.setAttribute('height', '0');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        const definitions = document.createElementNS(namespace, 'defs');
        svg.append(definitions);
        overlay.prepend(svg);
        return definitions;
      }

      ensureDesignArtworkClipPath(overlay, artwork) {
        const definitions = this.ensureDesignArtworkClipDefinitions(overlay);
        if (!definitions || !artwork) return null;

        if (!artwork.dataset.designZoneClipId) {
          this.designArtworkClipCounter += 1;
          artwork.dataset.designZoneClipId = `product-design-artwork-clip-${this.designStudioInstanceId}-${this.designArtworkClipCounter}`;
        }

        const clipId = artwork.dataset.designZoneClipId;
        let clipPath = definitions.querySelector(`#${clipId}`);
        if (!clipPath) {
          clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
          clipPath.id = clipId;
          clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');
          definitions.append(clipPath);
        }

        return { clipId, clipPath };
      }

      removeDesignArtworkZoneClip(overlay, artwork) {
        const layer = artwork?.querySelector('[data-role="product-design-artwork-zone-clip"]');
        layer?.style.removeProperty('clip-path');
        layer?.style.removeProperty('-webkit-clip-path');

        const clipId = artwork?.dataset.designZoneClipId || '';
        if (clipId) overlay?.querySelector(`#${clipId}`)?.remove();
        if (artwork) delete artwork.dataset.designZoneClipId;
      }

      clipDesignPolygonToUnitBox(points = []) {
        const clipAgainstBoundary = (polygon, isInside, getIntersection) => {
          if (!polygon.length) return [];

          const clipped = [];
          let previous = polygon[polygon.length - 1];
          let previousInside = isInside(previous);

          for (const current of polygon) {
            const currentInside = isInside(current);
            if (currentInside) {
              if (!previousInside) clipped.push(getIntersection(previous, current));
              clipped.push(current);
            } else if (previousInside) {
              clipped.push(getIntersection(previous, current));
            }
            previous = current;
            previousInside = currentInside;
          }

          return clipped;
        };
        const intersectX = (start, end, x) => {
          const distance = end.x - start.x;
          const ratio = distance ? (x - start.x) / distance : 0;
          return { x, y: start.y + (end.y - start.y) * ratio };
        };
        const intersectY = (start, end, y) => {
          const distance = end.y - start.y;
          const ratio = distance ? (y - start.y) / distance : 0;
          return { x: start.x + (end.x - start.x) * ratio, y };
        };

        return [
          [(point) => point.x >= 0, (start, end) => intersectX(start, end, 0)],
          [(point) => point.x <= 1, (start, end) => intersectX(start, end, 1)],
          [(point) => point.y >= 0, (start, end) => intersectY(start, end, 0)],
          [(point) => point.y <= 1, (start, end) => intersectY(start, end, 1)],
        ].reduce(
          (polygon, [isInside, getIntersection]) => clipAgainstBoundary(polygon, isInside, getIntersection),
          points,
        );
      }

      getDesignArtworkZoneClipParts(zone) {
        const zoneParts = this.getDesignZoneParts(zone);
        // Plain zones need the same fixed mask as multipart zones: cropping the
        // unrotated artwork bounds would cut visible content after rotation.
        return zoneParts.length ? zoneParts : [
          { left: 0, top: 0, width: 100, height: 100, rotation: 0, shape: 'rectangle' },
        ];
      }

      getDesignArtworkZoneClipPolygons(artwork, placement, zone) {
        const zoneParts = this.getDesignArtworkZoneClipParts(zone);

        const zoneShell = artwork?.closest('[data-role="product-design-zone-shell"]');
        const interaction = this.designInteraction;
        const shellRect = interaction && interaction.zoneShell === zoneShell && this.isDesignArtworkInteractionCurrent(interaction)
          ? interaction.zoneShellRect
          : this.getDesignZoneShellRect(zoneShell);
        const shellWidth = shellRect?.width || zoneShell?.offsetWidth || 0;
        const shellHeight = shellRect?.height || zoneShell?.offsetHeight || 0;
        if (!shellWidth || !shellHeight) return null;

        const artworkWidth = shellWidth * (placement.width / 100);
        const artworkHeight = shellHeight * (placement.height / 100);
        if (!artworkWidth || !artworkHeight) return null;

        const artworkCenterX = shellWidth * (placement.x / 100);
        const artworkCenterY = shellHeight * (placement.y / 100);
        const artworkRadians = (this.normalizeRotation(placement.rotation || 0) * Math.PI) / 180;
        const artworkCos = Math.cos(artworkRadians);
        const artworkSin = Math.sin(artworkRadians);

        return zoneParts
          .map((part) => {
            const partCenterX = shellWidth * ((part.left + part.width / 2) / 100);
            const partCenterY = shellHeight * ((part.top + part.height / 2) / 100);
            const halfWidth = shellWidth * (part.width / 200);
            const halfHeight = shellHeight * (part.height / 200);
            const partRadians = (this.normalizeRotation(part.rotation || 0) * Math.PI) / 180;
            const partCos = Math.cos(partRadians);
            const partSin = Math.sin(partRadians);
            const localPoints = part.shape === 'circle'
              ? Array.from({ length: 48 }, (_, index) => {
                  const angle = (index / 48) * Math.PI * 2;
                  return [Math.cos(angle) * halfWidth, Math.sin(angle) * halfHeight];
                })
              : [
                  [-halfWidth, -halfHeight],
                  [halfWidth, -halfHeight],
                  [halfWidth, halfHeight],
                  [-halfWidth, halfHeight],
                ];
            const corners = localPoints.map(([x, y]) => {
              const zoneX = partCenterX + x * partCos - y * partSin;
              const zoneY = partCenterY + x * partSin + y * partCos;
              const deltaX = zoneX - artworkCenterX;
              const deltaY = zoneY - artworkCenterY;
              return {
                x: 0.5 + (deltaX * artworkCos + deltaY * artworkSin) / artworkWidth,
                y: 0.5 + (-deltaX * artworkSin + deltaY * artworkCos) / artworkHeight,
              };
            });

            return this.clipDesignPolygonToUnitBox(corners);
          })
          .filter((polygon) => polygon.length >= 3);
      }

      updateDesignArtworkZoneClip(overlay, artwork, placement, zone) {
        const layer = this.ensureDesignArtworkZoneClipLayer(artwork);
        if (!layer) return false;

        const polygons = this.getDesignArtworkZoneClipPolygons(artwork, placement, zone);
        if (polygons === null) {
          layer.style.removeProperty('clip-path');
          layer.style.removeProperty('-webkit-clip-path');
          const clipId = artwork.dataset.designZoneClipId || '';
          if (clipId) overlay?.querySelector(`#${clipId}`)?.replaceChildren();
          return false;
        }

        const definition = this.ensureDesignArtworkClipPath(overlay, artwork);
        if (!definition) return false;

        const namespace = 'http://www.w3.org/2000/svg';
        polygons.forEach((polygon, index) => {
          let element = definition.clipPath.children[index];
          if (!element) {
            element = document.createElementNS(namespace, 'polygon');
            definition.clipPath.append(element);
          }
          const points = polygon.map((point) => `${point.x.toFixed(6)},${point.y.toFixed(6)}`).join(' ');
          if (element.getAttribute('points') !== points) element.setAttribute('points', points);
        });
        while (definition.clipPath.children.length > polygons.length) definition.clipPath.lastElementChild.remove();

        const clipReference = `url(#${definition.clipId})`;
        layer.style.setProperty('clip-path', clipReference);
        layer.style.setProperty('-webkit-clip-path', clipReference);
        return true;
      }

      updateDesignArtworkProjection(artwork, zone, placement) {
        const layer = this.ensureDesignArtworkZoneClipLayer(artwork);
        if (!layer) return;
        if (window.TodayMerchProductPreviewEffects) {
          window.TodayMerchProductPreviewEffects.update(this, artwork, zone, placement);
          return;
        }
        const existing = layer.querySelector('[data-role="product-design-artwork-projection"]');
        existing?.remove();
        layer.classList.remove('is-curved');
        delete layer.dataset.projectionSignature;
      }

      updateDesignArtworkElement(artwork, placement, zone = null, overlay = artwork?.closest('[data-role="product-design-studio"]'), options = {}) {
        if (!artwork || !placement) return null;
        const activeZone = zone || this.getActiveOverlayZone(overlay);
        const transient = options.transient === true;
        const constrained = transient ? this.constrainDesignArtwork(placement) : this.constrainDesignArtworkToZone(placement, activeZone);
        const cropLeftPercent = constrained.cropLeft * 100;
        const cropRightPercent = constrained.cropRight * 100;
        const cropTopPercent = constrained.cropTop * 100;
        const cropBottomPercent = constrained.cropBottom * 100;
        const visibleXPercent = Math.max(12, 100 - cropLeftPercent - cropRightPercent);
        const visibleYPercent = Math.max(12, 100 - cropTopPercent - cropBottomPercent);
        const visibleCenterXPercent = cropLeftPercent + visibleXPercent / 2;
        const visibleCenterYPercent = cropTopPercent + visibleYPercent / 2;
        const leftEdge = constrained.x - constrained.width / 2;
        const rightEdge = constrained.x + constrained.width / 2;
        const topEdge = constrained.y - constrained.height / 2;
        const bottomEdge = constrained.y + constrained.height / 2;
        // Rotation is a preview until release. Keep the complete design visible
        // outside the print boundary without changing the saved production geometry.
        if (transient) this.removeDesignArtworkZoneClip(overlay, artwork);
        const hasZonePartsClip = transient || this.updateDesignArtworkZoneClip(overlay, artwork, constrained, activeZone);
        const clipLeft = hasZonePartsClip
          ? cropLeftPercent
          : Math.max(cropLeftPercent, this.clampNumber((-leftEdge / constrained.width) * 100, 0, 96));
        const clipRight = hasZonePartsClip
          ? cropRightPercent
          : Math.max(cropRightPercent, this.clampNumber(((rightEdge - 100) / constrained.width) * 100, 0, 96));
        const clipTop = hasZonePartsClip
          ? cropTopPercent
          : Math.max(cropTopPercent, this.clampNumber((-topEdge / constrained.height) * 100, 0, 96));
        const clipBottom = hasZonePartsClip
          ? cropBottomPercent
          : Math.max(cropBottomPercent, this.clampNumber(((bottomEdge - 100) / constrained.height) * 100, 0, 96));
        const frame = this.getDesignArtworkFrameInsets(constrained, { zoneAspect: this.getDesignArtworkZoneAspect(activeZone) });

        artwork.style.setProperty('--product-design-artwork-x', `${constrained.x}%`);
        artwork.style.setProperty('--product-design-artwork-y', `${constrained.y}%`);
        artwork.style.setProperty('--product-design-artwork-width', `${constrained.width}%`);
        artwork.style.setProperty('--product-design-artwork-height', `${constrained.height}%`);
        artwork.style.setProperty('--product-design-artwork-rotation', `${constrained.rotation || 0}deg`);
        artwork.style.setProperty('--product-design-artwork-crop-top', `${cropTopPercent}%`);
        artwork.style.setProperty('--product-design-artwork-crop-right', `${cropRightPercent}%`);
        artwork.style.setProperty('--product-design-artwork-crop-bottom', `${cropBottomPercent}%`);
        artwork.style.setProperty('--product-design-artwork-crop-left', `${cropLeftPercent}%`);
        artwork.style.setProperty('--product-design-artwork-visible-center-x', `${visibleCenterXPercent}%`);
        artwork.style.setProperty('--product-design-artwork-visible-center-y', `${visibleCenterYPercent}%`);
        artwork.style.setProperty('--product-design-artwork-clip-top', `${clipTop}%`);
        artwork.style.setProperty('--product-design-artwork-clip-right', `${clipRight}%`);
        artwork.style.setProperty('--product-design-artwork-clip-bottom', `${clipBottom}%`);
        artwork.style.setProperty('--product-design-artwork-clip-left', `${clipLeft}%`);
        artwork.style.setProperty('--product-design-artwork-media-width', `${constrained.mediaWidth}%`);
        artwork.style.setProperty('--product-design-artwork-media-height', `${constrained.mediaHeight}%`);
        artwork.style.setProperty('--product-design-artwork-media-left', `${constrained.mediaLeft}%`);
        artwork.style.setProperty('--product-design-artwork-media-top', `${constrained.mediaTop}%`);
        this.setDesignArtworkFrameVariables(artwork, frame, '%');
        this.updateDesignArtworkRenderedFrame(artwork, constrained);
        this.updateDesignArtworkProjection(artwork, activeZone, constrained);

        return { constrained };
      }

      updateDesignArtwork(overlay, placement) {
        const artwork = overlay?.querySelector('[data-role="product-design-artwork"]');
        const zone = this.getActiveOverlayZone(overlay);
        const appliedPlacement = this.updateDesignArtworkElement(artwork, placement, zone, overlay);
        if (!appliedPlacement) return;
        // The zone and hidden toolbar do not change during a pointer gesture.
        if (this.designInteraction?.overlay === overlay) return;

        this.renderDesignZoneParts(
          overlay,
          zone,
          this.getDesignZonePlacement(zone),
          appliedPlacement.constrained,
        );
        this.updateDesignArtworkToolbar(overlay);
        this.updateDesignHistoryButtons(overlay);
      }

      getDesignArtworkVisualBounds(artwork) {
        const points = [...(artwork?.querySelectorAll('.product__design-artwork-handle--corner') || [])]
          .map((handle) => {
            const rect = handle.getBoundingClientRect();
            return {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            };
          })
          .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

        if (points.length >= 4) {
          return {
            left: Math.min(...points.map((point) => point.x)),
            right: Math.max(...points.map((point) => point.x)),
            top: Math.min(...points.map((point) => point.y)),
            bottom: Math.max(...points.map((point) => point.y)),
          };
        }

        const rect = artwork?.getBoundingClientRect();
        if (!rect) return null;
        return {
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom,
        };
      }

      updateDesignArtworkControls(overlay, artwork) {
        if (!artwork || artwork.hidden || overlay.hidden || overlay.dataset.mobileDesignSurface === 'true') return;

        const scale = this.getDesignVisualLayerScale(overlay);
        artwork.style.setProperty('--product-design-controls-inverse-scale', String(1 / scale.x));
        const style = getComputedStyle(artwork);
        const frameInset = (name, size) => {
          const raw = style.getPropertyValue(name).trim();
          const value = Number.parseFloat(raw);
          if (!Number.isFinite(value)) return 0;
          return this.clampNumber(raw.endsWith('%') ? value / 100 : value / Math.max(size, 1), 0, 1);
        };
        const width = Math.max(artwork.offsetWidth, 1) *
          Math.max(0.01, 1 - frameInset('--product-design-artwork-frame-left', artwork.offsetWidth) - frameInset('--product-design-artwork-frame-right', artwork.offsetWidth)) *
          scale.x;
        const height = Math.max(artwork.offsetHeight, 1) *
          Math.max(0.01, 1 - frameInset('--product-design-artwork-frame-top', artwork.offsetHeight) - frameInset('--product-design-artwork-frame-bottom', artwork.offsetHeight)) *
          scale.y;
        const compactHitInset = Math.min(3.5, width / 4, height / 4);
        artwork.style.setProperty('--product-design-compact-hit-inset', `${compactHitInset / scale.x}px`);
        const threshold = window.matchMedia('(pointer: coarse)').matches ? 104 : 60;
        // Do not replace a handle while it owns an active drag or resize gesture.
        if (!overlay.classList.contains('is-manipulating')) {
          artwork.classList.toggle('has-compact-controls', Math.min(width, height) < threshold);
        }
        const compact = artwork.classList.contains('has-compact-controls');
        const rotateTool = overlay.querySelector('[data-design-tool="rotate"]');
        if (rotateTool) rotateTool.hidden = !compact;
      }

      updateDesignArtworkToolbar(overlay) {
        const artwork = overlay?.querySelector('[data-role="product-design-artwork"]');
        const toolbar = overlay?.querySelector('[data-role="product-design-toolbar"]');
        if (!toolbar) return;
        if (overlay.classList.contains('is-manipulating')) {
          toolbar.hidden = true;
          return;
        }
        this.updateDesignArtworkControls(overlay, artwork);
        if (overlay.dataset.mobileDesignSurface === 'true') {
          toolbar.hidden = true;
          return;
        }

        const shouldShow = Boolean(
          this.designStudioState?.hasLogo &&
          overlay &&
          !overlay.hidden &&
          artwork &&
          !artwork.hidden &&
          artwork.classList.contains('is-selected') &&
          !overlay.classList.contains('is-previewing') &&
          !overlay.classList.contains('is-manipulating')
        );
        toolbar.hidden = !shouldShow;
        if (!shouldShow) {
          toolbar.classList.remove('has-tooltips-right', 'is-below');
          toolbar.style.removeProperty('--product-design-toolbar-scale');
          toolbar.style.removeProperty('--product-design-tooltip-available-width');
          return;
        }

        const canvasRect = overlay.getBoundingClientRect();
        const visualBounds = this.getDesignArtworkVisualBounds(artwork);
        if (!canvasRect.width || !canvasRect.height || !visualBounds) {
          toolbar.hidden = true;
          return;
        }

        toolbar.classList.remove('has-tooltips-right', 'is-below');
        toolbar.style.removeProperty('--product-design-toolbar-scale');
        const styles = window.getComputedStyle(toolbar);
        const canvasInset = parseFloat(styles.getPropertyValue('--product-design-toolbar-canvas-inset')) || 10;
        const artworkGap = parseFloat(styles.getPropertyValue('--product-design-toolbar-artwork-gap')) || 10;
        const availableWidth = Math.max(1, canvasRect.width - canvasInset * 2);
        const availableHeight = Math.max(1, canvasRect.height - canvasInset * 2);
        let toolbarRect = toolbar.getBoundingClientRect();

        const fitScale = Math.min(
          1,
          availableWidth / Math.max(toolbarRect.width, 1),
          availableHeight / Math.max(toolbarRect.height, 1),
        );
        if (fitScale < 0.999) {
          toolbar.style.setProperty('--product-design-toolbar-scale', String(fitScale));
          toolbarRect = toolbar.getBoundingClientRect();
        }

        if (!toolbarRect.width || !toolbarRect.height) {
          toolbar.hidden = true;
          return;
        }

        const visualCenterX = (visualBounds.left + visualBounds.right) / 2 - canvasRect.left;
        const desiredLeft = visualCenterX - toolbarRect.width / 2;
        const maximumLeft = Math.max(canvasInset, canvasRect.width - canvasInset - toolbarRect.width);
        const left = this.clampNumber(desiredLeft, canvasInset, maximumLeft);
        const rotateHandle = artwork.classList.contains('has-compact-controls')
          ? artwork.querySelector('[data-role="product-design-rotate"]')
          : null;
        const controlsTop = Math.min(visualBounds.top, rotateHandle?.getBoundingClientRect().top ?? visualBounds.top);
        const aboveTop = controlsTop - canvasRect.top - artworkGap - toolbarRect.height;
        const maximumTop = Math.max(canvasInset, canvasRect.height - canvasInset - toolbarRect.height);
        const top = this.clampNumber(aboveTop, canvasInset, maximumTop);

        toolbar.style.setProperty('--product-design-toolbar-x', `${left}px`);
        toolbar.style.setProperty('--product-design-toolbar-y', `${top}px`);
        toolbar.style.setProperty('--product-design-tooltip-available-width', `${Math.max(80, availableWidth)}px`);
      }

      setDesignArtworkFrameVariables(artwork, frame, unit = '%') {
        if (!artwork || !frame) return;

        artwork.style.setProperty('--product-design-artwork-frame-top', `${frame.top}${unit}`);
        artwork.style.setProperty('--product-design-artwork-frame-right', `${frame.right}${unit}`);
        artwork.style.setProperty('--product-design-artwork-frame-bottom', `${frame.bottom}${unit}`);
        artwork.style.setProperty('--product-design-artwork-frame-left', `${frame.left}${unit}`);
        artwork.style.setProperty('--product-design-artwork-frame-center-x', `${frame.centerX}${unit}`);
        artwork.style.setProperty('--product-design-artwork-frame-center-y', `${frame.centerY}${unit}`);
      }

      updateDesignArtworkRenderedFrame(artwork, placement) {
        const image = artwork?.querySelector('[data-role="product-design-artwork-image"]');
        if (!artwork || !placement || !image || image.hidden || !image.naturalWidth || !image.naturalHeight) return;

        const frame = this.getDesignArtworkRenderedFrame(artwork, image, placement);
        if (frame) this.setDesignArtworkFrameVariables(artwork, frame, 'px');
      }

      getDesignArtworkRenderedFrame(artwork, image, placement) {
        const interaction = this.designInteraction, metrics = interaction?.renderMetrics;
        const canReuse = metrics && metrics.artwork === artwork && metrics.image === image && metrics.width > 0 && metrics.height > 0
          && metrics.placement.width > 0 && metrics.placement.height > 0
          && metrics.naturalWidth === image.naturalWidth && metrics.naturalHeight === image.naturalHeight
          && metrics.imageSrc === image.getAttribute('src') && this.isDesignArtworkInteractionCurrent(interaction);
        // During a gesture the print plane is fixed. Derive the new CSS size
        // from its initial size instead of forcing layout after every write.
        const style = !canReuse && artwork?.nodeType === 1 ? getComputedStyle(artwork) : null;
        const artworkWidth = canReuse ? metrics.width * placement.width / metrics.placement.width
          : Number.parseFloat(style?.width) || artwork?.offsetWidth || 0;
        const artworkHeight = canReuse ? metrics.height * placement.height / metrics.placement.height
          : Number.parseFloat(style?.height) || artwork?.offsetHeight || 0;
        if (!artworkWidth || !artworkHeight || !image?.naturalWidth || !image.naturalHeight) return null;
        const api = window.TodayMerchStudioDesignRendering;
        const crop = this.getCachedTransparentImageCrop(image);
        const contentFrame = crop.contentFrame;
        const names = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop'];
        const raw = Object.fromEntries(names.filter(key => placement[key] !== undefined).map(key => [key, placement[key]]));
        if (contentFrame) raw.contentFrame = contentFrame;
        const geometry = api?.geometry(raw, { zoneAspect: artworkWidth / artworkHeight * placement.height / placement.width,
          naturalRatio: image.naturalWidth / image.naturalHeight, contentFrame });
        // Empty/unreadable replacements must never retain the preceding image's selection box.
        const visible = geometry?.placement || { cropLeft: placement.cropLeft || 0, cropRight: placement.cropRight || 0,
          cropTop: placement.cropTop || 0, cropBottom: placement.cropBottom || 0 };
        const left = visible.cropLeft * artworkWidth, right = visible.cropRight * artworkWidth;
        const top = visible.cropTop * artworkHeight, bottom = visible.cropBottom * artworkHeight;
        return { top, right, bottom, left, centerX: (artworkWidth + left - right) / 2, centerY: (artworkHeight + top - bottom) / 2 };
      }

      getCachedTransparentImageCrop(image) {
        // The shared reader caches by image identity, dimensions, source, kind and method version.
        return this.getTransparentImageCrop(image);
      }

      getDesignArtworkFrameInsets(placement, { zoneAspect = 1 } = {}) {
        const api = window.TodayMerchStudioDesignRendering;
        if (placement.contentFrame?.visibleBounds) {
          const fields = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop', 'contentFrame'];
          const raw = Object.fromEntries(fields.filter(key => placement[key] !== undefined).map(key => [key, placement[key]]));
          const visible = api?.geometry(raw, { zoneAspect, naturalRatio: placement.imageNaturalRatio, contentFrame: placement.contentFrame })?.placement;
          if (visible) {
            const left = visible.cropLeft * 100, right = visible.cropRight * 100, top = visible.cropTop * 100, bottom = visible.cropBottom * 100;
            return { left, right, top, bottom, centerX: (100 + left - right) / 2, centerY: (100 + top - bottom) / 2 };
          }
        }
        const cropLeftPercent = (placement.cropLeft || 0) * 100;
        const cropRightPercent = (placement.cropRight || 0) * 100;
        const cropTopPercent = (placement.cropTop || 0) * 100;
        const cropBottomPercent = (placement.cropBottom || 0) * 100;
        const fallback = {
          top: cropTopPercent,
          right: cropRightPercent,
          bottom: cropBottomPercent,
          left: cropLeftPercent,
        };

        const containerRatio = placement.width / Math.max(placement.height, 0.001);
        const imageNaturalRatio =
          placement.imageNaturalRatio || (placement.artworkRatio ? placement.artworkRatio * (placement.mediaWidth / placement.mediaHeight) : 0);

        if (!containerRatio || !imageNaturalRatio || !placement.mediaWidth || !placement.mediaHeight) {
          return {
            ...fallback,
            centerX: fallback.left + Math.max(0, 100 - fallback.left - fallback.right) / 2,
            centerY: fallback.top + Math.max(0, 100 - fallback.top - fallback.bottom) / 2,
          };
        }

        const mediaWidth = placement.mediaWidth;
        const mediaHeight = placement.mediaHeight;
        const mediaLeft = placement.mediaLeft || 0;
        const mediaTop = placement.mediaTop || 0;
        const mediaBoxRatio = (containerRatio * mediaWidth) / Math.max(mediaHeight, 0.001);
        let renderedLeft = mediaLeft;
        let renderedTop = mediaTop;
        let renderedWidth = mediaWidth;
        let renderedHeight = mediaHeight;

        if (mediaBoxRatio > imageNaturalRatio) {
          renderedWidth = (mediaHeight * imageNaturalRatio) / containerRatio;
          renderedLeft = mediaLeft + (mediaWidth - renderedWidth) / 2;
        } else {
          renderedHeight = (mediaWidth * containerRatio) / imageNaturalRatio;
          renderedTop = mediaTop + (mediaHeight - renderedHeight) / 2;
        }

        const contentLeft = renderedLeft + renderedWidth * (placement.imageCropLeft || 0);
        const contentTop = renderedTop + renderedHeight * (placement.imageCropTop || 0);
        const contentRight = 100 - (renderedLeft + renderedWidth * (1 - (placement.imageCropRight || 0)));
        const contentBottom = 100 - (renderedTop + renderedHeight * (1 - (placement.imageCropBottom || 0)));
        const visibleWidth = Math.max(0, 100 - contentLeft - contentRight);
        const visibleHeight = Math.max(0, 100 - contentTop - contentBottom);

        if (visibleWidth <= 0.1 || visibleHeight <= 0.1) {
          return {
            ...fallback,
            centerX: fallback.left + Math.max(0, 100 - fallback.left - fallback.right) / 2,
            centerY: fallback.top + Math.max(0, 100 - fallback.top - fallback.bottom) / 2,
          };
        }

        const frame = {
          top: contentTop + (cropTopPercent / 100) * visibleHeight,
          right: contentRight + (cropRightPercent / 100) * visibleWidth,
          bottom: contentBottom + (cropBottomPercent / 100) * visibleHeight,
          left: contentLeft + (cropLeftPercent / 100) * visibleWidth,
        };

        frame.top = this.clampNumber(frame.top, 0, 96);
        frame.right = this.clampNumber(frame.right, 0, 96);
        frame.bottom = this.clampNumber(frame.bottom, 0, 96);
        frame.left = this.clampNumber(frame.left, 0, 96);
        frame.centerX = frame.left + Math.max(0, 100 - frame.left - frame.right) / 2;
        frame.centerY = frame.top + Math.max(0, 100 - frame.top - frame.bottom) / 2;

        return frame;
      }

      getDesignArtworkVisibleGeometry(placement = {}, options = {}) {
        const width = Math.max(Number(placement.width || 0), 0.0001);
        const height = Math.max(Number(placement.height || 0), 0.0001);
        const frame = options.frame || this.getDesignArtworkFrameInsets(placement, options);
        const zoneAspect = options.zoneAspect > 0 ? options.zoneAspect : 1;
        const localLeft = -width / 2 + width * (frame.left / 100);
        const localRight = width / 2 - width * (frame.right / 100);
        const localTop = -height / 2 + height * (frame.top / 100);
        const localBottom = height / 2 - height * (frame.bottom / 100);
        const radians = (this.normalizeRotation(placement.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const points = [
          [localLeft, localTop],
          [localRight, localTop],
          [localRight, localBottom],
          [localLeft, localBottom],
        ].map(([x, y]) => ({
          x: Number(placement.x || 0) + x * cos - y * sin / zoneAspect,
          y: Number(placement.y || 0) + x * sin * zoneAspect + y * cos,
        }));
        const left = Math.min(...points.map((point) => point.x));
        const right = Math.max(...points.map((point) => point.x));
        const top = Math.min(...points.map((point) => point.y));
        const bottom = Math.max(...points.map((point) => point.y));

        return {
          points,
          left,
          right,
          top,
          bottom,
          centerX: (left + right) / 2,
          centerY: (top + bottom) / 2,
          width: right - left,
          height: bottom - top,
        };
      }

      getDesignAlignmentReference(zone, placement) {
        const parts = zone ? this.getDesignZoneParts(zone) : [];
        if (parts.length) {
          const part = this.selectDesignZonePart(parts, placement, { preferExistingPart: false });
          if (part) return part;
        }

        return {
          id: '',
          left: 0,
          top: 0,
          width: 100,
          height: 100,
          rotation: 0,
        };
      }

      getDesignAlignmentTargets(axis, reference) {
        const start = axis === 'x' ? reference.left : reference.top;
        const size = axis === 'x' ? reference.width : reference.height;
        return [start, start + size / 2, start + size];
      }

      getDesignAlignmentCandidates(placement, axis, options = {}) {
        const geometry = this.getDesignArtworkVisibleGeometry(placement, options);
        return axis === 'x'
          ? [
              { value: geometry.centerX, anchor: 'center', priority: 2 },
              { value: geometry.left, anchor: 'start', priority: 1 },
              { value: geometry.right, anchor: 'end', priority: 1 },
            ]
          : [
              { value: geometry.centerY, anchor: 'center', priority: 2 },
              { value: geometry.top, anchor: 'start', priority: 1 },
              { value: geometry.bottom, anchor: 'end', priority: 1 },
            ];
      }

      getDesignAlignmentSegment(axis, value, reference, zoneAspect = 1) {
        if (!reference.rotation && reference.shape !== 'circle') return {
          segmentStart: axis === 'x' ? reference.top : reference.left,
          segmentSize: axis === 'x' ? reference.height : reference.width,
        };
        // Intersect the guide with the actual part in pixel coordinates, so a
        // rotated or round zone never displays lines beyond its contour.
        const centerX = reference.left + reference.width / 2;
        const centerY = reference.top + reference.height / 2;
        const angle = (reference.rotation || 0) * Math.PI / 180;
        const cos = Math.cos(angle), sin = Math.sin(angle);
        const local = reference.shape === 'circle'
          ? Array.from({ length: 48 }, (_, index) => {
            const t = index * Math.PI / 24;
            return [Math.cos(t) * reference.width / 2, Math.sin(t) * reference.height / 2];
          })
          : [[-.5, -.5], [.5, -.5], [.5, .5], [-.5, .5]].map(([x, y]) => [x * reference.width, y * reference.height]);
        const points = local.map(([x, y]) => ({ x: centerX + x * cos - y * sin / zoneAspect, y: centerY + x * sin * zoneAspect + y * cos }));
        const crossAxis = axis === 'x' ? 'y' : 'x';
        const intersections = [];
        for (let index = 0; index < points.length; index += 1) {
          const a = points[index], b = points[(index + 1) % points.length];
          const distance = b[axis] - a[axis];
          if (Math.abs(distance) < 1e-9) continue;
          const ratio = (value - a[axis]) / distance;
          if (ratio >= 0 && ratio <= 1) intersections.push(a[crossAxis] + (b[crossAxis] - a[crossAxis]) * ratio);
        }
        if (intersections.length < 2) return { segmentStart: 0, segmentSize: 0 };
        const segmentStart = Math.min(...intersections);
        return { segmentStart, segmentSize: Math.max(...intersections) - segmentStart };
      }

      resolveDesignAlignment(placement, options = {}) {
        const snap = options.snap !== false;
        const threshold = options.threshold ?? 2.1;
        const displayThreshold = options.displayThreshold ?? threshold;
        const nextPlacement = { ...placement };
        const reference = this.getDesignAlignmentReference(options.zone || null, nextPlacement);
        const guides = [];

        if (reference.id) nextPlacement.partId = reference.id;

        ['x', 'y'].forEach((axis) => {
          const targets = this.getDesignAlignmentTargets(axis, reference);
          const candidates = this.getDesignAlignmentCandidates(nextPlacement, axis, options);
          const referenceCenter = targets[1];
          let bestMatch = null;

          targets.forEach((target) => {
            candidates.forEach((candidate) => {
              const delta = target - candidate.value;
              const distance = Math.abs(delta);
              const matchThreshold =
                candidate.anchor === 'center' && target === referenceCenter
                  ? threshold + 0.6
                  : threshold;
              if (distance > matchThreshold) return;

              if (
                !bestMatch ||
                distance < bestMatch.distance ||
                (distance === bestMatch.distance && candidate.priority > bestMatch.priority)
              ) {
                bestMatch = {
                  axis,
                  value: target,
                  delta,
                  distance,
                  anchor: candidate.anchor,
                  priority: candidate.priority,
                  kind: candidate.anchor === 'center' && target === referenceCenter ? 'center' : 'edge',
                };
              }
            });
          });

          if (!bestMatch || bestMatch.distance > displayThreshold) return;
          if (snap) nextPlacement[axis] += bestMatch.delta;

          const segment = this.getDesignAlignmentSegment(axis, bestMatch.value, reference, options.zoneAspect || 1);
          if (segment.segmentSize <= 0) return;
          guides.push({
            axis,
            value: bestMatch.value,
            anchor: bestMatch.anchor,
            kind: bestMatch.kind,
            partId: reference.id || '',
            ...segment,
          });
        });

        return {
          placement: this.constrainDesignArtwork(nextPlacement),
          guides,
        };
      }

      updateDesignAlignmentGuides(overlay, guides = []) {
        const layer = overlay?.querySelector('[data-role="product-design-alignment-guides"]');
        if (!layer) return;
        const signature = JSON.stringify(guides);
        if (layer.dataset.guideSignature === signature) return;
        layer.dataset.guideSignature = signature;
        layer.innerHTML = '';
        layer.hidden = !guides.length;
        overlay.classList.toggle('has-alignment-guides', Boolean(guides.length));

        guides.forEach((guide) => {
          const line = document.createElement('span');
          const isVertical = guide.axis === 'x';
          line.className = [
            'product__design-alignment-guide',
            `product__design-alignment-guide--${isVertical ? 'vertical' : 'horizontal'}`,
            `product__design-alignment-guide--${guide.kind || 'grid'}`,
          ].join(' ');
          line.style.setProperty(isVertical ? 'left' : 'top', `${guide.value}%`);
          line.style.setProperty(isVertical ? 'top' : 'left', `${guide.segmentStart ?? 0}%`);
          line.style.setProperty(isVertical ? 'height' : 'width', `${guide.segmentSize ?? 100}%`);
          line.dataset.anchor = guide.anchor || '';
          line.dataset.partId = guide.partId || '';
          layer.append(line);
        });
      }

      clearDesignAlignmentGuides(overlay) {
        this.updateDesignAlignmentGuides(overlay, []);
        const rotationGuide = overlay?.querySelector('[data-role="product-design-rotation-guide"]');
        if (rotationGuide) rotationGuide.hidden = true;
      }

      resolveDesignRotation(placement, options = {}) {
        const rotation = this.normalizeRotation(placement.rotation);
        const nearest = this.normalizeRotation(Math.round(rotation / 90) * 90);
        const previous = options.previousSnap;
        const held = Number.isFinite(previous) && Math.abs(this.normalizeRotation(rotation - previous)) <= 5;
        const snapped = options.snap !== false && (held || Math.abs(this.normalizeRotation(rotation - nearest)) <= (options.threshold ?? 3));
        return {
          placement: { ...placement, rotation: snapped ? (held ? previous : nearest) : rotation },
          snapped,
          guides: [],
        };
      }

      updateDesignRotationGuides(overlay, placement, snapped = false) {
        const shell = overlay?.querySelector('[data-role="product-design-zone-shell"]');
        if (!shell) return;
        let guide = shell.querySelector('[data-role="product-design-rotation-guide"]');
        if (!guide) {
          guide = document.createElement('span');
          guide.dataset.role = 'product-design-rotation-guide';
          guide.className = 'product__design-rotation-guide';
          guide.setAttribute('aria-hidden', 'true');
          guide.innerHTML = '<span class="product__design-rotation-guide-angle"></span>';
          shell.append(guide);
        }
        guide.hidden = false;
        guide.classList.toggle('is-snapped', snapped);
        guide.style.setProperty('--rotation-guide-x', `${placement.x}%`);
        guide.style.setProperty('--rotation-guide-y', `${placement.y}%`);
        const label = guide.firstElementChild;
        const text = `${Math.round(this.normalizeRotation(placement.rotation))}°`;
        if (label.textContent !== text) label.textContent = text;
      }

      fitDesignArtworkAfterRotation(placement, zone, overlay) {
        const shell = overlay?.querySelector('[data-role="product-design-zone-shell"]');
        const shellWidth = shell?.offsetWidth;
        const shellHeight = shell?.offsetHeight;
        if (!shellWidth || !shellHeight) return placement;
        const parts = this.getDesignArtworkZoneClipParts(zone);
        const part = this.selectDesignZonePart(parts, placement) || parts[0];
        if (!part) return placement;
        const artwork = overlay.querySelector('[data-role="product-design-artwork"]');
        const image = artwork?.querySelector('[data-role="product-design-artwork-image"]');
        const width = placement.width * shellWidth / 100;
        const height = placement.height * shellHeight / 100;
        // Convert the actual visible frame to pixels before rotating: percentage
        // coordinates distort angles whenever the print zone is not square.
        const rendered = image && this.getDesignArtworkRenderedFrame(artwork, image, placement);
        const fallback = this.getDesignArtworkFrameInsets(placement);
        const frame = rendered && artwork.offsetWidth && artwork.offsetHeight ? {
          left: rendered.left / artwork.offsetWidth, right: rendered.right / artwork.offsetWidth,
          top: rendered.top / artwork.offsetHeight, bottom: rendered.bottom / artwork.offsetHeight,
        } : Object.fromEntries(['left', 'right', 'top', 'bottom'].map(key => [key, fallback[key] / 100]));
        const partWidth = part.width * shellWidth / 100;
        const partHeight = part.height * shellHeight / 100;
        const partX = (part.left + part.width / 2) * shellWidth / 100;
        const partY = (part.top + part.height / 2) * shellHeight / 100;
        const relativeAngle = (placement.rotation - (part.rotation || 0)) * Math.PI / 180;
        const cos = Math.cos(relativeAngle), sin = Math.sin(relativeAngle);
        const points = [
          [width * (frame.left - 0.5), height * (frame.top - 0.5)],
          [width * (0.5 - frame.right), height * (frame.top - 0.5)],
          [width * (0.5 - frame.right), height * (0.5 - frame.bottom)],
          [width * (frame.left - 0.5), height * (0.5 - frame.bottom)],
        ].map(([x, y]) => ({ x: x * cos - y * sin, y: x * sin + y * cos }));
        const left = Math.min(...points.map(p => p.x)), right = Math.max(...points.map(p => p.x));
        const top = Math.min(...points.map(p => p.y)), bottom = Math.max(...points.map(p => p.y));
        const angle = (part.rotation || 0) * Math.PI / 180;
        const pc = Math.cos(angle), ps = Math.sin(angle);
        const dx = placement.x * shellWidth / 100 - partX, dy = placement.y * shellHeight / 100 - partY;
        let x = dx * pc + dy * ps, y = -dx * ps + dy * pc;
        let scale = Math.min(1, partWidth / Math.max(right - left, 0.001), partHeight / Math.max(bottom - top, 0.001));
        if (part.shape === 'circle') {
          const contained = points.every(p => ((p.x + x) / (partWidth / 2)) ** 2 + ((p.y + y) / (partHeight / 2)) ** 2 <= 1);
          if (contained) return placement;
          const cx = (left + right) / 2, cy = (top + bottom) / 2;
          scale = Math.min(1, ...points.map(p => 1 / Math.hypot((p.x - cx) / (partWidth / 2), (p.y - cy) / (partHeight / 2))));
          x = -cx * scale; y = -cy * scale;
        } else {
          x = this.clampNumber(x, -partWidth / 2 - left * scale, partWidth / 2 - right * scale);
          y = this.clampNumber(y, -partHeight / 2 - top * scale, partHeight / 2 - bottom * scale);
        }
        const next = {
          ...placement,
          x: (partX + x * pc - y * ps) / shellWidth * 100,
          y: (partY + x * ps + y * pc) / shellHeight * 100,
          width: placement.width * scale, height: placement.height * scale,
          partId: part.id || placement.partId,
        };
        if (next.artworkRatio && scale < 1) next.minimumFrameSize = Math.min(this.getDesignArtworkMinimumFrameSize(next), next.width, next.height);
        return next;
      }

      getDesignArtworkMinimumFrameSize(placement = {}) {
        return placement.artworkRatio && Number.isFinite(placement.minimumFrameSize) && placement.minimumFrameSize > 0
          ? this.clampNumber(placement.minimumFrameSize, 0.01, 2)
          : 2;
      }

      constrainDesignArtwork(placement = {}) {
        const contentFrame = window.TodayMerchStudioDesignRendering?.contentFrames.normalize(placement.contentFrame);
        const framed = Boolean(contentFrame?.visibleBounds);
        const artworkRatio = this.clampNumber(placement.artworkRatio || 0, 0, framed ? 1000000 : 100);
        const minimumFrameSize = this.getDesignArtworkMinimumFrameSize(placement);
        const hasMinimumFrameSize = artworkRatio && Number.isFinite(placement.minimumFrameSize) && placement.minimumFrameSize > 0;
        let width = this.clampNumber(placement.width ?? 52, hasMinimumFrameSize ? minimumFrameSize : 8, PRODUCT_DESIGN_MAX_FRAME_SIZE);
        let height = this.clampNumber(placement.height ?? 30, hasMinimumFrameSize ? minimumFrameSize : 5, PRODUCT_DESIGN_MAX_FRAME_SIZE);

        if (artworkRatio) {
          const heightFromWidth = width / artworkRatio;
          const widthFromHeight = height * artworkRatio;

          if (Math.abs(heightFromWidth - height) <= Math.abs(widthFromHeight - width)) {
            height = heightFromWidth;
          } else {
            width = widthFromHeight;
          }

          if (width > PRODUCT_DESIGN_MAX_FRAME_SIZE) {
            width = PRODUCT_DESIGN_MAX_FRAME_SIZE;
            height = width / artworkRatio;
          }

          if (height > PRODUCT_DESIGN_MAX_FRAME_SIZE) {
            height = PRODUCT_DESIGN_MAX_FRAME_SIZE;
            width = height * artworkRatio;
          }

          if (width < minimumFrameSize) {
            width = minimumFrameSize;
            height = width / artworkRatio;
          }

          if (height < minimumFrameSize) {
            height = minimumFrameSize;
            width = height * artworkRatio;
          }

          if (width > PRODUCT_DESIGN_MAX_FRAME_SIZE) {
            width = PRODUCT_DESIGN_MAX_FRAME_SIZE;
            height = width / artworkRatio;
          }

          if (height > PRODUCT_DESIGN_MAX_FRAME_SIZE) {
            height = PRODUCT_DESIGN_MAX_FRAME_SIZE;
            width = height * artworkRatio;
          }
        }

        return {
          x: this.clampNumber(placement.x ?? 50, framed ? -400 : -20, framed ? 400 : 120),
          y: this.clampNumber(placement.y ?? 50, framed ? -400 : -20, framed ? 400 : 120),
          width,
          height,
          // This minimum belongs to the saved geometry, like its rotation. Copies,
          // undo and replacement preserve it; a fresh manual upload has no override.
          ...(hasMinimumFrameSize ? { minimumFrameSize } : {}),
          partId: String(placement.partId || ''),
          rotation: this.normalizeRotation(placement.rotation || 0),
          cropTop: this.clampNumber(placement.cropTop || 0, 0, 0.88),
          cropRight: this.clampNumber(placement.cropRight || 0, 0, 0.88),
          cropBottom: this.clampNumber(placement.cropBottom || 0, 0, 0.88),
          cropLeft: this.clampNumber(placement.cropLeft || 0, 0, 0.88),
          mediaWidth: this.clampNumber(placement.mediaWidth ?? 100, 100, framed ? 100000000 : 1000),
          mediaHeight: this.clampNumber(placement.mediaHeight ?? 100, 100, framed ? 100000000 : 1000),
          mediaLeft: this.clampNumber(placement.mediaLeft ?? 0, framed ? -100000000 : -900, 0),
          mediaTop: this.clampNumber(placement.mediaTop ?? 0, framed ? -100000000 : -900, 0),
          artworkRatio,
          ...(contentFrame ? { contentFrame } : {}),
          imageNaturalRatio: this.clampNumber(placement.imageNaturalRatio || 0, 0, framed ? 1000000 : 100),
          imageCropTop: this.clampNumber(placement.imageCropTop || 0, 0, framed ? 1 - 1e-12 : 0.96),
          imageCropRight: this.clampNumber(placement.imageCropRight || 0, 0, framed ? 1 - 1e-12 : 0.96),
          imageCropBottom: this.clampNumber(placement.imageCropBottom || 0, 0, framed ? 1 - 1e-12 : 0.96),
          imageCropLeft: this.clampNumber(placement.imageCropLeft || 0, 0, framed ? 1 - 1e-12 : 0.96),
        };
      }

      getDesignZonePartDistance(part, x, y) {
        const centerX = part.left + part.width / 2;
        const centerY = part.top + part.height / 2;
        const radians = (-this.normalizeRotation(part.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const deltaX = x - centerX;
        const deltaY = y - centerY;
        const localX = deltaX * cos - deltaY * sin;
        const localY = deltaX * sin + deltaY * cos;
        const outsideX = Math.max(Math.abs(localX) - part.width / 2, 0);
        const outsideY = Math.max(Math.abs(localY) - part.height / 2, 0);
        return Math.hypot(outsideX, outsideY);
      }

      selectDesignZonePart(parts, placement, options = {}) {
        if (!parts.length) return null;

        const existingPart = parts.find((part) => part.id === placement.partId);
        if (existingPart && options.preferExistingPart !== false) return existingPart;

        if (!placement.partId && options.preferLargestWithoutPartId) {
          return [...parts].sort((left, right) => (
            right.width * right.height - left.width * left.height
          ))[0];
        }

        return parts
          .map((part, index) => ({
            part,
            index,
            distance: this.getDesignZonePartDistance(part, placement.x, placement.y),
            area: part.width * part.height,
          }))
          .sort((left, right) => (
            left.distance - right.distance ||
            right.area - left.area ||
            left.index - right.index
          ))[0]?.part || parts[0];
      }

      getDesignArtworkBoundsInPart(placement, part) {
        const geometry = this.getDesignArtworkVisibleGeometry(placement);
        const centerX = part.left + part.width / 2;
        const centerY = part.top + part.height / 2;
        const radians = (-this.normalizeRotation(part.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const points = geometry.points.map((point) => {
          const deltaX = point.x - centerX;
          const deltaY = point.y - centerY;
          return {
            x: deltaX * cos - deltaY * sin,
            y: deltaX * sin + deltaY * cos,
          };
        });

        return {
          left: Math.min(...points.map((point) => point.x)),
          right: Math.max(...points.map((point) => point.x)),
          top: Math.min(...points.map((point) => point.y)),
          bottom: Math.max(...points.map((point) => point.y)),
        };
      }

      constrainDesignArtworkToPart(placement, part) {
        if (!part) return placement;

        // The zone clip already hides every pixel outside the printable parts. Keep the
        // selected part for alignment and quote metadata, but preserve the user's exact
        // size and position so the frame can cross a print boundary while editing.
        return {
          ...placement,
          partId: part.id,
        };
      }

      constrainDesignArtworkToZone(placement = {}, zone = null, options = {}) {
        const constrained = this.constrainDesignArtwork(placement);
        const parts = this.getDesignZoneParts(zone);
        if (parts.length <= 1) {
          return {
            ...constrained,
            partId: parts[0]?.id || constrained.partId || '',
          };
        }

        const part = this.selectDesignZonePart(parts, constrained, options);
        return this.constrainDesignArtworkToPart(constrained, part);
      }

      clampNumber(value, min, max) {
        return Math.min(Math.max(Number(value) || 0, min), max);
      }

      normalizeRotation(value) {
        let rotation = Number(value) || 0;
        while (rotation > 180) rotation -= 360;
        while (rotation < -180) rotation += 360;
        return rotation;
      }

      getActiveOverlayZone(overlay) {
        const zoneKey = overlay?.dataset.zoneKey || '';
        return (this.designStudioState?.zones || []).find((zone) => zone.key === zoneKey) || this.getActiveDesignZone();
      }

      getActiveOverlayPlacement(overlay) {
        return this.getDesignArtworkPlacement(this.getActiveOverlayZone(overlay));
      }

      selectDesignArtworkItem(event, overlay, zoneKey, artworkId) {
        event.preventDefault();
        event.stopPropagation();

        this.setActiveDesignArtworkId(zoneKey, artworkId);
        this.setDesignArtworkSelected(overlay, true);
        this.renderDesignStudio();
      }

      setDesignArtworkSelected(overlay, selected = true) {
        const artwork = overlay?.querySelector('[data-role="product-design-artwork"]');
        const isSelected = Boolean(selected);
        overlay?.classList.toggle('is-previewing', !isSelected);
        if (!isSelected) this.clearDesignAlignmentGuides(overlay);
        artwork?.classList.toggle('is-selected', isSelected);
        this.updateDesignArtworkToolbar(overlay);
        this.updateDesignImagePanAvailability(overlay);
      }

      beginDesignInteraction(overlay) {
        overlay?.classList.remove('is-controls-idle');
        this.setDesignArtworkSelected(overlay, true);
        overlay?.classList.add('is-manipulating');
        this.clearDesignAlignmentGuides(overlay);
        window.clearTimeout(this.designToolbarTimer);
        const toolbar = overlay?.querySelector('[data-role="product-design-toolbar"]');
        if (toolbar) toolbar.hidden = true;
      }

      endDesignInteraction(overlay) {
        overlay?.classList.remove('is-manipulating');
        window.clearTimeout(this.designToolbarTimer);
        window.setTimeout(() => {
          if (!overlay?.classList.contains('is-manipulating')) this.clearDesignAlignmentGuides(overlay);
        }, 140);
        this.designToolbarTimer = window.setTimeout(() => {
          this.updateDesignArtworkToolbar(overlay);
          this.syncDesignControlsVisibility();
        }, 80);
      }

      bindDesignArtworkInteraction(event, captureTarget, onMove) {
        const interaction = this.designInteraction;
        if (!interaction) return;
        const zone = this.getActiveOverlayZone(interaction.overlay);
        const source = this.getDesignArtworkSource(zone, interaction.artworkId);
        interaction.variantId = this.activeVariantId || this.designStudioState?.variantId || '';
        interaction.fileKey = source?.fileKey || '';
        interaction.previewUrl = source?.previewUrl || '';
        interaction.pointerId = event.pointerId;
        interaction.pointerType = event.pointerType;
        interaction.captureTarget = captureTarget;
        interaction.abort = new AbortController();
        interaction.zoneShell = interaction.overlay.querySelector('[data-role="product-design-zone-shell"]');
        interaction.zoneShellRect = interaction.rect || this.getDesignZoneShellRect(interaction.zoneShell);
        const artwork = interaction.overlay.querySelector('[data-role="product-design-artwork"]');
        const image = artwork?.querySelector('[data-role="product-design-artwork-image"]');
        if (artwork && image && interaction.startPlacement) {
          const style = getComputedStyle(artwork);
          const width = Number.parseFloat(style.width) || artwork.offsetWidth;
          const height = Number.parseFloat(style.height) || artwork.offsetHeight;
          interaction.renderMetrics = {
            artwork, image, width, height, placement: { ...interaction.startPlacement },
            zoneAspect: this.getDesignArtworkZoneAspect(zone, image), imageSrc: image.getAttribute('src'),
            naturalWidth: image.naturalWidth, naturalHeight: image.naturalHeight,
            frame: this.getDesignArtworkRenderedFrame(artwork, image, interaction.startPlacement),
          };
          const { frame } = interaction.renderMetrics;
          if (frame && width && height) interaction.alignmentFrame = Object.fromEntries(
            ['left', 'right', 'top', 'bottom'].map(key => [key, frame[key] / (key === 'left' || key === 'right' ? width : height) * 100]),
          );
        }
        interaction.flushMove = () => {
          if (interaction.moveFrame) window.cancelAnimationFrame(interaction.moveFrame);
          interaction.moveFrame = 0;
          const pending = interaction.pendingMove;
          interaction.pendingMove = null;
          if (pending && this.designInteraction === interaction && this.isDesignArtworkInteractionCurrent(interaction)) onMove(pending);
        };
        const { signal } = interaction.abort;
        this.cancelDesignImagePan();
        this.cancelDesignImageTrackpadZoom();
        const move = (moveEvent) => {
          if (this.designInteraction !== interaction || moveEvent.pointerId !== interaction.pointerId) return;
          if (!this.isDesignArtworkInteractionCurrent(interaction)) return this.cancelDesignArtworkInteraction();
          if (moveEvent.cancelable) moveEvent.preventDefault();
          interaction.pendingMove = moveEvent;
          if (!interaction.moveFrame) interaction.moveFrame = window.requestAnimationFrame(interaction.flushMove);
        };
        const end = (endEvent) => {
          if (this.designInteraction !== interaction || endEvent.pointerId !== interaction.pointerId) return;
          this.cancelDesignArtworkInteraction();
          if (endEvent.type === 'pointerup' && interaction.type === 'drag' && interaction.pointerType === 'mouse' &&
            interaction.moved && this.isDesignArtworkInteractionCurrent(interaction)) {
            // A mouse release also dispatches click. Selection and the final
            // transform are already committed; do not rebuild the page again.
            const completed = { interaction, x: endEvent.clientX, y: endEvent.clientY };
            this.completedDesignDragClick = completed;
            window.setTimeout(() => {
              if (this.completedDesignDragClick === completed) this.completedDesignDragClick = null;
            }, 0);
          }
        };
        const cancel = () => {
          if (this.designInteraction === interaction) this.cancelDesignArtworkInteraction();
        };
        // Pointer capture does not capture wheel events. Consume them only for
        // this active manipulation, including when the pointer leaves the photo.
        const consumeScroll = (scrollEvent) => {
          if (this.designInteraction !== interaction) return;
          if (scrollEvent.cancelable) scrollEvent.preventDefault();
          scrollEvent.stopImmediatePropagation();
        };
        window.addEventListener('pointermove', move, { passive: false, signal });
        window.addEventListener('pointerup', end, { signal });
        window.addEventListener('pointercancel', end, { signal });
        window.addEventListener('blur', cancel, { signal });
        window.addEventListener('resize', cancel, { signal });
        captureTarget?.addEventListener('lostpointercapture', end, { signal });
        for (const type of ['wheel', 'gesturestart', 'gesturechange', 'gestureend']) {
          window.addEventListener(type, consumeScroll, { capture: true, passive: false, signal });
        }
        try { captureTarget?.setPointerCapture?.(event.pointerId); }
        catch { cancel(); }
      }

      isDesignArtworkInteractionCurrent(interaction) {
        if (!this.isConnected || !interaction?.overlay?.isConnected) return false;
        if ((this.activeVariantId || this.designStudioState?.variantId || '') !== interaction.variantId) return false;
        const zone = this.getActiveOverlayZone(interaction.overlay);
        if (zone?.key !== interaction.zoneKey) return false;
        const source = this.getDesignArtworkSource(zone, interaction.artworkId);
        return (source?.fileKey || '') === interaction.fileKey && (source?.previewUrl || '') === interaction.previewUrl;
      }

      consumeCompletedDesignDragClick(event, overlay) {
        const completed = this.completedDesignDragClick;
        this.completedDesignDragClick = null;
        const interaction = completed?.interaction;
        if (!interaction || interaction.overlay !== overlay || event.detail <= 0 || event.button !== 0 ||
          this.designStudioState?.activeZoneKey !== interaction.zoneKey || overlay.dataset.artworkId !== interaction.artworkId ||
          (event.pointerId !== undefined && event.pointerId !== interaction.pointerId) ||
          Math.abs(event.clientX - completed.x) > 1 || Math.abs(event.clientY - completed.y) > 1 ||
          event.target.closest('[data-role="product-design-artwork"]') !== interaction.captureTarget ||
          !this.isDesignArtworkInteractionCurrent(interaction)) return false;
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      cancelDesignArtworkInteraction() {
        const interaction = this.designInteraction;
        if (!interaction) return;
        // A release can arrive before the scheduled paint. Never lose its last
        // pointer sample, including on cancellation or a window focus change.
        const current = this.isDesignArtworkInteractionCurrent(interaction);
        interaction.flushMove?.();
        this.designInteraction = null;
        interaction.abort?.abort();
        if (interaction.captureTarget?.hasPointerCapture?.(interaction.pointerId)) {
          interaction.captureTarget.releasePointerCapture(interaction.pointerId);
        }
        if (interaction.overlay?.isConnected) {
          const zone = this.getActiveOverlayZone(interaction.overlay);
          if (current && interaction.previewPlacement) {
            const fitted = this.fitDesignArtworkAfterRotation(interaction.previewPlacement, zone, interaction.overlay);
            this.updateStoredDesignArtwork(interaction.zoneKey, fitted, interaction.artworkId);
          } else if (current) this.emitDesignArtworkTransform(zone);
          this.updateDesignArtwork(interaction.overlay, this.getDesignArtworkPlacement(zone, current ? interaction.artworkId : undefined));
          this.scheduleMobileDesignEditorSync();
          this.endDesignInteraction(interaction.overlay);
          if (interaction.layoutRefreshPending) this.scheduleDesignStudioLayoutUpdate();
        }
        else {
          interaction.overlay?.classList.remove('is-manipulating');
          window.clearTimeout(this.designToolbarTimer);
        }
      }

      startDesignArtworkDrag(event, overlay, touchArtwork = null) {
        this.completedDesignDragClick = null;
        if (event.button !== 0 || event.isPrimary === false || this.designInteraction) return;
        const artwork = touchArtwork || event.currentTarget;
        if (!artwork || artwork.hidden) return;
        if (!touchArtwork && (
          event.target.closest('[data-role="product-design-handle"]') ||
          event.target.closest('[data-role="product-design-rotate"]') ||
          event.target.closest('[data-role="product-design-tool"]')
        )) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.beginDesignInteraction(overlay);

        const zoneShell = overlay.querySelector('[data-role="product-design-zone-shell"]');
        const zone = this.getActiveOverlayZone(overlay);
        const artworkId = artwork.dataset.artworkId || this.getActiveDesignArtworkId(zone?.key || '');
        const placement = this.getDesignArtworkPlacement(zone, artworkId);
        this.recordDesignArtworkHistory(zone?.key || '', placement, artworkId);
        this.designInteraction = {
          type: 'drag',
          overlay,
          zoneKey: zone?.key || '',
          artworkId,
          rect: this.getDesignZoneShellRect(zoneShell),
          startClientX: event.clientX,
          startClientY: event.clientY,
          startPlacement: { ...placement },
        };

        this.bindDesignArtworkInteraction(event, artwork, (moveEvent) => this.moveDesignArtwork(moveEvent));
      }

      moveDesignArtwork(event) {
        const interaction = this.designInteraction;
        if (!interaction || interaction.type !== 'drag') return;
        if (Math.hypot(event.clientX - interaction.startClientX, event.clientY - interaction.startClientY) >= 3) interaction.moved = true;

        const delta = this.projectDesignPointerDelta(event.clientX - interaction.startClientX,
          event.clientY - interaction.startClientY, interaction.rect.rotation || 0);
        const deltaX = delta.x / interaction.rect.width * 100;
        const deltaY = delta.y / interaction.rect.height * 100;
        const nextPlacement = {
          ...interaction.startPlacement,
          x: interaction.startPlacement.x + deltaX,
          y: interaction.startPlacement.y + deltaY,
        };

        const alignment = this.resolveDesignAlignment(nextPlacement, {
          snap: true,
          zone: this.getActiveOverlayZone(interaction.overlay),
          zoneAspect: interaction.rect.width / interaction.rect.height,
          frame: interaction.alignmentFrame,
        });
        this.updateDesignAlignmentGuides(interaction.overlay, alignment.guides);

        this.updateStoredDesignArtwork(
          interaction.zoneKey,
          alignment.placement,
          interaction.artworkId,
          { preferExistingPart: false },
        );
        this.updateDesignArtwork(interaction.overlay, this.getDesignArtworkPlacement({ key: interaction.zoneKey }, interaction.artworkId));
      }

      getDesignCompactHandleTouchArtwork(event, overlay) {
        if (event.pointerType !== 'touch' || !event.currentTarget?.classList.contains('product__design-artwork-handle--compact')) return null;
        const artwork = event.currentTarget.closest('[data-role="product-design-artwork"]');
        if (!artwork || artwork.hidden) return null;
        const image = artwork.querySelector('[data-role="product-design-artwork-image"]');
        const zone = this.getActiveOverlayZone(overlay);
        const placement = this.getDesignArtworkPlacement(zone, artwork.dataset.artworkId);
        const frame = this.getDesignArtworkRenderedFrame(artwork, image, placement);
        if (!frame) return null;
        const style = getComputedStyle(artwork), width = Number.parseFloat(style.width), height = Number.parseFloat(style.height);
        const rect = artwork.getBoundingClientRect(), scale = this.getDesignVisualLayerScale(overlay);
        const delta = this.projectDesignPointerDelta((event.clientX - rect.x - rect.width / 2) / scale.x,
          (event.clientY - rect.y - rect.height / 2) / scale.y, placement.rotation + Number(artwork.closest('[data-role="product-design-zone-shell"]')?.dataset.photoRotation || 0));
        const x = width / 2 + delta.x, y = height / 2 + delta.y;
        const insetX = Math.min(3.5 / scale.x, (width - frame.left - frame.right) * .2);
        const insetY = Math.min(3.5 / scale.y, (height - frame.top - frame.bottom) * .2);
        // Touch browsers may retarget the logo's center to a nearby handle.
        // Use the unchanged finger coordinates, keeping the frame's edge and
        // the external handle available for resizing with the same pointer.
        return x > frame.left + insetX && x < width - frame.right - insetX
          && y > frame.top + insetY && y < height - frame.bottom - insetY ? artwork : null;
      }

      startDesignArtworkResize(event, overlay, handle) {
        if (event.button !== 0 || event.isPrimary === false || this.designInteraction) return;
        const touchArtwork = this.getDesignCompactHandleTouchArtwork(event, overlay);
        if (touchArtwork) {
          this.startDesignArtworkDrag(event, overlay, touchArtwork);
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.beginDesignInteraction(overlay);
        const captureTarget = event.currentTarget;

        const zoneShell = overlay.querySelector('[data-role="product-design-zone-shell"]');
        const zone = this.getActiveOverlayZone(overlay);
        const artworkId = overlay?.dataset.artworkId || this.getActiveDesignArtworkId(zone?.key || '');
        const placement = this.getDesignArtworkPlacement(zone, artworkId);
        this.recordDesignArtworkHistory(zone?.key || '', placement, artworkId);
        const artworkElement = event.currentTarget?.closest?.('[data-role="product-design-artwork"]');
        const artworkImage = artworkElement?.querySelector('[data-role="product-design-artwork-image"]');
        const zoneRect = this.getDesignZoneShellRect(zoneShell);
        const visualScale = this.getDesignVisualLayerScale(overlay);
        const layoutArtworkWidth = artworkElement?.offsetWidth
          || ((zoneRect.width / visualScale.x) * placement.width) / 100;
        const layoutArtworkHeight = artworkElement?.offsetHeight
          || ((zoneRect.height / visualScale.y) * placement.height) / 100;
        const artworkWidth = layoutArtworkWidth * visualScale.x;
        const artworkHeight = layoutArtworkHeight * visualScale.y;
        const renderedFrame = this.getDesignArtworkRenderedFrame(artworkElement, artworkImage, placement);
        const fallbackFrame = this.getDesignArtworkFrameInsets(placement);
        const frame = renderedFrame || {
          left: (fallbackFrame.left / 100) * layoutArtworkWidth,
          right: (fallbackFrame.right / 100) * layoutArtworkWidth,
          top: (fallbackFrame.top / 100) * layoutArtworkHeight,
          bottom: (fallbackFrame.bottom / 100) * layoutArtworkHeight,
        };
        const scaledFrame = {
          left: frame.left * visualScale.x,
          right: frame.right * visualScale.x,
          top: frame.top * visualScale.y,
          bottom: frame.bottom * visualScale.y,
        };
        const visibleFrameWidth = Math.max(1, artworkWidth - scaledFrame.left - scaledFrame.right);
        const visibleFrameHeight = Math.max(1, artworkHeight - scaledFrame.top - scaledFrame.bottom);
        const visibleCropX = Math.max(0.12, 1 - (placement.cropLeft || 0) - (placement.cropRight || 0));
        const visibleCropY = Math.max(0.12, 1 - (placement.cropTop || 0) - (placement.cropBottom || 0));
        this.designInteraction = {
          type: 'resize',
          overlay,
          handle,
          zoneKey: zone?.key || '',
          artworkId,
          rect: zoneRect,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startPlacement: { ...placement },
          frameMetrics: {
            artworkWidth,
            artworkHeight,
            left: scaledFrame.left,
            right: scaledFrame.right,
            top: scaledFrame.top,
            bottom: scaledFrame.bottom,
            visibleWidth: visibleFrameWidth,
            visibleHeight: visibleFrameHeight,
            cropReferenceWidth: visibleFrameWidth / visibleCropX,
            cropReferenceHeight: visibleFrameHeight / visibleCropY,
          },
        };

        this.bindDesignArtworkInteraction(event, captureTarget, (moveEvent) => this.resizeDesignArtwork(moveEvent));
      }

      projectDesignPointerDelta(deltaX, deltaY, rotation = 0) {
        const radians = (this.normalizeRotation(rotation) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        return {
          x: (deltaX * cos) + (deltaY * sin),
          y: (-deltaX * sin) + (deltaY * cos),
        };
      }

      getDesignZoneShellRect(shell, { unzoomed = false } = {}) {
        if (!shell) return null;
        const overlay = shell.closest('[data-role="product-design-studio"]');
        const visual = overlay?.querySelector('[data-role="product-design-visual-layer"]');
        const rect = unzoomed ? this.getDesignPreviewUnzoomedRect(shell, visual || shell) : shell.getBoundingClientRect();
        const rotation = Number(shell.dataset.photoRotation || 0);
        if (!rotation) return rect;
        const style = getComputedStyle(shell), zoom = unzoomed ? { x: 1, y: 1 } : this.getDesignVisualLayerScale(overlay);
        const width = Number.parseFloat(style.width) * zoom.x, height = Number.parseFloat(style.height) * zoom.y;
        if (!(width > 0 && height > 0)) return null;
        const left = rect.left + (rect.width - width) / 2, top = rect.top + (rect.height - height) / 2;
        return { x: left, y: top, left, top, right: left + width, bottom: top + height, width, height, rotation };
      }

      getDesignZoneShellPoint(rect, x, y) {
        const delta = this.projectDesignPointerDelta((x / 100 - .5) * rect.width, (y / 100 - .5) * rect.height, -(rect.rotation || 0));
        return { x: rect.left + rect.width / 2 + delta.x, y: rect.top + rect.height / 2 + delta.y };
      }

      getDesignVisualLayerScale(overlay) {
        const visualLayer = overlay?.querySelector('[data-role="product-design-visual-layer"]');
        const rect = visualLayer?.getBoundingClientRect();
        if (!visualLayer || !rect) return { x: 1, y: 1 };

        const scaleX = rect.width / Math.max(visualLayer.offsetWidth || rect.width, 1);
        const scaleY = rect.height / Math.max(visualLayer.offsetHeight || rect.height, 1);
        return {
          x: Number.isFinite(scaleX) && scaleX > 0 ? scaleX : 1,
          y: Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 1,
        };
      }

      calculateDesignArtworkResize(start, handle, deltaX, deltaY, rect, frameMetrics = null) {
        const safeRect = {
          width: Math.max(Number(rect?.width || 0), 1),
          height: Math.max(Number(rect?.height || 0), 1),
        };
        const localDelta = this.projectDesignPointerDelta(deltaX, deltaY, start.rotation || 0);
        const hasE = handle.includes('e');
        const hasW = handle.includes('w');
        const hasS = handle.includes('s');
        const hasN = handle.includes('n');
        const isCorner = (hasE || hasW) && (hasN || hasS);
        const cropTop = start.cropTop || 0;
        const cropRight = start.cropRight || 0;
        const cropBottom = start.cropBottom || 0;
        const cropLeft = start.cropLeft || 0;
        const artworkWidth = Math.max(
          Number(frameMetrics?.artworkWidth || 0),
          (start.width / 100) * safeRect.width,
          1,
        );
        const artworkHeight = Math.max(
          Number(frameMetrics?.artworkHeight || 0),
          (start.height / 100) * safeRect.height,
          1,
        );

        if (!isCorner) {
          const referenceWidth = Math.max(Number(frameMetrics?.cropReferenceWidth || artworkWidth), 1);
          const referenceHeight = Math.max(Number(frameMetrics?.cropReferenceHeight || artworkHeight), 1);
          let nextCropTop = cropTop;
          let nextCropRight = cropRight;
          let nextCropBottom = cropBottom;
          let nextCropLeft = cropLeft;

          if (hasE) nextCropRight = this.clampNumber(cropRight - (localDelta.x / referenceWidth), 0, 0.88 - cropLeft);
          if (hasW) nextCropLeft = this.clampNumber(cropLeft + (localDelta.x / referenceWidth), 0, 0.88 - cropRight);
          if (hasS) nextCropBottom = this.clampNumber(cropBottom - (localDelta.y / referenceHeight), 0, 0.88 - cropTop);
          if (hasN) nextCropTop = this.clampNumber(cropTop + (localDelta.y / referenceHeight), 0, 0.88 - cropBottom);

          return {
            ...start,
            cropTop: nextCropTop,
            cropRight: nextCropRight,
            cropBottom: nextCropBottom,
            cropLeft: nextCropLeft,
          };
        }

        const frameLeft = Number(frameMetrics?.left ?? (cropLeft * artworkWidth));
        const frameRight = Number(frameMetrics?.right ?? (cropRight * artworkWidth));
        const frameTop = Number(frameMetrics?.top ?? (cropTop * artworkHeight));
        const frameBottom = Number(frameMetrics?.bottom ?? (cropBottom * artworkHeight));
        const visibleWidth = Math.max(Number(frameMetrics?.visibleWidth || 0), artworkWidth - frameLeft - frameRight, 1);
        const visibleHeight = Math.max(Number(frameMetrics?.visibleHeight || 0), artworkHeight - frameTop - frameBottom, 1);
        const horizontalScale = 1 + ((hasE ? localDelta.x : -localDelta.x) / visibleWidth);
        const verticalScale = 1 + ((hasS ? localDelta.y : -localDelta.y) / visibleHeight);
        const requestedScale = Math.abs(horizontalScale - 1) >= Math.abs(verticalScale - 1)
          ? horizontalScale
          : verticalScale;
        const minScale = Math.max(8 / Math.max(start.width, 1), 5 / Math.max(start.height, 1));
        const maxScale = Math.min(
          PRODUCT_DESIGN_MAX_FRAME_SIZE / Math.max(start.width, 1),
          PRODUCT_DESIGN_MAX_FRAME_SIZE / Math.max(start.height, 1),
        );
        const scale = this.clampNumber(requestedScale, minScale, maxScale);
        const oppositeX = hasE ? frameLeft - (artworkWidth / 2) : (artworkWidth / 2) - frameRight;
        const oppositeY = hasS ? frameTop - (artworkHeight / 2) : (artworkHeight / 2) - frameBottom;
        const localCenterShiftX = (1 - scale) * oppositeX;
        const localCenterShiftY = (1 - scale) * oppositeY;
        const radians = (this.normalizeRotation(start.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const screenCenterShiftX = (localCenterShiftX * cos) - (localCenterShiftY * sin);
        const screenCenterShiftY = (localCenterShiftX * sin) + (localCenterShiftY * cos);

        return {
          ...start,
          x: start.x + (screenCenterShiftX / safeRect.width) * 100,
          y: start.y + (screenCenterShiftY / safeRect.height) * 100,
          width: start.width * scale,
          height: start.height * scale,
        };
      }

      resizeDesignArtwork(event) {
        const interaction = this.designInteraction;
        if (!interaction || interaction.type !== 'resize') return;

        const handle = interaction.handle || 'se';
        const start = interaction.startPlacement;
        const delta = this.projectDesignPointerDelta(event.clientX - interaction.startClientX,
          event.clientY - interaction.startClientY, interaction.rect.rotation || 0);
        const nextPlacement = this.calculateDesignArtworkResize(
          start,
          handle,
          delta.x,
          delta.y,
          interaction.rect,
          interaction.frameMetrics,
        );

        this.clearDesignAlignmentGuides(interaction.overlay);

        this.updateStoredDesignArtwork(interaction.zoneKey, nextPlacement, interaction.artworkId, { sizeLimitAttempt: true });
        this.updateDesignArtwork(interaction.overlay, this.getDesignArtworkPlacement({ key: interaction.zoneKey }, interaction.artworkId));
      }

      startDesignArtworkRotation(event, overlay) {
        if (event.button !== 0 || event.isPrimary === false || this.designInteraction) return;
        event.preventDefault();
        event.stopPropagation();
        this.beginDesignInteraction(overlay);
        const captureTarget = event.currentTarget;

        const zoneShell = overlay.querySelector('[data-role="product-design-zone-shell"]');
        const zone = this.getActiveOverlayZone(overlay);
        const artworkId = overlay?.dataset.artworkId || this.getActiveDesignArtworkId(zone?.key || '');
        const placement = this.getDesignArtworkPlacement(zone, artworkId);
        this.recordDesignArtworkHistory(zone?.key || '', placement, artworkId);
        const rect = this.getDesignZoneShellRect(zoneShell);
        const center = this.getDesignZoneShellPoint(rect, placement.x, placement.y);
        const centerX = center.x, centerY = center.y;
        const startPointerAngle = Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180 / Math.PI;

        this.designInteraction = {
          type: 'rotate',
          overlay,
          zoneKey: zone?.key || '',
          artworkId,
          centerX,
          centerY,
          startPointerAngle,
          startPlacement: { ...placement },
        };

        this.bindDesignArtworkInteraction(event, captureTarget, (moveEvent) => this.rotateDesignArtwork(moveEvent));
      }

      rotateDesignArtwork(event) {
        const interaction = this.designInteraction;
        if (!interaction || interaction.type !== 'rotate') return;

        const pointerAngle = Math.atan2(event.clientY - interaction.centerY, event.clientX - interaction.centerX) * 180 / Math.PI;
        const delta = pointerAngle - interaction.startPointerAngle;
        const nextPlacement = {
          ...interaction.startPlacement,
          rotation: interaction.startPlacement.rotation + delta,
        };
        const resolved = this.resolveDesignRotation(nextPlacement, { previousSnap: interaction.rotationSnap });
        interaction.rotationSnap = resolved.snapped ? resolved.placement.rotation : null;
        interaction.previewPlacement = resolved.placement;
        const artwork = interaction.overlay.querySelector('[data-role="product-design-artwork"]');
        this.updateDesignArtworkElement(artwork, resolved.placement, this.getActiveOverlayZone(interaction.overlay), interaction.overlay, { transient: true });
        this.updateDesignRotationGuides(interaction.overlay, resolved.placement, resolved.snapped);
      }

      async handleDesignArtworkTool(event, overlay, action) {
        event.preventDefault();
        event.stopPropagation();

        const zone = this.getActiveOverlayZone(overlay);
        if (!zone) return;

        const toolButton = event.target.closest('[data-role="product-design-tool"]');
        const artworkId = this.getActiveDesignArtworkId(zone.key);
        const artworkSource = this.getDesignArtworkSource(zone, artworkId);
        const placement = this.getDesignArtworkPlacement(zone, artworkId);
        let nextPlacement = { ...placement };

        switch (action) {
          case 'rotate':
            this.recordDesignArtworkHistory(zone.key, placement, artworkId);
            nextPlacement.rotation = this.normalizeRotation((placement.rotation || 0) + (event.shiftKey ? 1 : 15));
            break;
          case 'smaller':
          case 'larger': {
            const scale = action === 'smaller' ? 0.88 : 1.12;
            this.recordDesignArtworkHistory(zone.key, placement, artworkId);
            nextPlacement.width = placement.width * scale;
            nextPlacement.height = placement.height * scale;
            break;
          }
          case 'center': {
            this.recordDesignArtworkHistory(zone.key, placement, artworkId);
            const reference = this.getDesignAlignmentReference(zone, placement);
            const geometry = this.getDesignArtworkVisibleGeometry(nextPlacement);
            const centerX = reference.left + reference.width / 2;
            const centerY = reference.top + reference.height / 2;
            nextPlacement.x += centerX - geometry.centerX;
            nextPlacement.y += centerY - geometry.centerY;
            nextPlacement.partId = reference.id || nextPlacement.partId;
            this.updateDesignAlignmentGuides(overlay, [
              {
                axis: 'x',
                value: centerX,
                anchor: 'center',
                kind: 'center',
                partId: reference.id || '',
                segmentStart: reference.top,
                segmentSize: reference.height,
              },
              {
                axis: 'y',
                value: centerY,
                anchor: 'center',
                kind: 'center',
                partId: reference.id || '',
                segmentStart: reference.left,
                segmentSize: reference.width,
              },
            ]);
            window.setTimeout(() => this.clearDesignAlignmentGuides(overlay), 520);
            break;
          }
          case 'duplicate': {
            const offsetX = placement.x > 70 ? -10 : 10;
            const offsetY = placement.y > 70 ? -10 : 10;
            const duplicatePlacement = {
              ...placement,
              x: this.clampNumber(placement.x + offsetX, -20, 120),
              y: this.clampNumber(placement.y + offsetY, -20, 120),
            };
            const duplicate = this.addDesignArtworkCopy(zone.key, duplicatePlacement, artworkSource);
            if (duplicate) {
              const production = this.getDesignArtworkProductionContext(zone, artworkId, artworkSource);
              if (production?.record) {
                production.selector.cloneArtworkProductionSource?.(production.original, production.record, {
                  zoneKey: zone.key, artworkId: duplicate.id, fromArtworkId: artworkId,
                });
              }
              this.setActiveDesignArtworkId(zone.key, duplicate.id);
              this.resetDesignArtworkHistory(zone.key, duplicate.id);
              this.renderDesignStudio();
            }
            return;
          }
          case 'undo': {
            const previousPlacement = this.restoreDesignArtworkHistory(zone.key, 'undo', artworkId);
            if (previousPlacement) this.updateDesignArtwork(overlay, this.getDesignArtworkPlacement(zone, artworkId));
            return;
          }
          case 'redo': {
            const nextHistoryPlacement = this.restoreDesignArtworkHistory(zone.key, 'redo', artworkId);
            if (nextHistoryPlacement) this.updateDesignArtwork(overlay, this.getDesignArtworkPlacement(zone, artworkId));
            return;
          }
          case 'reset':
            this.recordDesignArtworkHistory(zone.key, placement, artworkId);
            nextPlacement = this.getDefaultDesignArtworkPlacement(zone);
            nextPlacement = this.getImageAwareDesignArtworkPlacement(
              nextPlacement,
              overlay?.querySelector('[data-role="product-design-artwork-image"]'), zone,
            );
            if (zone.key) {
              const stateKey = this.getDesignArtworkStateKey(zone.key, artworkId);
              const metricKey = this.getDesignArtworkImageMetricKey(
                zone,
                overlay?.querySelector('[data-role="product-design-artwork-image"]'),
                artworkSource,
              );
              if (metricKey) this.designArtworkImageMetricKeys.set(stateKey, metricKey);
            }
            break;
          case 'colors':
            this.openDesignArtworkColors(zone, artworkId, toolButton);
            return;
          case 'remove-background':
            await this.removeDesignArtworkBackground(overlay, zone, toolButton, artworkId);
            return;
          case 'delete':
            this.resetDesignArtworkHistory(zone.key, artworkId);
            if (artworkId !== this.getBaseDesignArtworkId()) {
              this.removeDesignArtworkCopy(zone.key, artworkId);
              this.setActiveDesignArtworkId(zone.key, this.getBaseDesignArtworkId());
              this.renderDesignStudio();
              return;
            }

            this.clearDesignArtworkForZone(zone.key);
            this.dispatchEvent(
              new CustomEvent('todaymerch:product-design-clear-logo', {
                bubbles: true,
                detail: {
                  source: 'media-gallery',
                  zoneKey: zone.key || '',
                },
              }),
            );
            return;
          default:
            return;
        }

        if (action === 'rotate') nextPlacement = this.fitDesignArtworkAfterRotation(nextPlacement, zone, overlay);
        this.updateStoredDesignArtwork(zone.key, nextPlacement, artworkId, { sizeLimitAttempt: action === 'larger' });
        this.renderDesignArtworkCopies(overlay, zone);
        this.updateDesignArtwork(overlay, this.getDesignArtworkPlacement(zone, artworkId));
      }

      async handleDesignPreviewDownload(event, overlay, button) {
        event.preventDefault();
        event.stopPropagation();

        if (!button || button.disabled) return;

        const slide = this.getActiveMediaSlide();
        const zone = this.getActiveOverlayZone(overlay) || this.getRenderableDesignZone(slide);
        if (!slide || !zone) return;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.remove('has-error');

        try {
          await this.waitForNextFrame();
          const blob = await this.createDesignPreviewPngBlob(slide, overlay, zone);
          this.downloadBlob(blob, this.getDesignPreviewFileName(zone));
        } catch (error) {
          console.warn('[Today Merch] PNG preview download failed.', error);
          button.classList.add('has-error');
          window.setTimeout(() => button.classList.remove('has-error'), 1400);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }

      async createProductDesignShareSnapshot() {
        const state = this.designStudioState;
        const variantId = this.getCurrentVariantId();
        if (!state?.hasLogo || !this.isDesignStudioStateForActiveVariant()) throw new Error('design_unavailable');
        const config = JSON.parse(this.getProductRoot().querySelector('[data-product-design-share-config]')?.textContent || '{}');
        const documentVariant = this.getSelectedVariantFromDocument();
        const selectedVariant = (config.variants || []).find((variant) => String(variant.id) === variantId)
          || (String(documentVariant?.id) === variantId ? documentVariant : null);
        const product = {
          id: config.product?.id,
          handle: config.product?.handle,
          title: config.product?.title,
          url: config.product?.url,
          variantId,
          variantTitle: selectedVariant?.title === 'Default Title' ? '' : selectedVariant?.title || '',
        };
        if (!product.handle || !product.title) throw new Error('product_unavailable');
        const zones = (state.zones || []).filter((zone) => zone.hasLogo);
        const groups = new Map();
        zones.forEach((zone) => {
          this.syncDesignArtworkSources(zone);
          const artworks = this.getDesignArtworkItems(zone);
          if (!zone.viewKey || !artworks.length || artworks.some((item) => !item.previewUrl)
            || (zone.artworks || []).some((item) => !item.previewUrl)) {
            throw new Error('artwork_preview_unavailable');
          }
          if (!groups.has(zone.viewKey)) groups.set(zone.viewKey, []);
          groups.get(zone.viewKey).push(zone);
        });
        if (!groups.size || groups.size > 8) throw new Error('design_views_unavailable');
        const originalActiveZoneKey = state.activeZoneKey;
        const views = [];
        const maxBytes = Math.min(900 * 1024, Math.floor(1.9 * 1024 * 1024 / groups.size));

        try {
          for (const [viewKey, viewZones] of groups) {
            const slide = this.getMediaSlideForView(viewKey);
            if (!slide || slide.dataset.mediaType !== 'image') throw new Error('media_unavailable');
            let canvas;
            let context;
            let containerRect;
            let scale;
            let simulationReference = false;
            for (const zone of viewZones) {
              if (state !== this.designStudioState || variantId !== this.getCurrentVariantId()) throw new Error('design_changed');
              state.activeZoneKey = zone.key;
              this.renderDesignStudio({ slide, zone });
              await this.waitForNextFrame();
              await this.waitForNextFrame();
              const overlay = this.designStudioOverlays.get(slide);
              const container = slide.querySelector('.product__media-item-container');
              const productImage = this.getDesignPreviewProductImage(container);
              const rect = container?.getBoundingClientRect();
              if (!overlay || !productImage || !rect?.width || !rect?.height) throw new Error('preview_unavailable');
              const shell = overlay.querySelector('[data-role="product-design-zone-shell"]');
              const shellRect = this.getDesignPreviewUnzoomedRect(shell, overlay.querySelector('[data-role="product-design-visual-layer"]') || shell);
              if (!shellRect?.width || !shellRect?.height) throw new Error('preview_unavailable');
              if (!canvas) {
                productImage.loading = 'eager';
                await this.waitForImageReady(productImage);
                containerRect = rect;
                scale = this.getDesignPreviewCanvasScale(rect, { maxEdge: 1200, maxPixels: 1440000, minScale: 0.1, desiredScale: 2 });
                canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(rect.width * scale));
                canvas.height = Math.max(1, Math.round(rect.height * scale));
                context = canvas.getContext('2d');
                if (!context) throw new Error('canvas_unavailable');
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, canvas.width, canvas.height);
                this.fillDesignPreviewBackground(context, container, canvas.width, canvas.height);
                this.drawDesignPreviewProductImage(context, productImage, containerRect, scale);
              }
              const preview = await this.drawDesignPreviewArtworks(context, overlay, zone, containerRect, scale);
              simulationReference ||= preview?.simulationReference === true;
            }
            if (simulationReference) this.drawDesignPreviewSimulationCaption(context);
            const blob = await this.createOptimizedDesignPreviewBlob(canvas, maxBytes);
            const image = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error('preview_unavailable'));
              reader.readAsDataURL(blob);
            });
            views.push({ label: viewZones.map((zone) => zone.label || zone.key).join(' · ').slice(0, 80), image });
          }
          if (state !== this.designStudioState || variantId !== this.getCurrentVariantId()) throw new Error('design_changed');
          return { product, views, locale: String(config.locale || document.documentElement.lang || 'fr').startsWith('fr') ? 'fr' : 'en' };
        } finally {
          if (this.designStudioState === state) state.activeZoneKey = originalActiveZoneKey;
          this.renderDesignStudio();
        }
      }

      canPrepareQuoteMockup(zone) {
        const native = this.designStudioState?.zones?.find(item => item.key === zone.key);
        const slide = this.getMediaSlideForView(native?.viewKey);
        const overlay = slide && (this.designStudioOverlays.get(slide) || slide.querySelector('[data-role="product-design-studio"]'));
        return Boolean(!this.designInteraction && !this.designImagePanInteraction && native?.hasLogo
          && overlay && !overlay.hidden && overlay.dataset.zoneKey === zone.key && slide === this.getActiveMediaSlide());
      }

      getQuoteMockupRenderKey(zone) {
        const native = this.designStudioState?.zones?.find(item => item.key === zone.key);
        const slide = this.getMediaSlideForView(native?.viewKey);
        const container = slide?.querySelector('.product__media-item-container') || slide;
        const image = container && this.getDesignPreviewProductImage(container);
        const rect = container?.getBoundingClientRect();
        const imageRect = image && this.getDesignPreviewProductImageRect(image);
        const style = image && window.getComputedStyle(image);
        // All values describe the saved view, independent of the active tab.
        return JSON.stringify({ zone: native, items: native && this.getDesignArtworkItems(native),
          activeArtwork: this.getActiveDesignArtworkId(zone.key), variant: this.getCurrentVariantId(),
          image: image?.currentSrc || image?.src || '', imageSize: [image?.naturalWidth, image?.naturalHeight],
          frame: [rect?.width, rect?.height, imageRect?.width, imageRect?.height,
            imageRect && rect ? imageRect.left - rect.left : 0, imageRect && rect ? imageRect.top - rect.top : 0],
          fit: [style?.objectFit, style?.objectPosition], background: container && window.getComputedStyle(container).backgroundColor,
          pixelRatio: window.devicePixelRatio, locale: document.documentElement.lang });
      }

      async createQuoteMockups(zones = [], { background = false, assertCurrent } = {}) {
        const requestedZones = Array.isArray(zones) ? zones : [];
        const stateZones = this.designStudioState?.zones || [];
        const originalActiveZoneKey = this.designStudioState?.activeZoneKey || '';
        const results = [];

        try {
          for (const requestedZone of requestedZones) {
            const zone = stateZones.find((entry) => entry.key === requestedZone.key);
            const label = requestedZone.label || zone?.label || requestedZone.key || 'Zone';
            let fallbackImage = '';

            try {
              const slide = this.getMediaSlideForView(zone?.viewKey) || this.getActiveMediaSlide();
              const previewContainer = slide?.querySelector('.product__media-item-container') || slide;
              const previewImage = previewContainer ? this.getDesignPreviewProductImage(previewContainer) : null;
              fallbackImage = String(previewImage?.currentSrc || previewImage?.src || '').trim();

              if (!zone?.hasLogo) {
                results.push({
                  key: requestedZone.key,
                  label,
                  status: 'unavailable',
                  reason: 'artwork_missing',
                  ...(fallbackImage ? { fallbackImage } : {}),
                });
                continue;
              }
              if (!slide || slide.dataset.mediaType !== 'image') throw new Error('media_unavailable');

              assertCurrent?.();
              if (!background) {
                this.designStudioState.activeZoneKey = zone.key;
                this.renderDesignStudio({ slide, zone });
                await this.waitForNextFrame();
                await this.waitForNextFrame();
              } else if (!this.canPrepareQuoteMockup(zone)) continue;

              const overlay = this.designStudioOverlays.get(slide) || slide.querySelector('[data-role="product-design-studio"]');
              if (!overlay) throw new Error('overlay_unavailable');

              let previewAnnotations = null;
              const blob = await this.createDesignPreviewPngBlob(slide, overlay, zone, {
                maxEdge: 1200,
                maxPixels: 1200 * 1200,
                maxBytes: 3 * 1024 * 1024,
                desiredScale: Math.max(1, window.devicePixelRatio || 1),
                minScale: 0.1,
                onPreviewAnnotations: value => { previewAnnotations = value; },
                background,
              });
              assertCurrent?.();
              if (blob.size > 3 * 1024 * 1024) throw new Error('mockup_too_large');

              const fileName = this.getDesignPreviewFileName(zone);
              const file = new File([blob], fileName, { type: 'image/png', lastModified: Date.now() });
              results.push({ key: requestedZone.key, label, status: 'generated', file, fallbackImage,
                ...(previewAnnotations ? { previewAnnotations } : {}) });
            } catch (error) {
              results.push({
                key: requestedZone.key,
                label,
                status: 'unavailable',
                reason: String(error?.message || 'export_failed').slice(0, 80),
                ...(fallbackImage ? { fallbackImage } : {}),
              });
            }
          }
        } finally {
          if (!background) {
            if (this.designStudioState) this.designStudioState.activeZoneKey = originalActiveZoneKey;
            this.renderDesignStudio();
          }
        }

        return results;
      }

      async createDesignPreviewPngBlob(slide, overlay, zone, options = {}) {
        const container = slide?.querySelector('.product__media-item-container') || slide;
        const productImage = this.getDesignPreviewProductImage(container);
        const containerRect = container?.getBoundingClientRect();
        if (!container || !productImage || !containerRect?.width || !containerRect?.height) {
          throw new Error('Invalid preview target');
        }

        if (!options.background) {
          productImage.loading = 'eager';
          productImage.setAttribute('fetchpriority', 'high');
        }
        await this.waitForImageReady(productImage);

        const scale = this.getDesignPreviewCanvasScale(containerRect, options);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext?.('2d');
        if (!context) throw new Error('Canvas unavailable');

        canvas.width = Math.max(1, Math.round(containerRect.width * scale));
        canvas.height = Math.max(1, Math.round(containerRect.height * scale));
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        this.fillDesignPreviewBackground(context, container, canvas.width, canvas.height);
        this.drawDesignPreviewProductImage(context, productImage, containerRect, scale);
        const preview = await this.drawDesignPreviewArtworks(context, overlay, zone, containerRect, scale);
        if (preview?.simulationReference) this.drawDesignPreviewSimulationCaption(context);

        const blob = await this.createOptimizedDesignPreviewBlob(canvas, Number(options.maxBytes || 0));
        if (typeof options.onPreviewAnnotations === 'function') options.onPreviewAnnotations(preview?.previewAnnotations || null);
        return blob;
      }

      drawDesignPreviewSimulationCaption(context) {
        const { width, height } = context.canvas;
        const label = 'Simulation — support et finitions indicatifs';
        const fontSize = Math.max(12, Math.min(24, width / 38));
        const padding = Math.max(8, fontSize * .7), stripHeight = Math.ceil(fontSize + padding * 2);
        context.save();
        context.fillStyle = 'rgba(255, 255, 255, 0.94)';
        context.fillRect(0, height - stripHeight, width, stripHeight);
        context.fillStyle = '#242424';
        context.font = `${fontSize}px Arial, sans-serif`;
        context.textAlign = 'center'; context.textBaseline = 'middle';
        context.fillText(label, width / 2, height - stripHeight / 2, width - padding * 2);
        context.restore();
      }

      async createOptimizedDesignPreviewBlob(sourceCanvas, maxBytes = 0) {
        let canvas = sourceCanvas;
        let blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Preview export failed');
        if (!maxBytes || blob.size <= maxBytes) return blob;

        for (let attempt = 0; attempt < 6 && blob.size > maxBytes; attempt += 1) {
          const ratio = Math.min(0.86, Math.max(0.55, Math.sqrt(maxBytes / Math.max(blob.size, 1)) * 0.92));
          const nextCanvas = document.createElement('canvas');
          nextCanvas.width = Math.max(240, Math.round(canvas.width * ratio));
          nextCanvas.height = Math.max(240, Math.round(canvas.height * ratio));
          const context = nextCanvas.getContext?.('2d');
          if (!context) throw new Error('Canvas unavailable');
          context.drawImage(canvas, 0, 0, nextCanvas.width, nextCanvas.height);
          canvas = nextCanvas;
          blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          if (!blob) throw new Error('Preview export failed');
        }

        if (blob.size > maxBytes) throw new Error('mockup_too_large');
        return blob;
      }

      getDesignPreviewProductImage(container) {
        if (!container) return null;
        return [...container.querySelectorAll('img')].find((image) => !image.closest('[data-role="product-design-studio"]')) || null;
      }

      getDesignPreviewCanvasScale(rect, options = {}) {
        const maxPixels = Number(options.maxPixels || 6200000);
        const maxEdge = Number(options.maxEdge || 2600);
        const desiredScale = Number(options.desiredScale || Math.min(2, Math.max(1.5, window.devicePixelRatio || 1.5)));
        const minScale = Number(options.minScale || (options.maxEdge ? 0.1 : 1));
        return Math.max(
          minScale,
          Math.min(
            desiredScale,
            maxEdge / Math.max(rect.width, rect.height, 1),
            Math.sqrt(maxPixels / Math.max(rect.width * rect.height, 1)),
          ),
        );
      }

      fillDesignPreviewBackground(context, container, width, height) {
        const backgroundColor = window.getComputedStyle(container).backgroundColor;
        if (!backgroundColor || backgroundColor === 'transparent' || backgroundColor === 'rgba(0, 0, 0, 0)') return;

        context.fillStyle = backgroundColor;
        context.fillRect(0, 0, width, height);
      }

      drawDesignPreviewProductImage(context, image, containerRect, scale) {
        const imageRect = this.getDesignPreviewProductImageRect(image);
        if (!imageRect.width || !imageRect.height) return;

        const style = window.getComputedStyle(image);
        this.drawCanvasImageFitted(
          context,
          image,
          (imageRect.left - containerRect.left) * scale,
          (imageRect.top - containerRect.top) * scale,
          imageRect.width * scale,
          imageRect.height * scale,
          style.objectFit || 'cover',
          style.objectPosition || '50% 50%',
        );
      }

      getDesignPreviewProductImageRect(image) {
        const zoomTarget = image.closest('.is-design-image-zoom-target');
        const isDesktopZoom = this.supportsProductPhotoZoom();
        if (!zoomTarget || !isDesktopZoom) return image.getBoundingClientRect();

        return this.getDesignPreviewUnzoomedRect(image, image);
      }

      getDesignPreviewUnzoomedRect(element, zoomReference = element) {
        const elementRect = element?.getBoundingClientRect();
        const referenceRect = zoomReference?.getBoundingClientRect();
        if (!elementRect || !referenceRect) return elementRect;

        const layoutWidth = zoomReference.offsetWidth || referenceRect.width;
        const layoutHeight = zoomReference.offsetHeight || referenceRect.height;
        const scaleX = layoutWidth ? referenceRect.width / layoutWidth : 1;
        const scaleY = layoutHeight ? referenceRect.height / layoutHeight : 1;
        if (
          !Number.isFinite(scaleX) ||
          !Number.isFinite(scaleY) ||
          (
            Math.abs(scaleX - 1) < 0.001 &&
            Math.abs(scaleY - 1) < 0.001
          )
        ) {
          return elementRect;
        }

        const isPannedDesignLayer = Boolean(
          this.supportsProductPhotoZoom() &&
          zoomReference.closest?.('.is-design-image-zoom-target')
        );
        const transformedCenterX = referenceRect.left + (referenceRect.width / 2);
        const transformedCenterY = referenceRect.top + (referenceRect.height / 2);
        const layoutCenterX = transformedCenterX - (isPannedDesignLayer ? this.designImagePanX : 0);
        const layoutCenterY = transformedCenterY - (isPannedDesignLayer ? this.designImagePanY : 0);
        const left = layoutCenterX + ((elementRect.left - transformedCenterX) / scaleX);
        const top = layoutCenterY + ((elementRect.top - transformedCenterY) / scaleY);
        const width = elementRect.width / scaleX;
        const height = elementRect.height / scaleY;
        return {
          left,
          top,
          right: left + width,
          bottom: top + height,
          width,
          height,
        };
      }

      async drawDesignPreviewArtworks(context, overlay, zone, containerRect, scale) {
        const zoneShell = overlay?.querySelector('[data-role="product-design-zone-shell"]');
        const visualLayer = overlay?.querySelector('[data-role="product-design-visual-layer"]');
        const zoneShellRect = this.getDesignZoneShellRect(zoneShell, { unzoomed: true });
        if (!zoneShellRect?.width || !zoneShellRect?.height) return;

        const activeArtworkId = this.getActiveDesignArtworkId(zone?.key);
        const artworkItems = this
          .getDesignArtworkItems(zone)
          .filter((item) => item.previewUrl)
          .sort((a, b) => {
            if (a.id === activeArtworkId) return 1;
            if (b.id === activeArtworkId) return -1;
            return 0;
          });

        let simulationReference = false;
        const annotations = [];
        for (const item of artworkItems) {
          const image = await this.getDesignPreviewArtworkImage(overlay, item);
          if (!image) continue;
          let displayCanvas = null;
          try { displayCanvas = await window.TodayMerchProductPreviewEffects?.forExport(this, overlay, zone, item, image); } catch { /* Native preview remains available. */ }
          const displayFrame = displayCanvas ? window.TodayMerchProductPreviewEffects?.getDisplayFrame?.(displayCanvas) : null;
          simulationReference ||= displayFrame?.simulationReference === true;
          const photoRotation = Number(zoneShellRect.rotation || 0);
          const centerX = (zoneShellRect.left + zoneShellRect.width / 2 - containerRect.left) * scale;
          const centerY = (zoneShellRect.top + zoneShellRect.height / 2 - containerRect.top) * scale;
          context.save();
          try {
            if (photoRotation) { context.translate(centerX, centerY); context.rotate(photoRotation * Math.PI / 180); context.translate(-centerX, -centerY); }
            this.drawDesignPreviewArtwork(context, displayCanvas || image, item.placement, zone, zoneShellRect, containerRect, scale,
              { fullFrame: Boolean(displayCanvas), displayFrame });
          } finally { context.restore(); }
          try {
            const annotation = this.getDesignPreviewArtworkAnnotation(context, image, item, zone, zoneShellRect, containerRect, scale, displayFrame);
            if (annotation) {
              if (photoRotation) annotation.points = annotation.points.map(point => {
                const delta = this.projectDesignPointerDelta(point.x * context.canvas.width - centerX,
                  point.y * context.canvas.height - centerY, -photoRotation);
                return { x: (centerX + delta.x) / context.canvas.width, y: (centerY + delta.y) / context.canvas.height };
              });
              annotations.push(annotation);
            }
          } catch { /* Optional measurements must not discard a usable preview. */ }
        }
        return { simulationReference,
          ...(annotations.length && annotations.length <= 12 && new Set(annotations.map(item => item.id)).size === annotations.length
            ? { previewAnnotations: { schemaVersion: 1, artworks: annotations } } : {}) };
      }

      getDesignPreviewArtworkAnnotation(context, image, item, zone, zoneShellRect, containerRect, scale, displayFrame = null) {
        // These points describe the logo drawn into this canvas, never the
        // supplier's maximum print area or a rectangle from another view.
        if (!this.hasDesignPhotoProjection(zone) || displayFrame && (displayFrame.simulationReference
          || displayFrame.x !== 0 || displayFrame.y !== 0 || displayFrame.width !== 1 || displayFrame.height !== 1)) return null;
        const constrained = this.constrainDesignArtworkToZone(item.placement, zone);
        const root = this.closest('product-info');
        const controller = window.TodayMerchProductDesignMeasurement?.attach(root);
        const measurement = controller?.measureZone(zone, constrained, this.activeVariantId || this.designStudioState?.variantId, item.id);
        let widthMm, heightMm, metrics;
        if (measurement?.available === true && measurement.contained === true) {
          widthMm = measurement.preciseWidthMm || measurement.widthMm;
          heightMm = measurement.preciseHeightMm || measurement.heightMm;
          metrics = controller.metricsFor(zone, item.id);
        } else {
          const supplier = window.TodayMerchXDProductV2?.get(root);
          if (!supplier || !(constrained.imageNaturalRatio > 0)) return null;
          const physical = supplier.readPersonalizationResumeGeometry(zone.key, constrained);
          widthMm = physical?.dimensions?.widthMm; heightMm = physical?.dimensions?.heightMm;
          metrics = { naturalRatio: constrained.imageNaturalRatio,
            alphaCrop: Object.fromEntries(['Top', 'Right', 'Bottom', 'Left'].map(side => [`crop${side}`, Number(constrained[`imageCrop${side}`] || 0)])) };
        }
        if (![widthMm, heightMm, context.canvas.width, context.canvas.height, scale].every(value => Number.isFinite(value) && value > 0)
          || !metrics || !item.id || String(item.id).length > 180) return null;
        const fields = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop'];
        const geometry = window.TodayMerchStudioDesignRendering?.geometry({ ...Object.fromEntries(fields.filter(key => constrained[key] !== undefined).map(key => [key, constrained[key]])), ...(constrained.contentFrame ? { contentFrame: constrained.contentFrame } : {}) }, {
          zoneAspect: zoneShellRect.width / zoneShellRect.height, naturalRatio: image.naturalWidth / image.naturalHeight, alphaCrop: metrics.alphaCrop, contentFrame: constrained.contentFrame,
        });
        if (!geometry) return null;
        const placement = geometry.placement;
        const width = zoneShellRect.width * placement.width / 100 * scale, height = zoneShellRect.height * placement.height / 100 * scale;
        const centerX = (zoneShellRect.left - containerRect.left + zoneShellRect.width * placement.x / 100) * scale;
        const centerY = (zoneShellRect.top - containerRect.top + zoneShellRect.height * placement.y / 100) * scale;
        const radians = placement.rotation * Math.PI / 180, cos = Math.cos(radians), sin = Math.sin(radians);
        const left = width * (placement.cropLeft - .5), right = width * (.5 - placement.cropRight);
        const top = height * (placement.cropTop - .5), bottom = height * (.5 - placement.cropBottom);
        const pixels = [[left, top], [right, top], [right, bottom], [left, bottom]].map(([x, y]) => ({ x: centerX + x * cos - y * sin, y: centerY + x * sin + y * cos }));
        const parts = this.getDesignArtworkZoneClipParts(zone);
        // A clipped or curved silhouette cannot be represented by four corners.
        const unclipped = parts.length ? parts.some(part => {
          if (Number(part.curvature || 0) !== 0) return false;
          const partX = (zoneShellRect.left - containerRect.left + zoneShellRect.width * (part.left + part.width / 2) / 100) * scale;
          const partY = (zoneShellRect.top - containerRect.top + zoneShellRect.height * (part.top + part.height / 2) / 100) * scale;
          const halfWidth = zoneShellRect.width * part.width / 200 * scale, halfHeight = zoneShellRect.height * part.height / 200 * scale;
          const angle = Number(part.rotation || 0) * Math.PI / 180;
          return halfWidth > 0 && halfHeight > 0 && pixels.every(point => {
            const dx = point.x - partX, dy = point.y - partY;
            const x = (dx * Math.cos(angle) + dy * Math.sin(angle)) / halfWidth;
            const y = (-dx * Math.sin(angle) + dy * Math.cos(angle)) / halfHeight;
            return part.shape === 'circle' ? x * x + y * y <= 1 + 1e-7 : Math.abs(x) <= 1 + 1e-7 && Math.abs(y) <= 1 + 1e-7;
          });
        }) : pixels.every(point => point.x >= (zoneShellRect.left - containerRect.left) * scale && point.y >= (zoneShellRect.top - containerRect.top) * scale
          && point.x <= (zoneShellRect.left - containerRect.left + zoneShellRect.width) * scale && point.y <= (zoneShellRect.top - containerRect.top + zoneShellRect.height) * scale);
        if (!unclipped) return null;
        const points = pixels.map(point => ({ x: point.x / context.canvas.width, y: point.y / context.canvas.height }));
        if (points.some(point => ![point.x, point.y].every(value => Number.isFinite(value) && value >= 0 && value <= 1))) return null;
        return { id: String(item.id), points, widthMm, heightMm };
      }

      async getDesignPreviewArtworkImage(overlay, item) {
        const artworkElement = [...overlay.querySelectorAll('[data-role="product-design-artwork"], [data-role="product-design-artwork-copy"]')].find(
          (element) => element.dataset.artworkId === item.id,
        );
        const visibleImage = artworkElement?.querySelector('img');
        if (visibleImage?.src) {
          await this.waitForImageReady(visibleImage);
          return visibleImage;
        }

        if (!item.previewUrl) return null;

        const image = new Image();
        image.decoding = 'async';
        if (!item.previewUrl.startsWith('blob:') && !item.previewUrl.startsWith('data:')) {
          image.crossOrigin = 'anonymous';
        }
        image.src = item.previewUrl;
        await this.waitForImageReady(image);
        return image;
      }

      clipDesignPreviewToZoneParts(context, zone, zoneShellRect, containerRect, scale) {
        const zoneParts = this.getDesignArtworkZoneClipParts(zone);

        const zoneOffsetX = (zoneShellRect.left - containerRect.left) * scale;
        const zoneOffsetY = (zoneShellRect.top - containerRect.top) * scale;
        let hasPath = false;
        context.beginPath();

        zoneParts.forEach((part) => {
          const centerX = zoneOffsetX + zoneShellRect.width * ((part.left + part.width / 2) / 100) * scale;
          const centerY = zoneOffsetY + zoneShellRect.height * ((part.top + part.height / 2) / 100) * scale;
          const halfWidth = zoneShellRect.width * (part.width / 200) * scale;
          const halfHeight = zoneShellRect.height * (part.height / 200) * scale;
          const radians = (this.normalizeRotation(part.rotation || 0) * Math.PI) / 180;
          const cos = Math.cos(radians);
          const sin = Math.sin(radians);
          const localPoints = part.shape === 'circle'
            ? Array.from({ length: 48 }, (_, index) => {
                const angle = (index / 48) * Math.PI * 2;
                return [Math.cos(angle) * halfWidth, Math.sin(angle) * halfHeight];
              })
            : [
                [-halfWidth, -halfHeight],
                [halfWidth, -halfHeight],
                [halfWidth, halfHeight],
                [-halfWidth, halfHeight],
              ];
          const corners = localPoints.map(([x, y]) => ({
            x: centerX + x * cos - y * sin,
            y: centerY + x * sin + y * cos,
          }));

          context.moveTo(corners[0].x, corners[0].y);
          corners.slice(1).forEach((corner) => context.lineTo(corner.x, corner.y));
          context.closePath();
          hasPath = true;
        });

        if (hasPath) context.clip();
        return hasPath;
      }

      drawDesignPreviewArtwork(context, image, placement, zone, zoneShellRect, containerRect, scale, { fullFrame = false, displayFrame = null } = {}) {
        const constrained = this.constrainDesignArtworkToZone(placement, zone);
        const artworkWidth = zoneShellRect.width * (constrained.width / 100) * scale;
        const artworkHeight = zoneShellRect.height * (constrained.height / 100) * scale;
        if (!artworkWidth || !artworkHeight) return;

        const centerX = (zoneShellRect.left - containerRect.left + zoneShellRect.width * (constrained.x / 100)) * scale;
        const centerY = (zoneShellRect.top - containerRect.top + zoneShellRect.height * (constrained.y / 100)) * scale;
        context.save();
        const hasZonePartsClip = this.clipDesignPreviewToZoneParts(
          context,
          zone,
          zoneShellRect,
          containerRect,
          scale,
        );
        // Expanded support already contains the native crop and passed the
        // supplier-zone check. Keep that zone clip, not the logo-sized mask.
        const expandedFrame = fullFrame && displayFrame && [displayFrame.x, displayFrame.y, displayFrame.width, displayFrame.height].every(Number.isFinite)
          && (displayFrame.x < 0 || displayFrame.y < 0 || displayFrame.width > 1 || displayFrame.height > 1);
        const clip = this.getDesignPreviewArtworkClip(constrained, { includeZoneBounds: !hasZonePartsClip });
        const clipLeft = artworkWidth * (clip.left / 100);
        const clipRight = artworkWidth * (clip.right / 100);
        const clipTop = artworkHeight * (clip.top / 100);
        const clipBottom = artworkHeight * (clip.bottom / 100);
        const clipWidth = Math.max(0, artworkWidth - clipLeft - clipRight);
        const clipHeight = Math.max(0, artworkHeight - clipTop - clipBottom);
        if (!clipWidth || !clipHeight) {
          context.restore();
          return;
        }

        context.translate(centerX, centerY);
        context.rotate(((constrained.rotation || 0) * Math.PI) / 180);
        if (!expandedFrame) {
          context.beginPath();
          context.rect(-artworkWidth / 2 + clipLeft, -artworkHeight / 2 + clipTop, clipWidth, clipHeight);
          context.clip();
        }
        const fields = ['x', 'y', 'width', 'height', 'rotation', 'cropTop', 'cropRight', 'cropBottom', 'cropLeft', 'mediaWidth', 'mediaHeight', 'mediaLeft', 'mediaTop', 'contentFrame'];
        const raw = Object.fromEntries(fields.filter(key => constrained[key] !== undefined).map(key => [key, constrained[key]]));
        const rendered = window.TodayMerchStudioDesignRendering?.geometry(raw, {
          zoneAspect: zoneShellRect.width / zoneShellRect.height,
          naturalRatio: (image.naturalWidth || image.width) / (image.naturalHeight || image.height),
          contentFrame: constrained.contentFrame,
        });
        const mediaX = -artworkWidth / 2 + artworkWidth * ((constrained.mediaLeft || 0) / 100);
        const mediaY = -artworkHeight / 2 + artworkHeight * ((constrained.mediaTop || 0) / 100);
        const mediaWidth = artworkWidth * ((constrained.mediaWidth || 100) / 100);
        const mediaHeight = artworkHeight * ((constrained.mediaHeight || 100) / 100);
        if (fullFrame) context.drawImage(image,
          -artworkWidth / 2 + artworkWidth * (displayFrame?.x || 0),
          -artworkHeight / 2 + artworkHeight * (displayFrame?.y || 0),
          artworkWidth * (displayFrame?.width || 1), artworkHeight * (displayFrame?.height || 1));
        else if (rendered) context.drawImage(image, -artworkWidth / 2 + artworkWidth * rendered.media.left / 100,
          -artworkHeight / 2 + artworkHeight * rendered.media.top / 100,
          artworkWidth * rendered.media.width / 100, artworkHeight * rendered.media.height / 100);
        else this.drawCanvasImageFitted(
          context,
          image,
          mediaX,
          mediaY,
          mediaWidth,
          mediaHeight,
          'contain',
          '50% 50%',
        );
        context.restore();
      }

      getDesignPreviewArtworkClip(placement, { includeZoneBounds = true } = {}) {
        const cropLeft = (placement.cropLeft || 0) * 100;
        const cropRight = (placement.cropRight || 0) * 100;
        const cropTop = (placement.cropTop || 0) * 100;
        const cropBottom = (placement.cropBottom || 0) * 100;
        const leftEdge = placement.x - placement.width / 2;
        const rightEdge = placement.x + placement.width / 2;
        const topEdge = placement.y - placement.height / 2;
        const bottomEdge = placement.y + placement.height / 2;

        return {
          top: includeZoneBounds
            ? Math.max(cropTop, this.clampNumber((-topEdge / placement.height) * 100, 0, 96))
            : cropTop,
          right: includeZoneBounds
            ? Math.max(cropRight, this.clampNumber(((rightEdge - 100) / placement.width) * 100, 0, 96))
            : cropRight,
          bottom: includeZoneBounds
            ? Math.max(cropBottom, this.clampNumber(((bottomEdge - 100) / placement.height) * 100, 0, 96))
            : cropBottom,
          left: includeZoneBounds
            ? Math.max(cropLeft, this.clampNumber((-leftEdge / placement.width) * 100, 0, 96))
            : cropLeft,
        };
      }

      drawCanvasImageFitted(context, image, x, y, width, height, objectFit = 'cover', objectPosition = '50% 50%') {
        const naturalWidth = image.naturalWidth || image.videoWidth || 0;
        const naturalHeight = image.naturalHeight || image.videoHeight || 0;
        if (!naturalWidth || !naturalHeight || !width || !height) return;

        const position = this.getCanvasObjectPosition(objectPosition);
        const fit = objectFit === 'scale-down' ? 'contain' : objectFit;

        if (fit === 'contain') {
          const scale = Math.min(width / naturalWidth, height / naturalHeight);
          const drawWidth = naturalWidth * scale;
          const drawHeight = naturalHeight * scale;
          context.drawImage(image, x + (width - drawWidth) * position.x, y + (height - drawHeight) * position.y, drawWidth, drawHeight);
          return;
        }

        if (fit === 'fill') {
          context.drawImage(image, x, y, width, height);
          return;
        }

        const coverScale = Math.max(width / naturalWidth, height / naturalHeight);
        const sourceWidth = Math.min(naturalWidth, width / coverScale);
        const sourceHeight = Math.min(naturalHeight, height / coverScale);
        const sourceX = (naturalWidth - sourceWidth) * position.x;
        const sourceY = (naturalHeight - sourceHeight) * position.y;
        context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
      }

      getCanvasObjectPosition(objectPosition) {
        const tokens = String(objectPosition || '50% 50%').trim().split(/\s+/).filter(Boolean);
        const keywordMap = {
          left: 0,
          top: 0,
          center: 0.5,
          right: 1,
          bottom: 1,
        };
        const parseToken = (token, fallback) => {
          if (token in keywordMap) return keywordMap[token];
          if (token.endsWith('%')) return this.clampNumber(parseFloat(token) / 100, 0, 1);
          return fallback;
        };

        if (tokens.length === 1) {
          const token = tokens[0];
          if (token === 'top' || token === 'bottom') return { x: 0.5, y: parseToken(token, 0.5) };
          return { x: parseToken(token, 0.5), y: 0.5 };
        }

        return {
          x: parseToken(tokens[0], 0.5),
          y: parseToken(tokens[1], 0.5),
        };
      }

      getDesignPreviewFileName(zone) {
        const title =
          this.closest('.product')?.querySelector('.product__title, [data-product-title], h1')?.textContent ||
          document.title ||
          'today-merch';
        const previewSuffix = this.string('previewFileSuffix') || 'preview';
        const rawName = `${title} ${zone?.label || ''} ${previewSuffix}`;
        const baseName = rawName
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 90);

        return `${baseName || 'today-merch-preview'}.png`;
      }

      downloadBlob(blob, fileName) {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.rel = 'noopener';
        anchor.style.display = 'none';
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      }

      getDesignBackgroundRemovalSource(zone, artworkId, artworkSource = this.getDesignArtworkSource(zone, artworkId)) {
        const copy = artworkId !== this.getBaseDesignArtworkId() ? this.getDesignArtworkCopy(zone?.key, artworkId) : null;
        // Detached duplicates have no independent production upload. Editing
        // only their preview would create a design the quote cannot reproduce.
        if (artworkId !== this.getBaseDesignArtworkId() && (!copy || !copy.sourceUploadId)) return null;
        const selector = this.getProductRoot()?.querySelector('product-marking-selector');
        const wrap = selector?.getZoneWrap?.(zone?.key);
        const files = wrap && selector.getLogoFiles?.(wrap) || [];
        const uploadId = copy?.sourceUploadId || this.getBaseDesignArtworkId();
        const entries = (zone?.artworks || []).filter((item) => item.id === uploadId);
        if (copy && entries.length !== 1 || entries.length > 1) return null;
        const entry = entries[0] || (!copy ? zone : null);
        const index = copy ? entry?.index : 0;
        if (!Number.isInteger(index) || index < 0 || !entry) return null;
        const original = files[index];
        if (!original || selector.getLogoFileKey(original) !== artworkSource.fileKey
          || entry.fileKey !== artworkSource.fileKey) return null;
        const production = selector.getArtworkProductionSource?.(original, zone.key, { artworkId }) || { file: original };
        // Upload order and the original identity disambiguate two distinct files
        // with identical names, sizes, MIME types and modification timestamps.
        const originalIdentity = entry.originalIdentity || artworkSource.originalIdentity;
        if (originalIdentity && production.originalIdentity !== originalIdentity) return null;
        if (!originalIdentity && files.filter((file) => selector.getLogoFileKey(file) === artworkSource.fileKey).length !== 1) return null;
        // An auxiliary mockup is never a substitute for the production artwork.
        if (selector.getStudioPreviewSource?.(original, zone.key, { artworkId }) && selector.getStudioPreviewSource(original, zone.key, { artworkId }) !== production.file) return null;
        const scope = selector.getArtworkProductionScope?.(zone.key, { artworkId });
        return { file: production.file, original, scopeKey: scope?.key || '', techniqueId: scope?.techniqueId,
          productionFileKey: production.productionFileKey || '', variantKey: selector.getVariantStateKey?.() || '' };
      }

      showDesignBackgroundRemovalNotice(overlay, button, reason) {
        const lang = document.documentElement.lang.split('-')[0];
        const messages = {
          duplicate: {
            fr: 'Retirez le fond sur le fichier importé avant de le dupliquer.',
            de: 'Entfernen Sie den Hintergrund vor dem Duplizieren aus der hochgeladenen Datei.',
            en: 'Remove the background from the uploaded file before duplicating it.',
          },
          unsupported: {
            fr: 'Le fichier original est conservé. Le retrait du fond est disponible pour les images PNG, JPEG, WebP, AVIF et BMP.',
            de: 'Die Originaldatei bleibt erhalten. Die Hintergrundentfernung unterstützt PNG-, JPEG-, WebP-, AVIF- und BMP-Bilder.',
            en: 'The original file is preserved. Background removal supports PNG, JPEG, WebP, AVIF and BMP images.',
          },
        };
        const message = (messages[reason] || messages.unsupported)[lang] || (messages[reason] || messages.unsupported).en;
        if (button) button.title = message;
        if (overlay?.dataset?.mobileDesignSurface === 'true' && this.mobileDesignEditor?.status) {
          this.mobileDesignEditor.status.textContent = message;
        }
      }

      async loadDesignBackgroundRemovalImage(file) {
        if (!(file instanceof Blob) || !file.size) return null;
        const type = String(file.type || '').toLowerCase().split(';')[0];
        const extension = String(file.name || '').split('.').pop().toLowerCase();
        // Keep vectors, documents and animated formats intact. Rasterizing their
        // screen preview would discard the source quality and document structure.
        if (['svg', 'pdf', 'eps', 'ai', 'gif', 'tif', 'tiff'].includes(extension)) return null;
        if (type && !['image/png', 'image/jpeg', 'image/webp', 'image/avif', 'image/bmp'].includes(type)) return null;
        if (!type && !['png', 'jpg', 'jpeg', 'webp', 'avif', 'bmp'].includes(extension)) return null;
        const url = URL.createObjectURL(file);
        const image = new Image();
        const cleanup = () => { image.removeAttribute('src'); URL.revokeObjectURL(url); };
        try {
          image.src = url;
          await this.waitForImageReady(image);
          return { image, cleanup };
        } catch (error) {
          cleanup();
          throw error;
        }
      }

      async removeDesignArtworkBackground(overlay, zone, button, artworkId = this.getActiveDesignArtworkId(zone?.key)) {
        const artworkSource = this.getDesignArtworkSource(zone, artworkId);
        if (!zone?.key || !artworkSource.previewUrl) return;
        const source = this.getDesignBackgroundRemovalSource(zone, artworkId, artworkSource);
        if (!source) {
          const copy = artworkId !== this.getBaseDesignArtworkId() ? this.getDesignArtworkCopy(zone.key, artworkId) : null;
          if (button && copy && !copy.sourceUploadId) {
            button.disabled = true;
            this.showDesignBackgroundRemovalNotice(overlay, button, 'duplicate');
          }
          return;
        }
        const operationStateKey = this.getDesignArtworkStateKey(zone.key, artworkId);
        const isCurrentArtwork = () => {
          const currentZone = this.designStudioState?.zones?.find((item) => item.key === zone.key);
          const currentSource = this.getDesignArtworkSource(currentZone, artworkId);
          const currentFile = currentZone && this.getDesignBackgroundRemovalSource(currentZone, artworkId, currentSource);
          return this.isConnected && currentZone && this.getDesignArtworkStateKey(zone.key, artworkId) === operationStateKey
            && (artworkId === this.getBaseDesignArtworkId() || Boolean(this.getDesignArtworkCopy(zone.key, artworkId)))
            && currentSource.fileKey === artworkSource.fileKey
            && currentSource.previewUrl === artworkSource.previewUrl
            && currentFile?.file === source.file && currentFile.original === source.original
            && currentFile.productionFileKey === source.productionFileKey && currentFile.scopeKey === source.scopeKey;
        };

        const previousDisabled = Boolean(button?.disabled);
        const scanStartedAt = this.getHighResolutionTime();
        let loadedSource;
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
        overlay?.classList.add('is-removing-background');

        try {
          await this.waitForNextFrame();
          loadedSource = await this.loadDesignBackgroundRemovalImage(source.file);
          if (!loadedSource) {
            if (isCurrentArtwork()) this.showDesignBackgroundRemovalNotice(overlay, button, 'unsupported');
            return;
          }
          if (!isCurrentArtwork()) return;
          const fileName = this.getBackgroundRemovedFileName(source.file.name || artworkSource.fileName || 'logo.png');
          const file = await this.createBackgroundRemovedArtworkFile(loadedSource.image, fileName, { isCurrent: isCurrentArtwork });
          if (!file) return;
          const updateToken = `${Date.now()}-${Math.random()}`;
          await this.waitForMinimumDuration(scanStartedAt, 150);
          if (!isCurrentArtwork()) return;
          const stateKey = operationStateKey;
          this.designArtworkImageUpdateModes.set(stateKey, 'preserve-placement');

          this.dispatchEvent(
            new CustomEvent('todaymerch:product-design-update-logo', {
              bubbles: true,
              detail: {
                source: 'media-gallery',
                zoneKey: zone.key || '',
                artworkId,
                file,
                originalFile: source.original,
                sourceFile: source.file,
                sourceProductionFileKey: source.productionFileKey,
                sourceScopeKey: source.scopeKey,
                techniqueId: source.techniqueId,
                variantKey: source.variantKey,
                action: 'remove-background',
                updateToken,
              },
            }),
          );
          window.setTimeout(() => {
            if (this.designArtworkImageUpdateModes.get(stateKey) === 'preserve-placement') {
              this.designArtworkImageUpdateModes.delete(stateKey);
            }
          }, 3000);
        } catch (error) {
          if (error?.name === 'AbortError' || !isCurrentArtwork()) return;
          overlay?.classList.add('has-background-removal-error');
          window.setTimeout(() => overlay?.classList.remove('has-background-removal-error'), 900);
          if (overlay?.dataset?.mobileDesignSurface === 'true') throw error;
        } finally {
          loadedSource?.cleanup();
          overlay?.classList.remove('is-removing-background');
          if (button) {
            button.disabled = previousDisabled;
            button.removeAttribute('aria-busy');
          }
          this.syncDesignControlsVisibility();
        }
      }

      getHighResolutionTime() {
        return window.performance?.now?.() || Date.now();
      }

      waitForNextFrame() {
        return new Promise((resolve) => window.requestAnimationFrame(resolve));
      }

      waitForMinimumDuration(startedAt, duration) {
        const elapsed = this.getHighResolutionTime() - startedAt;
        if (elapsed >= duration) return Promise.resolve();
        return new Promise((resolve) => window.setTimeout(resolve, duration - elapsed));
      }

      waitForImageReady(image, timeout = 8000) {
        if (!image) return Promise.reject(new Error('Image unavailable'));

        if (image.complete) {
          if (image.naturalWidth && image.naturalHeight) {
            if (typeof image.decode === 'function') return image.decode().catch(() => {});
            return Promise.resolve();
          }

          return Promise.reject(new Error('Image loading failed'));
        }

        return new Promise((resolve, reject) => {
          const timeoutId = timeout
            ? window.setTimeout(() => {
                cleanup();
                reject(new Error('Image loading timed out'));
              }, timeout)
            : null;
          const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId);
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
          };
          const onLoad = () => {
            cleanup();
            if (typeof image.decode === 'function') {
              image.decode().catch(() => {}).finally(resolve);
              return;
            }
            resolve();
          };
          const onError = () => {
            cleanup();
            reject(new Error('Image loading failed'));
          };

          image.addEventListener('load', onLoad, { once: true });
          image.addEventListener('error', onError, { once: true });
        });
      }

      getBackgroundRemovedFileName(fileName) {
        const baseName = String(fileName || 'logo')
          .replace(/\.[a-z0-9]+$/i, '')
          .trim() || 'logo';
        const suffix = String(this.string('transparentFileSuffix') || 'transparent-background')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'transparent-background';
        return `${baseName}-${suffix}.png`;
      }

      async createBackgroundRemovedArtworkFile(image, fileName, { isCurrent } = {}) {
        const width = image.naturalWidth || 0;
        const height = image.naturalHeight || 0;
        if (!width || !height) throw new Error('Invalid image size');
        // Preserve source pixels. Reject oversized artwork instead of silently
        // reducing the resolution of the file used for printing.
        if (width * height > 16000000 || Math.max(width, height) > 8192) {
          throw new Error('Background removal supports up to 16 million pixels and an 8192 pixel edge');
        }

        const assertCurrent = () => {
          if (typeof isCurrent === 'function' && !isCurrent()) {
            const error = new Error('Artwork background removal superseded');
            error.name = 'AbortError';
            throw error;
          }
        };
        const yieldToBrowser = async () => {
          await new Promise((resolve) => window.setTimeout(resolve, 0));
          assertCurrent();
        };
        assertCurrent();
        const canvas = document.createElement('canvas');
        try {
          const context = canvas.getContext?.('2d', { willReadFrequently: true });
          if (!context) throw new Error('Canvas unavailable');
          // This small canvas only rejects unsafe inputs; it is never exported
          // or used as a mask for the full-resolution artwork.
          const sampleScale = Math.min(1, 512 / Math.max(width, height));
          canvas.width = Math.max(1, Math.round(width * sampleScale));
          canvas.height = Math.max(1, Math.round(height * sampleScale));
          context.imageSmoothingEnabled = false;
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const sample = context.getImageData(0, 0, canvas.width, canvas.height);
          if (this.shouldPreserveExistingTransparentArtwork(this.getArtworkAlphaProfile(sample.data, canvas.width, canvas.height))) return null;
          if (!this.estimateArtworkBackgroundColor(sample.data, canvas.width, canvas.height)?.isSimple) return null;

          const modules = window.TodayMerchProductModules;
          const root = modules ? this.closest('product-info') : null;
          if (!window.TodayMerchArtworkBackgroundWorker && modules?.has(root, 'backgroundRemoval')) {
            // CSP/network failures retain the original synchronous fallback before any pixels transfer.
            await modules.ensure(root, 'backgroundRemoval').catch(() => {});
          }
          await yieldToBrowser();
          canvas.width = width;
          canvas.height = height;
          context.drawImage(image, 0, 0);
          let imageData = context.getImageData(0, 0, width, height);
          const result = window.TodayMerchArtworkBackgroundWorker
            ? await window.TodayMerchArtworkBackgroundWorker.process(imageData, { controller: this, isCurrent })
            : { available: false };
          assertCurrent();
          if (result.available) {
            if (!result.pixels) return null;
            imageData = new ImageData(result.pixels, width, height);
          } else {
            const data = imageData.data;
            if (this.shouldPreserveExistingTransparentArtwork(this.getArtworkAlphaProfile(imageData.data, width, height))) return null;
            const background = this.estimateArtworkBackgroundColor(data, width, height);
            if (!background?.isSimple) return null;

            const originalAlpha = new Uint8Array(width * height);
            let originalOpaquePixels = 0;
            for (let index = 0; index < originalAlpha.length; index += 1) {
              originalAlpha[index] = data[index * 4 + 3];
              if (originalAlpha[index] >= 244) originalOpaquePixels += 1;
            }
            await yieldToBrowser();
            const mask = this.getConnectedArtworkBackgroundMask(data, width, height, background);
            await yieldToBrowser();
            this.cleanGlobalArtworkBackgroundResidues(data, width, height, background, mask);
            this.applyArtworkBackgroundRemoval(data, width, height, background, mask);
            await yieldToBrowser();
            this.polishArtworkForegroundEdges(data, width, height, background, mask);

            let removedOpaquePixels = 0;
            let retainedOpacity = 0;
            for (let index = 0; index < originalAlpha.length; index += 1) {
              const alpha = data[index * 4 + 3];
              if (originalAlpha[index] >= 244 && alpha <= 2) removedOpaquePixels += 1;
              retainedOpacity += alpha / 255;
            }
            if (!removedOpaquePixels || retainedOpacity < Math.max(4, originalOpaquePixels * 0.005)) return null;
          }

          context.putImageData(imageData, 0, 0);
          await yieldToBrowser();
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
          assertCurrent();
          if (!blob) throw new Error('Background removal export failed');
          if (blob.size > 10 * 1024 * 1024) throw new Error('Transparent artwork exceeds the 10 MiB file limit');
          return new File([blob], fileName, { type: 'image/png', lastModified: Date.now() });
        } finally {
          canvas.width = 1;
          canvas.height = 1;
        }
      }


      getArtworkAlphaProfile(data, width, height) {
        const pixelCount = width * height;
        if (!pixelCount) return null;

        let transparent = 0;
        let semiTransparent = 0;
        let edgeTransparent = 0;
        let edgePixels = 0;

        for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += 1) {
          const x = pixelIndex % width;
          const y = Math.floor(pixelIndex / width);
          const alpha = data[pixelIndex * 4 + 3];
          const isEdge = x === 0 || y === 0 || x === width - 1 || y === height - 1;

          if (alpha <= 2) {
            transparent += 1;
            if (isEdge) edgeTransparent += 1;
          } else if (alpha < 244) {
            semiTransparent += 1;
          }

          if (isEdge) edgePixels += 1;
        }

        return {
          transparentRatio: transparent / pixelCount,
          semiTransparentRatio: semiTransparent / pixelCount,
          edgeTransparentRatio: edgePixels ? edgeTransparent / edgePixels : 0,
        };
      }

      shouldPreserveExistingTransparentArtwork(profile) {
        if (!profile) return false;

        return (
          profile.transparentRatio > 0.012 ||
          profile.edgeTransparentRatio > 0.08 ||
          profile.semiTransparentRatio > 0.035
        );
      }

      estimateArtworkBackgroundColor(data, width, height) {
        const minSide = Math.min(width, height);
        const band = this.clampNumber(Math.round(minSide * 0.055), 2, 28);
        const step = Math.max(1, Math.floor(minSide / 520));
        const cornerLimitX = Math.min(width, band * 3);
        const cornerLimitY = Math.min(height, band * 3);
        const cornerClusters = [];
        const edgeClusters = [];

        const addPixel = (clusters, x, y) => {
          const index = (y * width + x) * 4;
          const alpha = data[index + 3];
          if (alpha < 24) return;

          this.addArtworkBackgroundCluster(clusters, data[index], data[index + 1], data[index + 2], alpha);
        };

        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            const isEdge = x < band || x >= width - band || y < band || y >= height - band;
            if (!isEdge) continue;

            addPixel(edgeClusters, x, y);

            const isCorner =
              (x < cornerLimitX || x >= width - cornerLimitX) &&
              (y < cornerLimitY || y >= height - cornerLimitY);
            if (isCorner) addPixel(cornerClusters, x, y);
          }
        }

        const clusters = cornerClusters.length ? cornerClusters : edgeClusters;
        if (!clusters.length) return null;

        const sortedClusters = clusters
          .filter((cluster) => cluster.weight > 0)
          .sort((a, b) => b.count - a.count);
        const dominantCluster = sortedClusters[0];
        if (!dominantCluster) return null;

        const background = {
          r: dominantCluster.r,
          g: dominantCluster.g,
          b: dominantCluster.b,
          hardTolerance: 24,
          softTolerance: 58,
        };

        const distances = [];
        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            const isEdge = x < band || x >= width - band || y < band || y >= height - band;
            if (!isEdge) continue;

            const index = (y * width + x) * 4;
            if (data[index + 3] < 24) continue;
            distances.push(this.getArtworkColorDistance(data[index], data[index + 1], data[index + 2], background));
          }
        }

        distances.sort((a, b) => a - b);
        const totalClusterCount = sortedClusters.reduce((sum, cluster) => sum + cluster.count, 0) || 1;
        const p50 = distances[Math.floor(distances.length * 0.5)] || 12;
        const p70 = distances[Math.floor(distances.length * 0.7)] || 18;
        const p92 = distances[Math.floor(distances.length * 0.92)] || 42;
        const dominantRatio = this.clampNumber(dominantCluster.count / totalClusterCount, 0, 1);
        const closeEdgeLimit = Math.max(18, p50 * 1.75);
        const closeEdgeRatio = distances.length
          ? distances.filter((distance) => distance <= closeEdgeLimit).length / distances.length
          : 0;
        const edgeUniformity = 1 - this.clampNumber((p92 - 24) / 88, 0, 1);
        const confidence = this.clampNumber(
          dominantRatio * 0.42 + closeEdgeRatio * 0.34 + edgeUniformity * 0.24,
          0,
          1,
        );
        const neutralLight = this.isNeutralLightArtworkBackground(background);
        const isSimple = confidence >= 0.62 || (neutralLight && confidence >= 0.52);
        const conservative = confidence < 0.52;
        const toleranceScale = conservative ? 0.72 : isSimple ? 1 : 0.88;

        background.confidence = confidence;
        background.edgeUniformity = edgeUniformity;
        background.isSimple = isSimple;
        background.conservative = conservative;
        background.isNeutralLight = neutralLight;
        background.hardTolerance = this.clampNumber(Math.max(16, p70 * 0.88) * toleranceScale, 14, conservative ? 34 : 46);
        background.softTolerance = this.clampNumber(
          Math.max(34, p92 * (isSimple ? 1.2 : 0.98), background.hardTolerance + 14),
          background.hardTolerance + 14,
          conservative ? 72 : 96,
        );
        background.residueTolerance = this.clampNumber(
          background.softTolerance * (isSimple ? 1.2 : 1.08),
          background.softTolerance + 8,
          conservative ? 88 : 124,
        );
        background.palette = sortedClusters
          .filter((cluster, index) => {
            if (index === 0) return true;
            if (index > (isSimple ? 5 : 2)) return false;
            if (cluster.count < dominantCluster.count * (isSimple ? 0.065 : 0.14)) return false;
            return this.getArtworkColorDistance(cluster.r, cluster.g, cluster.b, background) <= background.softTolerance * (isSimple ? 0.95 : 0.78);
          })
          .slice(0, isSimple ? 4 : 2)
          .map((cluster) => ({
            r: cluster.r,
            g: cluster.g,
            b: cluster.b,
          }));

        return background;
      }

      addArtworkBackgroundCluster(clusters, r, g, b, alpha = 255) {
        let bestCluster = null;
        let bestDistance = Infinity;

        clusters.forEach((cluster) => {
          const distance = this.getArtworkColorDistance(r, g, b, cluster);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestCluster = cluster;
          }
        });

        if (!bestCluster || bestDistance > 38) {
          clusters.push({
            r,
            g,
            b,
            count: 1,
            weight: alpha,
          });
          return;
        }

        const nextWeight = bestCluster.weight + alpha;
        bestCluster.r = (bestCluster.r * bestCluster.weight + r * alpha) / nextWeight;
        bestCluster.g = (bestCluster.g * bestCluster.weight + g * alpha) / nextWeight;
        bestCluster.b = (bestCluster.b * bestCluster.weight + b * alpha) / nextWeight;
        bestCluster.weight = nextWeight;
        bestCluster.count += 1;
      }

      getConnectedArtworkBackgroundMask(data, width, height, background) {
        const pixelCount = width * height;
        const visited = new Uint8Array(pixelCount);
        const strength = new Uint8Array(pixelCount);
        const queue = new Uint32Array(pixelCount);
        let queueLength = 0;
        const enqueue = (x, y) => {
          if (x < 0 || y < 0 || x >= width || y >= height) return;
          const pixelIndex = y * width + x;
          if (visited[pixelIndex]) return;

          const dataIndex = pixelIndex * 4;
          const alpha = data[dataIndex + 3];
          if (alpha <= 8) {
            visited[pixelIndex] = 1;
            strength[pixelIndex] = 255;
            queue[queueLength++] = pixelIndex;
            return;
          }

          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          if (distance > background.softTolerance) return;

          visited[pixelIndex] = 1;
          strength[pixelIndex] = this.getArtworkRemovalStrength(distance, background.hardTolerance, background.softTolerance);
          queue[queueLength++] = pixelIndex;
        };

        for (let x = 0; x < width; x += 1) {
          enqueue(x, 0);
          enqueue(x, height - 1);
        }

        for (let y = 1; y < height - 1; y += 1) {
          enqueue(0, y);
          enqueue(width - 1, y);
        }

        for (let cursor = 0; cursor < queueLength; cursor += 1) {
          const pixelIndex = queue[cursor];
          const x = pixelIndex % width;
          const y = Math.floor(pixelIndex / width);
          enqueue(x + 1, y);
          enqueue(x - 1, y);
          enqueue(x, y + 1);
          enqueue(x, y - 1);
        }

        this.cleanArtworkResidualMask(data, width, height, background, visited, strength);
        this.cleanInteriorArtworkBackgroundPockets(data, width, height, background, visited, strength);
        this.featherArtworkBackgroundMask(data, width, height, background, visited, strength);
        this.stabilizeArtworkRemovalMask(width, height, background, strength);

        return strength;
      }

      cleanArtworkResidualMask(data, width, height, background, visited, strength) {
        const residueTolerance = background.residueTolerance || background.softTolerance * 1.2;
        const conservative = Boolean(background.conservative);
        const confidence = background.confidence ?? 1;
        const neighborThreshold = conservative ? 148 : 112;
        const maxPasses = background.isSimple ? 3 : 2;

        for (let pass = 0; pass < maxPasses; pass += 1) {
          const updates = [];

          for (let y = 1; y < height - 1; y += 1) {
            for (let x = 1; x < width - 1; x += 1) {
              const pixelIndex = y * width + x;
              if (visited[pixelIndex]) continue;

              const neighborStrength = Math.max(
                strength[pixelIndex - 1],
                strength[pixelIndex + 1],
                strength[pixelIndex - width],
                strength[pixelIndex + width],
                strength[pixelIndex - width - 1],
                strength[pixelIndex - width + 1],
                strength[pixelIndex + width - 1],
                strength[pixelIndex + width + 1],
              );
              if (neighborStrength < neighborThreshold) continue;

              const dataIndex = pixelIndex * 4;
              const alpha = data[dataIndex + 3];
              if (alpha <= 10) {
                updates.push({ pixelIndex, strength: 255, visited: true });
                continue;
              }

              const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
              if (distance > residueTolerance) continue;

              const edgeConfidence = this.clampNumber(neighborStrength / 255, 0.42, 1);
              const removalStrength = this.getArtworkRemovalStrength(
                distance,
                Math.max(12, background.hardTolerance * 0.82),
                residueTolerance,
              );
              const residueWeight = background.isSimple ? 0.55 + edgeConfidence * 0.38 : 0.32 + edgeConfidence * 0.34 * confidence;
              const nextStrength = Math.round(removalStrength * residueWeight);

              if (nextStrength > strength[pixelIndex]) {
                updates.push({
                  pixelIndex,
                  strength: nextStrength,
                  visited: nextStrength > (conservative ? 206 : 172),
                });
              }
            }
          }

          if (!updates.length) return;

          updates.forEach((update) => {
            strength[update.pixelIndex] = Math.max(strength[update.pixelIndex], update.strength);
            if (update.visited) visited[update.pixelIndex] = 1;
          });
        }
      }

      cleanInteriorArtworkBackgroundPockets(data, width, height, background, visited, strength) {
        if ((background.confidence ?? 1) < 0.42) return;

        const pixelCount = width * height;
        const checked = new Uint8Array(pixelCount);
        const tolerance = background.isSimple
          ? background.residueTolerance || background.softTolerance * 1.18
          : Math.min(background.softTolerance, background.hardTolerance + 22);
        const maxArea = Math.max(48, Math.round(pixelCount * (background.isSimple ? 0.28 : 0.1)));
        const minArea = 2;
        const queue = [];
        const component = [];

        for (let startIndex = 0; startIndex < pixelCount; startIndex += 1) {
          if (checked[startIndex] || visited[startIndex]) continue;

          const startDataIndex = startIndex * 4;
          const startAlpha = data[startDataIndex + 3];
          if (startAlpha <= 8) {
            checked[startIndex] = 1;
            strength[startIndex] = 255;
            continue;
          }

          const startDistance = this.getArtworkColorDistance(
            data[startDataIndex],
            data[startDataIndex + 1],
            data[startDataIndex + 2],
            background,
          );
          if (startDistance > tolerance) {
            checked[startIndex] = 1;
            continue;
          }

          queue.length = 0;
          component.length = 0;
          queue.push(startIndex);
          checked[startIndex] = 1;
          let touchesEdge = false;
          let distanceTotal = 0;

          for (let cursor = 0; cursor < queue.length; cursor += 1) {
            const pixelIndex = queue[cursor];
            const x = pixelIndex % width;
            const y = Math.floor(pixelIndex / width);
            const dataIndex = pixelIndex * 4;
            const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);

            component.push(pixelIndex);
            distanceTotal += distance;
            if (x <= 1 || y <= 1 || x >= width - 2 || y >= height - 2) touchesEdge = true;

            const enqueuePocketNeighbor = (nextIndex) => {
              if (nextIndex < 0 || nextIndex >= pixelCount || checked[nextIndex] || visited[nextIndex]) return;

              const nextX = nextIndex % width;
              if (Math.abs(nextX - x) > 1) return;

              const nextDataIndex = nextIndex * 4;
              const nextAlpha = data[nextDataIndex + 3];
              if (nextAlpha <= 8) {
                checked[nextIndex] = 1;
                strength[nextIndex] = 255;
                return;
              }

              const nextDistance = this.getArtworkColorDistance(
                data[nextDataIndex],
                data[nextDataIndex + 1],
                data[nextDataIndex + 2],
                background,
              );
              if (nextDistance > tolerance) {
                checked[nextIndex] = 1;
                return;
              }

              checked[nextIndex] = 1;
              queue.push(nextIndex);
            };

            enqueuePocketNeighbor(pixelIndex - 1);
            enqueuePocketNeighbor(pixelIndex + 1);
            enqueuePocketNeighbor(pixelIndex - width);
            enqueuePocketNeighbor(pixelIndex + width);

            if (component.length > maxArea * 1.45 && !background.isSimple) break;
          }

          if (touchesEdge || component.length < minArea || component.length > maxArea) continue;

          const averageDistance = distanceTotal / Math.max(component.length, 1);
          const componentPressure = component.reduce(
            (pressure, pixelIndex) => Math.max(pressure, this.getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, 2)),
            0,
          );
          const shouldRemove =
            averageDistance <= background.hardTolerance * (background.isSimple ? 1.65 : 1.25) ||
            componentPressure >= (background.isSimple ? 72 : 128);

          if (!shouldRemove) continue;

          component.forEach((pixelIndex) => {
            const dataIndex = pixelIndex * 4;
            const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
            const removalStrength = this.getArtworkRemovalStrength(
              distance,
              Math.max(10, background.hardTolerance * 0.72),
              tolerance,
            );
            const nextStrength = Math.round(removalStrength * (background.isSimple ? 0.94 : 0.68));
            strength[pixelIndex] = Math.max(strength[pixelIndex], nextStrength);
            if (nextStrength > 180) visited[pixelIndex] = 1;
          });
        }
      }

      featherArtworkBackgroundMask(data, width, height, background, visited, strength) {
        const updates = [];
        const softTolerance = background.residueTolerance || background.softTolerance * 1.18;
        const conservative = Boolean(background.conservative);
        const neighborThreshold = conservative ? 176 : 150;

        for (let y = 1; y < height - 1; y += 1) {
          for (let x = 1; x < width - 1; x += 1) {
            const pixelIndex = y * width + x;
            if (visited[pixelIndex]) continue;

            const neighborStrength = Math.max(
              strength[pixelIndex - 1],
              strength[pixelIndex + 1],
              strength[pixelIndex - width],
              strength[pixelIndex + width],
            );
            if (neighborStrength < neighborThreshold) continue;

            const dataIndex = pixelIndex * 4;
            const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
            if (distance > softTolerance) continue;

            updates.push({
              pixelIndex,
              strength: Math.round(
                this.getArtworkRemovalStrength(distance, background.hardTolerance * 0.9, softTolerance) *
                  this.clampNumber(neighborStrength / 255, conservative ? 0.3 : 0.42, conservative ? 0.58 : 0.8),
              ),
            });
          }
        }

        updates.forEach((update) => {
          strength[update.pixelIndex] = Math.max(strength[update.pixelIndex], update.strength);
        });
      }

      stabilizeArtworkRemovalMask(width, height, background, strength) {
        if (background.isSimple) return;

        let strongPixels = 0;
        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          if (strength[pixelIndex] > 235) strongPixels += 1;
        }

        const strongRatio = strongPixels / Math.max(width * height, 1);
        if (strongRatio < 0.92) return;

        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          strength[pixelIndex] = Math.round(strength[pixelIndex] * 0.58);
        }
      }

      cleanGlobalArtworkBackgroundResidues(data, width, height, background, strength) {
        const confidence = background.confidence ?? 1;
        if (confidence < 0.38) return;

        const isNeutralLightBackground = background.isNeutralLight ?? this.isNeutralLightArtworkBackground(background);
        const globalWeight = background.isSimple ? 1 : this.clampNumber((confidence - 0.34) / 0.34, 0.22, 0.72);
        const hardResidueTolerance = isNeutralLightBackground
          ? Math.min(background.hardTolerance * (background.isSimple ? 1.05 : 0.82), background.residueTolerance || background.softTolerance)
          : background.hardTolerance * 0.72;
        const softResidueTolerance = isNeutralLightBackground
          ? Math.min(background.softTolerance * (background.isSimple ? 1.02 : 0.84), background.residueTolerance || background.softTolerance)
          : background.hardTolerance * 0.88;

        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          if (strength[pixelIndex] > 235) continue;

          const dataIndex = pixelIndex * 4;
          const alpha = data[dataIndex + 3];
          if (alpha <= 8) {
            strength[pixelIndex] = 255;
            continue;
          }

          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          if (distance <= hardResidueTolerance) {
            const targetStrength = isNeutralLightBackground ? Math.round(255 * globalWeight) : Math.round(218 * globalWeight);
            strength[pixelIndex] = Math.max(strength[pixelIndex], targetStrength);
            continue;
          }

          if (!isNeutralLightBackground || distance > softResidueTolerance) continue;

          const edgePressure = this.getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, 2);
          if (edgePressure < (background.isSimple ? 118 : 164)) continue;

          const removalStrength = this.getArtworkRemovalStrength(distance, hardResidueTolerance, softResidueTolerance);
          strength[pixelIndex] = Math.max(
            strength[pixelIndex],
            Math.round(removalStrength * this.clampNumber(edgePressure / 255, 0.36, background.isSimple ? 0.86 : 0.56) * globalWeight),
          );
        }

        // Some raster exports include a pale one-pixel canvas frame. It is
        // separate from the logo, but falls outside the white matte tolerance
        // and otherwise becomes an engraved rectangle. Require all four pale
        // sides and a clear inner band; never erase arbitrary edge artwork.
        if (!background.isSimple || !isNeutralLightBackground || background.edgeUniformity < 0.75
          || width < 16 || height < 16) return;
        const border = [];
        for (let x = 0; x < width; x += 1) border.push(x, (height - 1) * width + x);
        for (let y = 1; y < height - 1; y += 1) border.push(y * width, y * width + width - 1);
        const matteColor = { r: background.r, g: background.g, b: background.b };
        let framePixels = 0;
        for (const pixelIndex of border) {
          const index = pixelIndex * 4;
          const r = data[index], g = data[index + 1], b = data[index + 2];
          const distance = this.getArtworkColorDistance(r, g, b, matteColor);
          if (data[index + 3] < 244 || Math.max(r, g, b) - Math.min(r, g, b) > 18 || distance > 80) return;
          if (distance > background.hardTolerance * 0.5) framePixels += 1;
        }
        if (framePixels / border.length < 0.9) return;
        for (let side = 0; side < 4; side += 1) {
          const length = side < 2 ? width : height;
          let clear = 0, total = 0;
          for (let depth = 1; depth <= 3; depth += 1) {
            for (let offset = 1; offset < length - 1; offset += 1) {
              const x = side === 2 ? depth : side === 3 ? width - 1 - depth : offset;
              const y = side === 0 ? depth : side === 1 ? height - 1 - depth : offset;
              total += 1;
              if (strength[y * width + x] > 235) clear += 1;
            }
          }
          if (clear / total < 0.98) return;
        }
        for (const pixelIndex of border) strength[pixelIndex] = 255;
      }

      getNearbyArtworkRemovalPressure(strength, width, height, pixelIndex, radius = 1) {
        const x = pixelIndex % width;
        const y = Math.floor(pixelIndex / width);
        let pressure = 0;

        for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
          const nextY = y + offsetY;
          if (nextY < 0 || nextY >= height) continue;

          for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
            if (!offsetX && !offsetY) continue;
            const nextX = x + offsetX;
            if (nextX < 0 || nextX >= width) continue;
            pressure = Math.max(pressure, strength[nextY * width + nextX]);
          }
        }

        return pressure;
      }

      isNeutralLightArtworkBackground(background) {
        const luma = background.r * 0.299 + background.g * 0.587 + background.b * 0.114;
        const maxChannel = Math.max(background.r, background.g, background.b);
        const minChannel = Math.min(background.r, background.g, background.b);
        return luma >= 188 && maxChannel - minChannel <= 42;
      }

      refineArtworkEdgeMatte(data, width, height, background, mask) {
        // Recover coverage from C = alpha * F + (1 - alpha) * B. Only accept
        // a nearby ink sample when all RGB channels fit the same matte. This
        // removes the baked-in background fringe without blurring solid ink.
        if (background.skipEdgeMatte || !background.isSimple || background.edgeUniformity < 0.75) return;
        const refined = new Uint8Array(width * height);
        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          const index = pixelIndex * 4;
          if (data[index + 3] < 244) continue;
          const r = data[index], g = data[index + 1], b = data[index + 2];
          const sample = this.getNearestArtworkBackgroundColor(r, g, b, background);
          const cr = r - sample.r, cg = g - sample.g, cb = b - sample.b;
          const magnitude = cr * cr + cg * cg + cb * cb;
          if (magnitude < 12 || this.getNearbyArtworkRemovalPressure(mask, width, height, pixelIndex, 1) < 200) continue;
          const x = pixelIndex % width, y = Math.floor(pixelIndex / width);
          let bestMagnitude = magnitude, coverage = 1;
          for (let dy = -3; dy <= 3; dy += 1) {
            const sy = y + dy;
            if (sy < 0 || sy >= height) continue;
            for (let dx = -3; dx <= 3; dx += 1) {
              const sx = x + dx;
              if (sx < 0 || sx >= width || (!dx && !dy)) continue;
              const neighbor = sy * width + sx, ni = neighbor * 4;
              if (mask[neighbor] > 8 || data[ni + 3] < 250) continue;
              const fr = data[ni] - sample.r, fg = data[ni + 1] - sample.g, fb = data[ni + 2] - sample.b;
              const fm = fr * fr + fg * fg + fb * fb;
              if (fm <= bestMagnitude + 16) continue;
              const alpha = (cr * fr + cg * fg + cb * fb) / fm;
              if (alpha <= 0 || alpha >= 0.995) continue;
              const er = cr - alpha * fr, eg = cg - alpha * fg, eb = cb - alpha * fb;
              if (er * er + eg * eg + eb * eb > 27) continue;
              bestMagnitude = fm;
              coverage = alpha;
            }
          }
          if (coverage < 1) {
            mask[pixelIndex] = Math.round((1 - coverage) * 255);
            refined[pixelIndex] = 1;
          }
        }
        mask.refinedEdges = refined;
      }

      applyArtworkBackgroundRemoval(data, width, height, background, mask) {
        this.refineArtworkEdgeMatte(data, width, height, background, mask);
        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          const removalStrength = mask[pixelIndex];
          if (!removalStrength) continue;

          const dataIndex = pixelIndex * 4;
          const alpha = data[dataIndex + 3];
          if (!alpha) continue;

          const originalRed = data[dataIndex];
          const originalGreen = data[dataIndex + 1];
          const originalBlue = data[dataIndex + 2];
          const originalDistance = this.getArtworkColorDistance(originalRed, originalGreen, originalBlue, background);
          const backgroundSample = this.getNearestArtworkBackgroundColor(originalRed, originalGreen, originalBlue, background);
          const removalRatio = removalStrength / 255;
          let nextAlpha = Math.round(alpha * (1 - removalRatio));

          if (nextAlpha > 2 && (removalRatio > 0.08 || mask.refinedEdges?.[pixelIndex])) {
            const retainedRatio = this.clampNumber(nextAlpha / Math.max(alpha, 1), mask.refinedEdges?.[pixelIndex] ? 1 / 255 : background.conservative ? 0.12 : 0.06, 1);
            data[dataIndex] = this.clampColorChannel((originalRed - backgroundSample.r * (1 - retainedRatio)) / retainedRatio);
            data[dataIndex + 1] = this.clampColorChannel((originalGreen - backgroundSample.g * (1 - retainedRatio)) / retainedRatio);
            data[dataIndex + 2] = this.clampColorChannel((originalBlue - backgroundSample.b * (1 - retainedRatio)) / retainedRatio);
          }

          const snapAlpha = background.isSimple ? 16 : 10;
          if (!mask.refinedEdges?.[pixelIndex] && nextAlpha <= snapAlpha && originalDistance <= (background.residueTolerance || background.softTolerance) * (background.isSimple ? 1.05 : 0.92)) {
            nextAlpha = 0;
          }

          data[dataIndex + 3] = nextAlpha;

          if (nextAlpha <= 2) {
            data[dataIndex] = Math.round(background.r);
            data[dataIndex + 1] = Math.round(background.g);
            data[dataIndex + 2] = Math.round(background.b);
            data[dataIndex + 3] = 0;
          }
        }
      }

      polishArtworkForegroundEdges(data, width, height, background, mask) {
        if ((background.confidence ?? 1) < 0.48) return;

        const isNeutralLightBackground = background.isNeutralLight ?? this.isNeutralLightArtworkBackground(background);
        const tolerance = isNeutralLightBackground
          ? Math.min((background.residueTolerance || background.softTolerance) * 1.08, background.softTolerance + 26)
          : background.softTolerance * 0.82;
        const updates = [];

        for (let pixelIndex = 0; pixelIndex < width * height; pixelIndex += 1) {
          const dataIndex = pixelIndex * 4;
          const alpha = data[dataIndex + 3];
          if (alpha <= 8 || mask[pixelIndex] > 128 || mask.refinedEdges?.[pixelIndex]) continue;

          const edgePressure = this.getNearbyArtworkRemovalPressure(mask, width, height, pixelIndex, 1);
          if (edgePressure < (background.isSimple ? 138 : 184)) continue;

          const distance = this.getArtworkColorDistance(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          if (distance > tolerance) continue;

          const removalRatio =
            (this.getArtworkRemovalStrength(distance, Math.max(8, background.hardTolerance * 0.48), tolerance) / 255) *
            this.clampNumber(edgePressure / 255, 0.28, background.isSimple ? 0.58 : 0.34);
          if (removalRatio <= 0.05) continue;

          updates.push({ pixelIndex, removalRatio });
        }

        updates.forEach(({ pixelIndex, removalRatio }) => {
          const dataIndex = pixelIndex * 4;
          const alpha = data[dataIndex + 3];
          const backgroundSample = this.getNearestArtworkBackgroundColor(data[dataIndex], data[dataIndex + 1], data[dataIndex + 2], background);
          const nextAlpha = Math.round(alpha * (1 - removalRatio));
          const retainedRatio = this.clampNumber(nextAlpha / Math.max(alpha, 1), 0.08, 1);

          data[dataIndex] = this.clampColorChannel((data[dataIndex] - backgroundSample.r * (1 - retainedRatio)) / retainedRatio);
          data[dataIndex + 1] = this.clampColorChannel((data[dataIndex + 1] - backgroundSample.g * (1 - retainedRatio)) / retainedRatio);
          data[dataIndex + 2] = this.clampColorChannel((data[dataIndex + 2] - backgroundSample.b * (1 - retainedRatio)) / retainedRatio);
          data[dataIndex + 3] = nextAlpha <= 8 ? 0 : nextAlpha;
        });
      }

      getArtworkRemovalStrength(distance, hardTolerance, softTolerance) {
        if (distance <= hardTolerance) return 255;
        if (distance >= softTolerance) return 0;

        const progress = (distance - hardTolerance) / Math.max(softTolerance - hardTolerance, 1);
        const eased = progress * progress * (3 - 2 * progress);
        return Math.round((1 - eased) * 255);
      }

      getArtworkColorDistance(r, g, b, background) {
        const getDistance = (sample) => {
          const dr = r - sample.r;
          const dg = g - sample.g;
          const db = b - sample.b;
          const colorDistance = Math.sqrt(dr * dr * 0.32 + dg * dg * 0.5 + db * db * 0.18);
          const luma = r * 0.299 + g * 0.587 + b * 0.114;
          const backgroundLuma = sample.r * 0.299 + sample.g * 0.587 + sample.b * 0.114;
          return colorDistance + Math.abs(luma - backgroundLuma) * 0.22;
        };

        if (Array.isArray(background.palette) && background.palette.length) {
          return background.palette.reduce((bestDistance, sample) => Math.min(bestDistance, getDistance(sample)), Infinity);
        }

        return getDistance(background);
      }

      getNearestArtworkBackgroundColor(r, g, b, background) {
        const samples = Array.isArray(background.palette) && background.palette.length ? background.palette : [background];

        return samples.reduce(
          (best, sample) => {
            const distance = this.getArtworkColorDistance(r, g, b, sample);
            return distance < best.distance ? { sample, distance } : best;
          },
          { sample: background, distance: Infinity },
        ).sample;
      }

      clampColorChannel(value) {
        return Math.min(Math.max(Math.round(Number(value) || 0), 0), 255);
      }

      getDesignViewOptions() {
        if (this.zoneInteractions?.enabled) return this.zoneInteractions.getViewOptions();
        const profileOptions = this.getProfilePlacementNavigationOptions();
        if (profileOptions.length) return profileOptions;
        const allZones = this.designStudioState?.zones || [];
        const zones = allZones.filter(zone => zone.recommendedForPlacement !== false || zone.hasLogo);
        if (zones.some(zone => zone.emplacementKey)) {
          const groups = new Map();
          for (const zone of zones) {
            const key = zone.emplacementKey || this.getDesignZonePlacementKey(zone);
            const previous = groups.get(key);
            if (!previous || zone.key === this.designStudioState?.activeZoneKey
              || previous.key !== this.designStudioState?.activeZoneKey && zone.hasLogo && !previous.hasLogo) groups.set(key, zone);
          }
          return [...groups.values()].map(zone => ({ key: zone.viewKey || 'front', zoneKey: zone.key,
            placementKey: this.getDesignZonePlacementKey(zone), label: zone.emplacementLabel || zone.label }));
        }
        if (zones.length) {
          if (this.hasSharedDesignViewZones(zones)) {
            const seenPlacements = new Set();
            return zones.reduce((options, zone) => {
              const viewKey = String(zone?.viewKey || '').trim();
              const zoneKey = String(zone?.key || '').trim();
              const placementKey = this.getDesignZonePlacementKey(zone);
              const optionKey = `${this.normalizeDesignSwitcherView(viewKey)}::${placementKey}`;
              if (!viewKey || !zoneKey || !placementKey || seenPlacements.has(optionKey)) return options;
              seenPlacements.add(optionKey);
              options.push({
                key: viewKey,
                zoneKey,
                placementKey,
                label: zone?.label || this.getDesignViewLabel(viewKey, zones),
              });
              return options;
            }, []);
          }

          const seen = new Set();
          return zones.reduce((options, zone) => {
            const viewKey = String(zone?.viewKey || '').trim();
            const normalizedViewKey = this.normalizeStudioKey(viewKey);
            if (!viewKey || seen.has(normalizedViewKey)) return options;
            seen.add(normalizedViewKey);
            options.push({
              key: viewKey,
              label: this.getDesignViewLabel(viewKey, zones),
            });
            return options;
          }, []);
        }

        return allZones.length ? [] : [{ key: 'front', label: this.string('viewFront') }];
      }

      getProfilePlacementNavigationOptions() {
        const selector = this.getProductRoot()?.querySelector('product-marking-selector');
        return (selector?.getStudioPlacementNavigationOptions?.() || []).map(option => ({
          key: option.viewKey || 'front', zoneKey: option.nativeZoneKey, profileKey: option.profileKey,
          placementKey: option.emplacementKey, label: option.emplacementLabel, active: option.active,
        }));
      }

      hasSharedDesignViewZones(zones = this.designStudioState?.zones || []) {
        const counts = new Map();
        zones.forEach((zone) => {
          const viewKey = this.normalizeDesignSwitcherView(zone?.viewKey || '');
          if (!viewKey) return;
          counts.set(viewKey, (counts.get(viewKey) || 0) + 1);
        });
        return Array.from(counts.values()).some((count) => count > 1);
      }

      getDesignZonePlacementKey(zone) {
        return this.normalizeStudioKey(
          zone?.placementKey ||
          zone?.placement_key ||
          zone?.placementId ||
          zone?.groupId ||
          zone?.group_id ||
          zone?.id ||
          '',
        ) || String(zone?.key || '').trim();
      }

      getDesignViewLabel(viewKey, zones = this.designStudioState?.zones || []) {
        const normalizedViewKey = this.normalizeStudioKey(viewKey);
        const configuredView = this.getTodayStudioViews().find((view) => (
          this.normalizeStudioKey(view?.key || view?.view || '') === normalizedViewKey
        ));
        if (configuredView?.label) return configuredView.label;

        const zone = zones.find((item) => this.normalizeStudioKey(item?.viewKey || '') === normalizedViewKey);
        if (zone?.label) return zone.label;

        const labels = {
          front: this.string('viewFront'),
          back: this.string('viewBack'),
          right_sleeve: this.string('viewRightSleeve'),
          left_sleeve: this.string('viewLeftSleeve'),
          sleeve: this.string('viewSleeve'),
        };
        return labels[normalizedViewKey] || String(viewKey || this.string('viewFallback')).replace(/[_-]+/g, ' ');
      }

      normalizeDesignSwitcherView(viewKey) {
        const normalizedViewKey = this.normalizeStudioKey(viewKey || 'front');

        if (normalizedViewKey === 'back') return 'back';
        if (normalizedViewKey === 'right_sleeve') return 'right_sleeve';
        if (normalizedViewKey === 'left_sleeve') return 'left_sleeve';
        if (normalizedViewKey === 'sleeve') return 'sleeve';
        if (normalizedViewKey === 'front') return 'front';

        return normalizedViewKey || 'front';
      }

      getDesignZoneForSwitcherView(viewKey, zoneKey = '', placementKey = '') {
        const normalizedViewKey = this.normalizeDesignSwitcherView(viewKey);
        const zones = this.designStudioState?.zones || [];
        if (!zones.length) return null;

        const explicitZone = zones.find((zone) => String(zone?.key || '').trim() === String(zoneKey || '').trim()
          && this.normalizeDesignSwitcherView(zone.viewKey) === normalizedViewKey
          && (!placementKey || this.getDesignZonePlacementKey(zone) === this.normalizeStudioKey(placementKey)));
        if (explicitZone) return explicitZone;

        const normalizedPlacementKey = this.normalizeStudioKey(placementKey || '');
        if (normalizedPlacementKey) {
          const placementMatch = zones.find((zone) => (
            this.getDesignZonePlacementKey(zone) === normalizedPlacementKey &&
            this.normalizeDesignSwitcherView(zone.viewKey) === normalizedViewKey
          )) || zones.find((zone) => this.getDesignZonePlacementKey(zone) === normalizedPlacementKey);
          if (placementMatch) return placementMatch;
        }

        const normalizedZoneKey = String(zoneKey || '').trim();
        if (normalizedZoneKey) {
          const zoneMatch = zones.find((zone) => String(zone?.key || '').trim() === normalizedZoneKey);
          if (zoneMatch) return zoneMatch;
        }

        const exactZone = zones.find((zone) => this.normalizeDesignSwitcherView(zone.viewKey) === normalizedViewKey);
        if (exactZone) return exactZone;

        if (normalizedViewKey === 'right_sleeve' || normalizedViewKey === 'left_sleeve') {
          return zones.find((zone) => this.normalizeDesignSwitcherView(zone.viewKey) === 'sleeve') || null;
        }

        if (normalizedViewKey === 'sleeve') {
          return zones.find((zone) => {
            const zoneViewKey = this.normalizeDesignSwitcherView(zone.viewKey);
            return zoneViewKey === 'right_sleeve' || zoneViewKey === 'left_sleeve';
          }) || null;
        }

        return null;
      }

      ensureDesignViewSwitcher() {
        if (this.designViewSwitcher?.isConnected) return this.designViewSwitcher;

        const viewerWrap =
          this.querySelector('.product__media-gallery-viewer-wrap') ||
          this.elements?.viewer?.closest?.('.product__media-gallery-viewer-wrap') ||
          this;
        const switcher = document.createElement('div');
        switcher.className = 'product__design-view-switcher';
        switcher.dataset.role = 'product-design-view-switcher';
        switcher.setAttribute('aria-label', this.string('viewSelector'));
        switcher.hidden = true;
        switcher.innerHTML = '<span class="product__design-view-glider" data-role="product-design-view-glider" aria-hidden="true"></span>';
        switcher.addEventListener('pointerover', (event) => {
          if (event.pointerType !== 'mouse' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          const button = event.target.closest('[data-role="product-design-view"]');
          if (!button || button.contains(event.relatedTarget)) return;
          this.handleDesignViewHover(button);
        });
        switcher.addEventListener('click', (event) => {
          const viewButton = event.target.closest('[data-role="product-design-view"]');
          if (!viewButton) return;
          this.handleDesignViewSwitch(event, null, viewButton.dataset.designView, viewButton.dataset.zoneKey, viewButton.dataset.placementKey, viewButton.dataset.profileKey);
        });
        viewerWrap.appendChild(switcher);
        this.designViewSwitcher = switcher;
        return switcher;
      }

      handleDesignViewHover(button) {
        if (!button.isConnected || this.designViewSwitcher?.hidden || this.zoneInteractions?.locked) return;
        if (button.classList.contains('is-active')) return;
        if (button.dataset.profileKey) return;
        const zone = this.designStudioState?.zones?.find(item => item.key === button.dataset.zoneKey);
        if (zone && !this.hasDesignPhotoSurface(zone)) return;
        this.handleDesignViewSwitch(null, null, button.dataset.designView, button.dataset.zoneKey, button.dataset.placementKey);
      }

      hideDesignViewSwitcher() {
        const switcher =
          this.designViewSwitcher?.isConnected
            ? this.designViewSwitcher
            : this.querySelector('[data-role="product-design-view-switcher"]');

        if (!switcher) return;

        this.designViewSwitcher = switcher;
        switcher.hidden = true;
        switcher.classList.remove('has-glider-ready', 'is-gliding');
        switcher.querySelectorAll('[data-role="product-design-view"]').forEach((button) => {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        });
      }

      teardownDesignViewSwitcher() {
        const switcher = this.designViewSwitcher;
        if (!switcher) return;

        window.cancelAnimationFrame(switcher.productDesignViewFrame);
        switcher.productDesignViewFrame = null;
        switcher.productDesignViewResizeObserver?.disconnect();
        switcher.productDesignViewResizeObserver = null;
        if (switcher.productDesignViewGlideTimer) {
          window.clearTimeout(switcher.productDesignViewGlideTimer);
          switcher.productDesignViewGlideTimer = null;
        }

        const glider = switcher.querySelector('[data-role="product-design-view-glider"]');
        const animation = glider?.productDesignViewAnimation;
        if (animation) {
          animation.onfinish = null;
          animation.oncancel = null;
          animation.cancel();
          glider.productDesignViewAnimation = null;
        }
        switcher.classList.remove('is-gliding');
      }

      updateDesignViewSwitcher(overlay, activeZone, activeSlide, options = {}) {
        const switcher = this.ensureDesignViewSwitcher();
        if (!switcher) return;

        if (!this.isDesignStudioStateForActiveVariant() || !activeZone || activeSlide?.dataset.mediaType !== 'image') {
          this.hideDesignViewSwitcher();
          return;
        }

        const activeViewKey = this.normalizeDesignSwitcherView(activeZone?.viewKey || activeSlide?.dataset.markingView || 'front');
        const activePlacementKey = this.getDesignZonePlacementKey(activeZone);
        this.renderDesignViewSwitcherOptions(switcher);
        switcher.hidden = this.getDesignViewOptions().length === 0;

        switcher.querySelectorAll('[data-role="product-design-view"]').forEach((button) => {
          const buttonViewKey = this.normalizeDesignSwitcherView(button.dataset.designView || '');
          const buttonZoneKey = button.dataset.zoneKey || '';
          const buttonPlacementKey = button.dataset.placementKey || '';
          const matchingZone = this.getDesignZoneForSwitcherView(buttonViewKey, buttonZoneKey, buttonPlacementKey);
          const profileOption = button.dataset.profileKey && this.getProfilePlacementNavigationOptions().find(option => option.profileKey === button.dataset.profileKey);
          const isActive = profileOption ? profileOption.active === true
            : buttonPlacementKey ? buttonPlacementKey === activePlacementKey && buttonViewKey === activeViewKey : buttonViewKey === activeViewKey;

          button.dataset.zoneKey = buttonZoneKey || matchingZone?.key || '';
          button.dataset.placementKey = buttonPlacementKey || this.getDesignZonePlacementKey(matchingZone);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.updateDesignViewGlider(switcher, options);
      }

      renderDesignViewSwitcherOptions(switcher) {
        const options = this.getDesignViewOptions();
        // Load the switcher's views before a pointer reaches either destination.
        options.forEach((view) => {
          const image = this.getMediaSlideForView(view.key)?.querySelector('.product__media > img');
          if (image && image.loading !== 'eager') {
            image.loading = 'eager';
            image.decode?.().catch(() => {});
          }
        });
        const signature = options.map((view) => `${view.key}:${view.zoneKey || ''}:${view.placementKey || ''}:${view.profileKey || ''}:${view.label}`).join('|');
        if (switcher.dataset.optionsSignature === signature) return;

        const viewButtons = options
          .map(
            (view) =>
              `<button type="button" class="product__design-view-button" data-role="product-design-view" data-design-view="${this.escapeAttr(view.key)}" data-zone-key="${this.escapeAttr(view.zoneKey || '')}" data-placement-key="${this.escapeAttr(view.placementKey || '')}" data-profile-key="${this.escapeAttr(view.profileKey || '')}" aria-pressed="false">${this.escapeHtml(view.label)}</button>`,
          )
          .join('');

        switcher.dataset.optionsSignature = signature;
        switcher.innerHTML = `
          <span class="product__design-view-glider" data-role="product-design-view-glider" aria-hidden="true"></span>
          ${viewButtons}
        `;
      }

      updateDesignViewGlider(switcher, options = {}) {
        const glider = switcher?.querySelector('[data-role="product-design-view-glider"]');
        if (!switcher || !glider) return;

        if (!switcher.productDesignViewResizeObserver && 'ResizeObserver' in window) {
          switcher.productDesignViewResizeObserver = new ResizeObserver(() => this.updateDesignViewGlider(switcher));
          switcher.productDesignViewResizeObserver.observe(switcher);
        }

        window.cancelAnimationFrame(switcher.productDesignViewFrame);
        switcher.productDesignViewFrame = window.requestAnimationFrame(() => {
          switcher.productDesignViewFrame = null;
          const activeButton = switcher.querySelector('[data-role="product-design-view"].is-active');
          if (!activeButton || !switcher.isConnected || switcher.hidden) return;
          const switcherRect = switcher.getBoundingClientRect();
          const buttonRect = activeButton.getBoundingClientRect();
          // Absolute offsets start inside the border, unlike bounding client rectangles.
          const x = buttonRect.left - switcherRect.left - switcher.clientLeft;
          const gliderRect = glider.getBoundingClientRect();
          const currentX = gliderRect.left - switcherRect.left - switcher.clientLeft;
          const currentWidth = gliderRect.width;
          const hasReadyGlider = switcher.classList.contains('has-glider-ready') && currentWidth > 0;
          const targetX = parseFloat(switcher.style.getPropertyValue('--product-design-view-glider-x'));
          const targetWidth = parseFloat(switcher.style.getPropertyValue('--product-design-view-glider-width'));
          // Artwork edits can render the gallery repeatedly without changing the selected view.
          // Keep an in-flight view change intact and do not animate an unchanged destination.
          if (hasReadyGlider && Math.abs(targetX - x) < 0.1 && Math.abs(targetWidth - buttonRect.width) < 0.1) return;
          const shouldGlide =
            hasReadyGlider &&
            !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
            (options.forceGlide === true || Math.abs(currentX - x) > 0.5 || Math.abs(currentWidth - buttonRect.width) > 0.5);

          if (glider.productDesignViewAnimation) {
            glider.productDesignViewAnimation.onfinish = null;
            glider.productDesignViewAnimation.oncancel = null;
            glider.productDesignViewAnimation.cancel();
            glider.productDesignViewAnimation = null;
          }

          switcher.style.setProperty('--product-design-view-glider-x', `${x}px`);
          switcher.style.setProperty('--product-design-view-glider-width', `${buttonRect.width}px`);
          switcher.classList.add('has-glider-ready');

          if (shouldGlide && typeof glider.animate === 'function') {
            switcher.classList.add('is-gliding');
            const animation = glider.animate(
              [
                {
                  transform: `translate3d(${currentX}px, 0, 0)`,
                  width: `${currentWidth}px`,
                },
                {
                  transform: `translate3d(${x}px, 0, 0)`,
                  width: `${buttonRect.width}px`,
                },
              ],
              {
                duration: 450,
                easing: 'cubic-bezier(0.22, 1.1, 0.36, 1)',
                fill: 'both',
              },
            );
            glider.productDesignViewAnimation = animation;
            animation.onfinish = () => {
              if (glider.productDesignViewAnimation !== animation) return;
              animation.onfinish = null;
              animation.oncancel = null;
              glider.productDesignViewAnimation = null;
              switcher.classList.remove('is-gliding');
              animation.cancel();
            };
            animation.oncancel = () => {
              if (glider.productDesignViewAnimation !== animation) return;
              glider.productDesignViewAnimation = null;
              switcher.classList.remove('is-gliding');
            };
            return;
          }

          switcher.classList.toggle('is-gliding', shouldGlide);
          if (switcher.productDesignViewGlideTimer) {
            window.clearTimeout(switcher.productDesignViewGlideTimer);
          }
          switcher.productDesignViewGlideTimer = window.setTimeout(() => {
            switcher.classList.remove('is-gliding');
            switcher.productDesignViewGlideTimer = null;
          }, 450);
        });
      }

      handleDesignViewSwitch(event, overlay, viewKey, zoneKey = '', placementKey = '', profileKey = '') {
        event?.preventDefault();
        event?.stopPropagation();

        if (profileKey) {
          if (this.zoneInteractions?.locked) return;
          const selector = this.getProductRoot()?.querySelector('product-marking-selector');
          if (typeof selector?.selectStudioPlacementCandidate !== 'function') return;
          selector.selectStudioPlacementCandidate(profileKey);
          const selected = this.getActiveDesignZone();
          this.renderDesignStudio();
          if (this.getSupplierPrintPlane(selected) && !this.hasDesignPhotoSurface(selected)) {
            this.requestMobileDesignEditor({ zoneKey: selected.key, allowEmpty: true, opener: event?.target });
          }
          return;
        }

        const normalizedViewKey = this.normalizeDesignSwitcherView(viewKey);
        const targetZone = this.designStudioState?.zones?.find(zone => zone.key === zoneKey)
          || this.getDesignZoneForSwitcherView(normalizedViewKey, zoneKey, placementKey);
        if (!targetZone || this.zoneInteractions?.locked) return;
        const targetViewKey = targetZone?.viewKey || normalizedViewKey;
        const supplierPlaneEditor = Boolean(this.getSupplierPrintPlane(targetZone)) && !this.hasDesignPhotoSurface(targetZone);

        if (!supplierPlaneEditor && this.getActiveDesignZone()?.key === targetZone.key && this.getActiveMediaSlide() === this.getMediaSlideForView(targetViewKey)) return;
        if (!supplierPlaneEditor) this.slideToDesignView(targetViewKey, { preserveViewport: true });
        const activeSlide = this.getActiveMediaSlide();
        const currentOverlay = this.designStudioOverlays.get(activeSlide) || overlay;
        this.setDesignArtworkSelected(currentOverlay, true);

        this.designZoneSelectionInProgress = true;
        try {
          this.dispatchEvent(
            new CustomEvent('todaymerch:product-design-zone-selected', {
              bubbles: true,
              detail: {
                source: 'media-gallery',
                zoneKey: targetZone?.key || '',
                viewKey: targetViewKey,
                interaction: 'view-switcher',
              },
            }),
          );
        } finally { this.designZoneSelectionInProgress = false; }

        // The selector may suppress the echo while the gallery is selecting.
        // Read its committed state before refreshing overlays or opening a plane.
        const selectedState = this.getProductRoot()?.querySelector('product-marking-selector')?.getStudioState?.();
        if (selectedState) this.designStudioState = selectedState;
        this.renderDesignStudio();
        this.updateDesignViewSwitcher(currentOverlay, targetZone, activeSlide, { forceGlide: true });
        this.zoneInteractions?.sync();
        if (supplierPlaneEditor) this.requestMobileDesignEditor({ zoneKey: targetZone.key, allowEmpty: true, opener: event?.currentTarget });
      }

      getRenderableDesignZone(activeSlide) {
        const zones = this.designStudioState?.zones || [];
        if (!zones.length) return null;

        const activeZone = this.getActiveDesignZone();
        const activeViewKey = activeSlide?.dataset.markingView || '';

        if (this.getSupplierPrintPlane(activeZone) && !this.hasDesignPhotoSurface(activeZone)) return activeZone;

        if (!activeZone || (!activeViewKey && !activeZone.photoMediaId)) return activeZone || this.designStudioState?.logoZones?.[0] || zones[0];

        if (this.isZoneVisibleInView(activeZone, activeViewKey, activeSlide)) {
          return activeZone;
        }

        return (
          zones.find((zone) => zone.hasLogo && this.isZoneVisibleInView(zone, activeViewKey, activeSlide)) ||
          zones.find((zone) => this.isZoneVisibleInView(zone, activeViewKey, activeSlide)) ||
          null
        );
      }

      getActiveDesignZone() {
        const zones = this.designStudioState?.zones || [];
        return zones.find((zone) => zone.key === this.designStudioState?.activeZoneKey) || this.designStudioState?.logoZones?.[0] || zones[0] || null;
      }

      isZoneVisibleInView(zone, viewKey, slide = null) {
        if (!zone) return false;

        // A qualified projection belongs to one exact photo. Semantic labels
        // such as "back" can occur on several colours or gallery images.
        if (zone.photoMediaId) {
          const mediaId = this.getResourceNumericId(zone.photoMediaId);
          if (!mediaId) return false;
          return slide ? this.getResourceNumericId(slide.dataset?.mediaId) === mediaId
            : this.normalizeStudioKey(viewKey) === this.normalizeStudioKey(`photo-${mediaId}`);
        }
        if (!viewKey) return false;

        const zoneViewKey = this.normalizeStudioKey(zone.viewKey || '');
        const mediaViewKey = this.normalizeStudioKey(viewKey);
        if (slide && zoneViewKey === this.normalizeStudioKey(this.getPhotoViewKey(slide))) return true;
        if (zoneViewKey === mediaViewKey) return true;
        if ((zoneViewKey === 'right_sleeve' || zoneViewKey === 'left_sleeve') && mediaViewKey === 'sleeve') return true;
        if (zoneViewKey === 'sleeve' && (mediaViewKey === 'right_sleeve' || mediaViewKey === 'left_sleeve')) return true;

        return mediaViewKey === 'front' && (zoneViewKey === 'custom' || zoneViewKey === 'heart' || zoneViewKey === 'chest');
      }

      getSupplierPrintPlane(zone) {
        const plane = zone?.supplierPrintPlane;
        if (!plane || typeof plane !== 'object' || Array.isArray(plane) || plane.kind !== 'supplier_print_plane') return null;
        const widthMm = Number(plane.widthMm), heightMm = Number(plane.heightMm);
        if (![widthMm, heightMm].every(Number.isFinite) || widthMm <= 0 || heightMm <= 0) return null;
        return { ...plane, widthMm, heightMm, aspectRatio: widthMm / heightMm };
      }

      hasDesignPhotoProjection(zone) {
        if (!this.getSupplierPrintPlane(zone)) return zone?.photoProjectionStatus !== 'reference_only';
        return Boolean(this.getConfiguredDesignZonePlacement(zone?.photoPlacement));
      }

      hasDesignPhotoSurface(zone) {
        return this.hasDesignPhotoProjection(zone) || Boolean(this.getSupplierPrintPlane(zone)
          && zone?.photoPreviewMode === 'unfolded_360'
          && this.getConfiguredDesignZonePlacement(zone.photoPreviewPlacement));
      }

      getDesignCoordinatePlane(zone) {
        const plane = this.getSupplierPrintPlane(zone);
        if (!plane) return null;
        return window.TodayMerchStudioDesignRendering?.coordinatePlanes?.normalize({ planeId: plane.planeId,
          sourceGeometrySha256: plane.sourceGeometrySha256, widthMm: plane.widthMm, heightMm: plane.heightMm,
          supplierPositionId: plane.supplierPositionId }) || null;
      }

      restoreDesignArtworkCoordinateMode(zoneKey, transform, { variantId } = {}) {
        const scoped = variantId ? `variant-${variantId}::${zoneKey || 'default'}` : this.getScopedDesignZoneKey(zoneKey);
        const previousMode = this.designArtworkCoordinateModes?.get(scoped), previousPlane = this.designArtworkNativePlanes.get(scoped);
        const previousRestored = this.designArtworkRestoredPlanes?.get(scoped);
        const coordinatePlane = window.TodayMerchStudioDesignRendering?.coordinatePlanes?.normalize(transform?.coordinatePlane);
        const mode = ['supplier_plane', 'unfolded_360'].includes(transform?.previewMode) && coordinatePlane ? 'native_restore_pending' : 'legacy_photo';
        this.designArtworkCoordinateModes.set(scoped, mode);
        this.designArtworkNativePlanes.delete(scoped);
        if (coordinatePlane && mode === 'native_restore_pending') this.designArtworkRestoredPlanes.set(scoped, coordinatePlane);
        else this.designArtworkRestoredPlanes.delete(scoped);
        return Object.freeze({ mode, previousMode, rollback: () => {
          if (previousMode === undefined) this.designArtworkCoordinateModes.delete(scoped); else this.designArtworkCoordinateModes.set(scoped, previousMode);
          if (previousPlane === undefined) this.designArtworkNativePlanes.delete(scoped); else this.designArtworkNativePlanes.set(scoped, previousPlane);
          if (previousRestored === undefined) this.designArtworkRestoredPlanes.delete(scoped); else this.designArtworkRestoredPlanes.set(scoped, previousRestored);
        } });
      }

      usesNativeDesignPrintPlane(zone) {
        const plane = this.getSupplierPrintPlane(zone), scoped = this.getScopedDesignZoneKey(zone?.key);
        if (!plane) return false;
        const mode = this.designArtworkCoordinateModes?.get(scoped);
        if (mode === 'legacy_photo') return false;
        if (mode === 'native_restore_pending') {
          const restored = this.designArtworkRestoredPlanes?.get(scoped), current = this.getDesignCoordinatePlane(zone);
          if (!window.TodayMerchStudioDesignRendering?.coordinatePlanes?.matches(restored, current)) return false;
          this.designArtworkCoordinateModes.set(scoped, 'native');
          this.designArtworkNativePlanes.set(scoped, current);
          this.designArtworkRestoredPlanes.delete(scoped);
        }
        return true;
      }

      isLegacyDesignPhotoPlane(zone) {
        const mode = this.designArtworkCoordinateModes?.get(this.getScopedDesignZoneKey(zone?.key));
        return mode === 'legacy_photo' || mode === 'native_restore_pending' && !this.usesNativeDesignPrintPlane(zone);
      }

      getRetainedDesignPrintPlane(zone) {
        if (this.usesNativeDesignPrintPlane(zone)) return null;
        const plane = this.designArtworkRestoredPlanes?.get(this.getScopedDesignZoneKey(zone?.key));
        return plane ? { ...plane, aspectRatio: plane.widthMm / plane.heightMm } : null;
      }

      isUnfoldedDesignPrintPlane(zone) {
        return Boolean(this.usesNativeDesignPrintPlane(zone) && (zone?.photoPreviewMode === 'unfolded_360'
          || this.normalizeStudioKey(zone?.key || '') === 'wrap'));
      }

      getNativeDesignPrintPlanePlacement(zone, slide = null) {
        const retained = this.getRetainedDesignPrintPlane(zone), plane = retained || this.getSupplierPrintPlane(zone);
        if (!plane || !retained && !this.usesNativeDesignPrintPlane(zone)) return null;
        // photoPlacement is the rotated bounding box. Fit in the reviewed local
        // rectangle, then rotate only its display; millimetres keep their axes.
        const photoPart = !retained && !this.isUnfoldedDesignPrintPlane(zone)
          && ['approved', 'calibrated'].includes(zone?.photoProjectionStatus) && zone?.photoParts?.length === 1
          ? zone.photoParts[0] : null;
        const localPhoto = photoPart && ['x', 'y', 'width', 'height', 'rotation'].every(key => Number.isFinite(photoPart[key]))
          && photoPart.width > 0 && photoPart.height > 0 ? photoPart : null;
        const anchor = localPhoto || zone?.photoPreviewPlacement || zone?.photoPlacement || { x: 7, y: 7, width: 86, height: 86 };
        if (![anchor.x, anchor.y, anchor.width, anchor.height].every(Number.isFinite) || anchor.width <= 0 || anchor.height <= 0) return null;
        slide ||= this.getMediaSlideForView?.(zone?.viewKey) || this.getActiveMediaSlide?.();
        const container = slide?.querySelector?.('.product__media-item-container') || slide;
        const rect = container?.getBoundingClientRect?.();
        const image = slide?.querySelector?.('.product__media img[src], img[src]');
        const containerAspect = rect?.width > 0 && rect?.height > 0 ? rect.width / rect.height
          : image?.naturalWidth > 0 && image?.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 1;
        let width = anchor.width, height = anchor.height;
        if (width / height * containerAspect > plane.aspectRatio) width = height * plane.aspectRatio / containerAspect;
        else height = width * containerAspect / plane.aspectRatio;
        return { x: anchor.x + (anchor.width - width) / 2, y: anchor.y + (anchor.height - height) / 2, width, height,
          ...(localPhoto?.rotation ? { rotation: localPhoto.rotation } : {}) };
      }

      getSupplierPrintPlanePlacement(slide, zone, { allowUnprojected = false } = {}) {
        const plane = this.getSupplierPrintPlane(zone);
        if (!plane) return null;
        const unfolded = this.getNativeDesignPrintPlanePlacement(zone, slide);
        if (unfolded) return unfolded;
        const photoPlacement = this.getConfiguredDesignZonePlacement(zone?.photoPlacement)
          || zone?.photoPreviewMode === 'unfolded_360' && this.getConfiguredDesignZonePlacement(zone.photoPreviewPlacement);
        if (photoPlacement) return photoPlacement;
        if (!allowUnprojected) return null;
        const container = slide?.querySelector?.('.product__media-item-container') || slide;
        const width = Number(container?.clientWidth || container?.getBoundingClientRect?.().width || 0);
        const height = Number(container?.clientHeight || container?.getBoundingClientRect?.().height || 0);
        const containerAspect = width > 0 && height > 0 ? width / height : 1;
        const max = 86;
        let zoneWidth = max, zoneHeight = max;
        if (plane.aspectRatio >= containerAspect) zoneHeight = max * containerAspect / plane.aspectRatio;
        else zoneWidth = max * plane.aspectRatio / containerAspect;
        return { x: (100 - zoneWidth) / 2, y: (100 - zoneHeight) / 2, width: zoneWidth, height: zoneHeight };
      }

      getConfiguredDesignZonePlacement(zone) {
        const x = Number(zone?.x);
        const y = Number(zone?.y);
        const width = Number(zone?.width);
        const height = Number(zone?.height);

        if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

        const boundedX = Math.min(Math.max(x, 0), 99);
        const boundedY = Math.min(Math.max(y, 0), 99);
        return {
          x: boundedX,
          y: boundedY,
          width: Math.min(Math.max(width, 1), 100 - boundedX),
          height: Math.min(Math.max(height, 1), 100 - boundedY),
        };
      }

      getDesignZonePlacement(zone) {
        const unfolded = this.getNativeDesignPrintPlanePlacement(zone);
        if (unfolded) return unfolded;
        const configuredPlacement = this.getConfiguredDesignZonePlacement(zone?.photoPlacement)
          || zone?.photoPreviewMode === 'unfolded_360' && this.getConfiguredDesignZonePlacement(zone.photoPreviewPlacement)
          || this.getConfiguredDesignZonePlacement(zone);
        if (configuredPlacement) return configuredPlacement;

        const viewKey = this.normalizeStudioKey(zone?.viewKey || 'front');
        const labelKey = this.normalizeStudioKey(zone?.label || '');
        const key = this.normalizeStudioKey(zone?.key || '');

        if (viewKey === 'back') {
          return { x: 35, y: 33, width: 30, height: 34 };
        }

        if (viewKey === 'right_sleeve') {
          return { x: 24, y: 43, width: 13, height: 20 };
        }

        if (viewKey === 'left_sleeve') {
          return { x: 63, y: 43, width: 13, height: 20 };
        }

        if (viewKey === 'sleeve') {
          return { x: 24, y: 43, width: 13, height: 20 };
        }

        if (labelKey.includes('coeur') || labelKey.includes('heart') || key === '2') {
          return { x: 41, y: 39, width: 13, height: 10 };
        }

        if (labelKey.includes('anti_coeur') || labelKey.includes('chest') || labelKey.includes('poitrine') || key === '3') {
          return { x: 46, y: 41, width: 14, height: 11 };
        }

        if (viewKey === 'custom') {
          return { x: 34, y: 40, width: 32, height: 30 };
        }

        return { x: 34, y: 41, width: 32, height: 34 };
      }

      getDesignZoneParts(zone, placement) {
        if (this.usesNativeDesignPrintPlane(zone) || this.getRetainedDesignPrintPlane(zone)) return [];
        // Native planes have no photo parts. Resolve the photo projection only
        // when it is used, so reading artwork data cannot force native layout.
        if (placement === undefined) placement = this.getDesignZonePlacement(zone);
        const parts = zone?.photoPreviewMode === 'unfolded_360' && Array.isArray(zone.photoPreviewParts) ? zone.photoPreviewParts
          : Array.isArray(zone?.photoParts) && this.hasDesignPhotoProjection(zone) ? zone.photoParts
          : Array.isArray(zone?.parts) ? zone.parts : [];
        const hasCircularPart = parts.some((part) => String(part?.shape || '').toLowerCase() === 'circle');
        const hasCurvedPart = parts.some((part) => this.getDesignPartCurvature(part) !== 0);
        const hasRotatedPart = parts.some((part) => this.normalizeRotation(part?.rotation || part?.angle || 0) !== 0);
        if (!parts.length || (parts.length <= 1 && !zone?.supplierGeometry && !hasCircularPart && !hasCurvedPart && !hasRotatedPart) || !placement?.width || !placement?.height) return [];

        return parts
          .map((part, index) => {
            const x = Number(part?.x);
            const y = Number(part?.y);
            const width = Number(part?.width);
            const height = Number(part?.height);
            if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return null;

            return {
              id: String(part?.id || `part-${index + 1}`),
              left: this.clampNumber(((x - placement.x) / placement.width) * 100, -100, 200),
              top: this.clampNumber(((y - placement.y) / placement.height) * 100, -100, 200),
              width: this.clampNumber((width / placement.width) * 100, 0.1, 300),
              height: this.clampNumber((height / placement.height) * 100, 0.1, 300),
              rotation: this.normalizeRotation(part?.rotation || part?.angle || 0),
              shape: String(part?.shape || '').toLowerCase() === 'circle' ? 'circle' : 'rectangle',
              ...(this.getDesignPartCurvature(part) ? { curvature: this.getDesignPartCurvature(part) } : {}),
            };
          })
          .filter(Boolean);
      }

      getDesignPartCurvature(part) {
        return this.clampNumber(Number(part?.curvature || 0), -40, 40);
      }

      formatSvgNumber(value) {
        const number = Number(value);
        if (!Number.isFinite(number)) return '0';
        return number.toFixed(4).replace(/\.?0+$/, '');
      }

      getDesignZonePartPath(part) {
        const left = Number(part?.left || 0);
        const top = Number(part?.top || 0);
        const width = Number(part?.width || 0);
        const height = Number(part?.height || 0);
        if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return '';

        const rotation = this.normalizeRotation(part?.rotation || 0);
        if (part?.shape === 'circle') {
          const centerX = left + width / 2;
          const centerY = top + height / 2;
          const radians = (rotation * Math.PI) / 180;
          const cos = Math.cos(radians);
          const sin = Math.sin(radians);
          const points = Array.from({ length: 48 }, (_, index) => {
            const angle = (index / 48) * Math.PI * 2;
            const x = Math.cos(angle) * width / 2;
            const y = Math.sin(angle) * height / 2;
            return {
              x: centerX + x * cos - y * sin,
              y: centerY + x * sin + y * cos,
            };
          });
          return [
            `M ${this.formatSvgNumber(points[0].x)} ${this.formatSvgNumber(points[0].y)}`,
            ...points.slice(1).map((point) => `L ${this.formatSvgNumber(point.x)} ${this.formatSvgNumber(point.y)}`),
            'Z',
          ].join(' ');
        }
        const curvature = this.getDesignPartCurvature(part);
        if (!rotation && !curvature) {
          const right = left + width;
          const bottom = top + height;
          return [
            `M ${this.formatSvgNumber(left)} ${this.formatSvgNumber(top)}`,
            `H ${this.formatSvgNumber(right)}`,
            `V ${this.formatSvgNumber(bottom)}`,
            `H ${this.formatSvgNumber(left)}`,
            'Z',
          ].join(' ');
        }

        const centerX = left + width / 2;
        const centerY = top + height / 2;
        const radians = (rotation * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const rotatePoint = (x, y) => ({
          x: centerX + (x - centerX) * cos - (y - centerY) * sin,
          y: centerY + (x - centerX) * sin + (y - centerY) * cos,
        });
        if (curvature) {
          const bend = -(curvature / 40) * height * 0.08;
          const topLeft = rotatePoint(left, top);
          const topMiddle = rotatePoint(centerX, top + bend * 2);
          const topRight = rotatePoint(left + width, top);
          const bottomRight = rotatePoint(left + width, top + height);
          const bottomMiddle = rotatePoint(centerX, top + height + bend * 2);
          const bottomLeft = rotatePoint(left, top + height);
          return [
            `M ${this.formatSvgNumber(topLeft.x)} ${this.formatSvgNumber(topLeft.y)}`,
            `Q ${this.formatSvgNumber(topMiddle.x)} ${this.formatSvgNumber(topMiddle.y)} ${this.formatSvgNumber(topRight.x)} ${this.formatSvgNumber(topRight.y)}`,
            `L ${this.formatSvgNumber(bottomRight.x)} ${this.formatSvgNumber(bottomRight.y)}`,
            `Q ${this.formatSvgNumber(bottomMiddle.x)} ${this.formatSvgNumber(bottomMiddle.y)} ${this.formatSvgNumber(bottomLeft.x)} ${this.formatSvgNumber(bottomLeft.y)}`,
            'Z',
          ].join(' ');
        }
        const corners = [
          [-width / 2, -height / 2],
          [width / 2, -height / 2],
          [width / 2, height / 2],
          [-width / 2, height / 2],
        ].map(([x, y]) => ({
          x: centerX + x * cos - y * sin,
          y: centerY + x * sin + y * cos,
        }));

        return [
          `M ${this.formatSvgNumber(corners[0].x)} ${this.formatSvgNumber(corners[0].y)}`,
          ...corners.slice(1).map((corner) => `L ${this.formatSvgNumber(corner.x)} ${this.formatSvgNumber(corner.y)}`),
          'Z',
        ].join(' ');
      }

      getDesignZonePartGridPath(part, ratios = [0.25, 0.5, 0.75]) {
        const left = Number(part?.left || 0);
        const top = Number(part?.top || 0);
        const width = Number(part?.width || 0);
        const height = Number(part?.height || 0);
        if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return '';

        const centerX = left + width / 2;
        const centerY = top + height / 2;
        const radians = (this.normalizeRotation(part?.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const rotatePoint = (x, y) => ({
          x: centerX + (x - centerX) * cos - (y - centerY) * sin,
          y: centerY + (x - centerX) * sin + (y - centerY) * cos,
        });
        const curvature = this.getDesignPartCurvature(part);
        if (curvature) {
          const bend = -(curvature / 40) * height * 0.08;
          const curvedPaths = [];
          for (const ratio of ratios) {
            const verticalOffset = bend * Math.sin(Math.PI * ratio);
            const horizontalY = top + height * ratio;
            const normalizedOffset = (ratio - 0.5) * 2;
            const chordRatio = part?.shape === 'circle'
              ? Math.sqrt(Math.max(0, 1 - (normalizedOffset ** 2)))
              : 1;
            const verticalHalf = (height / 2) * chordRatio;
            const horizontalHalf = (width / 2) * chordRatio;
            const verticalX = left + width * ratio;
            const verticalStart = rotatePoint(verticalX, centerY - verticalHalf + verticalOffset);
            const verticalEnd = rotatePoint(verticalX, centerY + verticalHalf + verticalOffset);
            const horizontalStart = rotatePoint(centerX - horizontalHalf, horizontalY);
            const horizontalMiddle = rotatePoint(centerX, horizontalY + bend * 2 * chordRatio);
            const horizontalEnd = rotatePoint(centerX + horizontalHalf, horizontalY);
            curvedPaths.push(
              `M ${this.formatSvgNumber(verticalStart.x)} ${this.formatSvgNumber(verticalStart.y)} L ${this.formatSvgNumber(verticalEnd.x)} ${this.formatSvgNumber(verticalEnd.y)}`,
              `M ${this.formatSvgNumber(horizontalStart.x)} ${this.formatSvgNumber(horizontalStart.y)} Q ${this.formatSvgNumber(horizontalMiddle.x)} ${this.formatSvgNumber(horizontalMiddle.y)} ${this.formatSvgNumber(horizontalEnd.x)} ${this.formatSvgNumber(horizontalEnd.y)}`,
            );
          }
          return curvedPaths.join(' ');
        }
        const gridLines = [];

        for (const ratio of ratios) {
          if (part?.shape === 'circle') {
            const normalizedOffset = (ratio - 0.5) * 2;
            const chordRatio = Math.sqrt(Math.max(0, 1 - (normalizedOffset ** 2)));
            const verticalX = left + width * ratio;
            const verticalHalf = (height / 2) * chordRatio;
            const horizontalY = top + height * ratio;
            const horizontalHalf = (width / 2) * chordRatio;
            gridLines.push([
              rotatePoint(verticalX, centerY - verticalHalf),
              rotatePoint(verticalX, centerY + verticalHalf),
            ]);
            gridLines.push([
              rotatePoint(centerX - horizontalHalf, horizontalY),
              rotatePoint(centerX + horizontalHalf, horizontalY),
            ]);
          } else {
            gridLines.push([
              rotatePoint(left + width * ratio, top),
              rotatePoint(left + width * ratio, top + height),
            ]);
            gridLines.push([
              rotatePoint(left, top + height * ratio),
              rotatePoint(left + width, top + height * ratio),
            ]);
          }
        }

        return gridLines
          .map(([start, end]) => [
            `M ${this.formatSvgNumber(start.x)} ${this.formatSvgNumber(start.y)}`,
            `L ${this.formatSvgNumber(end.x)} ${this.formatSvgNumber(end.y)}`,
          ].join(' '))
          .join(' ');
      }

      getUnifiedDesignZoneGridPath(parts, ratios = [0.25, 0.5, 0.75]) {
        if (parts.length < 2 || parts.some((part) =>
          part.shape === 'circle' || this.getDesignPartCurvature(part) || this.normalizeRotation(part.rotation || 0)
        )) return null;

        const rects = parts.map((part) => ({
          left: part.left,
          top: part.top,
          right: part.left + part.width,
          bottom: part.top + part.height,
        }));
        const epsilon = 1e-7;
        // Only join a continuous surface. A shared corner alone does not connect it.
        const connected = new Set([0]);
        const pending = [0];
        while (pending.length) {
          const current = rects[pending.pop()];
          rects.forEach((rect, index) => {
            if (connected.has(index)) return;
            const overlapX = Math.min(current.right, rect.right) - Math.max(current.left, rect.left);
            const overlapY = Math.min(current.bottom, rect.bottom) - Math.max(current.top, rect.top);
            if (overlapX < -epsilon || overlapY < -epsilon || (overlapX <= epsilon && overlapY <= epsilon)) return;
            connected.add(index);
            pending.push(index);
          });
        }
        if (connected.size !== rects.length) return null;

        const left = Math.min(...rects.map((rect) => rect.left));
        const right = Math.max(...rects.map((rect) => rect.right));
        const top = Math.min(...rects.map((rect) => rect.top));
        const bottom = Math.max(...rects.map((rect) => rect.bottom));
        const paths = [];
        const addLine = (position, vertical) => {
          const intervals = rects
            .filter((rect) => vertical
              ? position >= rect.left - epsilon && position <= rect.right + epsilon
              : position >= rect.top - epsilon && position <= rect.bottom + epsilon)
            .map((rect) => vertical ? [rect.top, rect.bottom] : [rect.left, rect.right])
            .sort((a, b) => a[0] - b[0]);
          const merged = [];
          intervals.forEach(([start, end]) => {
            const previous = merged[merged.length - 1];
            if (previous && start <= previous[1] + epsilon) previous[1] = Math.max(previous[1], end);
            else merged.push([start, end]);
          });
          merged.forEach(([start, end]) => {
            const points = vertical ? [position, start, position, end] : [start, position, end, position];
            const [x1, y1, x2, y2] = points.map((value) => this.formatSvgNumber(value));
            paths.push(`M ${x1} ${y1} L ${x2} ${y2}`);
          });
        };
        for (const ratio of ratios) {
          addLine(left + (right - left) * ratio, true);
          addLine(top + (bottom - top) * ratio, false);
        }
        return paths.join(' ');
      }

      getDesignZoneBoundaryPath(parts, fallbackPaths = []) {
        if (parts.some((part) => part?.shape === 'circle' || this.getDesignPartCurvature(part))) return fallbackPaths.join(' ');
        const axisAligned = parts.every((part) => Math.abs(this.normalizeRotation(part?.rotation || 0)) < 0.01);
        if (!axisAligned) return fallbackPaths.join(' ');

        const rects = parts
          .map((part) => {
            const left = this.clampNumber(part.left, -100, 200);
            const top = this.clampNumber(part.top, -100, 200);
            const right = this.clampNumber(part.left + part.width, -100, 200);
            const bottom = this.clampNumber(part.top + part.height, -100, 200);
            return right > left && bottom > top ? { left, top, right, bottom } : null;
          })
          .filter(Boolean);
        if (!rects.length) return fallbackPaths.join(' ');

        const uniqueSorted = (values) => [...new Set(values.map((value) => Number(value.toFixed(4))))].sort((a, b) => a - b);
        const xs = uniqueSorted(rects.flatMap((rect) => [rect.left, rect.right]));
        const ys = uniqueSorted(rects.flatMap((rect) => [rect.top, rect.bottom]));
        const occupied = (xIndex, yIndex) => {
          if (xIndex < 0 || yIndex < 0 || xIndex >= xs.length - 1 || yIndex >= ys.length - 1) return false;
          const centerX = (xs[xIndex] + xs[xIndex + 1]) / 2;
          const centerY = (ys[yIndex] + ys[yIndex + 1]) / 2;
          return rects.some((rect) => centerX > rect.left && centerX < rect.right && centerY > rect.top && centerY < rect.bottom);
        };
        const segmentPath = [];

        for (let yIndex = 0; yIndex < ys.length - 1; yIndex += 1) {
          for (let xIndex = 0; xIndex < xs.length - 1; xIndex += 1) {
            if (!occupied(xIndex, yIndex)) continue;

            const left = xs[xIndex];
            const right = xs[xIndex + 1];
            const top = ys[yIndex];
            const bottom = ys[yIndex + 1];

            if (!occupied(xIndex, yIndex - 1)) {
              segmentPath.push(`M ${this.formatSvgNumber(left)} ${this.formatSvgNumber(top)} L ${this.formatSvgNumber(right)} ${this.formatSvgNumber(top)}`);
            }
            if (!occupied(xIndex + 1, yIndex)) {
              segmentPath.push(`M ${this.formatSvgNumber(right)} ${this.formatSvgNumber(top)} L ${this.formatSvgNumber(right)} ${this.formatSvgNumber(bottom)}`);
            }
            if (!occupied(xIndex, yIndex + 1)) {
              segmentPath.push(`M ${this.formatSvgNumber(right)} ${this.formatSvgNumber(bottom)} L ${this.formatSvgNumber(left)} ${this.formatSvgNumber(bottom)}`);
            }
            if (!occupied(xIndex - 1, yIndex)) {
              segmentPath.push(`M ${this.formatSvgNumber(left)} ${this.formatSvgNumber(bottom)} L ${this.formatSvgNumber(left)} ${this.formatSvgNumber(top)}`);
            }
          }
        }

        return segmentPath.join(' ') || fallbackPaths.join(' ');
      }

      getRecommendedDesignGuideParts(zone, parts, artworkPlacement = {}) {
        if (parts.length <= 2 || parts.some(part => part.shape !== 'rectangle' || Number(part.rotation || 0) !== 0 || Number(part.curvature || 0) !== 0)) return parts;
        // Connected parts describe one compound contour. Never simplify them.
        const touches = (a, b) => a.left <= b.left + b.width + 0.001 && a.left + a.width >= b.left - 0.001
          && a.top <= b.top + b.height + 0.001 && a.top + a.height >= b.top - 0.001;
        if (parts.some((part, index) => parts.slice(index + 1).some(other => touches(part, other)))) return parts;
        const used = new Set((this.getDesignArtworkItems(zone) || []).filter(item => item.fileName)
          .map(item => item.placement?.partId).filter(Boolean));
        const ranked = [...parts].sort((a, b) => Number(b.id === artworkPlacement.partId) - Number(a.id === artworkPlacement.partId)
          || Number(used.has(b.id)) - Number(used.has(a.id)) || b.width * b.height - a.width * a.height);
        const recommended = new Set(ranked.slice(0, 2).map(part => part.id));
        return parts.filter(part => recommended.has(part.id) || used.has(part.id));
      }

      renderDesignZoneParts(overlay, zone, placement, artworkPlacement = {}, { gridRatios } = {}) {
        const zoneControl = overlay?.querySelector('[data-role="product-design-zone"]');
        const partsLayer = overlay?.querySelector('[data-role="product-design-zone-parts"]');
        if (!zoneControl || !partsLayer) return;

        const allParts = this.getDesignZoneParts(zone, placement);
        const parts = this.getRecommendedDesignGuideParts(zone, allParts, artworkPlacement);
        partsLayer.dataset.recommendedPartCount = String(Math.min(parts.length, 2));
        partsLayer.dataset.displayedPartCount = String(parts.length);
        const hasRotatedParts = parts.some((part) => part.rotation !== 0);
        overlay.classList?.toggle('has-rotated-zone-parts', hasRotatedParts);
        zoneControl.classList.toggle('has-rotated-zone-parts', hasRotatedParts);
        // Rotate in the same pixel metric as the artwork clip and canvas export.
        // The saved shell remains the coordinate frame, including any overflowing corners.
        const shellRect = hasRotatedParts
          ? overlay.querySelector('[data-role="product-design-zone-shell"]')?.getBoundingClientRect()
          : null;
        const viewWidth = shellRect?.width || 100;
        const viewHeight = shellRect?.height || 100;
        const renderParts = hasRotatedParts
          ? parts.map((part) => ({
            ...part,
            left: part.left * viewWidth / 100,
            top: part.top * viewHeight / 100,
            width: part.width * viewWidth / 100,
            height: part.height * viewHeight / 100,
          }))
          : parts;
        const paths = renderParts.map((part) => this.getDesignZonePartPath(part)).filter(Boolean);
        const unifiedGridPath = this.getUnifiedDesignZoneGridPath(renderParts, gridRatios);
        const hasUnifiedParts = unifiedGridPath !== null;
        const gridPath = unifiedGridPath ?? renderParts.map((part) => this.getDesignZonePartGridPath(part, gridRatios)).filter(Boolean).join(' ');
        zoneControl.classList.toggle('has-zone-parts', paths.length > 0);
        zoneControl.classList.toggle('has-qualified-xd-zone-parts', paths.length > 0 && this.isQualifiedXDDesignZone(zone));
        zoneControl.classList.toggle('has-unified-zone-parts', hasUnifiedParts);

        if (!paths.length) {
          partsLayer.hidden = true;
          partsLayer.innerHTML = '';
          delete partsLayer.dataset.renderSignature;
          return;
        }

        const boundaryPath = hasRotatedParts ? paths.join(' ') : this.getDesignZoneBoundaryPath(parts, paths);
        const mergedPath = paths.join(' ');
        const activePart =
          parts.find((part) => part.id === artworkPlacement.partId) ||
          this.selectDesignZonePart(parts, artworkPlacement, { preferExistingPart: false }) ||
          parts[0];
        const activePartPath = hasUnifiedParts
          ? ''
          : this.getDesignZonePartPath(renderParts.find((part) => part.id === activePart?.id));
        const renderSignature = [
          viewWidth,
          viewHeight,
          mergedPath,
          gridPath,
          boundaryPath,
          activePartPath,
          activePart?.id || '',
        ].join('|');
        if (partsLayer.dataset.renderSignature === renderSignature) return;

        partsLayer.hidden = false;
        partsLayer.dataset.renderSignature = renderSignature;
        partsLayer.innerHTML = `
          <svg class="product__design-zone-shape-svg" viewBox="0 0 ${this.formatSvgNumber(viewWidth)} ${this.formatSvgNumber(viewHeight)}" preserveAspectRatio="none" aria-hidden="true" focusable="false">
            <path class="product__design-zone-shape-fill" d="${this.escapeAttr(mergedPath)}"></path>
            <path class="product__design-zone-shape-grid" d="${this.escapeAttr(gridPath)}"></path>
            <path class="product__design-zone-shape-stroke" d="${this.escapeAttr(boundaryPath)}"></path>
            <path class="product__design-zone-shape-active-part" d="${this.escapeAttr(activePartPath)}"></path>
          </svg>
        `;
      }

      ensureDesignStudioOverlay(slide) {
        const cachedOverlay = this.designStudioOverlays.get(slide);
        if (cachedOverlay) {
          this.observeDesignStudioContainer(cachedOverlay.parentElement);
          return cachedOverlay;
        }

        const container = slide.querySelector('.product__media-item-container');
        const cornerHandles = ['nw', 'ne', 'se', 'sw']
          .map(
            (handle) =>
              `<button type="button" class="product__design-artwork-handle product__design-artwork-handle--corner product__design-artwork-handle--${handle}" data-role="product-design-handle" data-design-handle="${handle}" aria-label="${this.escapeAttr(this.string(`resize${handle.toUpperCase()}`))}"></button>`,
          )
          .join('');
        const sideHandles = ['n', 'e', 's', 'w']
          .map(
            (handle) =>
              `<button type="button" class="product__design-artwork-handle product__design-artwork-handle--side product__design-artwork-handle--${handle}" data-role="product-design-handle" data-design-handle="${handle}" aria-label="${this.escapeAttr(this.string(`crop${handle.toUpperCase()}`))}"></button>`,
          )
          .join('');
        const label = (key) => this.escapeAttr(this.string(key));
        const overlay = document.createElement('div');
        overlay.className = 'product__design-studio';
        overlay.dataset.role = 'product-design-studio';
        overlay.hidden = true;
        overlay.innerHTML = `
          <button type="button" class="product__design-download product__design-share" data-role="product-design-share" aria-label="${label('shareDesign')}" title="${label('shareDesign')}" aria-haspopup="dialog" hidden>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4"></path></svg>
          </button>
          <button type="button" class="product__design-download" data-role="product-design-download" aria-label="${label('downloadPreview')}" title="${label('downloadPreview')}" hidden>
            <svg class="lucide lucide-download" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" x2="12" y1="15" y2="3"></line></svg>
          </button>
          ${this.getDesignImageZoomMarkup()}
          <div class="product__design-visual-layer" data-role="product-design-visual-layer">
            <div class="product__design-zone-shell" data-role="product-design-zone-shell">
              <div class="product__design-zone" data-role="product-design-zone" role="button" tabindex="0">
              <span class="product__design-zone-grid" aria-hidden="true"></span>
              <span class="product__design-zone-parts" data-role="product-design-zone-parts" aria-hidden="true" hidden></span>
              <span class="product__design-alignment-guides" data-role="product-design-alignment-guides" aria-hidden="true" hidden></span>
              <div class="product__design-toolbar" data-role="product-design-toolbar" role="toolbar" aria-label="${label('toolbarLabel')}" hidden>
                <button type="button" data-role="product-design-tool" data-design-tool="smaller" aria-label="${label('smallerDescription')}" data-description="${label('smallerLabel')}">
                  <svg class="lucide lucide-minimize-2" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="4 14 10 14 10 20"></polyline><polyline points="20 10 14 10 14 4"></polyline><line x1="14" x2="21" y1="10" y2="3"></line><line x1="3" x2="10" y1="21" y2="14"></line></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="larger" aria-label="${label('largerDescription')}" data-description="${label('largerLabel')}">
                  <svg class="lucide lucide-maximize-2" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" x2="14" y1="3" y2="10"></line><line x1="3" x2="10" y1="21" y2="14"></line></svg>
                </button>
                <span class="product__design-toolbar-divider" aria-hidden="true"></span>
                <button type="button" data-role="product-design-tool" data-design-tool="rotate" aria-label="${label('rotateLabel')}" data-description="${label('rotateLabel')}" hidden>
                  <svg class="lucide lucide-rotate-cw" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="undo" aria-label="${label('undoDescription')}" data-description="${label('undoLabel')}">
                  <svg class="lucide lucide-undo-2" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 14 4 9l5-5"></path><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5A5.5 5.5 0 0 1 14.5 20H11"></path></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="redo" aria-label="${label('redoDescription')}" data-description="${label('redoLabel')}">
                  <svg class="lucide lucide-redo-2" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 14 5-5-5-5"></path><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5 5.5 5.5 0 0 0 9.5 20H13"></path></svg>
                </button>
                <span class="product__design-toolbar-divider" aria-hidden="true"></span>
                <button type="button" data-role="product-design-tool" data-design-tool="colors" aria-label="${label('colorsLabel')}" data-description="${label('colorsLabel')}" aria-haspopup="dialog" aria-expanded="false">
                  <svg class="lucide lucide-palette" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3a9 9 0 1 0 0 18h1.5a2.5 2.5 0 0 0 0-5H13a1.5 1.5 0 0 1 0-3h2.5A5.5 5.5 0 0 0 21 7.5C21 5 17 3 12 3Z"></path><circle cx="7.5" cy="10" r=".75"></circle><circle cx="11" cy="7" r=".75"></circle><circle cx="15.5" cy="7.5" r=".75"></circle><circle cx="7.5" cy="14.5" r=".75"></circle></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="remove-background" aria-label="${label('removeBackgroundDescription')}" data-description="${label('removeBackgroundLabel')}">
                  <svg class="lucide lucide-sparkles" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"></path><path d="M5 3v4"></path><path d="M19 17v4"></path><path d="M3 5h4"></path><path d="M17 19h4"></path></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="duplicate" aria-label="${label('duplicateDescription')}" data-description="${label('duplicateLabel')}">
                  <svg class="lucide lucide-copy" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg>
                </button>
                <button type="button" data-role="product-design-tool" data-design-tool="delete" aria-label="${label('deleteDescription')}" data-description="${label('deleteLabel')}">
                  <svg class="lucide lucide-trash-2" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg>
                </button>
              </div>
              <div class="product__design-artwork-copies" data-role="product-design-artwork-copies"></div>
              <div class="product__design-artwork" data-role="product-design-artwork" tabindex="0" aria-label="${label('artworkLabel')}" hidden>
                <span class="product__design-artwork-mask" aria-hidden="true">
                  <img data-role="product-design-artwork-image" alt="" width="320" height="220" hidden>
                  <span data-role="product-design-artwork-name"></span>
                </span>
                ${cornerHandles}
                ${sideHandles}
                <button type="button" class="product__design-artwork-handle product__design-artwork-handle--compact" data-role="product-design-handle" data-design-handle="se" aria-label="${label('resizeSE')}"></button>
                <button type="button" class="product__design-rotate-handle" data-role="product-design-rotate" aria-label="${label('rotateLabel')}">
                  <svg class="lucide lucide-rotate-cw" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path><path d="M21 3v5h-5"></path></svg>
                </button>
              </div>
              <span class="product__design-zone-label" data-role="product-design-zone-label"></span>
            </div>
          </div>
          </div>
        `;

        const toolbar = overlay.querySelector('[data-role="product-design-toolbar"]');
        if (toolbar) overlay.append(toolbar);

        overlay.addEventListener('click', (event) => {
          if (this.consumeCompletedDesignDragClick(event, overlay)) return;
          const imageZoomButton = event.target.closest('[data-design-image-zoom-action]');
          if (imageZoomButton) {
            return this.handleDesignImageZoom(event, imageZoomButton.dataset.designImageZoomAction);
          }

          const viewButton = event.target.closest('[data-role="product-design-view"]');
          if (viewButton) return this.handleDesignViewSwitch(event, overlay, viewButton.dataset.designView, viewButton.dataset.zoneKey, viewButton.dataset.placementKey, viewButton.dataset.profileKey);

          const shareButton = event.target.closest('[data-role="product-design-share"]');
          if (shareButton) {
            event.preventDefault();
            event.stopPropagation();
            this.dispatchEvent(new CustomEvent('todaymerch:product-design-share', {
              bubbles: true,
              detail: { gallery: this, trigger: shareButton },
            }));
            return;
          }

          const downloadButton = event.target.closest('[data-role="product-design-download"]');
          if (downloadButton) return this.handleDesignPreviewDownload(event, overlay, downloadButton);

          const toolButton = event.target.closest('[data-role="product-design-tool"]');
          if (toolButton) return this.handleDesignArtworkTool(event, overlay, toolButton.dataset.designTool);
          if (event.target.closest('[data-role="product-design-toolbar"]')) return;

          if (event.target.closest('[data-role="product-design-handle"]') || event.target.closest('[data-role="product-design-rotate"]')) return;

          const zoneControl = event.target.closest('[data-role="product-design-zone"]');
          if (!zoneControl) {
            event.preventDefault();
            event.stopPropagation();
            this.setDesignArtworkSelected(overlay, false);
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          this.setDesignArtworkSelected(overlay, true);
          this.dispatchEvent(
            new CustomEvent('todaymerch:product-design-zone-selected', {
              bubbles: true,
              detail: {
                source: 'media-gallery',
                zoneKey: zoneControl.dataset.zoneKey || overlay.dataset.zoneKey || '',
                viewKey: overlay.dataset.viewKey || '',
              },
            }),
          );
        });

        overlay.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          if (event.target.closest('[data-role="product-design-rotate"]')) {
            return this.handleDesignArtworkTool(event, overlay, 'rotate');
          }
          const zoneControl = event.target.closest('[data-role="product-design-zone"]');
          if (!zoneControl) return;

          event.preventDefault();
          this.setDesignArtworkSelected(overlay, true);
          this.dispatchEvent(
            new CustomEvent('todaymerch:product-design-zone-selected', {
              bubbles: true,
              detail: {
                source: 'media-gallery',
                zoneKey: zoneControl.dataset.zoneKey || overlay.dataset.zoneKey || '',
                viewKey: overlay.dataset.viewKey || '',
              },
            }),
          );
        });

        overlay
          .querySelector('[data-role="product-design-artwork"]')
          ?.addEventListener('pointerdown', (event) => this.startDesignArtworkDrag(event, overlay));
        overlay.querySelectorAll('[data-role="product-design-handle"]').forEach((handle) => {
          handle.addEventListener('pointerdown', (event) => {
            this.startDesignArtworkResize(event, overlay, handle.dataset.designHandle);
          });
        });
        overlay
          .querySelector('[data-role="product-design-rotate"]')
          ?.addEventListener('pointerdown', (event) => this.startDesignArtworkRotation(event, overlay));

        container?.append(overlay);
        if (slide.dataset.mobileDesignSurface !== 'true') this.observeDesignStudioContainer(container);
        this.designStudioOverlays.set(slide, overlay);
        return overlay;
      }

      toggleSliderDraggableState() {
        const slider = this.sliderInstance?.slider;
        if (!slider || slider.destroyed) return;

        if (slider.params) {
          slider.params.grabCursor = false;
          slider.params.allowTouchMove = false;
          slider.params.simulateTouch = false;
        }

        if (slider.allowTouchMove !== false) {
          slider.allowTouchMove = false;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;
        this.completedVariantMediaNotification = null;

        const variantId = String(variant.id || this.getCurrentVariantId() || '').trim();
        if (variantId) {
          this.activeVariantId = variantId;
          const configuredAssignments = this.getVariantMediaAssignments(variantId, variant);
          if (
            configuredAssignments.length &&
            this.applyVariantMediaForVariant(variantId, variant, { forceSlide: true })
          ) {
            // Product-info announces this same successful application through
            // document and pubsub synchronously. Later responses/binding refreshes
            // must still apply, even when their variant id has not changed.
            const completed = { variant, variantId, studioConfig: this.todayStudioConfig, variantGallery: this.todayVariantGallery };
            this.completedVariantMediaNotification = completed;
            Promise.resolve().then(() => {
              if (this.completedVariantMediaNotification === completed) this.completedVariantMediaNotification = null;
            });
            return;
          }
        }

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slides = [...this.querySelectorAll('.product__media-item')];
          const featuredMediaId = String(featuredMedia.id || '').trim();
          const targetSlide = featuredMediaId
            ? slides.find((slide) => String(slide.dataset.mediaId || '').trim() === featuredMediaId)
            : null;
          const fallbackIndex = Math.max((Number(featuredMedia.position) || 1) - 1, 0);
          const targetIndex = targetSlide ? slides.indexOf(targetSlide) : fallbackIndex;
          this.selectedMediaIndex = targetIndex;
          this.sliderInstance.slider.slideTo(targetIndex, 0, false);
          this.syncThumbActiveState(targetIndex, targetSlide);
          this.renderDesignStudio();
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });
        this.refreshDesignStudioMediaMap();
        this.renderDesignStudio();

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
